// This file is auto-generated by @hey-api/openapi-ts

export type ClientOptions = {
    baseUrl: `${string}://${string}` | (string & {});
};

/**
 * A1CertificateListResponse
 *
 * Every certificate the platform holds, newest upload first.
 *
 * Superseded and expired ones included, and that is deliberate: "what did we
 * sign April's filings with?" is a question about a certificate that is no
 * longer current, and a list that hid them would make the store look like it
 * had forgotten.
 */
export type A1CertificateListResponse = {
    /**
     * Count
     *
     * Number of items in `items`.
     */
    count: number;
    /**
     * Items
     */
    items: Array<A1CertificateResponse>;
};

/**
 * A1CertificateResponse
 *
 * One stored certificate: who holds it, until when, and whether it counts.
 *
 * ``status`` is derived at read time from the validity window, the
 * deactivation stamp and whether a later upload replaced this one. It is not a
 * column: a stored status is a status that is wrong from the moment a
 * certificate expires until something happens to notice.
 */
export type A1CertificateResponse = {
    /**
     * Deactivated At
     *
     * When somebody stopped it without replacing it. `null` otherwise.
     */
    deactivated_at?: string | null;
    /**
     * Deactivated By Display Name
     */
    deactivated_by_display_name?: string | null;
    /**
     * Fingerprint Sha256
     *
     * SHA-256 of the certificate's DER encoding, lower-case hex. Not a secret -- it is a digest of the public half -- and it is what an operator compares against the authority's own portal.
     */
    fingerprint_sha256: string;
    /**
     * Id
     */
    id: string;
    /**
     * Issuer Name
     *
     * The certification authority that issued it.
     */
    issuer_name: string;
    /**
     * Not Valid After
     */
    not_valid_after: string;
    /**
     * Not Valid Before
     */
    not_valid_before: string;
    /**
     * Serial Number
     *
     * The certificate's serial number, hexadecimal, as every other tool prints it.
     */
    serial_number: string;
    /**
     * `active`, `expiring_soon` (inside 30 days of expiry), `expired`, `superseded` (a later upload for the same CNPJ replaced it) or `deactivated` (stopped by hand, with no replacement). Derived on every read.
     */
    status: CertificateStatus;
    /**
     * Subject Cnpj
     *
     * The fourteen digits the certificate is filed under, unpunctuated.
     */
    subject_cnpj: string;
    /**
     * Subject Cnpj Formatted
     *
     * The same digits written the way a Brazilian reads them.
     */
    subject_cnpj_formatted: string;
    /**
     * Subject Name
     *
     * The holder, as the certificate's subject says -- with the `:CNPJ` suffix ICP-Brasil appends removed, because the digits have a field of their own.
     */
    subject_name: string;
    /**
     * Superseded By Certificate Id
     *
     * The certificate that replaced this one. `null` while it is the current one for its CNPJ. Read off the replacement rather than stored here, so a superseded row is never written to.
     */
    superseded_by_certificate_id?: string | null;
    /**
     * Supersedes Certificate Id
     *
     * The certificate this one replaced, if it replaced one.
     */
    supersedes_certificate_id?: string | null;
    /**
     * Uploaded At
     */
    uploaded_at: string;
    /**
     * Uploaded By Display Name
     *
     * The uploader's email, kept on the row so the record survives the account.
     */
    uploaded_by_display_name: string;
    /**
     * Uploaded By User Id
     */
    uploaded_by_user_id: string;
};

/**
 * AdmissionSubmission
 *
 * Put somebody on the contract from `effective_date`, which is their first covered day.
 */
export type AdmissionSubmission = {
    /**
     * Action
     */
    action: 'admission';
    /**
     * Birth Date
     */
    birth_date: string;
    /**
     * Cpf
     *
     * The person's CPF. Punctuation is ignored, so `123.456.789-09` and `12345678909` are the same number. Both check digits are verified; a mistyped or transposed digit is rejected rather than stored.
     */
    cpf: string;
    /**
     * Effective Date
     *
     * Calendar date the change takes effect, `YYYY-MM-DD`, in America/Sao_Paulo. Mandatory for every action. It may fall in a future month, in the current cycle, or in the past -- a date before the last snapshot sent to the insurer is accepted and flagged as retroactive.
     */
    effective_date: string;
    /**
     * Full Name
     */
    full_name: string;
    /**
     * Salary
     *
     * Monthly salary, to at most two decimal places. Send it as a JSON string to be certain no precision is lost in transit; it is stored and returned as an exact decimal and never as a binary float.
     */
    salary: number | string;
};

/**
 * Artefact
 *
 * Which sealed artefact of a stage somebody asks for.
 *
 * One word in the URL, in the storage key and at the tail of the context.
 */
export const Artefact = {
    SCREENSHOT: 'screenshot',
    HTML: 'html',
    FILE: 'file',
    TRACE: 'trace'
} as const;

/**
 * Artefact
 *
 * Which sealed artefact of a stage somebody asks for.
 *
 * One word in the URL, in the storage key and at the tail of the context.
 */
export type Artefact = typeof Artefact[keyof typeof Artefact];

/**
 * AttachmentOutcome
 *
 * What became of one attached file.
 */
export const AttachmentOutcome = {
    SPREADSHEET: 'spreadsheet',
    DOCUMENT: 'document',
    IGNORED: 'ignored',
    REJECTED: 'rejected'
} as const;

/**
 * AttachmentOutcome
 *
 * What became of one attached file.
 */
export type AttachmentOutcome = typeof AttachmentOutcome[keyof typeof AttachmentOutcome];

/**
 * AuditAction
 *
 * What happened. Values are part of the read API's contract.
 *
 * Three of them describe a change to a record, the next one a customer company's
 * registry number, the next five an economic group and which companies are in
 * it, the next three a setting of a company or a group -- its file-name labels
 * and its termination-document policy -- the next eight an authentication,
 * session or credential event, the next six what happened to a roster delta, the next
 * two the monthly snapshot, the two after them the review gate over it, the
 * three after those the submission at the end of the cycle and the three after
 * *those* the off-cycle notice beside it, then three a spreadsheet upload, five
 * the mail bridge that feeds one, six what was done with a supporting
 * document, three the platform's own signing certificate, six the bridge to
 * eSocial, one what a customer was told, and the last two this deployment's
 * simulated clock being moved and put back. They
 * share one enumeration because they share one table: "who signed in at 09:02
 * and what did they change at 09:03?" is one question, and answering it from
 * two stores is how the second one ends up incomplete.
 */
export const AuditAction = {
    CREATE: 'create',
    UPDATE: 'update',
    DELETE: 'delete',
    COMPANY_CNPJ_SET: 'company.cnpj_set',
    COMPANY_GROUP_CREATED: 'company_group.created',
    COMPANY_GROUP_RENAMED: 'company_group.renamed',
    COMPANY_GROUP_ASSIGNED: 'company.group_assigned',
    COMPANY_GROUP_MOVED: 'company.group_moved',
    COMPANY_GROUP_REMOVED: 'company.group_removed',
    COMPANY_FILE_NAME_LABEL_SET: 'company.file_name_label_set',
    COMPANY_GROUP_FILE_NAME_LABEL_SET: 'company_group.file_name_label_set',
    COMPANY_TERMINATION_DOCUMENT_POLICY_SET: 'company.termination_document_policy_set',
    SIGN_IN_SUCCEEDED: 'sign_in.succeeded',
    SIGN_IN_FAILED: 'sign_in.failed',
    MACHINE_CLIENT_AUTHENTICATED: 'machine_client.authenticated',
    CREDENTIAL_ISSUED: 'credential.issued',
    CREDENTIAL_ROTATED: 'credential.rotated',
    CREDENTIAL_REVOKED: 'credential.revoked',
    REFRESH_TOKEN_ISSUED: 'refresh_token.issued',
    REFRESH_TOKEN_REUSED: 'refresh_token.reused',
    SIGN_OUT_SUCCEEDED: 'sign_out.succeeded',
    ROSTER_DELTA_SUBMITTED: 'roster_delta.submitted',
    ROSTER_DELTA_APPROVED: 'roster_delta.approved',
    ROSTER_DELTA_REJECTED: 'roster_delta.rejected',
    ROSTER_DELTA_CANCELLED: 'roster_delta.cancelled',
    ROSTER_DELTA_RETURNED: 'roster_delta.returned',
    ROSTER_DELTA_DATE_CORRECTED: 'roster_delta.date_corrected',
    SNAPSHOT_GENERATED: 'snapshot.generated',
    SNAPSHOT_APPROVED: 'snapshot.approved',
    REVIEW_CYCLE_APPROVED: 'review_cycle.approved',
    REVIEW_CYCLE_REOPENED: 'review_cycle.reopened',
    SUBMISSION_PREPARED: 'submission.prepared',
    SUBMISSION_SENT: 'submission.sent',
    SUBMISSION_FAILED: 'submission.failed',
    CONSOLIDATED_SUBMISSION_PREPARED: 'consolidated_submission.prepared',
    CONSOLIDATED_SUBMISSION_SENT: 'consolidated_submission.sent',
    CONSOLIDATED_SUBMISSION_FAILED: 'consolidated_submission.failed',
    GROUP_SUBMISSION_PREPARED: 'group_submission.prepared',
    GROUP_SUBMISSION_SENT: 'group_submission.sent',
    GROUP_SUBMISSION_FAILED: 'group_submission.failed',
    OFF_CYCLE_NOTICE_RAISED: 'off_cycle_notice.raised',
    OFF_CYCLE_NOTICE_SENT: 'off_cycle_notice.sent',
    OFF_CYCLE_NOTICE_FAILED: 'off_cycle_notice.failed',
    SPREADSHEET_UPLOAD_SUBMITTED: 'spreadsheet_upload.submitted',
    SPREADSHEET_UPLOAD_COMMITTED: 'spreadsheet_upload.committed',
    SPREADSHEET_UPLOAD_ABSENCES_READ: 'spreadsheet_upload.absences_read',
    GROUP_SPREADSHEET_UPLOAD_SUBMITTED: 'group_spreadsheet_upload.submitted',
    GROUP_SPREADSHEET_UPLOAD_COMMITTED: 'group_spreadsheet_upload.committed',
    MAIL_MESSAGE_INGESTED: 'mail_message.ingested',
    MAIL_MESSAGE_REFUSED: 'mail_message.refused',
    MAIL_MESSAGE_DISCARDED: 'mail_message.discarded',
    MAILBOX_SENDER_REGISTERED: 'mailbox_sender.registered',
    MAILBOX_SENDER_REVOKED: 'mailbox_sender.revoked',
    DOCUMENT_UPLOADED: 'document.uploaded',
    DOCUMENT_LINKED: 'document.linked',
    DOCUMENT_UNLINKED: 'document.unlinked',
    DOCUMENT_DOWNLOADED: 'document.downloaded',
    DOCUMENT_SUPERSEDED: 'document.superseded',
    DOCUMENT_ACCESS_REFUSED: 'document.access_refused',
    CERTIFICATE_UPLOADED: 'certificate.uploaded',
    CERTIFICATE_SUPERSEDED: 'certificate.superseded',
    CERTIFICATE_DEACTIVATED: 'certificate.deactivated',
    ESOCIAL_DIAGNOSTIC_RAN: 'esocial.diagnostic_ran',
    ESOCIAL_EVENTS_FETCHED: 'esocial.events_fetched',
    ESOCIAL_EVENTS_COMMITTED: 'esocial.events_committed',
    ESOCIAL_PROBE_RAN: 'esocial.probe_ran',
    ESOCIAL_PROBE_EVIDENCE_READ: 'esocial.probe_evidence_read',
    ESOCIAL_WORKER_LIST_CAPTURED: 'esocial.worker_list_captured',
    ESOCIAL_PORTAL_WALK_RAN: 'esocial.portal_walk_ran',
    ESOCIAL_PORTAL_WALK_EVIDENCE_READ: 'esocial.portal_walk_evidence_read',
    ESOCIAL_PORTAL_WALK_BUNDLE_READ: 'esocial.portal_walk_bundle_read',
    ESOCIAL_PORTAL_SESSION_RAN: 'esocial.portal_session_ran',
    ESOCIAL_WORKER_REPORT_REQUESTED: 'esocial.worker_report_requested',
    ESOCIAL_WORKER_REPORT_COLLECTED: 'esocial.worker_report_collected',
    ESOCIAL_WORKER_REPORT_FILE_READ: 'esocial.worker_report_file_read',
    NOTIFICATION_DELIVERED: 'notification.delivered',
    CLOCK_SIMULATION_SET: 'clock.simulation_set',
    CLOCK_SIMULATION_CLEARED: 'clock.simulation_cleared'
} as const;

/**
 * AuditAction
 *
 * What happened. Values are part of the read API's contract.
 *
 * Three of them describe a change to a record, the next one a customer company's
 * registry number, the next five an economic group and which companies are in
 * it, the next three a setting of a company or a group -- its file-name labels
 * and its termination-document policy -- the next eight an authentication,
 * session or credential event, the next six what happened to a roster delta, the next
 * two the monthly snapshot, the two after them the review gate over it, the
 * three after those the submission at the end of the cycle and the three after
 * *those* the off-cycle notice beside it, then three a spreadsheet upload, five
 * the mail bridge that feeds one, six what was done with a supporting
 * document, three the platform's own signing certificate, six the bridge to
 * eSocial, one what a customer was told, and the last two this deployment's
 * simulated clock being moved and put back. They
 * share one enumeration because they share one table: "who signed in at 09:02
 * and what did they change at 09:03?" is one question, and answering it from
 * two stores is how the second one ends up incomplete.
 */
export type AuditAction = typeof AuditAction[keyof typeof AuditAction];

/**
 * AuditChannel
 *
 * How the change reached us.
 *
 * The same action taken through the API and taken by a nightly job are
 * different events to whoever is investigating one, so the channel is recorded
 * rather than inferred from the actor.
 */
export const AuditChannel = {
    API: 'api',
    CLI: 'cli',
    SCHEDULED: 'scheduled',
    BACKGROUND: 'background'
} as const;

/**
 * AuditChannel
 *
 * How the change reached us.
 *
 * The same action taken through the API and taken by a nightly job are
 * different events to whoever is investigating one, so the channel is recorded
 * rather than inferred from the actor.
 */
export type AuditChannel = typeof AuditChannel[keyof typeof AuditChannel];

/**
 * AuditEntryListResponse
 *
 * A page of the trail, most recent first.
 */
export type AuditEntryListResponse = {
    /**
     * Count
     *
     * Number of entries in this page.
     */
    count: number;
    /**
     * Items
     */
    items: Array<AuditEntryResponse>;
    /**
     * Limit
     *
     * The page size that was applied.
     */
    limit: number;
    /**
     * Offset
     *
     * How many entries were skipped.
     */
    offset: number;
};

/**
 * AuditEntryResponse
 *
 * One recorded thing that happened.
 */
export type AuditEntryResponse = {
    action: AuditAction;
    /**
     * Actor Id
     *
     * The user, machine client or system principal responsible. Never null: work with nobody behind it is attributed to the system principal.
     */
    actor_id: string;
    /**
     * What kind of principal acted.
     */
    actor_type: AuditPrincipalType;
    /**
     * Changed Fields
     *
     * Names of the fields this entry covers, sorted. The keys of `changes`, available without reading the values.
     */
    changed_fields: Array<string>;
    /**
     * Changes
     *
     * Each changed field, with its value before and after.
     */
    changes: {
        [key: string]: FieldChangeResponse;
    };
    /**
     * How the change reached the service.
     */
    channel: AuditChannel;
    /**
     * Client Address
     *
     * Address the request came from, when there was a request.
     */
    client_address?: string | null;
    /**
     * Company Id
     *
     * The customer company the change belongs to. `null` for something outside every tenancy, such as an insurer or a vyttech staff account.
     */
    company_id?: string | null;
    /**
     * Correlation Id
     *
     * Shared by every entry produced by one request or one scheduled run, so a change made up of several writes comes back as several entries of one id.
     */
    correlation_id: string;
    /**
     * Entity Id
     *
     * Identifier of the affected record. A UUID for everything with one.
     */
    entity_id: string;
    /**
     * Entity Type
     *
     * The kind of record affected, named as the domain names it.
     */
    entity_type: string;
    /**
     * Id
     */
    id: string;
    /**
     * Occurred At
     *
     * When it happened, in UTC.
     */
    occurred_at: string;
};

/**
 * AuditPrincipalType
 *
 * What kind of actor did it.
 *
 * The first three mirror :class:`~app.auth.principal.PrincipalType` value for
 * value, deliberately: an audit entry naming a kind of principal the
 * authentication layer cannot produce would be unreadable.
 *
 * The fourth kind of principal in this product -- the customer company itself
 * -- is not in this list because it never acts. It is the identity the two
 * customer-side principals act *as*, and it is recorded on every entry they
 * produce as ``company_id`` rather than as an actor.
 *
 * :data:`SYSTEM` is the fifth: the server acting on its own behalf, for
 * scheduled and background work and for the authentication events that happen
 * before anybody has been identified. It exists so that ``actor_id`` is never
 * null -- "nobody did this" is not an answer an audit trail may give.
 */
export const AuditPrincipalType = {
    VYTTECH_STAFF: 'vyttech_staff',
    CUSTOMER_USER: 'customer_user',
    MACHINE_CLIENT: 'machine_client',
    SYSTEM: 'system'
} as const;

/**
 * AuditPrincipalType
 *
 * What kind of actor did it.
 *
 * The first three mirror :class:`~app.auth.principal.PrincipalType` value for
 * value, deliberately: an audit entry naming a kind of principal the
 * authentication layer cannot produce would be unreadable.
 *
 * The fourth kind of principal in this product -- the customer company itself
 * -- is not in this list because it never acts. It is the identity the two
 * customer-side principals act *as*, and it is recorded on every entry they
 * produce as ``company_id`` rather than as an actor.
 *
 * :data:`SYSTEM` is the fifth: the server acting on its own behalf, for
 * scheduled and background work and for the authentication events that happen
 * before anybody has been identified. It exists so that ``actor_id`` is never
 * null -- "nobody did this" is not an answer an audit trail may give.
 */
export type AuditPrincipalType = typeof AuditPrincipalType[keyof typeof AuditPrincipalType];

/**
 * BaselineImportCounts
 *
 * What a baseline import read and wrote, counted.
 */
export type BaselineImportCounts = {
    /**
     * Already Present
     *
     * Rows whose person was already on the contract. Zero on a first run; every row when the same file is run again.
     */
    already_present: number;
    /**
     * Coverage Periods Opened
     */
    coverage_periods_opened: number;
    /**
     * People Registered
     *
     * People added to the roster. Zero when re-running the same file.
     */
    people_registered: number;
    /**
     * Row Count
     *
     * People rows read from the file.
     */
    row_count: number;
    /**
     * Salary Versions Recorded
     */
    salary_versions_recorded: number;
};

/**
 * BaselineImportListResponse
 *
 * Every baseline import run against one contract, newest first.
 */
export type BaselineImportListResponse = {
    /**
     * Count
     */
    count: number;
    /**
     * Items
     */
    items: Array<BaselineImportResponse>;
};

/**
 * BaselineImportResponse
 *
 * What one run of the baseline import did, and where its source went.
 *
 * Counts, not contents. The people it created are read back through the roster
 * endpoints, which is the one place the decrypted population is served.
 */
export type BaselineImportResponse = {
    /**
     * Baseline Date
     *
     * The form's `baseline_date`: the date the imported population is insured from by default. A row carrying its own `Início da cobertura` is insured from that date instead; this value is the one for every row that does not.
     */
    baseline_date: string;
    /**
     * Contract Id
     */
    contract_id: string;
    /**
     * Coverage Periods Opened
     */
    coverage_periods_opened: number;
    /**
     * Id
     */
    id: string;
    /**
     * Imported At
     *
     * When the run completed.
     */
    imported_at: string;
    /**
     * Imported By User Id
     *
     * The staff user who ran it; `null` if that account has since been deleted.
     */
    imported_by_user_id: string | null;
    /**
     * Overridden
     *
     * True when a staff user overrode the refusal to import onto a contract that already had movements of its own.
     */
    overridden: boolean;
    /**
     * People Registered
     *
     * People this run added to the contract. Zero when re-running the same file.
     */
    people_registered: number;
    /**
     * Row Count
     *
     * Data rows in the source, all of which were valid.
     */
    row_count: number;
    /**
     * Salary Versions Recorded
     */
    salary_versions_recorded: number;
    /**
     * Source Byte Count
     */
    source_byte_count: number;
    /**
     * Source Filename
     */
    source_filename: string;
    /**
     * Source Media Type
     */
    source_media_type: string;
    /**
     * Source Sha256
     *
     * SHA-256 of the uploaded bytes. Two uploads of the same file share it.
     */
    source_sha256: string;
};

/**
 * Body_import_baseline_api_v1_contracts__contract_id__baseline_imports_post
 */
export type BodyImportBaselineApiV1ContractsContractIdBaselineImportsPost = {
    /**
     * Baseline Date
     *
     * The calendar date, in America/Sao_Paulo, that the people in the file are insured from by default. A row whose optional `Início da cobertura` column is filled is insured from that date instead; a blank cell, or a file without the column, falls back to this one.
     */
    baseline_date: string;
    /**
     * File
     *
     * The population, as an `.xlsx` workbook or a CSV.
     */
    file: Blob | File;
    /**
     * Override
     *
     * Import even though the contract already has movements of its own. Recorded on the resulting import record.
     */
    override?: boolean;
};

/**
 * Body_import_company_baseline_api_v1_companies__company_id__baseline_imports_post
 */
export type BodyImportCompanyBaselineApiV1CompaniesCompanyIdBaselineImportsPost = {
    /**
     * Baseline Date
     *
     * The calendar date, in America/Sao_Paulo, that the people in the file are insured from by default. A row whose optional `Início da cobertura` column is filled is insured from that date instead; a blank cell, or a file without the column, falls back to this one.
     */
    baseline_date: string;
    /**
     * File
     *
     * The sheet, as an `.xlsx` workbook or a CSV.
     */
    file: Blob | File;
    /**
     * Override
     *
     * Import even though a contract the file touches already has movements of its own. Recorded on that contract's import record.
     */
    override?: boolean;
};

/**
 * Body_import_group_baseline_api_v1_groups__group_id__baseline_imports_post
 */
export type BodyImportGroupBaselineApiV1GroupsGroupIdBaselineImportsPost = {
    /**
     * Baseline Date
     *
     * The calendar date, in America/Sao_Paulo, that the people in the file are insured from by default. A row whose optional `Início da cobertura` column is filled is insured from that date instead; a blank cell, or a file without the column, falls back to this one.
     */
    baseline_date: string;
    /**
     * File
     *
     * The sheet, as an `.xlsx` workbook or a CSV.
     */
    file: Blob | File;
    /**
     * Override
     *
     * Import even though a contract the file touches already has movements of its own. Recorded on that contract's import record.
     */
    override?: boolean;
};

/**
 * Body_supersede_document_api_v1_documents__document_id__supersede_post
 */
export type BodySupersedeDocumentApiV1DocumentsDocumentIdSupersedePost = {
    /**
     * How the file reached us: `portal`, `api` or `email`. Omit it and it is taken from the kind of principal uploading -- `api` for a machine client, `portal` for a person. A mailbox ingester sends `email` explicitly.
     */
    channel?: DocumentChannel | null;
    /**
     * File
     *
     * The document itself. Its type is determined by inspecting the bytes, so the extension is irrelevant: accepted types are application/pdf, image/heic, image/jpeg, image/png, image/tiff, image/webp.
     */
    file: Blob | File;
    /**
     * What the paperwork is: `termination_agreement`, `admission_paperwork` or `other`. Keyed by whoever supplies the document; nothing is inferred from its contents.
     */
    kind: DocumentKind;
};

/**
 * Body_upload_certificate_api_v1_certificates_post
 */
export type BodyUploadCertificateApiV1CertificatesPost = {
    /**
     * File
     *
     * The A1 certificate itself: the PKCS#12 archive, normally named `.pfx` or `.p12`. It is opened with the supplied password before anything is stored, so a file that is not a PKCS#12 archive, or one this password does not open, creates no row at all.
     */
    file: Blob | File;
    /**
     * Password
     *
     * The password that opens the archive. Stored encrypted, under its own envelope context, because PKCS#12 has no other way in -- an archive nobody can open is a file rather than a credential. **It is never returned by any endpoint**, here or elsewhere.
     */
    password: string;
};

/**
 * Body_upload_company_spreadsheet_api_v1_companies__company_id__spreadsheet_uploads_post
 */
export type BodyUploadCompanySpreadsheetApiV1CompaniesCompanyIdSpreadsheetUploadsPost = {
    /**
     * Effective Date
     *
     * The date to apply to rows whose movement carries none of its own. A legacy sheet has nowhere to write a date, so this is how its movements get one; a row with its own date ignores this. Rows left with no date from any source are rejected saying so, never dated today.
     */
    effective_date?: string | null;
    /**
     * File
     *
     * The filled sheet, as `.xlsx` or `.csv`. Both layouts are accepted and which one this is is read off the header, not declared. Column names are matched ignoring accents, capitals and surrounding spaces.
     */
    file: Blob | File;
};

/**
 * Body_upload_document_api_v1_documents_post
 */
export type BodyUploadDocumentApiV1DocumentsPost = {
    /**
     * How the file reached us: `portal`, `api` or `email`. Omit it and it is taken from the kind of principal uploading -- `api` for a machine client, `portal` for a person. A mailbox ingester sends `email` explicitly.
     */
    channel?: DocumentChannel | null;
    /**
     * File
     *
     * The document itself. Its type is determined by inspecting the bytes, so the extension is irrelevant: accepted types are application/pdf, image/heic, image/jpeg, image/png, image/tiff, image/webp.
     */
    file: Blob | File;
    /**
     * What the paperwork is: `termination_agreement`, `admission_paperwork` or `other`. Keyed by whoever supplies the document; nothing is inferred from its contents.
     */
    kind: DocumentKind;
};

/**
 * Body_upload_group_spreadsheet_api_v1_groups__group_id__spreadsheet_uploads_post
 */
export type BodyUploadGroupSpreadsheetApiV1GroupsGroupIdSpreadsheetUploadsPost = {
    /**
     * Effective Date
     *
     * The date to apply to rows whose movement carries none of its own. A legacy sheet has nowhere to write a date, so this is how its movements get one; a row with its own date ignores this. Rows left with no date from any source are rejected saying so, never dated today.
     */
    effective_date?: string | null;
    /**
     * File
     *
     * The filled sheet, as `.xlsx` or `.csv`. Both layouts are accepted and which one this is is read off the header, not declared. Column names are matched ignoring accents, capitals and surrounding spaces.
     */
    file: Blob | File;
};

/**
 * Body_upload_spreadsheet_api_v1_contracts__contract_id__spreadsheet_uploads_post
 */
export type BodyUploadSpreadsheetApiV1ContractsContractIdSpreadsheetUploadsPost = {
    /**
     * Effective Date
     *
     * The date to apply to rows whose movement carries none of its own. A legacy sheet has nowhere to write a date, so this is how its movements get one; a row with its own date ignores this. Rows left with no date from any source are rejected saying so, never dated today.
     */
    effective_date?: string | null;
    /**
     * File
     *
     * The filled sheet, as `.xlsx` or `.csv`. Both layouts are accepted and which one this is is read off the header, not declared. Column names are matched ignoring accents, capitals and surrounding spaces.
     */
    file: Blob | File;
};

/**
 * BrowserBackend
 *
 * ``I8LIFE_BROWSER_BACKEND``'s values, as the API names which browser ran (#477).
 */
export const BrowserBackend = {
    CHROMIUM: 'chromium',
    REMOTE_CHROMIUM: 'remote_chromium',
    MEMORY: 'memory',
    DISABLED: 'disabled'
} as const;

/**
 * BrowserBackend
 *
 * ``I8LIFE_BROWSER_BACKEND``'s values, as the API names which browser ran (#477).
 */
export type BrowserBackend = typeof BrowserBackend[keyof typeof BrowserBackend];

/**
 * BulkDeltaCreate
 *
 * Many deltas in one call, for the importers that never send one at a time.
 */
export type BulkDeltaCreate = {
    /**
     * Items
     *
     * Between 1 and 1000 deltas. Each is validated on its own: an item that fails is reported at its index in `failures` and the rest are still submitted.
     */
    items: Array<{
        /**
         * Action
         */
        action: 'admission';
        /**
         * Birth Date
         */
        birth_date: string;
        /**
         * Cpf
         *
         * The person's CPF. Punctuation is ignored, so `123.456.789-09` and `12345678909` are the same number. Both check digits are verified; a mistyped or transposed digit is rejected rather than stored.
         */
        cpf: string;
        /**
         * Effective Date
         *
         * Calendar date the change takes effect, `YYYY-MM-DD`, in America/Sao_Paulo. Mandatory for every action. It may fall in a future month, in the current cycle, or in the past -- a date before the last snapshot sent to the insurer is accepted and flagged as retroactive.
         */
        effective_date: string;
        /**
         * Full Name
         */
        full_name: string;
        /**
         * Salary
         *
         * Monthly salary, to at most two decimal places. Send it as a JSON string to be certain no precision is lost in transit; it is stored and returned as an exact decimal and never as a binary float.
         */
        salary: number | string;
    } | {
        /**
         * Action
         */
        action: 'termination';
        /**
         * Cpf
         *
         * The person's CPF. Punctuation is ignored, so `123.456.789-09` and `12345678909` are the same number. Both check digits are verified; a mistyped or transposed digit is rejected rather than stored.
         */
        cpf: string;
        /**
         * Effective Date
         *
         * Calendar date the change takes effect, `YYYY-MM-DD`, in America/Sao_Paulo. Mandatory for every action. It may fall in a future month, in the current cycle, or in the past -- a date before the last snapshot sent to the insurer is accepted and flagged as retroactive.
         */
        effective_date: string;
    } | {
        /**
         * Action
         */
        action: 'salary_change';
        /**
         * Cpf
         *
         * The person's CPF. Punctuation is ignored, so `123.456.789-09` and `12345678909` are the same number. Both check digits are verified; a mistyped or transposed digit is rejected rather than stored.
         */
        cpf: string;
        /**
         * Effective Date
         *
         * Calendar date the change takes effect, `YYYY-MM-DD`, in America/Sao_Paulo. Mandatory for every action. It may fall in a future month, in the current cycle, or in the past -- a date before the last snapshot sent to the insurer is accepted and flagged as retroactive.
         */
        effective_date: string;
        /**
         * Salary
         *
         * Monthly salary, to at most two decimal places. Send it as a JSON string to be certain no precision is lost in transit; it is stored and returned as an exact decimal and never as a binary float.
         */
        salary: number | string;
    } | {
        /**
         * Action
         */
        action: 'leave';
        /**
         * Cpf
         *
         * The person's CPF. Punctuation is ignored, so `123.456.789-09` and `12345678909` are the same number. Both check digits are verified; a mistyped or transposed digit is rejected rather than stored.
         */
        cpf: string;
        /**
         * Effective Date
         *
         * Calendar date the change takes effect, `YYYY-MM-DD`, in America/Sao_Paulo. Mandatory for every action. It may fall in a future month, in the current cycle, or in the past -- a date before the last snapshot sent to the insurer is accepted and flagged as retroactive.
         */
        effective_date: string;
        /**
         * Leave End Date
         *
         * Last day away, inclusive, `YYYY-MM-DD`. Omit it -- or send `null` -- when the return date is not known yet; the leave is then open-ended and is completed by a later delta that supersedes it. It may not come before `effective_date`.
         */
        leave_end_date?: string | null;
    }>;
};

/**
 * BulkDeltaFailure
 *
 * One item of a bulk submission that was not accepted, and why.
 */
export type BulkDeltaFailure = {
    /**
     * The same codes the error envelope uses.
     */
    code: ErrorCode;
    /**
     * Details
     *
     * Field-level problems, when there are any.
     */
    details?: Array<ErrorDetail>;
    /**
     * Index
     *
     * Position of the failed item in the submitted list.
     */
    index: number;
    /**
     * Message
     */
    message: string;
};

/**
 * BulkDeltaResponse
 *
 * The outcome of a bulk submission: what went in, and what did not.
 *
 * A partial failure is a `200`, not an error: the accepted items really were
 * accepted, and answering with a failure status would make a caller roll back
 * work that was done.
 */
export type BulkDeltaResponse = {
    /**
     * Failed Count
     */
    failed_count: number;
    /**
     * Failures
     *
     * Items that were not created, each at the index it was sent at.
     */
    failures: Array<BulkDeltaFailure>;
    /**
     * Submitted
     *
     * The deltas that were created.
     */
    submitted: Array<DeltaResponse>;
    /**
     * Submitted Count
     */
    submitted_count: number;
};

/**
 * CertificateStatus
 *
 * What a screen says about one certificate. Values are part of the contract.
 */
export const CertificateStatus = {
    ACTIVE: 'active',
    EXPIRING_SOON: 'expiring_soon',
    EXPIRED: 'expired',
    SUPERSEDED: 'superseded',
    DEACTIVATED: 'deactivated'
} as const;

/**
 * CertificateStatus
 *
 * What a screen says about one certificate. Values are part of the contract.
 */
export type CertificateStatus = typeof CertificateStatus[keyof typeof CertificateStatus];

/**
 * ClockSimulationRequest
 *
 * The instant this deployment should start believing it is.
 */
export type ClockSimulationRequest = {
    /**
     * Simulated Now
     *
     * The instant to move the clock to. Send an offset and it is taken exactly as given; send a naive value — `2026-08-20T09:00:00` — and it is read as São Paulo wall-clock time, the same convention every other datetime this API accepts follows.
     *
     * What is stored is the *difference* from the real clock at the moment of this request, so simulated time carries on advancing from here. Two events a second apart still get two distinct, ordered timestamps.
     */
    simulated_now: string;
};

/**
 * ClockStateResponse
 *
 * What time this deployment thinks it is, and what time it really is.
 */
export type ClockStateResponse = {
    /**
     * Is Simulated
     *
     * Whether a simulation is in force. `false` means `simulated_now` and `real_now` are the same reading rather than two that happen to agree.
     */
    is_simulated: boolean;
    /**
     * Offset Seconds
     *
     * `simulated_now - real_now`, in seconds; negative for a clock moved into the past. This is the value that is actually stored, and it is what makes the simulation survive a restart and reach every worker.
     */
    offset_seconds: number;
    /**
     * Real Now
     *
     * The machine's own clock, in UTC. Never simulated, whatever else on this deployment is.
     */
    real_now: string;
    /**
     * Set At
     *
     * The **real** instant the running simulation was set, or `null` when none is running.
     */
    set_at?: string | null;
    /**
     * Simulated Now
     *
     * What every other endpoint on this deployment believes the current instant to be, in UTC. Equal to `real_now` when no simulation is running. It advances at the real clock's rate — this is a clock that was moved, not a clock that was stopped.
     */
    simulated_now: string;
    /**
     * Simulation Available
     *
     * Whether this deployment may simulate its clock at all. Always `true` where this resource answers, because in production the resource is not registered and every path under `/clock` is a `404`. It is reported anyway so that a client which has already decided the control exists can read one field rather than remember why.
     */
    simulation_available: boolean;
};

/**
 * CompanyBaselineImportContract
 *
 * One contract a company-wide import loaded, and the record it left there.
 */
export type CompanyBaselineImportContract = {
    /**
     * Already Present
     *
     * Rows whose person was already on the contract. Zero on a first run; every row when the same file is run again.
     */
    already_present: number;
    /**
     * Baseline Import Id
     *
     * The contract's own import record, readable at `GET /contracts/{contract_id}/baseline-imports/{baseline_import_id}`.
     */
    baseline_import_id: string;
    /**
     * Contract Id
     */
    contract_id: string;
    /**
     * Coverage Periods Opened
     */
    coverage_periods_opened: number;
    /**
     * Modulo
     */
    modulo: number;
    /**
     * Overridden
     *
     * True when this contract had moved on and the import overrode it.
     */
    overridden: boolean;
    /**
     * People Registered
     *
     * People added to the roster. Zero when re-running the same file.
     */
    people_registered: number;
    /**
     * Row Count
     *
     * People rows read from the file.
     */
    row_count: number;
    /**
     * Salary Versions Recorded
     */
    salary_versions_recorded: number;
    /**
     * Subestipulante
     */
    subestipulante: number;
};

/**
 * CompanyBaselineImportResponse
 *
 * What a company-wide baseline import did, per contract and in total.
 */
export type CompanyBaselineImportResponse = {
    /**
     * Baseline Date
     *
     * The form's `baseline_date`: the date the imported population is insured from by default. A row carrying its own `Início da cobertura` is insured from that date instead; this value is the one for every row that does not.
     */
    baseline_date: string;
    /**
     * Company Id
     */
    company_id: string;
    /**
     * Contracts
     *
     * One entry per contract that received rows, by subestipulante then módulo.
     */
    contracts: Array<CompanyBaselineImportContract>;
    /**
     * Source Filename
     */
    source_filename: string;
    /**
     * Source Media Type
     *
     * The format read from the file's content: an `.xlsx` workbook or a CSV.
     */
    source_media_type: string;
    totals: BaselineImportCounts;
};

/**
 * CompanyCNPJUpdate
 *
 * Set or correct one company's CNPJ.
 *
 * A body of one field rather than a general company PATCH: a name and a slug
 * are edited, a registry number is *established*, and the two want different
 * permissions and a different entry in the trail.
 */
export type CompanyCnpjUpdate = {
    /**
     * Cnpj
     *
     * The company's CNPJ, the identifier eSocial and the insurers recognise an employer by. Formatted (`12.345.678/0001-95`), unformatted and whitespace-padded input are all accepted and stored as the same canonical fourteen characters; both check digits are verified, and a number that fails them is a `422` naming what is wrong. The alphanumeric form the Receita Federal issues from July 2026 (`12.ABC.345/01DE-35`) is a CNPJ here exactly as the classic one is. Unique across companies.
     */
    cnpj: string;
};

/**
 * CompanyCreate
 *
 * Register a customer company: the tenant every other record belongs to.
 */
export type CompanyCreate = {
    /**
     * Cnpj
     *
     * The company's CNPJ, the identifier eSocial and the insurers recognise an employer by. Formatted (`12.345.678/0001-95`), unformatted and whitespace-padded input are all accepted and stored as the same canonical fourteen characters; both check digits are verified, and a number that fails them is a `422` naming what is wrong. The alphanumeric form the Receita Federal issues from July 2026 (`12.ABC.345/01DE-35`) is a CNPJ here exactly as the classic one is. Unique across companies.
     */
    cnpj: string;
    /**
     * Name
     */
    name: string;
    /**
     * Slug
     *
     * Lower-case identifier used in support and in URLs. Unique across companies. Optional: when omitted it is derived from `name` -- accents stripped, lower-cased, every run of other characters one hyphen -- and suffixed `-2`, `-3` and so on if another company already has it. A name with nothing to derive one from is a `422` on `name`. Sent explicitly, it is stored exactly as given, and one that already exists is a `409`.
     */
    slug?: string | null;
    /**
     * What the review queue does about a termination with no supporting document linked to it. `block`: the `missing_evidence` flag is `blocking`, the movement cannot be approved (`409`) and neither can its cycle until a document is linked. `warn`: the flag is a `warning`, for an analyst to look at and then decide -- what every company had before the setting existed. `off`: no `missing_evidence` flag is raised at all, for a customer that never sends termination paperwork. A documented termination carries no flag under any of them. Defaults to `warn`.
     */
    termination_document_policy?: TerminationDocumentPolicy;
};

/**
 * CompanyGroupAssignment
 *
 * Which economic group a company is in: set, move or clear it.
 */
export type CompanyGroupAssignment = {
    /**
     * Group Id
     *
     * The group the company should be in, or `null` to take it out of its group. Naming the group it is already in changes nothing.
     */
    group_id: string | null;
};

/**
 * CompanyGroupCreate
 *
 * Register an economic group (*grupo econômico*).
 */
export type CompanyGroupCreate = {
    /**
     * Name
     */
    name: string;
    /**
     * Slug
     *
     * Lower-case identifier, under the same rules as a company slug. Unique across groups, and stable: renaming a group never changes it.
     */
    slug: string;
};

/**
 * CompanyGroupDetailResponse
 *
 * One economic group and the companies in it, by name.
 */
export type CompanyGroupDetailResponse = {
    /**
     * Created At
     */
    created_at: string;
    /**
     * File Name Label
     *
     * The group's insurer file-name label, or `null` when it has none. Every company in the group without a label of its own uses it in its file names. Set with `POST /groups/{group_id}/file-name-label`.
     */
    file_name_label: string | null;
    /**
     * Id
     */
    id: string;
    /**
     * Member Count
     *
     * How many companies are in the group.
     */
    member_count: number;
    /**
     * Members
     */
    members: Array<CompanyGroupMember>;
    /**
     * Name
     */
    name: string;
    /**
     * Slug
     */
    slug: string;
};

/**
 * CompanyGroupListResponse
 *
 * The economic groups that match the query; with none, every group by name.
 *
 * ``count`` is every group that matches, not the length of this page, as on
 * :class:`CompanyListResponse`.
 */
export type CompanyGroupListResponse = {
    /**
     * Count
     *
     * How many groups match the filters, across every page. Equal to the number of `items` when no `limit` was sent.
     */
    count: number;
    /**
     * Items
     */
    items: Array<CompanyGroupResponse>;
    /**
     * Limit
     *
     * The `limit` that was asked for, or `null` for an unpaged list.
     */
    limit: number | null;
    /**
     * Offset
     *
     * The `offset` that was asked for.
     */
    offset: number;
};

/**
 * CompanyGroupListSort
 *
 * The column ``GET /groups`` sorts by -- one per column of the staff table.
 */
export const CompanyGroupListSort = {
    NAME: 'name',
    SLUG: 'slug',
    MEMBER_COUNT: 'member_count',
    CREATED_AT: 'created_at'
} as const;

/**
 * CompanyGroupListSort
 *
 * The column ``GET /groups`` sorts by -- one per column of the staff table.
 */
export type CompanyGroupListSort = typeof CompanyGroupListSort[keyof typeof CompanyGroupListSort];

/**
 * CompanyGroupMember
 *
 * One company in an economic group.
 */
export type CompanyGroupMember = {
    /**
     * Cnpj
     *
     * The canonical fourteen characters, or `null` for a company without one.
     */
    cnpj: string | null;
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    /**
     * Slug
     */
    slug: string;
};

/**
 * CompanyGroupRename
 *
 * Rename an economic group. The slug is stable and cannot be changed here.
 */
export type CompanyGroupRename = {
    /**
     * Name
     */
    name: string;
};

/**
 * CompanyGroupResponse
 *
 * One economic group, with how many companies are in it.
 */
export type CompanyGroupResponse = {
    /**
     * Created At
     */
    created_at: string;
    /**
     * File Name Label
     *
     * The group's insurer file-name label, or `null` when it has none. Every company in the group without a label of its own uses it in its file names. Set with `POST /groups/{group_id}/file-name-label`.
     */
    file_name_label: string | null;
    /**
     * Id
     */
    id: string;
    /**
     * Member Count
     *
     * How many companies are in the group.
     */
    member_count: number;
    /**
     * Name
     */
    name: string;
    /**
     * Slug
     */
    slug: string;
};

/**
 * CompanyGroupSummary
 *
 * The economic group a company belongs to, as every company payload names it.
 */
export type CompanyGroupSummary = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    /**
     * Slug
     */
    slug: string;
};

/**
 * CompanyListResponse
 *
 * The companies the request's principal can see, narrowed by the query.
 *
 * ``count`` is every company that matches, not the length of this page: with
 * no ``limit`` the two are the same, with one it is what a pager counts pages
 * from.
 */
export type CompanyListResponse = {
    /**
     * Count
     *
     * How many companies match the scope and filters, across every page. Equal to the number of `items` when no `limit` was sent.
     */
    count: number;
    /**
     * Items
     */
    items: Array<CompanyResponse>;
    /**
     * Limit
     *
     * The `limit` that was asked for, or `null` for an unpaged list.
     */
    limit: number | null;
    /**
     * Offset
     *
     * The `offset` that was asked for.
     */
    offset: number;
};

/**
 * CompanyListSort
 *
 * The column ``GET /companies`` sorts by -- one per column of the staff table.
 */
export const CompanyListSort = {
    NAME: 'name',
    SLUG: 'slug',
    CNPJ: 'cnpj',
    GROUP: 'group',
    CREATED_AT: 'created_at'
} as const;

/**
 * CompanyListSort
 *
 * The column ``GET /companies`` sorts by -- one per column of the staff table.
 */
export type CompanyListSort = typeof CompanyListSort[keyof typeof CompanyListSort];

/**
 * CompanyOutcome
 *
 * How one company of a session went. Values are part of the API contract.
 */
export const CompanyOutcome = {
    COMPLETED: 'completed',
    FAILED: 'failed',
    SKIPPED: 'skipped'
} as const;

/**
 * CompanyOutcome
 *
 * How one company of a session went. Values are part of the API contract.
 */
export type CompanyOutcome = typeof CompanyOutcome[keyof typeof CompanyOutcome];

/**
 * CompanyRef
 *
 * The customer company a principal acts for.
 */
export type CompanyRef = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    /**
     * Slug
     */
    slug: string;
};

/**
 * CompanyResponse
 *
 * One customer company.
 */
export type CompanyResponse = {
    /**
     * Cnpj
     *
     * The canonical fourteen characters, without punctuation. `null` only on a company registered before the field existed; every company created since carries one, and a staff operation backfills the rest.
     */
    cnpj: string | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * File Name Label
     *
     * The company's own insurer file-name label, or `null` when it has none. Set with `POST /companies/{company_id}/file-name-label`.
     */
    file_name_label: string | null;
    /**
     * File Name Label In Force
     *
     * The label insurer file names of this company use: its own label when it has one, otherwise its group's. `null` when neither is set and the registered `name` is used, as it always was.
     */
    file_name_label_in_force: string | null;
    /**
     * The economic group (*grupo econômico*) this company belongs to, or `null` for a company in none. A company is in at most one group. Membership changes nobody's access: a customer still sees only its own company.
     */
    group: CompanyGroupSummary | null;
    /**
     * Id
     */
    id: string;
    /**
     * Is Active
     *
     * A suspended company keeps its data, and none of its principals can sign in.
     */
    is_active: boolean;
    /**
     * Name
     */
    name: string;
    /**
     * Slug
     */
    slug: string;
    /**
     * What the review queue does about a termination with no supporting document linked to it. `block`: the `missing_evidence` flag is `blocking`, the movement cannot be approved (`409`) and neither can its cycle until a document is linked. `warn`: the flag is a `warning`, for an analyst to look at and then decide -- what every company had before the setting existed. `off`: no `missing_evidence` flag is raised at all, for a customer that never sends termination paperwork. A documented termination carries no flag under any of them. Set with `PUT /companies/{company_id}/termination-document-policy`.
     */
    termination_document_policy: TerminationDocumentPolicy;
};

/**
 * CompanyRosterResponse
 *
 * Everyone insured under any contract of one company on one date, one item per person.
 */
export type CompanyRosterResponse = {
    /**
     * As Of
     *
     * The date projected, as a calendar date in America/Sao_Paulo.
     */
    as_of: string;
    /**
     * Company Id
     */
    company_id: string;
    /**
     * Count
     *
     * How many people, not how many entries.
     */
    count: number;
    /**
     * Items
     *
     * One item per CPF, sorted by name -- compared without regard to case -- and then by CPF.
     */
    items: Array<RosterPerson>;
};

/**
 * ComparedPersonResponse
 *
 * One person on one side of the comparison, with whatever moved.
 */
export type ComparedPersonResponse = {
    /**
     * Changes
     *
     * Empty except for a changed person.
     */
    changes?: Array<SnapshotFieldChangeResponse>;
    /**
     * Insured Person Id
     */
    insured_person_id: string | null;
    /**
     * Values
     *
     * The person's row on the side that has it: this snapshot for somebody added or changed, the previous one for somebody removed. When the previous side is the roster, a removed person is their stored exclusion row if the layout writes one, and otherwise their name, birth date and CPF under the layout's column keys.
     */
    values: {
        [key: string]: string;
    };
};

/**
 * ComponentStatus
 */
export const ComponentStatus = { OK: 'ok', DEGRADED: 'degraded' } as const;

/**
 * ComponentStatus
 */
export type ComponentStatus = typeof ComponentStatus[keyof typeof ComponentStatus];

/**
 * ConsolidatedMemberResponse
 *
 * One contract inside a company's single file, and where its own month stands.
 */
export type ConsolidatedMemberResponse = {
    /**
     * Approved
     *
     * True when the contract's cycle has a sign-off standing.
     */
    approved: boolean;
    /**
     * Contract Id
     */
    contract_id: string;
    /**
     * Cutoff Date
     */
    cutoff_date: string;
    /**
     * Modulo
     */
    modulo: number;
    /**
     * Row Count
     *
     * Rows that snapshot puts in the file. `null` until it is approved.
     */
    row_count?: number | null;
    /**
     * Snapshot Id
     *
     * The frozen snapshot the sign-off pinned, whose rows go into the file.
     */
    snapshot_id?: string | null;
    /**
     * This contract's own stage, exactly as its standing reports it.
     */
    stage: CycleStage;
    /**
     * Subestipulante
     */
    subestipulante: number;
    /**
     * The contract's own attempt standing for the month.
     */
    submission?: SubmissionResponse | null;
};

/**
 * ConsolidatedPrepare
 *
 * Package one company's month with one insurer as a single file.
 */
export type ConsolidatedPrepare = {
    /**
     * Cycle Month
     *
     * The month being filed, written `YYYY-MM`. The cutoff date it resolves to comes from the contract's `reference_date_rule`, and is never sent by a caller.
     */
    cycle_month: string;
};

/**
 * ConsolidatedStandingResponse
 *
 * One company's month with one insurer whose layout files one file for all of it.
 */
export type ConsolidatedStandingResponse = {
    /**
     * Approved Count
     *
     * How many of the members have a sign-off standing.
     */
    approved_count: number;
    /**
     * Attempts
     *
     * Every file prepared for the month, newest first, refused ones included.
     */
    attempts: Array<ConsolidatedSubmissionResponse>;
    /**
     * Can Prepare
     *
     * True when the file can be prepared now: every member is approved and no file is standing for the month.
     */
    can_prepare: boolean;
    /**
     * Company Id
     */
    company_id: string;
    /**
     * Company Name
     */
    company_name: string;
    /**
     * Contract Count
     */
    contract_count: number;
    /**
     * Cycle Month
     *
     * The month being filed, written `YYYY-MM`. The cutoff date it resolves to comes from the contract's `reference_date_rule`, and is never sent by a caller.
     */
    cycle_month: string;
    /**
     * Group Id
     *
     * Set when the company belongs to an economic group: its month is then filed in the group's single file, at `/submissions/groups/{group_id}/insurers/{insurer_id}`, and `can_prepare` is false here. `stage` then reads the members' own attempts.
     */
    group_id?: string | null;
    /**
     * Group Name
     */
    group_name?: string | null;
    /**
     * Insurer Code
     */
    insurer_code: string;
    /**
     * Insurer Id
     */
    insurer_id: string;
    /**
     * Insurer Name
     */
    insurer_name: string;
    /**
     * Layout Code
     */
    layout_code: string;
    /**
     * Members
     *
     * Every contract the company has with the insurer, in the file's order.
     */
    members: Array<ConsolidatedMemberResponse>;
    /**
     * The month in one word: `open` while any member has no sign-off, `approved` once all do and no file is standing, `prepared` and `sent` as the file is, and `failed` when the last file was refused.
     */
    stage: CycleStage;
    /**
     * The file standing for the month, prepared or filed.
     */
    submission?: ConsolidatedSubmissionResponse | null;
    /**
     * Unapproved Contract Ids
     *
     * The members still without a sign-off, which is what holds the file back.
     */
    unapproved_contract_ids: Array<string>;
};

/**
 * ConsolidatedSubmissionResponse
 *
 * One company's month with one insurer, filed as one file, and what came back.
 */
export type ConsolidatedSubmissionResponse = {
    channel?: SubmissionChannel | null;
    /**
     * Company Id
     */
    company_id: string;
    /**
     * Contract Count
     *
     * How many contracts the file packages.
     */
    contract_count: number;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Cycle Month
     *
     * The month being filed, written `YYYY-MM`. The cutoff date it resolves to comes from the contract's `reference_date_rule`, and is never sent by a caller.
     */
    cycle_month: string;
    /**
     * Failed At
     */
    failed_at?: string | null;
    failed_by?: PrincipalReference | null;
    /**
     * Failure Reason
     */
    failure_reason?: string | null;
    /**
     * File Byte Count
     */
    file_byte_count: number;
    /**
     * File Media Type
     */
    file_media_type: string;
    /**
     * File Name
     */
    file_name: string;
    /**
     * File Sha256
     *
     * SHA-256 of the file's bytes. Downloading it from `/submissions/consolidated/{id}/file` returns exactly these bytes.
     */
    file_sha256: string;
    /**
     * Has Proof
     */
    has_proof: boolean;
    /**
     * Id
     */
    id: string;
    /**
     * Insurer Id
     */
    insurer_id: string;
    /**
     * Layout Code
     */
    layout_code: string;
    /**
     * Notes
     */
    notes?: string | null;
    /**
     * Prepared At
     */
    prepared_at: string;
    /**
     * Who packaged the file.
     */
    prepared_by: PrincipalReference;
    /**
     * Proof Document Id
     */
    proof_document_id?: string | null;
    /**
     * Reference
     */
    reference?: string | null;
    /**
     * Row Count
     *
     * Rows in the file: the sum of the members' approved snapshots.
     */
    row_count: number;
    sent_by?: PrincipalReference | null;
    /**
     * `prepared` once the file is packaged, `sent` once it is filed and evidenced, `failed` when the insurer refused it. Every member submission carries the same status.
     */
    status: SubmissionStatus;
    /**
     * Submission Ids
     *
     * The member contracts' own attempts this file packages, in the order their rows are in it. Each one's `covers_through` is the watermark that moves when the file is marked sent.
     */
    submission_ids: Array<string>;
    /**
     * Submitted At
     */
    submitted_at?: string | null;
};

/**
 * ContractCreate
 *
 * Create a contract for the company named by `X-Company-Id`.
 */
export type ContractCreate = {
    /**
     * Apolice
     *
     * The policy number -- APÓLICE in the insurer's file. 1 to 20 digits, as a string because it does not fit a 32-bit integer; whitespace is trimmed and punctuation refused. Shared by every contract under the same policy. `null` when not recorded.
     */
    apolice?: string | null;
    /**
     * Cutoff Day
     *
     * Day of the month the submission period closes on. A movement recorded after it belongs to the following cycle. A day past the end of a short month means that month's last day.
     */
    cutoff_day: number;
    /**
     * Insurer Contract Number
     *
     * The insurer's number for the contract this policy sits under -- CONTRATO in their file. 1 to 20 digits, sent and returned as a string so leading zeros survive; surrounding whitespace is trimmed and punctuation is refused rather than stripped. Not unique: every contract under the same policy shares it. `null` when not recorded.
     */
    insurer_contract_number?: string | null;
    /**
     * Insurer Id
     *
     * Which insurer this contract is with.
     */
    insurer_id: string;
    /**
     * What insurance this contract is: `vida` (life) or `saude` (health). It tells two contracts of one company apart when the same person is under both. Descriptive only: nothing in the monthly file or its submission reads it, and the insurer's codes, not the kind, are what make a contract unique. More kinds may be added in a later minor version, so treat an unfamiliar value as one you do not know rather than as an error. Defaults to `vida`.
     */
    kind?: ContractKind;
    /**
     * Modulo
     *
     * The insurer's identifier for the population within the company. Independent of `subestipulante` even though today's contracts set the two alike.
     */
    modulo: number;
    /**
     * Whether this contract's insurer takes an urgent movement between cutoffs, and which kinds. `not_permitted` means the monthly file is the only channel and coverage attaches when they process it; `admissions_only` means they take an urgent admission early and handle everything else monthly; `all_movements` means any of the three may go early.
     *
     * **It defaults to `not_permitted`, and that is a statement rather than a placeholder**: we do not assume an insurer accepts anything outside the monthly file until somebody has asked them, because a notice they ignore is worse than no notice -- it tells an analyst a new hire has been dealt with. Like `reference_date_rule`, this is a term of the insurer's contract, so changing what the product does about it is recording it here rather than shipping a release.
     */
    off_cycle_rule?: OffCycleRule;
    /**
     * Which calendar date within a cycle month the monthly snapshot reads the population at. `cutoff_date` uses this contract's `cutoff_day`; the other two use the first or the last day of the month the cycle covers. It decides whether somebody terminated mid-month is on that month's file, which is why it is a term of the contract here rather than a rule in the file builder.
     */
    reference_date_rule?: ReferenceDateRule;
    /**
     * Subestipulante
     *
     * The insurer's identifier for this company, as they assigned it. Stored on the contract and only here -- it is never carried per insured person.
     */
    subestipulante: number;
    /**
     * How often the movement file goes to the insurer.
     */
    submission_cadence?: SubmissionCadence;
};

/**
 * ContractKind
 *
 * What insurance a contract is: life (``vida``) or health (``saude``).
 *
 * A closed vocabulary, so that one person under two contracts of the same
 * company can be told apart by what each contract covers. Later kinds are
 * added here and to the ``contract_kind`` type by a migration. Nothing in the
 * snapshot, layout or submission engines reads it: the insurer's codes are
 * still what tell two populations apart in a file.
 */
export const ContractKind = { VIDA: 'vida', SAUDE: 'saude' } as const;

/**
 * ContractKind
 *
 * What insurance a contract is: life (``vida``) or health (``saude``).
 *
 * A closed vocabulary, so that one person under two contracts of the same
 * company can be told apart by what each contract covers. Later kinds are
 * added here and to the ``contract_kind`` type by a migration. Nothing in the
 * snapshot, layout or submission engines reads it: the insurer's codes are
 * still what tell two populations apart in a file.
 */
export type ContractKind = typeof ContractKind[keyof typeof ContractKind];

/**
 * ContractListResponse
 *
 * Every contract within the request's company scope.
 */
export type ContractListResponse = {
    /**
     * Count
     */
    count: number;
    /**
     * Items
     */
    items: Array<ContractResponse>;
};

/**
 * ContractResponse
 *
 * One contract, with the insurer's own identifiers for the population it covers.
 */
export type ContractResponse = {
    /**
     * Apolice
     *
     * The policy number -- APÓLICE in the insurer's file. 1 to 20 digits, as a string because it does not fit a 32-bit integer; whitespace is trimmed and punctuation refused. Shared by every contract under the same policy. `null` when not recorded.
     */
    apolice: string | null;
    /**
     * Company Id
     */
    company_id: string;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Cutoff Day
     *
     * Day of the month the submission period closes on. A movement recorded after it belongs to the following cycle. A day past the end of a short month means that month's last day.
     */
    cutoff_day: number;
    /**
     * Id
     */
    id: string;
    /**
     * The insurer this contract is with, in full -- so a client can name it without a second call.
     */
    insurer: InsurerResponse;
    /**
     * Insurer Contract Number
     *
     * The insurer's number for the contract this policy sits under -- CONTRATO in their file. 1 to 20 digits, sent and returned as a string so leading zeros survive; surrounding whitespace is trimmed and punctuation is refused rather than stripped. Not unique: every contract under the same policy shares it. `null` when not recorded.
     */
    insurer_contract_number: string | null;
    /**
     * Insurer Id
     */
    insurer_id: string;
    /**
     * What insurance this contract is: `vida` (life) or `saude` (health). It tells two contracts of one company apart when the same person is under both. Descriptive only: nothing in the monthly file or its submission reads it, and the insurer's codes, not the kind, are what make a contract unique. More kinds may be added in a later minor version, so treat an unfamiliar value as one you do not know rather than as an error. Every contract created before it existed is `vida`.
     */
    kind: ContractKind;
    /**
     * Modulo
     *
     * The insurer's identifier for the population within the company. Independent of `subestipulante` even though today's contracts set the two alike.
     */
    modulo: number;
    /**
     * Whether this contract's insurer takes an urgent movement between cutoffs, and which kinds. `not_permitted` means the monthly file is the only channel and coverage attaches when they process it; `admissions_only` means they take an urgent admission early and handle everything else monthly; `all_movements` means any of the three may go early.
     *
     * **It defaults to `not_permitted`, and that is a statement rather than a placeholder**: we do not assume an insurer accepts anything outside the monthly file until somebody has asked them, because a notice they ignore is worse than no notice -- it tells an analyst a new hire has been dealt with. Like `reference_date_rule`, this is a term of the insurer's contract, so changing what the product does about it is recording it here rather than shipping a release.
     */
    off_cycle_rule: OffCycleRule;
    /**
     * Which calendar date within a cycle month the monthly snapshot reads the population at. `cutoff_date` uses this contract's `cutoff_day`; the other two use the first or the last day of the month the cycle covers. It decides whether somebody terminated mid-month is on that month's file, which is why it is a term of the contract here rather than a rule in the file builder.
     */
    reference_date_rule: ReferenceDateRule;
    /**
     * Subestipulante
     *
     * The insurer's identifier for this company, as they assigned it. Stored on the contract and only here -- it is never carried per insured person.
     */
    subestipulante: number;
    submission_cadence: SubmissionCadence;
    /**
     * Updated At
     */
    updated_at: string;
};

/**
 * ContractUpdate
 *
 * Change a contract. Every field is optional; omitted means unchanged.
 *
 * Most of these columns have no empty state, so for them there is nothing a
 * null could mean other than "leave it alone" -- which is what it does. The
 * two policy references are the exception: they are optional on the contract,
 * so an explicit ``null`` clears one, and only omitting it leaves it alone.
 */
export type ContractUpdate = {
    /**
     * Apolice
     *
     * The policy number -- APÓLICE in the insurer's file. 1 to 20 digits, as a string because it does not fit a 32-bit integer; whitespace is trimmed and punctuation refused. Shared by every contract under the same policy. `null` when not recorded.
     *
     * Omitted means unchanged; an explicit `null` clears the recorded value.
     */
    apolice?: string | null;
    /**
     * Cutoff Day
     *
     * Day of the month the submission period closes on. A movement recorded after it belongs to the following cycle. A day past the end of a short month means that month's last day.
     */
    cutoff_day?: number | null;
    /**
     * Insurer Contract Number
     *
     * The insurer's number for the contract this policy sits under -- CONTRATO in their file. 1 to 20 digits, sent and returned as a string so leading zeros survive; surrounding whitespace is trimmed and punctuation is refused rather than stripped. Not unique: every contract under the same policy shares it. `null` when not recorded.
     *
     * Omitted means unchanged; an explicit `null` clears the recorded value.
     */
    insurer_contract_number?: string | null;
    /**
     * Insurer Id
     */
    insurer_id?: string | null;
    /**
     * What insurance this contract is: `vida` (life) or `saude` (health). It tells two contracts of one company apart when the same person is under both. Descriptive only: nothing in the monthly file or its submission reads it, and the insurer's codes, not the kind, are what make a contract unique. More kinds may be added in a later minor version, so treat an unfamiliar value as one you do not know rather than as an error.
     */
    kind?: ContractKind | null;
    /**
     * Modulo
     *
     * The insurer's identifier for the population within the company. Independent of `subestipulante` even though today's contracts set the two alike.
     */
    modulo?: number | null;
    /**
     * Whether this contract's insurer takes an urgent movement between cutoffs, and which kinds. `not_permitted` means the monthly file is the only channel and coverage attaches when they process it; `admissions_only` means they take an urgent admission early and handle everything else monthly; `all_movements` means any of the three may go early.
     *
     * **It defaults to `not_permitted`, and that is a statement rather than a placeholder**: we do not assume an insurer accepts anything outside the monthly file until somebody has asked them, because a notice they ignore is worse than no notice -- it tells an analyst a new hire has been dealt with. Like `reference_date_rule`, this is a term of the insurer's contract, so changing what the product does about it is recording it here rather than shipping a release.
     */
    off_cycle_rule?: OffCycleRule | null;
    /**
     * Which calendar date within a cycle month the monthly snapshot reads the population at. `cutoff_date` uses this contract's `cutoff_day`; the other two use the first or the last day of the month the cycle covers. It decides whether somebody terminated mid-month is on that month's file, which is why it is a term of the contract here rather than a rule in the file builder.
     */
    reference_date_rule?: ReferenceDateRule | null;
    /**
     * Subestipulante
     *
     * The insurer's identifier for this company, as they assigned it. Stored on the contract and only here -- it is never carried per insured person.
     */
    subestipulante?: number | null;
    submission_cadence?: SubmissionCadence | null;
};

/**
 * CoveragePeriodResponse
 *
 * A stretch of days a person was insured for. Both ends inclusive.
 */
export type CoveragePeriodResponse = {
    /**
     * Created At
     *
     * When this period was recorded.
     */
    created_at: string;
    /**
     * Effective From
     *
     * First covered day, inclusive.
     */
    effective_from: string;
    /**
     * Effective To
     *
     * Last covered day, inclusive. `null` means coverage is open-ended.
     */
    effective_to?: string | null;
    /**
     * Id
     */
    id: string;
};

/**
 * CycleApprovalListResponse
 *
 * Every sign-off of one contract, withdrawn ones included, most recent cycle first.
 */
export type CycleApprovalListResponse = {
    /**
     * Contract Id
     */
    contract_id: string;
    /**
     * Count
     */
    count: number;
    /**
     * Items
     */
    items: Array<CycleApprovalResponse>;
};

/**
 * CycleApprovalRequest
 *
 * Sign a cutoff off, over the file that was previewed.
 */
export type CycleApprovalRequest = {
    /**
     * Acknowledge Exceptions
     *
     * Approve a cycle whose file could not render every person. Required when `exception_count` is above zero: sending the insurer a file that is missing people has to be somebody's decision rather than a default.
     */
    acknowledge_exceptions?: boolean;
    /**
     * Cycle Month
     *
     * The month whose cycle this is, written `YYYY-MM`. The cutoff date it resolves to comes from the contract's `reference_date_rule`, and is never sent by a caller.
     */
    cycle_month: string;
    /**
     * Snapshot Id
     *
     * The snapshot you previewed. Sent so that approving a file that has been regenerated since is a `409` rather than a signature on something nobody read. Omit it to approve whatever the cycle's current snapshot is.
     */
    snapshot_id?: string | null;
    /**
     * Snapshot Sha256
     *
     * The digest of the file you previewed, checked the same way and for the same reason as `snapshot_id`.
     */
    snapshot_sha256?: string | null;
};

/**
 * CycleApprovalResponse
 *
 * One sign-off of one cutoff: who, for which date, over exactly which file.
 */
export type CycleApprovalResponse = {
    /**
     * Approved At
     */
    approved_at: string;
    approved_by: PrincipalReference;
    /**
     * Company Id
     */
    company_id: string;
    /**
     * Contract Id
     */
    contract_id: string;
    /**
     * Cutoff Date
     *
     * The single calendar date this cycle reads the roster at -- the cutoff being approved. Derived from the contract; never supplied.
     */
    cutoff_date: string;
    /**
     * Cycle Month
     *
     * The month whose cycle this is, written `YYYY-MM`. The cutoff date it resolves to comes from the contract's `reference_date_rule`, and is never sent by a caller.
     */
    cycle_month: string;
    /**
     * Delta Count
     *
     * How many movements the cycle contained when it was approved.
     */
    delta_count: number;
    /**
     * Id
     */
    id: string;
    /**
     * Is Ready For Submission
     *
     * True while this sign-off stands. What the submission task reads before producing or transmitting anything.
     */
    is_ready_for_submission: boolean;
    /**
     * Reopened At
     */
    reopened_at?: string | null;
    /**
     * Who withdrew the sign-off. `null` while it stands.
     */
    reopened_by?: PrincipalReference | null;
    /**
     * Reopened Reason
     */
    reopened_reason?: string | null;
    /**
     * Snapshot Id
     *
     * The file that was approved. Frozen at the moment of approval.
     */
    snapshot_id: string;
    /**
     * Snapshot Sha256
     *
     * SHA-256 of the approved file's bytes, so whoever sends it can check they are sending what was signed off.
     */
    snapshot_sha256: string;
    /**
     * `approved` is the sign-off that stands and marks the cycle ready for submission. `reopened` is one that was withdrawn afterwards; the row stays, because the withdrawal is part of what happened.
     */
    status: CycleApprovalStatus;
};

/**
 * CycleApprovalStatus
 *
 * Where one sign-off of one cycle stands. Values are part of the API contract.
 */
export const CycleApprovalStatus = { APPROVED: 'approved', REOPENED: 'reopened' } as const;

/**
 * CycleApprovalStatus
 *
 * Where one sign-off of one cycle stands. Values are part of the API contract.
 */
export type CycleApprovalStatus = typeof CycleApprovalStatus[keyof typeof CycleApprovalStatus];

/**
 * CyclePreviewResponse
 *
 * The file this cutoff would produce, and what moved since the last one sent.
 */
export type CyclePreviewResponse = {
    /**
     * Rows added, removed and changed against the last approved snapshot, or against the previous cycle's roster when there is none.
     */
    comparison: SnapshotComparisonResponse;
    /**
     * Contract Id
     */
    contract_id: string;
    /**
     * Cycle Month
     *
     * The month whose cycle this is, written `YYYY-MM`. The cutoff date it resolves to comes from the contract's `reference_date_rule`, and is never sent by a caller.
     */
    cycle_month: string;
    /**
     * Previous Snapshot Id
     *
     * The last approved snapshot this was compared against. `null` when the contract has none before this cycle, in which case it was compared against the roster as of the previous cycle's reference date.
     */
    previous_snapshot_id?: string | null;
    /**
     * Regenerated
     *
     * True when this call replaced a draft rather than creating one.
     */
    regenerated: boolean;
    /**
     * The snapshot as #212 built it. Its `file_sha256` is what to send back in `snapshot_sha256` when approving.
     */
    snapshot: SnapshotResponse;
};

/**
 * CycleReopenRequest
 *
 * Withdraw a cutoff's sign-off, saying why.
 */
export type CycleReopenRequest = {
    /**
     * Cycle Month
     *
     * The month whose cycle this is, written `YYYY-MM`. The cutoff date it resolves to comes from the contract's `reference_date_rule`, and is never sent by a caller.
     */
    cycle_month: string;
    /**
     * Reason
     *
     * Why the cycle is no longer ready to send. Mandatory: it was ready and now it is not, and somebody will ask.
     */
    reason: string;
};

/**
 * CycleStage
 *
 * How far through the month one contract's cycle has got.
 *
 * The rhythm read as a single word, so the screen that answers "what still has
 * to go out this month?" is a filter rather than four booleans every client
 * reconciles for itself. Derived at read time from the cycle's approval and its
 * submission; nothing stores it.
 */
export const CycleStage = {
    OPEN: 'open',
    APPROVED: 'approved',
    PREPARED: 'prepared',
    SENT: 'sent',
    FAILED: 'failed'
} as const;

/**
 * CycleStage
 *
 * How far through the month one contract's cycle has got.
 *
 * The rhythm read as a single word, so the screen that answers "what still has
 * to go out this month?" is a filter rather than four booleans every client
 * reconciles for itself. Derived at read time from the cycle's approval and its
 * submission; nothing stores it.
 */
export type CycleStage = typeof CycleStage[keyof typeof CycleStage];

/**
 * CycleStandingResponse
 *
 * One contract's month, as the rhythm reads it.
 */
export type CycleStandingResponse = {
    /**
     * The sign-off standing for this cycle, or `null`.
     */
    approval?: CycleApprovalResponse | null;
    /**
     * Company Id
     */
    company_id: string;
    /**
     * Company Name
     */
    company_name: string;
    /**
     * Consolidated
     *
     * True when this insurer's layout files one file per company and month rather than one per contract. Every contract of the same `company_id` and `insurer_id` with this flag goes out in that one file, read and worked at `/submissions/companies/{company_id}/insurers/{insurer_id}`; the per-contract prepare, sent and failed operations refuse it.
     */
    consolidated: boolean;
    /**
     * Contract Id
     */
    contract_id: string;
    /**
     * Cutoff Date
     *
     * The single calendar date this cycle reads the roster at. Derived from the contract's `reference_date_rule`; never supplied.
     */
    cutoff_date: string;
    /**
     * Cycle Month
     *
     * The month being filed, written `YYYY-MM`. The cutoff date it resolves to comes from the contract's `reference_date_rule`, and is never sent by a caller.
     */
    cycle_month: string;
    /**
     * Group Id
     *
     * The economic group the contract's company belongs to, if any. With `consolidated: true`, every such contract of the group with this insurer goes out in one file for the whole group instead, read and worked at `/submissions/groups/{group_id}/insurers/{insurer_id}`.
     */
    group_id?: string | null;
    /**
     * Group Name
     */
    group_name?: string | null;
    /**
     * Insurer Code
     */
    insurer_code: string;
    /**
     * Insurer Id
     */
    insurer_id: string;
    /**
     * Insurer Name
     */
    insurer_name: string;
    /**
     * Is Overdue
     *
     * True when the cutoff has passed and the month has still not been filed. A statement about our own calendar rather than about a deadline the insurer set -- the contract records when we read the roster, not when they want the file.
     */
    is_overdue: boolean;
    /**
     * Is Past Cutoff
     *
     * True when the cutoff date has been and gone.
     */
    is_past_cutoff: boolean;
    /**
     * The most recent attempt the insurer refused, if there was one. Shown beside the stage rather than instead of it: a month refused once and then filed is `sent`, and the refusal is still worth seeing.
     */
    last_failure?: SubmissionResponse | null;
    /**
     * Modulo
     */
    modulo: number;
    /**
     * How far through the month this contract has got: `open` while the cutoff is unsigned, `approved` once it is signed and nothing is packaged, `prepared` once the file is out of the building, `sent` once it is filed and evidenced, and `failed` when the last attempt was refused and the cycle is waiting to be packaged again.
     */
    stage: CycleStage;
    /**
     * Subestipulante
     */
    subestipulante: number;
    /**
     * The attempt standing for this cycle -- prepared or filed -- or `null`.
     */
    submission?: SubmissionResponse | null;
};

/**
 * DatabaseHealth
 */
export type DatabaseHealth = {
    /**
     * Error
     *
     * Failure class when the probe did not succeed.
     */
    error?: string | null;
    /**
     * Latency Ms
     *
     * Round-trip time of the connectivity probe, in milliseconds.
     */
    latency_ms?: number | null;
    /**
     * Whether the database answered a trivial query.
     */
    status: ComponentStatus;
};

/**
 * DeltaAction
 *
 * The single change a delta requests. Exactly one per delta, always.
 *
 * The insurer's spreadsheet expresses three of these as mutually exclusive
 * columns. That layout is an artefact of their file and is translated away at
 * the edge; nothing in this product reproduces it -- which is also why a leave
 * has no column there and is not read from a sheet at all.
 */
export const DeltaAction = {
    ADMISSION: 'admission',
    TERMINATION: 'termination',
    SALARY_CHANGE: 'salary_change',
    LEAVE: 'leave'
} as const;

/**
 * DeltaAction
 *
 * The single change a delta requests. Exactly one per delta, always.
 *
 * The insurer's spreadsheet expresses three of these as mutually exclusive
 * columns. That layout is an artefact of their file and is translated away at
 * the edge; nothing in this product reproduces it -- which is also why a leave
 * has no column there and is not read from a sheet at all.
 */
export type DeltaAction = typeof DeltaAction[keyof typeof DeltaAction];

/**
 * DeltaCancellation
 *
 * Withdrawing a delta that has not been approved.
 */
export type DeltaCancellation = {
    /**
     * Reason
     *
     * Optional note about why the request was withdrawn.
     */
    reason?: string | null;
};

/**
 * DeltaCreate
 *
 * A single submission, optionally correcting an approved delta.
 */
export type DeltaCreate = {
    /**
     * Delta
     */
    delta: ({
        action: 'admission';
    } & AdmissionSubmission) | ({
        action: 'termination';
    } & TerminationSubmission) | ({
        action: 'salary_change';
    } & SalaryChangeSubmission) | ({
        action: 'leave';
    } & LeaveSubmission);
    /**
     * Supersedes Delta Id
     *
     * The approved delta this one corrects. An approved delta is never edited; a correction is a new delta that names it here, and both stay visible. Only an approved delta can be superseded -- an unapproved one is cancelled and resubmitted.
     */
    supersedes_delta_id?: string | null;
};

/**
 * DeltaListResponse
 *
 * A page of deltas, newest submission first.
 */
export type DeltaListResponse = {
    /**
     * Count
     *
     * How many deltas are in this page.
     */
    count: number;
    /**
     * Items
     */
    items: Array<DeltaResponse>;
    /**
     * Limit
     */
    limit: number;
    /**
     * Offset
     */
    offset: number;
};

/**
 * DeltaRejection
 *
 * Why a delta is being refused. The reason is mandatory and goes to the submitter.
 */
export type DeltaRejection = {
    /**
     * Reason
     *
     * What is wrong with this request, in words the submitter can act on.
     */
    reason: string;
};

/**
 * DeltaResponse
 *
 * One requested change, and everything known about where it stands.
 */
export type DeltaResponse = {
    action: DeltaAction;
    /**
     * Birth Date
     *
     * Admissions only.
     */
    birth_date?: string | null;
    /**
     * Company Id
     */
    company_id: string;
    /**
     * Contract Id
     */
    contract_id: string;
    /**
     * Cpf
     *
     * Canonical eleven digits.
     */
    cpf: string;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Decided At
     */
    decided_at?: string | null;
    /**
     * Who approved, rejected or cancelled this. `null` while submitted.
     */
    decided_by?: PrincipalReference | null;
    /**
     * Decision Reason
     *
     * Mandatory on a rejection; optional on a cancellation.
     */
    decision_reason?: string | null;
    /**
     * Effective Date
     *
     * When the change takes effect. For a termination this is the last covered day, inclusive; for a leave it is the first day away. A vyttech analyst reviewing the cycle may replace this with the date the signed paperwork says; when they have, `original_effective_date` is what was filed.
     */
    effective_date: string;
    /**
     * Effective Date Corrected At
     */
    effective_date_corrected_at?: string | null;
    /**
     * Who replaced the effective date. `null` if nobody has.
     */
    effective_date_corrected_by?: PrincipalReference | null;
    /**
     * Full Name
     *
     * Admissions only.
     */
    full_name?: string | null;
    /**
     * Id
     */
    id: string;
    /**
     * Identity Verified
     *
     * Whether this person's CPF, name and date of birth were confirmed against the Receita Federal's CPF registry when the movement was filed. Three values, and the third is the one to read carefully.
     *
     * `true` — all three agreed.
     *
     * `false` — **not verified**: the registry could not be reached, timed out, or answered unreadably, or the deployment has no registry credential configured. The movement was accepted anyway, so this marker is the only record that the check never ran. A movement whose name or date of birth *contradicts* the registry is rejected at submission and never appears here, so `false` always means "nobody found out" and never "we found out and it was wrong".
     *
     * `null` — the question does not arise: this is not an admission, or it is one filed before the check existed.
     */
    identity_verified?: boolean | null;
    /**
     * Insured Person Id
     *
     * The roster record this delta is about, once there is one. `null` for an admission of somebody the roster has never seen, until it is approved.
     */
    insured_person_id?: string | null;
    /**
     * Is Retroactive
     *
     * True when the effective date falls on or before the last snapshot submitted to the insurer. Such a delta is accepted and kept, never rejected for being late.
     */
    is_retroactive: boolean;
    /**
     * Leave End Date
     *
     * Leaves only, and optional even there: the last day away, inclusive. `null` on an open-ended leave, which is completed by a later delta that supersedes this one and carries the date.
     */
    leave_end_date?: string | null;
    /**
     * Original Effective Date
     *
     * The effective date as it was submitted, kept when a vyttech analyst replaced it with the one the signed paperwork says. `null` on a movement nobody has corrected, which is almost all of them. The row still answers what the customer filed as well as what we act on.
     */
    original_effective_date?: string | null;
    /**
     * Requires Review
     *
     * True when a human should look at this before it is approved. Today that is exactly the retroactive deltas. Filter the list on `requires_review` to read the review queue.
     */
    requires_review: boolean;
    /**
     * Salary
     *
     * Admissions and salary changes only. An exact decimal, serialised as a string so no precision is lost.
     */
    salary?: string | null;
    status: DeltaStatus;
    /**
     * Submitted At
     */
    submitted_at: string;
    submitted_by: PrincipalReference;
    /**
     * Superseded By Delta Id
     *
     * The delta that corrects this one, if one has been submitted. The original is never edited or removed.
     */
    superseded_by_delta_id?: string | null;
    /**
     * Supersedes Delta Id
     *
     * The approved delta this one corrects, if any.
     */
    supersedes_delta_id?: string | null;
};

/**
 * DeltaStatus
 *
 * Where a delta is in its life. Values are part of the API contract.
 */
export const DeltaStatus = {
    SUBMITTED: 'submitted',
    APPROVED: 'approved',
    REJECTED: 'rejected',
    RETURNED: 'returned',
    CANCELLED: 'cancelled'
} as const;

/**
 * DeltaStatus
 *
 * Where a delta is in its life. Values are part of the API contract.
 */
export type DeltaStatus = typeof DeltaStatus[keyof typeof DeltaStatus];

/**
 * DiagnosticFailure
 *
 * Why a stage failed, as something a client can branch on.
 *
 * The ``summary`` beside it says the same thing in English, for whoever is
 * integrating; this is what a screen keys its own pt-BR sentence on, so the
 * two never have to be kept in step by translating prose.
 */
export const DiagnosticFailure = {
    NO_CERTIFICATE: 'no_certificate',
    CERTIFICATE_UNUSABLE: 'certificate_unusable',
    CERTIFICATE_UNREADABLE: 'certificate_unreadable',
    ENDPOINT_UNREACHABLE: 'endpoint_unreachable',
    HANDSHAKE_REFUSED: 'handshake_refused',
    QUERY_UNANSWERED: 'query_unanswered',
    QUERY_REFUSED: 'query_refused',
    BROWSER_UNAVAILABLE: 'browser_unavailable',
    BROWSER_CERTIFICATE_UNUSABLE: 'browser_certificate_unusable',
    BROWSER_NAVIGATION_FAILED: 'browser_navigation_failed',
    BROWSER_CAPTCHA_CHALLENGED: 'browser_captcha_challenged',
    BROWSER_TWO_STEP_REQUIRED: 'browser_two_step_required',
    BROWSER_TWO_STEP_UNANSWERED: 'browser_two_step_unanswered'
} as const;

/**
 * DiagnosticFailure
 *
 * Why a stage failed, as something a client can branch on.
 *
 * The ``summary`` beside it says the same thing in English, for whoever is
 * integrating; this is what a screen keys its own pt-BR sentence on, so the
 * two never have to be kept in step by translating prose.
 */
export type DiagnosticFailure = typeof DiagnosticFailure[keyof typeof DiagnosticFailure];

/**
 * DiagnosticStage
 *
 * The four checks, in the order they run. Values are part of the contract.
 */
export const DiagnosticStage = {
    CERTIFICATE: 'certificate',
    HANDSHAKE: 'handshake',
    QUERY: 'query',
    BROWSER: 'browser'
} as const;

/**
 * DiagnosticStage
 *
 * The four checks, in the order they run. Values are part of the contract.
 */
export type DiagnosticStage = typeof DiagnosticStage[keyof typeof DiagnosticStage];

/**
 * DocumentChannel
 *
 * How the file got to us.
 *
 * Distinct from :class:`~app.audit.actions.AuditChannel`, which records how the
 * *request* arrived. A PDF pulled out of a mailbox by a later task is uploaded
 * over the API by a machine client, so the audit channel is ``api`` while the
 * document's own channel is ``email`` -- and "which of these came in by email?"
 * is a question about the document, not about the call that carried it.
 */
export const DocumentChannel = {
    PORTAL: 'portal',
    API: 'api',
    EMAIL: 'email'
} as const;

/**
 * DocumentChannel
 *
 * How the file got to us.
 *
 * Distinct from :class:`~app.audit.actions.AuditChannel`, which records how the
 * *request* arrived. A PDF pulled out of a mailbox by a later task is uploaded
 * over the API by a machine client, so the audit channel is ``api`` while the
 * document's own channel is ``email`` -- and "which of these came in by email?"
 * is a question about the document, not about the call that carried it.
 */
export type DocumentChannel = typeof DocumentChannel[keyof typeof DocumentChannel];

/**
 * DocumentKind
 *
 * What sort of paperwork this is.
 *
 * The list is short on purpose. It is not a taxonomy of everything an HR
 * department produces -- it is the distinction that matters when an insurer or
 * a customer disputes a roster change, which is "does this paper prove the
 * termination?". Anything else is ``other``, keyed by a human who had the
 * document in front of them.
 */
export const DocumentKind = {
    TERMINATION_AGREEMENT: 'termination_agreement',
    ADMISSION_PAPERWORK: 'admission_paperwork',
    OTHER: 'other'
} as const;

/**
 * DocumentKind
 *
 * What sort of paperwork this is.
 *
 * The list is short on purpose. It is not a taxonomy of everything an HR
 * department produces -- it is the distinction that matters when an insurer or
 * a customer disputes a roster change, which is "does this paper prove the
 * termination?". Anything else is ``other``, keyed by a human who had the
 * document in front of them.
 */
export type DocumentKind = typeof DocumentKind[keyof typeof DocumentKind];

/**
 * DocumentLinkCreate
 *
 * Which delta a document is being claimed as evidence for.
 */
export type DocumentLinkCreate = {
    /**
     * Delta Id
     *
     * The delta this document justifies. It must belong to the same company as the document; a delta of any other company is reported as unknown.
     */
    delta_id: string;
};

/**
 * DocumentLinkResponse
 *
 * One assertion that a document is evidence for one delta.
 *
 * A removed link is reported too, with ``unlinked_at`` set. That the two were
 * linked for a time is part of the record, so it is not hidden from the client
 * that has to show the history.
 */
export type DocumentLinkResponse = {
    /**
     * Delta Id
     */
    delta_id: string;
    /**
     * Is Live
     *
     * Whether this link currently stands.
     */
    is_live: boolean;
    /**
     * Linked At
     */
    linked_at: string;
    linked_by: PrincipalReference;
    /**
     * Unlinked At
     *
     * When the link was withdrawn. `null` for a live link. The row is kept either way; a link is never deleted.
     */
    unlinked_at?: string | null;
    /**
     * Who withdrew the link. `null` while it is live.
     */
    unlinked_by?: PrincipalReference | null;
};

/**
 * DocumentListResponse
 */
export type DocumentListResponse = {
    /**
     * Count
     *
     * Number of items in this page, not the total available.
     */
    count: number;
    /**
     * Items
     */
    items: Array<DocumentResponse>;
    /**
     * Limit
     */
    limit: number;
    /**
     * Offset
     */
    offset: number;
};

/**
 * DocumentResponse
 *
 * One stored document: what it is, where it came from, and what it backs.
 */
export type DocumentResponse = {
    /**
     * Byte Size
     *
     * Size of the document itself, in bytes.
     */
    byte_size: number;
    /**
     * How the file reached us: `portal`, `api` or `email`. Distinct from the channel the audit trail records, which is how the *request* arrived.
     */
    channel: DocumentChannel;
    /**
     * Company Id
     */
    company_id: string;
    /**
     * Content Sha256
     *
     * SHA-256 of the document, hex. Verified on every download, so a byte that changed in storage is an error rather than a different file.
     */
    content_sha256: string;
    /**
     * Content Type
     *
     * Determined by inspecting the file's own leading bytes, never from the extension or from the `Content-Type` the client declared.
     */
    content_type: string;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Id
     */
    id: string;
    /**
     * Is Current
     *
     * True when nothing has replaced this version. Exactly one version of a chain.
     */
    is_current: boolean;
    /**
     * Is Unmatched
     *
     * True when no delta is currently linked. An ordinary state, not an error: paperwork often arrives before anyone knows which change it belongs to.
     */
    is_unmatched: boolean;
    kind: DocumentKind;
    /**
     * Linked Delta Ids
     *
     * The deltas this document is linked to right now. Empty when unmatched.
     */
    linked_delta_ids?: Array<string>;
    /**
     * Links
     *
     * Every delta this document has been claimed as evidence for, live and withdrawn, oldest first.
     */
    links?: Array<DocumentLinkResponse>;
    /**
     * Original Filename
     *
     * The name the uploader's file had. A label only: the extension is not consulted for anything, and nothing is parsed out of it.
     */
    original_filename: string;
    /**
     * Received At
     */
    received_at: string;
    /**
     * Superseded By Document Id
     *
     * The version that replaced this one, if one has. The superseded document is kept and stays downloadable; it is simply no longer current.
     */
    superseded_by_document_id?: string | null;
    /**
     * Supersedes Document Id
     *
     * The version this one replaces, if it replaces one.
     */
    supersedes_document_id?: string | null;
    supplied_by: PrincipalReference;
};

/**
 * EffectiveDateCorrection
 *
 * Replace a movement's effective date with the one the paperwork says.
 */
export type EffectiveDateCorrection = {
    /**
     * Effective Date
     *
     * The date this movement really takes effect. For a termination it is the last covered day, inclusive. The value being replaced is kept on the movement as `original_effective_date`.
     */
    effective_date: string;
};

/**
 * EligibleMovementListResponse
 *
 * Everything on this contract the insurer has not been told about.
 */
export type EligibleMovementListResponse = {
    /**
     * Company Id
     */
    company_id: string;
    /**
     * Contract Id
     */
    contract_id: string;
    /**
     * Count
     */
    count: number;
    /**
     * Items
     */
    items: Array<EligibleMovementResponse>;
    /**
     * Notifiable Count
     *
     * How many of them this contract's rule would let go early.
     */
    notifiable_count: number;
    /**
     * Off Cycle Rule
     *
     * The contract's own permission, repeated here so a client reading this listing knows whether anything in it can actually be notified without a second call. `not_permitted` means every notice on this contract is a `409`, which is the contract being quoted back rather than a fault.
     */
    off_cycle_rule: string;
    /**
     * Today
     *
     * Today in São Paulo, as this listing measured the countdowns against.
     */
    today: string;
};

/**
 * EligibleMovementResponse
 *
 * One approved movement the insurer has not been told about yet.
 */
export type EligibleMovementResponse = {
    /**
     * Cutoff Date
     *
     * The single calendar date that cycle reads the roster at -- the cutoff this movement is waiting for. Derived from the contract's `reference_date_rule`; never supplied.
     */
    cutoff_date: string;
    /**
     * Cycle Month
     *
     * The cycle whose monthly file carries this movement anyway, written `YYYY-MM`. An off-cycle notice does not replace that file and does not take the movement off it: the roster is effective-dated, the snapshot projects it, and the person appears on the next file exactly as they would have. The insurer reconciles against that file, so a person left off it because they had been notified early would vanish from their records.
     */
    cycle_month: string;
    /**
     * Days Until Cutoff
     *
     * Days between today in São Paulo and that cutoff. Negative when the cutoff has already passed, which happens to a movement approved late: the file it goes on is still the next one to be produced.
     *
     * A statement about our own calendar rather than about a deadline the insurer set -- the contract records when we read the roster, not when they process what we send. Whether the wait is too long is a judgement, and it is recorded as the notice's `reason` rather than decided here.
     */
    days_until_cutoff: number;
    /**
     * The movement itself, exactly as the movements API serves it.
     */
    delta: DeltaResponse;
    /**
     * Is Notifiable
     *
     * Whether this contract's `off_cycle_rule` lets this action go early. Reported rather than filtered out, so a screen can say which movements could be notified and which wait, instead of silently showing one kind.
     */
    is_notifiable: boolean;
};

/**
 * ErrorBody
 */
export type ErrorBody = {
    /**
     * Stable, machine-readable identifier for the failure.
     */
    code: ErrorCode;
    /**
     * Correlation Id
     *
     * Correlation id of the request that produced this error.
     */
    correlation_id?: string | null;
    /**
     * Details
     *
     * Zero or more specific problems that make up this failure.
     */
    details?: Array<ErrorDetail>;
    /**
     * Message
     *
     * Human-readable summary. Not intended for machine parsing.
     */
    message: string;
};

/**
 * ErrorCode
 *
 * Machine-readable error codes. Values are part of the public contract.
 */
export const ErrorCode = {
    BAD_REQUEST: 'bad_request',
    VALIDATION_ERROR: 'validation_error',
    IDENTITY_MISMATCH: 'identity_mismatch',
    UNAUTHORIZED: 'unauthorized',
    FORBIDDEN: 'forbidden',
    NOT_FOUND: 'not_found',
    METHOD_NOT_ALLOWED: 'method_not_allowed',
    CONFLICT: 'conflict',
    IDEMPOTENCY_KEY_REQUIRED: 'idempotency_key_required',
    IDEMPOTENCY_KEY_REUSED: 'idempotency_key_reused',
    IDEMPOTENCY_KEY_IN_PROGRESS: 'idempotency_key_in_progress',
    PAYLOAD_TOO_LARGE: 'payload_too_large',
    UNSUPPORTED_MEDIA_TYPE: 'unsupported_media_type',
    UNPROCESSABLE_ENTITY: 'unprocessable_entity',
    TOO_MANY_REQUESTS: 'too_many_requests',
    INTERNAL_ERROR: 'internal_error',
    SERVICE_UNAVAILABLE: 'service_unavailable'
} as const;

/**
 * ErrorCode
 *
 * Machine-readable error codes. Values are part of the public contract.
 */
export type ErrorCode = typeof ErrorCode[keyof typeof ErrorCode];

/**
 * ErrorDetail
 *
 * One specific thing that was wrong, e.g. a single invalid field.
 */
export type ErrorDetail = {
    /**
     * Column
     *
     * The column a `missing_columns` refusal is about, as the published template labels it. One detail per missing column, so a sheet missing three has three details. `null` for every other reason.
     */
    column?: string | null;
    /**
     * Field
     *
     * Dotted path to the offending input, when the problem is input-specific. It names the field a client should highlight or correct.
     */
    field?: string | null;
    /**
     * Limit
     *
     * The ceiling a `too_many_rows` or `file_too_large` refusal exceeded: a count of data rows, or a size in bytes. `null` for every other reason.
     */
    limit?: number | null;
    /**
     * Message
     *
     * Human-readable description of this detail.
     */
    message: string;
    /**
     * Why an uploaded file was refused as a whole, when that is the failure. Set on a `validation_error` from a spreadsheet upload, and stable where `message` is not: branch on this, never on the prose. `null` on every other kind of detail.
     */
    reason?: FileRefusalReason | null;
    /**
     * Registry Value
     *
     * What an external registry of record holds for `field`, when the failure is that the request disagrees with it. Set on `identity_mismatch`: the name as the Receita Federal wrote it, or the date of birth as ISO 8601. `null` on every other kind of detail, and on a CPF the registry has never issued -- there is no record there to quote.
     */
    registry_value?: string | null;
};

/**
 * ErrorEnvelope
 *
 * The response body returned by *every* non-2xx response.
 */
export type ErrorEnvelope = {
    error: ErrorBody;
};

/**
 * EsocialCertificateSummary
 *
 * The certificate the run tested, identified well enough to act on.
 *
 * A subset of `A1CertificateResponse`, not a second source of truth: every
 * field here is copied from the same row that endpoint serves, and nothing
 * here is absent from it.
 */
export type EsocialCertificateSummary = {
    /**
     * Fingerprint Sha256
     *
     * SHA-256 of the certificate's DER encoding -- a digest of its public half.
     */
    fingerprint_sha256: string;
    /**
     * Id
     */
    id: string;
    /**
     * Not Valid After
     */
    not_valid_after: string;
    /**
     * Derived on every read, exactly as `GET /certificates` derives it.
     */
    status: CertificateStatus;
    /**
     * Subject Cnpj
     */
    subject_cnpj: string;
    /**
     * Subject Cnpj Formatted
     */
    subject_cnpj_formatted: string;
    /**
     * Subject Name
     */
    subject_name: string;
};

/**
 * EsocialDiagnosticRequest
 *
 * What to test. Everything on it is optional, because the useful case is empty.
 */
export type EsocialDiagnosticRequest = {
    /**
     * Browser Url
     *
     * The page the `browser` stage visits, for this run only. Omit it to visit `I8LIFE_BROWSER_DIAGNOSTIC_URL`, the eSocial login page by default. Must be an `https://` URL.
     *
     * **This chooses where the browser goes, not who sees the certificate.** The A1 certificate is offered only to the origins in `I8LIFE_BROWSER_CERTIFICATE_ORIGINS`; a URL on any other origin is visited with nothing offered, and the stage's summary says so.
     */
    browser_url?: string | null;
    /**
     * Company Id
     *
     * The customer company to run the employer query for. Omit it to test the certificate and the connection alone, which is what somebody setting a deployment up needs and needs first -- there is nothing to select yet at that point.
     *
     * Naming a company whose CNPJ has not been recorded is `422`, **before any network call is made**: eSocial identifies an employer by its CNPJ and by nothing else, so there is no request to send.
     */
    company_id?: string | null;
};

/**
 * EsocialDiagnosticResponse
 *
 * The whole run: every stage, and the facts the stages turned up.
 */
export type EsocialDiagnosticResponse = {
    /**
     * Which browser the `browser` stage ran on (`I8LIFE_BROWSER_BACKEND`): `chromium`, headless on the backend, or `remote_chromium`, a Chromium running elsewhere and driven over the DevTools protocol, which is never given the certificate. `null` when the stage did not run.
     */
    browser_backend?: BrowserBackend | null;
    /**
     * Browser Final Url
     *
     * Where the `browser` stage's visit ended, after redirects. `null` unless that stage passed.
     */
    browser_final_url?: string | null;
    /**
     * Browser Http Status
     *
     * The HTTP status of the page the visit ended on. `null` unless the `browser` stage passed, or when the page had no response of its own.
     */
    browser_http_status?: number | null;
    /**
     * Browser Page Title
     *
     * The title of that page. `null` unless the `browser` stage passed.
     */
    browser_page_title?: string | null;
    /**
     * Browser Screenshot Png Base64
     *
     * A PNG screenshot of that page, base64-encoded. `null` unless the `browser` stage passed. It is the evidence of how the portal treated the visit -- a public login page or an error -- and it is not kept: the audit entry records the stage, the final URL and the status, never the picture.
     */
    browser_screenshot_png_base64?: string | null;
    /**
     * The certificate the run picked -- the newest installed one that may still be used. `null` only when none is installed at all.
     */
    certificate?: EsocialCertificateSummary | null;
    /**
     * Company Id
     *
     * The company the query stage asked about, when one was named.
     */
    company_id?: string | null;
    /**
     * Employer Cnpj
     *
     * That company's CNPJ, which is what eSocial was asked about.
     */
    employer_cnpj?: string | null;
    /**
     * Employer Cnpj Formatted
     */
    employer_cnpj_formatted?: string | null;
    /**
     * Endpoint
     *
     * The address the handshake stage dialled.
     */
    endpoint: string;
    /**
     * Which eSocial this deployment is pointed at: `producao_restrita`, the government's rehearsal environment, or `producao`. Set with `I8LIFE_ESOCIAL_ENVIRONMENT`.
     */
    environment: EsocialEnvironment;
    /**
     * Esocial Response Code
     *
     * eSocial's own `cdResposta` for the query stage, reported verbatim whether it was an acceptance or a refusal. `null` when the query stage did not run.
     */
    esocial_response_code?: number | null;
    /**
     * Esocial Response Message
     *
     * eSocial's own `descResposta`, in its words and its language.
     */
    esocial_response_message?: string | null;
    /**
     * Events Found
     *
     * How many events eSocial reported for that employer in the window, when the query stage passed. Zero is a pass: an employer with nothing filed this month is answered, and being answered is what the stage tests.
     */
    events_found?: number | null;
    /**
     * Passed
     *
     * True when no stage failed. A skipped stage is not a failure.
     */
    passed: boolean;
    /**
     * Stages
     *
     * All four checks, in the order they run.
     */
    stages: Array<EsocialStageResponse>;
    /**
     * Tls Version
     *
     * The protocol the handshake negotiated, when it completed.
     */
    tls_version?: string | null;
};

/**
 * EsocialEnvironment
 *
 * Which eSocial a deployment talks to. Values are part of the API contract.
 */
export const EsocialEnvironment = { PRODUCAO_RESTRITA: 'producao_restrita', PRODUCAO: 'producao' } as const;

/**
 * EsocialEnvironment
 *
 * Which eSocial a deployment talks to. Values are part of the API contract.
 */
export type EsocialEnvironment = typeof EsocialEnvironment[keyof typeof EsocialEnvironment];

/**
 * EsocialEventListResponse
 */
export type EsocialEventListResponse = {
    /**
     * Count
     */
    count: number;
    /**
     * Items
     */
    items: Array<EsocialEventResponse>;
    /**
     * Limit
     */
    limit: number;
    /**
     * Offset
     */
    offset: number;
};

/**
 * EsocialEventResponse
 *
 * One staged event: what it is, who it is about, when, and what came of it.
 *
 * **A staged event is not a roster change and neither is a committed one.**
 * Fetching stages it and creates nothing; confirming the run turns it into a
 * *submitted* delta, which is a request waiting for a vyttech reviewer. No
 * roster fact moves on either step.
 *
 * The event's own XML is not in this shape and cannot be: it is sealed in the
 * object store and served, whole and unmodified, by its own endpoint.
 */
export type EsocialEventResponse = {
    /**
     * Company Id
     */
    company_id: string;
    /**
     * Delta Id
     *
     * The movement this event became, present only on a `committed` one. It is what makes the delta's intake channel `esocial` in the review queue — there is no channel column on a movement.
     */
    delta_id?: string | null;
    /**
     * Employer Cnpj
     */
    employer_cnpj: string;
    /**
     * Employer Cnpj Formatted
     */
    employer_cnpj_formatted: string;
    /**
     * Esocial Event Id
     *
     * eSocial's own identifier for the event, which is what makes it unique for this employer. Two fetches over overlapping windows stage it once.
     */
    esocial_event_id: string;
    /**
     * Event Date
     *
     * The date the event is *about*: the admission, the termination, the first day away, the day a change takes effect. `null` when this version did not recognise the event's shape -- the event is staged either way, and its bytes are kept.
     */
    event_date?: string | null;
    /**
     * Event End Date
     *
     * The second date, on the one event type that has one: the last day of an *afastamento*, when it was already known when the event was filed.
     */
    event_end_date?: string | null;
    /**
     * eSocial's own code: `S-2200` admission, `S-2205` registration change, `S-2206` contract change, `S-2230` leave, `S-2299` termination. Their codes rather than a vocabulary of ours, because they are what the developer manual and the employer's accountant both use.
     */
    event_type: EsocialEventType;
    /**
     * Fetch Id
     *
     * The run that brought this event in, so it can be traced to a person.
     */
    fetch_id: string;
    /**
     * Id
     */
    id: string;
    /**
     * Outcome Reason
     *
     * Why this event produced no delta: the delta layer's own refusal, or the sentence saying what the event changed that the roster does not model. Present exactly on `no_action` and `refused`, and carrying no CPF, name or salary — the reasons are written to earn a plain column.
     */
    outcome_reason?: string | null;
    /**
     * Period
     *
     * The `YYYY-MM` competence the event belongs to, for grouping.
     */
    period?: string | null;
    /**
     * Receipt
     *
     * The receipt number eSocial answered the original filing with.
     */
    receipt?: string | null;
    /**
     * Staged At
     */
    staged_at: string;
    /**
     * Where this event got to.
     *
     * - **`staged`** — kept, and nothing made of it: no delta, no roster fact, no review item. Every event a fetch brings back starts here.
     * - **`committed`** — translated into a requested change. `delta_id` names it, and it is `submitted`: a vyttech reviewer still has to approve it before any roster fact moves.
     * - **`no_action`** — read, and carrying no change this product's roster models. `outcome_reason` says which. No delta exists and none is missing.
     * - **`refused`** — the delta layer would not accept it. `outcome_reason` carries its own words. Nothing was written.
     */
    status: StagedEventStatus;
    /**
     * Worker Cpf Masked
     *
     * The CPF the event names, masked. Enough to recognise a person beside a name, and not enough to be a copy of their registry number in a log, a screenshot or a support ticket. The full number is encrypted at rest and is not served by this API.
     */
    worker_cpf_masked?: string | null;
    /**
     * Worker Name
     *
     * The name the event carries, when it carries one. Absent from the events eSocial files about somebody it assumes we already know.
     */
    worker_name?: string | null;
};

/**
 * EsocialEventType
 *
 * The five eSocial events this product fetches, by eSocial's own code.
 *
 * The codes are passed through rather than mapped onto a vocabulary of ours,
 * because they are what the developer manual, the employer's accountant and
 * the government's own error messages all use. A translation would mean an
 * operator reading ``admissao`` on our screen and ``S-2200`` in the ticket
 * they are about to open.
 */
export const EsocialEventType = {
    S_2200: 'S-2200',
    S_2205: 'S-2205',
    S_2206: 'S-2206',
    S_2230: 'S-2230',
    S_2299: 'S-2299'
} as const;

/**
 * EsocialEventType
 *
 * The five eSocial events this product fetches, by eSocial's own code.
 *
 * The codes are passed through rather than mapped onto a vocabulary of ours,
 * because they are what the developer manual, the employer's accountant and
 * the government's own error messages all use. A translation would mean an
 * operator reading ``admissao`` on our screen and ``S-2200`` in the ticket
 * they are about to open.
 */
export type EsocialEventType = typeof EsocialEventType[keyof typeof EsocialEventType];

/**
 * EsocialFetchCommitResponse
 *
 * What confirming one run did: the run as it now stands, and every event of it.
 *
 * The three counts on `fetch` account for every event the run staged:
 * `committed + no_action + refused = staged`. Each event carries its own
 * outcome, so a batch where one movement was refused is readable line by line
 * rather than as a number.
 */
export type EsocialFetchCommitResponse = {
    /**
     * Events
     *
     * Every event of this run, in the order it was staged, each with what became of it: a `delta_id`, a refusal, or the reason it needed nothing.
     */
    events: Array<EsocialEventResponse>;
    fetch: EsocialFetchResponse;
};

/**
 * EsocialFetchConfirmRequest
 *
 * Which contract the run's movements belong to. The one thing eSocial cannot say.
 */
export type EsocialFetchConfirmRequest = {
    /**
     * Contract Id
     *
     * The contract to file the movements against. Required, and it has to belong to the company the run asked about — a contract of another company is `422`.
     *
     * It is an argument rather than something derived because the government's answer does not contain it: eSocial identifies an employer by CNPJ, and a company with two contracts has two populations the same employer files for. Guessing between them would put people on the wrong insurer's file.
     */
    contract_id: string;
};

/**
 * EsocialFetchListResponse
 */
export type EsocialFetchListResponse = {
    /**
     * Count
     */
    count: number;
    /**
     * Items
     */
    items: Array<EsocialFetchResponse>;
    /**
     * Limit
     */
    limit: number;
    /**
     * Offset
     */
    offset: number;
};

/**
 * EsocialFetchRequest
 *
 * Which company to fetch for, and optionally over which stretch of days.
 */
export type EsocialFetchRequest = {
    /**
     * Company Id
     *
     * The customer company whose events to fetch. Its recorded CNPJ is what eSocial is asked about, so a company without one is `422` **before any network call is made** -- eSocial identifies an employer by its CNPJ and by nothing else.
     */
    company_id: string;
    /**
     * Window End
     *
     * Last day to ask about, inclusive. Must be on or after `window_start`; a window that ends before it starts is `422`.
     */
    window_end?: string | null;
    /**
     * Window Start
     *
     * First day to ask about, inclusive. Omit both ends for the configured default window, which is what the button on the staff screen sends.
     */
    window_start?: string | null;
};

/**
 * EsocialFetchResponse
 *
 * One run of the intake: who it asked about, what it spent, and what came of it.
 *
 * The four counts are the summary an operator watches, and they account for
 * each other: `staged + skipped + failed = fetched`. `refused_consultations`
 * is beside them rather than in them, because a worker eSocial would not
 * answer about contributed no events to account for -- and "one event would
 * not download" and "the government would not answer" are different problems.
 * `refused_event_types` is the same number under its older name, kept because
 * nothing published in `/api/v1` is ever withdrawn.
 *
 * **A run is a slice of a company, not a sweep of one.** Discovery is one
 * consultation per worker -- the employer consultation answers `402` to every
 * event type this product reads -- and eSocial allows about ten calls a day,
 * so `call_budget`, `calls_spent`, `workers_consulted`, `workers_remaining`
 * and `budget_exhausted` are how much of the employer this run actually
 * looked at. They are what tells `refused`, `nothing filed` and `the budget
 * ran out` apart, which are three different mornings and used to look alike.
 */
export type EsocialFetchResponse = {
    /**
     * Budget Exhausted
     *
     * Whether the run stopped because it had no calls left, with workers still unconsulted. **This is the third answer an empty result can have**, beside `the employer filed nothing` and `the government refused us`: run it again and it carries on with `workers_remaining`.
     */
    budget_exhausted?: boolean;
    /**
     * Call Budget
     *
     * How many eSocial calls this run was allowed — consultations and downloads counted together, because the government's own daily allowance counts them together.
     */
    call_budget: number;
    /**
     * Calls Spent
     *
     * How many it made. Never more than `call_budget`.
     */
    calls_spent: number;
    /**
     * Committed
     *
     * Staged events that became submitted movements. Zero until confirmed.
     */
    committed?: number;
    /**
     * Company Id
     */
    company_id: string;
    /**
     * Confirmed At
     */
    confirmed_at?: string | null;
    /**
     * Confirmed By Display Name
     *
     * Who confirmed it. Always a vyttech staff member.
     */
    confirmed_by_display_name?: string | null;
    /**
     * Consulted Cpfs Masked
     *
     * The workers this run consulted, masked. **The full number is nowhere in this contract**: the run stores a keyed digest beside the mask, the eleven digits travel only inside the request eSocial is sent, and that request is sealed.
     */
    consulted_cpfs_masked?: Array<string>;
    /**
     * Contract Id
     *
     * The contract the events were committed against, named by whoever confirmed. `null` until then: eSocial answers about an employer, and which of that employer's contracts a movement belongs to is not in the government's answer.
     */
    contract_id?: string | null;
    /**
     * Employer Cnpj
     */
    employer_cnpj: string;
    /**
     * Employer Cnpj Formatted
     */
    employer_cnpj_formatted: string;
    /**
     * Which eSocial this run talked to, as it was configured at the time.
     */
    environment: EsocialEnvironment;
    /**
     * Esocial Response Code
     *
     * eSocial's own `cdResposta`, reported verbatim.
     */
    esocial_response_code?: number | null;
    /**
     * Failed
     *
     * How many were listed and could not be taken.
     */
    failed: number;
    /**
     * Failure Reason
     *
     * What went wrong, in one sentence, when something did -- usually eSocial's own `descResposta`, in their words and their language.
     */
    failure_reason?: string | null;
    /**
     * Fetched
     *
     * Events eSocial listed for this employer across the five types.
     */
    fetched: number;
    /**
     * Finished At
     */
    finished_at?: string | null;
    /**
     * Id
     */
    id: string;
    /**
     * Is Confirmed
     *
     * Whether this batch has been turned into movements. A run is confirmed once; confirming it again is `409`.
     */
    is_confirmed?: boolean;
    /**
     * No Action
     *
     * Staged events carrying no change this roster models — a cadastral edit to a field we do not track, an *alteração* with no remuneration in it. Each one says which, on the event.
     */
    no_action?: number;
    /**
     * Refused
     *
     * Staged events the delta layer would not accept. Each carries the refusal's own words; the rest of the batch committed regardless.
     */
    refused?: number;
    /**
     * Refused Consultations
     *
     * How many worker consultations eSocial would not answer at all. Not part of the sum above: a consultation nobody could make listed no events.
     */
    refused_consultations: number;
    /**
     * Refused Event Types
     *
     * **Deprecated; read `refused_consultations` instead**, which carries the same number. It was the count of *event types* the employer consultation would not list, and there are no event types being asked for any more: produção answers `402` to every one of them on that method, so discovery asks about one worker at a time. The field stays because nothing published in `/api/v1` is ever withdrawn — see `docs/partner/versioning.md` — and it still answers the question it was read for: *how many listings did the government refuse?*
     *
     * @deprecated
     */
    refused_event_types: number;
    /**
     * Remaining Cpfs Masked
     *
     * The workers still to ask about, masked. Same discipline.
     */
    remaining_cpfs_masked?: Array<string>;
    /**
     * Requested By Display Name
     *
     * Who ran it. Email for a person, client id for a machine.
     */
    requested_by_display_name: string;
    /**
     * Roster Fallback
     *
     * Whether discovery had no stored monthly worker list to work from and used the roster alone. It matters: a roster-only run **cannot find an admission nobody has told us about**, by construction. Capture the month's `S-1299` closing at `POST /esocial/worker-lists` first.
     */
    roster_fallback?: boolean;
    /**
     * Skipped
     *
     * How many we already held, recognised by eSocial's own event identifier. Running the same fetch twice stages nothing and skips everything.
     */
    skipped: number;
    /**
     * Staged
     *
     * Of those, how many became new staged rows.
     */
    staged: number;
    /**
     * Started At
     */
    started_at: string;
    /**
     * `completed` when every event type was answered and everything listed was taken or already held; `partial` when some of it worked; `failed` when none of it did. **Zero events staged is `completed`** -- an employer who filed nothing in the window is an answer, and the usual one.
     */
    status: EsocialFetchStatus;
    /**
     * Window End
     */
    window_end: string;
    /**
     * Window Start
     */
    window_start: string;
    /**
     * Workers Consulted
     *
     * How many workers this run asked eSocial about.
     */
    workers_consulted?: number;
    /**
     * Workers Remaining
     *
     * How many were left for the next run over the same window.
     */
    workers_remaining?: number;
};

/**
 * EsocialFetchResultResponse
 *
 * What one press of the button did: the run, and the events it staged.
 */
export type EsocialFetchResultResponse = {
    /**
     * Events
     *
     * The events this run staged, newest last. Only the new ones: an event the fetch skipped as already held is counted and not repeated here.
     */
    events: Array<EsocialEventResponse>;
    fetch: EsocialFetchResponse;
};

/**
 * EsocialFetchStatus
 *
 * How one fetch run ended, as a thing a screen colours a row by.
 *
 * Derived from what the run did rather than decided independently of it -- see
 * :meth:`of` -- so a run cannot claim to have completed while reporting
 * failures. It is persisted anyway, because "show me the fetches that went
 * wrong" should be an indexed filter rather than arithmetic over four columns.
 *
 * **Zero events is not a failure.** An employer who filed nothing in the window
 * is an answer, and the most common one on a quiet month; a status that made it
 * look like a fault would teach an operator to ignore the colour.
 */
export const EsocialFetchStatus = {
    COMPLETED: 'completed',
    PARTIAL: 'partial',
    FAILED: 'failed'
} as const;

/**
 * EsocialFetchStatus
 *
 * How one fetch run ended, as a thing a screen colours a row by.
 *
 * Derived from what the run did rather than decided independently of it -- see
 * :meth:`of` -- so a run cannot claim to have completed while reporting
 * failures. It is persisted anyway, because "show me the fetches that went
 * wrong" should be an indexed filter rather than arithmetic over four columns.
 *
 * **Zero events is not a failure.** An employer who filed nothing in the window
 * is an answer, and the most common one on a quiet month; a status that made it
 * look like a fault would teach an operator to ignore the colour.
 */
export type EsocialFetchStatus = typeof EsocialFetchStatus[keyof typeof EsocialFetchStatus];

/**
 * EsocialPortalBrowserResponse
 *
 * Which browser this deployment's portal walks drive (#477). Never where it is.
 */
export type EsocialPortalBrowserResponse = {
    /**
     * `I8LIFE_BROWSER_BACKEND`: `chromium` (headless, on the backend), `remote_chromium` (a Chromium somebody else runs, reached over the DevTools protocol), `memory` (the test suite's) or `disabled`.
     */
    backend: BrowserBackend;
    /**
     * Remote
     *
     * Whether a person is at the browser: on a remote one they sign in to gov.br and solve the captcha when it shows, and the walk does the rest.
     */
    remote: boolean;
};

/**
 * EsocialPortalSessionCompany
 *
 * One company of a session: its walk, how it went, and how long it took.
 */
export type EsocialPortalSessionCompany = {
    /**
     * Company Id
     */
    company_id: string;
    /**
     * Duration Seconds
     *
     * How long its walk took, in seconds.
     */
    duration_seconds?: number | null;
    /**
     * Employer Cnpj
     */
    employer_cnpj: string;
    /**
     * Employer Cnpj Formatted
     */
    employer_cnpj_formatted: string;
    failed_stage?: WalkStage | null;
    /**
     * Failure
     *
     * The failed stage's `failure`, sanitised.
     */
    failure?: string | null;
    /**
     * Failure Code
     *
     * `procuracao_missing` when the chooser refused the company's CNPJ; `session_expired` when the session's login had gone at its start -- it and every later company get it, and the session never signs in again; otherwise whatever the failed stage's `failure_code` is.
     */
    failure_code?: DiagnosticFailure | PortalWalkFailure | null;
    /**
     * Finished At
     */
    finished_at?: string | null;
    /**
     * `completed`: every stage planned for it ran. `failed`: a stage failed, or the session's login had gone -- `failure_code` says which. `skipped`: not walked, `skipped_reason` says why.
     */
    outcome: CompanyOutcome;
    /**
     * Portal Message
     *
     * The portal's own words on the failed stage, verbatim and scrubbed.
     */
    portal_message?: string | null;
    /**
     * Position
     *
     * Its place in the session, from zero.
     */
    position: number;
    /**
     * Remedy
     *
     * What to go and do about `failure_code`. Names the company.
     */
    remedy?: string | null;
    /**
     * Skipped Reason
     */
    skipped_reason?: 'login_failed' | 'stop_after' | 'browser_closed' | null;
    /**
     * Started At
     */
    started_at?: string | null;
    /**
     * Walk Id
     *
     * Its walk, readable at `/esocial/portal-walks/{walk_id}` with its evidence and bundle. `null` for a company that was not walked.
     */
    walk_id?: string | null;
};

/**
 * EsocialPortalSessionListResponse
 */
export type EsocialPortalSessionListResponse = {
    /**
     * Count
     */
    count: number;
    /**
     * Items
     */
    items: Array<EsocialPortalSessionResponse>;
    /**
     * Limit
     */
    limit: number;
    /**
     * Offset
     */
    offset: number;
};

/**
 * EsocialPortalSessionRequest
 *
 * One session: the companies in order, and the plan every one of them follows.
 */
export type EsocialPortalSessionRequest = {
    /**
     * For a `collect` session only.
     */
    collect?: EsocialPortalWalkCollect | null;
    /**
     * Company Ids
     *
     * The companies to walk, in order, 1 to 50, each once. Every one must be visible to the caller and hold a CNPJ, or the session is refused with `422` before any browser is opened.
     */
    company_ids: Array<string>;
    /**
     * For a `request` session only; every company's request.
     */
    filters?: EsocialPortalWalkFilters | null;
    /**
     * As on a single walk: `request` or `collect`, for every company.
     */
    mode: WalkMode;
    /**
     * `login` stops after the shared login, every company `skipped`; any other stage of the mode applies to every company's walk.
     */
    stop_after?: WalkStage | null;
    /**
     * Timeout Seconds
     *
     * As on a single walk: how long each page action may take.
     */
    timeout_seconds?: number | null;
};

/**
 * EsocialPortalSessionResponse
 *
 * One session: the shared login, and one entry per company, in order.
 */
export type EsocialPortalSessionResponse = {
    browser_backend?: BrowserBackend | null;
    collect?: EsocialPortalWalkCollectEcho | null;
    /**
     * Companies
     */
    companies: Array<EsocialPortalSessionCompany>;
    /**
     * Duration Seconds
     *
     * From the session's start to its end, in seconds: what the reports module's 30-minute countdown is judged against.
     */
    duration_seconds?: number | null;
    environment: EsocialEnvironment;
    filters: EsocialPortalWalkFiltersEcho;
    /**
     * Finished At
     */
    finished_at?: string | null;
    /**
     * Id
     */
    id: string;
    /**
     * The shared login, recorded exactly as a single walk's `login` stage is -- evidence, certificate hops, trace -- and readable at `/esocial/portal-sessions/{id}/login/{artefact}`.
     */
    login: EsocialPortalWalkStage;
    mode: WalkMode;
    /**
     * `completed` when every company ran to its last planned stage (with `stop_after=login`, when the login did); `partial` when at least one company failed or was not walked; `failed` when the shared login failed, every company then `skipped` with `login_failed`.
     */
    outcome?: SessionOutcome | null;
    /**
     * Requested By Display Name
     */
    requested_by_display_name: string;
    /**
     * Started At
     */
    started_at: string;
    stop_after?: WalkStage | null;
    /**
     * Timeout Seconds
     */
    timeout_seconds?: number | null;
};

/**
 * EsocialPortalWalkActionScreenshot
 *
 * A viewport screenshot taken right after one action of a stage.
 */
export type EsocialPortalWalkActionScreenshot = {
    /**
     * Action
     */
    action: 'navigate' | 'click' | 'fill' | 'choose' | 'set_checked';
    /**
     * Number
     *
     * The action's place in the stage, from one, counting every navigate, click, fill, choose and set_checked. Readable at `/esocial/portal-walks/{run_id}/stages/{index}/actions/{number}`.
     */
    number: number;
    /**
     * Slug
     *
     * The action and its target as a file name's tail, as the bundle names it.
     */
    slug: string;
    /**
     * Target
     *
     * What it acted on, in words: the locator, or the URL navigated to.
     */
    target?: string | null;
};

/**
 * EsocialPortalWalkCertificateHop
 *
 * One request of the certificate leg (#457), made by the backend rather than the browser.
 *
 * Never a query string, a cookie value or a body: gov.br's
 * ``authorization_id`` and the sign-in's code live in the queries, and the
 * session in the values.
 */
export type EsocialPortalWalkCertificateHop = {
    /**
     * Cookies Set
     *
     * The names of the cookies this answer set. Names only, never values.
     */
    cookies_set?: Array<string>;
    /**
     * Location
     *
     * Where a redirect pointed, without its query string; `null` for a page.
     */
    location?: string | null;
    /**
     * Method
     *
     * The HTTP method.
     */
    method: string;
    /**
     * Status
     *
     * The answer's HTTP status; `null` when the connection itself failed.
     */
    status?: number | null;
    /**
     * Url
     *
     * Where the request went, without its query string.
     */
    url: string;
};

/**
 * EsocialPortalWalkCollect
 *
 * Which report a `collect` run downloads, and in which format.
 */
export type EsocialPortalWalkCollect = {
    /**
     * The download format: xlsx, csv or pdf.
     */
    format?: FileFormat;
    /**
     * Requested At
     *
     * The *Solicitado em* of the report to download, matched to the precision the portal shows (minutes, or seconds when it shows them), in São Paulo time when no offset is given. Absent: the newest *Processado* report.
     */
    requested_at?: string | null;
};

/**
 * EsocialPortalWalkCollectEcho
 */
export type EsocialPortalWalkCollectEcho = {
    format: FileFormat;
    /**
     * Requested At
     */
    requested_at?: string | null;
};

/**
 * EsocialPortalWalkFilters
 *
 * The *Relação de Trabalhadores* filters a `request` run types into the portal.
 *
 * Every one optional. A range whose end comes before its start, and a CPF
 * that is not one, are refused here with `422` — before any browser is
 * launched, because each request the portal refuses is one of its ten a day.
 */
export type EsocialPortalWalkFilters = {
    /**
     * Active Only
     *
     * Only workers whose vínculo is active.
     */
    active_only?: boolean | null;
    /**
     * Admission From
     */
    admission_from?: string | null;
    /**
     * Admission To
     */
    admission_to?: string | null;
    /**
     * Cpf
     *
     * One worker. Checked as a CPF before launch, typed into the portal's form, stored only as a blind index and echoed masked to `***.***.*NN-NN`.
     */
    cpf?: string | null;
    /**
     * Termination From
     */
    termination_from?: string | null;
    /**
     * Termination To
     */
    termination_to?: string | null;
};

/**
 * EsocialPortalWalkFiltersEcho
 *
 * The filters as given, with the CPF masked.
 */
export type EsocialPortalWalkFiltersEcho = {
    /**
     * Active Only
     */
    active_only?: boolean | null;
    /**
     * Admission From
     */
    admission_from?: string | null;
    /**
     * Admission To
     */
    admission_to?: string | null;
    /**
     * Cpf
     */
    cpf?: string | null;
    /**
     * Termination From
     */
    termination_from?: string | null;
    /**
     * Termination To
     */
    termination_to?: string | null;
};

/**
 * EsocialPortalWalkListResponse
 */
export type EsocialPortalWalkListResponse = {
    /**
     * Count
     */
    count: number;
    /**
     * Items
     */
    items: Array<EsocialPortalWalkResponse>;
    /**
     * Limit
     */
    limit: number;
    /**
     * Offset
     */
    offset: number;
};

/**
 * EsocialPortalWalkListedReport
 *
 * One row of *Relatórios Solicitados*. Only the columns the walk knows; none names anybody.
 */
export type EsocialPortalWalkListedReport = {
    /**
     * Available Until
     */
    available_until?: string | null;
    /**
     * Filters
     *
     * The portal's own *Filtros* text.
     */
    filters?: string | null;
    /**
     * Formats
     *
     * The download formats the row has enabled.
     */
    formats: Array<FileFormat>;
    /**
     * Position
     *
     * The row's place in the table, from zero.
     */
    position: number;
    /**
     * Records Retrieved
     */
    records_retrieved?: number | null;
    /**
     * Report Type
     */
    report_type?: string | null;
    /**
     * Requested At
     *
     * *Solicitado em*, as the portal shows it.
     */
    requested_at?: string | null;
    /**
     * State
     *
     * The portal's word for the state, verbatim.
     */
    state?: string | null;
    /**
     * That word parsed; `unknown` when the walk does not know it.
     */
    state_parsed: ReportState;
};

/**
 * EsocialPortalWalkRequest
 *
 * One walk: which company, which mode, how far, and with what.
 */
export type EsocialPortalWalkRequest = {
    /**
     * For a `collect` run only.
     */
    collect?: EsocialPortalWalkCollect | null;
    /**
     * Company Id
     *
     * The company to walk the portal for. Its CNPJ is the represented employer entered on the procurador profile.
     */
    company_id: string;
    /**
     * For a `request` run only.
     */
    filters?: EsocialPortalWalkFilters | null;
    /**
     * `request` walks login → profile → module → request → list; `collect` walks login → profile → module → list → download.
     */
    mode: WalkMode;
    /**
     * Stop after this stage; the rest are recorded as `skipped`. Must be one of the mode's stages.
     */
    stop_after?: WalkStage | null;
    /**
     * Timeout Seconds
     *
     * How long each page action may take on this run, in seconds, between 5 and 300: the page's default timeout, every navigation and every wait. Absent: the deployment's `I8LIFE_BROWSER_TIMEOUT_SECONDS` (30 unless configured). A longer one tells a slow certificate leg apart from one that never answers. Recorded on the run.
     */
    timeout_seconds?: number | null;
};

/**
 * EsocialPortalWalkResponse
 *
 * One walk: what was asked, how far it got, and what each stage found.
 */
export type EsocialPortalWalkResponse = {
    /**
     * Which browser drove the walk (`I8LIFE_BROWSER_BACKEND`): `chromium`, headless on the backend, or `remote_chromium`, a Chromium somebody else runs, reached over the DevTools protocol and never given the certificate. `null` on walks recorded before it was.
     */
    browser_backend?: BrowserBackend | null;
    collect?: EsocialPortalWalkCollectEcho | null;
    /**
     * Company Id
     */
    company_id: string;
    /**
     * Employer Cnpj
     */
    employer_cnpj: string;
    /**
     * Employer Cnpj Formatted
     */
    employer_cnpj_formatted: string;
    environment: EsocialEnvironment;
    filters: EsocialPortalWalkFiltersEcho;
    /**
     * Finished At
     */
    finished_at?: string | null;
    /**
     * Id
     */
    id: string;
    mode: WalkMode;
    /**
     * Outcome
     *
     * `failed` when any stage failed, `completed` otherwise.
     */
    outcome: 'completed' | 'failed';
    /**
     * Requested By Display Name
     */
    requested_by_display_name: string;
    /**
     * Session Id
     *
     * The session walk this was one company of (#479); its `login` is then `inherited`. `null` for a walk that signed in by itself.
     */
    session_id?: string | null;
    /**
     * Stage Count
     *
     * How many stages the mode has; every one is listed.
     */
    stage_count: number;
    /**
     * Stages
     */
    stages: Array<EsocialPortalWalkStage>;
    /**
     * Started At
     */
    started_at: string;
    stop_after?: WalkStage | null;
    /**
     * Timeout Seconds
     *
     * How long each page action could take on this run, in seconds. `null` on walks recorded before it was.
     */
    timeout_seconds?: number | null;
};

/**
 * EsocialPortalWalkStage
 *
 * What one stage did and found, and which artefacts it sealed.
 */
export type EsocialPortalWalkStage = {
    /**
     * Action Screenshots
     *
     * The viewport screenshots taken after each action, in order, at most 20 per stage. A number missing from the sequence is an action whose screenshot could not be taken.
     */
    action_screenshots?: Array<EsocialPortalWalkActionScreenshot>;
    /**
     * Action Screenshots Dropped
     *
     * Actions past the cap of 20, which were not shot.
     */
    action_screenshots_dropped?: number;
    /**
     * Artefacts
     *
     * The sealed artefacts this stage kept, each readable at `/esocial/portal-walks/{run_id}/stages/{index}/{artefact}`. `trace` is the stage's action log, navigations, network log, console and the requests still pending when it ended, as JSON.
     */
    artefacts: Array<Artefact>;
    /**
     * Captcha Solve Seconds
     *
     * On `login`, when `captcha_solved_by_person`: the seconds between the challenge showing and gov.br's form being posted.
     */
    captcha_solve_seconds?: number | null;
    /**
     * Captcha Solved By Person
     *
     * On `login`: whether gov.br showed a visible captcha challenge that the person at a remote browser (`I8LIFE_BROWSER_BACKEND=remote_chromium`) solved, after which the walk carried on by itself. `false` when nobody had to; `null` on every other stage and on walks run before #477.
     */
    captcha_solved_by_person?: boolean | null;
    /**
     * Certificate Hops
     *
     * On `login`, when `I8LIFE_BROWSER_CERTIFICATE_LEG` is `backend`: the requests the backend made presenting the A1 certificate after gov.br's *Seu certificado digital*, in order, up to the hand-over to the browser or the failure. `null` when the leg did not run -- on every other stage, on a walk whose browser clicked the option itself, and on a walk run before #457.
     */
    certificate_hops?: Array<EsocialPortalWalkCertificateHop> | null;
    /**
     * Chosen Row
     *
     * On `download`: the row downloaded.
     */
    chosen_row?: number | null;
    /**
     * Employer Number
     *
     * On `module`: the employer number the screen shows.
     */
    employer_number?: string | null;
    /**
     * Failure
     *
     * Why the stage failed, in this API's words. Sanitised.
     */
    failure?: string | null;
    /**
     * Failure Code
     *
     * When the browser itself failed -- it would not start, the certificate could not be bundled for it, or a page never arrived (the network, the TLS handshake, the timeout) -- the same code the connection diagnostic's `browser` stage gives that failure. When the portal answered with something named (#479): `procuracao_missing` on `profile`, the chooser refusing the company's CNPJ; `session_expired` on a session's `profile`, the session's login gone when the company started. `null` for a page that arrived and did not have what the stage looked for: `failure` says which.
     */
    failure_code?: DiagnosticFailure | PortalWalkFailure | null;
    /**
     * File Byte Size
     */
    file_byte_size?: number | null;
    /**
     * File Content Type
     */
    file_content_type?: string | null;
    file_format?: FileFormat | null;
    /**
     * File Name
     *
     * On `download`: the portal's filename.
     */
    file_name?: string | null;
    /**
     * File Reference Date
     *
     * On `download`: the portal's *Data da Informação*, read off the name of the file the page fetched from the portal's storage (the last date in it). Filings received up to that day are in the file. `null` when the name does not carry it.
     */
    file_reference_date?: string | null;
    /**
     * Finished At
     */
    finished_at?: string | null;
    /**
     * Index
     *
     * Position in the walk, from zero.
     */
    index: number;
    name: WalkStage;
    /**
     * Portal Message
     *
     * The portal's own answer, verbatim: on `request`, to the request; on `profile`, its refusal of the procuração.
     */
    portal_message?: string | null;
    /**
     * Portal State
     *
     * The portal's state word: on `list`, the newest row's; on `download`, the downloaded row's.
     */
    portal_state?: string | null;
    portal_state_parsed?: ReportState | null;
    /**
     * Profiles Offered
     *
     * On `profile`: the profiles the portal offered.
     */
    profiles_offered?: Array<string>;
    /**
     * Records Retrieved
     */
    records_retrieved?: number | null;
    /**
     * Remedy
     *
     * What to go and do about a `failure_code`, in the diagnostic's words; for `procuracao_missing` it names the company and the CNPJ the procuração must be granted to. `null` whenever `failure_code` is.
     */
    remedy?: string | null;
    /**
     * Report Type
     *
     * On `module`: the report type shown.
     */
    report_type?: string | null;
    /**
     * Rows
     *
     * On `list`: the table, row by row.
     */
    rows?: Array<EsocialPortalWalkListedReport>;
    /**
     * Session Id
     *
     * On an `inherited` `login`: the session walk whose login this company's walk ran under (#479). Its evidence is the session's, at `/esocial/portal-sessions/{session_id}/login/{artefact}`.
     */
    session_id?: string | null;
    /**
     * Skipped Reason
     */
    skipped_reason?: 'stop_after' | 'earlier_stage_failed' | null;
    /**
     * Started At
     */
    started_at?: string | null;
    status: StageStatus;
    /**
     * Title
     *
     * The page title then.
     */
    title?: string | null;
    /**
     * Two Step Completed By Person
     *
     * On `login`: whether gov.br answered the certificate sign-in with its two-step verification (*Verificação em duas etapas*) and the person at a remote browser typed the code from the gov.br app, after the walk had ticked *Não solicitar verificação em duas etapas novamente neste navegador*. `false` when gov.br did not ask; `null` on every other stage and on walks run before #483.
     */
    two_step_completed_by_person?: boolean | null;
    /**
     * Two Step Seconds
     *
     * On `login`, when `two_step_completed_by_person`: the seconds between the two-step page being handed to the person and the portal arriving.
     */
    two_step_seconds?: number | null;
    /**
     * Url
     *
     * Where the page was when the stage ended.
     */
    url?: string | null;
};

/**
 * EsocialProbeIdentifierResponse
 *
 * One identifier a consultation returned: how to ask for it, and its receipt.
 */
export type EsocialProbeIdentifierResponse = {
    /**
     * Id
     */
    id: string;
    /**
     * Nr Rec
     */
    nr_rec: string;
};

/**
 * EsocialProbeRequest
 *
 * Which company to probe, and the ordered steps to run against it.
 */
export type EsocialProbeRequest = {
    /**
     * Company Id
     *
     * The customer company to ask eSocial about. Its recorded CNPJ is what goes in `ideEmpregador`, so a company without one is `422` **before any network call is made**.
     */
    company_id: string;
    /**
     * Steps
     *
     * The calls to make, **in order, one at a time**. At most 8 per run.
     *
     * **A refused step does not stop the run.** eSocial's own refusal codes are answers — `402` and `406` are the *expected* result of asking the employer consultation for a worker event — and a run that stopped at the first would throw away the steps that were the point.
     */
    steps: Array<EsocialProbeStepRequest>;
};

/**
 * EsocialProbeResponse
 *
 * One whole probe run: what was asked, of which eSocial, and what came back.
 */
export type EsocialProbeResponse = {
    /**
     * Company Id
     */
    company_id: string;
    /**
     * Employer Cnpj
     */
    employer_cnpj: string;
    /**
     * Employer Cnpj Formatted
     */
    employer_cnpj_formatted: string;
    /**
     * Which eSocial answered. The first thing anybody reading this evidence has to know, because the two environments answer differently and that is half of what is being measured.
     */
    environment: EsocialEnvironment;
    /**
     * Finished At
     */
    finished_at?: string | null;
    /**
     * Id
     *
     * The run, which is also what the stored exchanges are keyed by.
     */
    id: string;
    /**
     * Started At
     */
    started_at: string;
    /**
     * Step Count
     *
     * How many steps the run was asked for, which is how many it reports.
     */
    step_count: number;
    /**
     * Steps
     *
     * One result per step, in the order they ran. A failed step is here too.
     */
    steps: Array<EsocialProbeStepResponse>;
};

/**
 * EsocialProbeStepRequest
 *
 * One call to make. Which fields are required depends on `kind`.
 *
 * Five shapes in one object rather than a tagged union, because that is how a
 * probe run is written and read: an ordered array somebody edits between runs,
 * where every entry has a position, an `nr_insc_mode` and its own filters. The
 * validator below is what holds each kind to the fields it needs, so an
 * incomplete step is a `422` naming the missing one rather than a request
 * eSocial refuses at the cost of one of the day's ten calls.
 */
export type EsocialProbeStepRequest = {
    /**
     * Cpf
     *
     * The worker a `worker_query` asks about. Punctuation is accepted. It **travels to eSocial in plain because there is no other way to ask this question**, and it is kept only inside the encrypted request this run stores — the answer masks it, the stored row holds a blind index of it, and the audit entry has no field for it at all.
     */
    cpf?: string | null;
    /**
     * End
     *
     * Last moment of a `worker_query`'s window. **Refused before sending** when the window spans more than 31 days or when it ends less than 1 hour(s) ago — eSocial refuses both, and a refusal we can predict costs one of the day's calls for nothing.
     */
    end?: string | null;
    /**
     * Event Id
     *
     * The event a `download_by_id` asks for, as a query returned it.
     */
    event_id?: string | null;
    /**
     * Which of the five calls this step makes.
     *
     * - **`table_query`** — `ConsultarIdentificadoresEventosTabela`. Needs `tp_evt`. The employer's own registration data, which is the cheapest question this service answers.
     * - **`employer_query`** — `ConsultarIdentificadoresEventosEmpregador`. Needs `tp_evt` and `per_apur`.
     * - **`worker_query`** — `ConsultarIdentificadoresEventosTrabalhador`. Needs `cpf`, `start` and `end`.
     * - **`download_by_id`** — `SolicitarDownloadEventosPorId`. Needs `event_id`.
     * - **`download_by_receipt`** — `SolicitarDownloadEventosPorNrRecibo`. Needs `receipt`. The only step that can answer whether a closing's receipt carries `cpfs`.
     */
    kind: ProbeStepKind;
    /**
     * Which form of the company's CNPJ goes in `ideEmpregador/nrInsc`: `full` for all fourteen digits, `base` for the eight-digit root. The schema accepts both and the developer manual does not say which eSocial wants, which is exactly why it is a per-step dimension — run the same query twice and compare.
     */
    nr_insc_mode?: NrInscMode;
    /**
     * Per Apur
     *
     * The competence an employer query filters on, `YYYY-MM`. Exactly one month, so the step costs exactly one call.
     */
    per_apur?: string | null;
    /**
     * Receipt
     *
     * The `nrRec` a `download_by_receipt` asks for, as a query returned it.
     */
    receipt?: string | null;
    /**
     * Start
     *
     * First moment of a `worker_query`'s window. A moment rather than a day: this consultation filters on when eSocial *received* an event, not on the day the event is about.
     */
    start?: string | null;
    /**
     * Tp Evt
     *
     * eSocial's six-character event code. Required for the two query kinds.
     */
    tp_evt?: string | null;
};

/**
 * EsocialProbeStepResponse
 *
 * What one step did, in the terms somebody plans the next call in.
 *
 * **Everything eSocial said is verbatim.** `cd_resposta` and `desc_resposta`
 * are the service's own, unparaphrased, because the whole exercise is to find
 * out what it says.
 *
 * **Nothing here is personal.** `parameters.cpf` is masked to four digits, and
 * `receipt_cpf_count` is a count — the values inside a closing's `cpfs` are
 * never read out of the response, let alone served.
 */
export type EsocialProbeStepResponse = {
    /**
     * Cd Resposta
     *
     * eSocial's own response code, verbatim.
     */
    cd_resposta?: number | null;
    /**
     * Desc Resposta
     *
     * eSocial's own description, verbatim.
     */
    desc_resposta?: string | null;
    /**
     * Event Returned
     *
     * Whether a download brought event XML back at all.
     */
    event_returned?: boolean;
    /**
     * Event Root Element
     *
     * The root element of the event that came back — `evtAdmissao`, `evtDeslig`, `evtFechaEvPer`. The answer to *did we get the kind of thing we asked for?*, at no cost in personal data.
     */
    event_root_element?: string | null;
    /**
     * Failure
     *
     * Why this step produced no answer, in this API's own words — a transport failure, an answer this client could not read (a SOAP fault carries its `faultstring` here), or a window this client refused before sending. **Never an eSocial response code**: that is an answer, and it is in `cd_resposta`.
     *
     * A step with a `failure` may still carry `request_key` and `response_key`, and when it does those bytes are the whole of what came back.
     */
    failure?: string | null;
    /**
     * Http Status
     *
     * The HTTP status underneath eSocial's answer. `null` if none arrived.
     */
    http_status?: number | null;
    /**
     * Identifier Count
     *
     * How many identifiers came back — at most fifty.
     */
    identifier_count?: number;
    /**
     * Identifiers
     *
     * The `id` and `nrRec` pairs returned. Safe to show and meant to be: they are the employer's own event keys, and they are what the next step of the plan is addressed by.
     */
    identifiers?: Array<EsocialProbeIdentifierResponse>;
    /**
     * Index
     *
     * Position in the run, from zero.
     */
    index: number;
    kind: ProbeStepKind;
    /**
     * Method
     *
     * The SOAP operation this step really invoked, by eSocial's own name — so the evidence reads against the developer manual, which knows these names and not ours.
     */
    method: string;
    /**
     * Nr Insc
     *
     * The digits that actually went into `ideEmpregador/nrInsc`.
     */
    nr_insc: string;
    nr_insc_mode: NrInscMode;
    /**
     * Parameters
     *
     * What the step was asked for, echoed back, with any CPF masked.
     */
    parameters: {
        [key: string]: string;
    };
    /**
     * Receipt Cpf Count
     *
     * How many entries that `cpfs` holds. **A count and never the values**: if a closing really does list every worker in the month, this number is the finding and the numbers themselves are not served by this API.
     */
    receipt_cpf_count?: number | null;
    /**
     * Receipt Has Cpfs
     *
     * Whether that `rec` contains a `cpfs` element — **the finding this whole probe exists for**. `null` when no `rec` came back at all: *there was no receipt* and *the receipt had no cpfs* are two different answers.
     */
    receipt_has_cpfs?: boolean | null;
    /**
     * Receipt Returned
     *
     * Whether the answer carried eSocial's own `rec` return document.
     */
    receipt_returned?: boolean;
    /**
     * Request Key
     *
     * Where the raw request is sealed in the object store. An internal address, served here so that the evidence of one run is traceable to the objects that hold it — **not** something to send back: the bytes are read with `GET /esocial/probes/{run_id}/steps/{step_index}/request`, which works the address out from the run and the step and accepts no key from a caller.
     *
     * Present whenever eSocial answered at all — **including when what it answered could not be read**, such as a SOAP fault, which is the case these bytes matter most for. `null` only when nothing was exchanged: a window refused before sending, a handshake, a timeout.
     */
    request_key?: string | null;
    /**
     * Response Key
     *
     * Where the raw response is sealed in the object store, read back the same way at `…/steps/{step_index}/response`.
     */
    response_key?: string | null;
    /**
     * Total Reported
     *
     * How many eSocial says matched the filter, which may exceed `identifier_count`: the service answers at most fifty per query.
     */
    total_reported?: number | null;
};

/**
 * EsocialStageResponse
 *
 * One of the four checks, and what became of it.
 */
export type EsocialStageResponse = {
    /**
     * Why it failed, as a stable value to branch on: `no_certificate`, `certificate_unusable`, `certificate_unreadable`, `endpoint_unreachable`, `handshake_refused`, `query_unanswered`, `query_refused`, `browser_unavailable`, `browser_certificate_unusable` or `browser_navigation_failed`. Branch on this, never on `summary`.
     */
    failure?: DiagnosticFailure | null;
    /**
     * `passed`, `failed` or `skipped`. The web-service stages stop at the first `failed`; the `browser` stage depends on `certificate` alone, so a run can carry one failure from each channel.
     */
    outcome: StageOutcome;
    /**
     * Remedy
     *
     * One sentence saying what to go and do. Present only when something failed.
     */
    remedy?: string | null;
    /**
     * `certificate`, `handshake`, `query` or `browser`, always in that order and always all four -- a stage that did not run is reported `skipped` rather than left out.
     */
    stage: DiagnosticStage;
    /**
     * Summary
     *
     * One sentence saying what happened, in this API's own language.
     */
    summary: string;
};

/**
 * EsocialWorkerListDetailResponse
 *
 * One capture and the workers it stored, masked.
 *
 * `workers` is empty for every ending but `captured`, and that is not a gap: a
 * month with no closing, a closing already held and a refusal all store no
 * workers on purpose, and `worker_list.status` says which.
 */
export type EsocialWorkerListDetailResponse = {
    worker_list: EsocialWorkerListResponse;
    /**
     * Workers
     *
     * Every worker the closing named, in the order the government wrote them, each by their **masked** CPF.
     */
    workers: Array<EsocialWorkerListEntryResponse>;
};

/**
 * EsocialWorkerListEntryResponse
 *
 * One worker the closing named, masked.
 *
 * **There is no unmasked form of this anywhere in the API.** `cpf_masked` is
 * the roster's own mask -- enough to recognise somebody whose number you
 * already know, and not enough to be a copy of their registry number in a log
 * line, a screenshot or a support ticket.
 */
export type EsocialWorkerListEntryResponse = {
    /**
     * Cpf Masked
     *
     * The worker's CPF, masked. The full number is encrypted at rest and is **not served by this API at all**; there is no field here that could carry it, and no CPF travels in any path or query string on this surface.
     */
    cpf_masked: string;
    /**
     * Id
     */
    id: string;
    /**
     * Position
     *
     * Which row of the closing's `cpfs` this was, counting from one. The order the government wrote them in, which is the order this is served in — and the only thing a refusal message about an unreadable row is allowed to name.
     */
    position: number;
    /**
     * Receipts
     *
     * The row's remaining non-empty fields: that worker's periodic-event receipt numbers, as eSocial wrote them. A receipt identifies a filing rather than a person, so these are served in plain. The evidence does not say what distinguishes one position from another, so nothing here pretends to — they are in the order they arrived.
     */
    receipts?: Array<string>;
};

/**
 * EsocialWorkerListListResponse
 */
export type EsocialWorkerListListResponse = {
    /**
     * Count
     */
    count: number;
    /**
     * Items
     */
    items: Array<EsocialWorkerListResponse>;
    /**
     * Limit
     */
    limit: number;
    /**
     * Offset
     */
    offset: number;
};

/**
 * EsocialWorkerListRequest
 *
 * Which company to capture, and for which month. The whole of the input.
 */
export type EsocialWorkerListRequest = {
    /**
     * Company Id
     *
     * The customer company whose closing to read. Its recorded CNPJ is what eSocial is asked about -- as the **eight-digit base root**, which is the form produção answers to -- so a company without one is `422` **before any network call is made**.
     */
    company_id: string;
    /**
     * Competence
     *
     * The month to capture, `YYYY-MM`. eSocial's own `perApur`: a closing is filed against exactly one, and asking for a wider stretch would be asking for more than one closing. Anything that is not a `YYYY-MM` month is `422` before any call is made.
     */
    competence: string;
};

/**
 * EsocialWorkerListResponse
 *
 * One capture: which month, how it ended, and what the government said.
 *
 * Four endings, and each is a different morning — see `status`. The counts and
 * eSocial's own `cdResposta` are beside it because "the list is empty" has
 * four causes and an operator has to be able to tell which one they are
 * looking at.
 */
export type EsocialWorkerListResponse = {
    /**
     * Company Id
     */
    company_id: string;
    /**
     * Competence
     *
     * The month this list is of, `YYYY-MM`.
     */
    competence: string;
    /**
     * Employer Cnpj
     */
    employer_cnpj: string;
    /**
     * Employer Cnpj Formatted
     */
    employer_cnpj_formatted: string;
    /**
     * Entry Count
     *
     * How many workers the closing named. Zero for every ending but `captured`.
     */
    entry_count: number;
    /**
     * Which eSocial this capture talked to, as it was configured at the time.
     */
    environment: EsocialEnvironment;
    /**
     * Esocial Event Id
     *
     * eSocial's own identifier for that closing, reported beside the receipt.
     */
    esocial_event_id?: string | null;
    /**
     * Esocial Response Code
     *
     * eSocial's own `cdResposta`, verbatim, from the last call that answered.
     */
    esocial_response_code?: number | null;
    /**
     * Esocial Response Message
     *
     * eSocial's own `descResposta`, verbatim — their words and their language.
     */
    esocial_response_message?: string | null;
    /**
     * Failure Reason
     *
     * Why this capture stored nothing, when it stored nothing for a reason of ours rather than of theirs — a `cpfs` row that would not parse. **It names the row's position and never its contents**: the first field of a bad row is still somebody's registry number.
     */
    failure_reason?: string | null;
    /**
     * Finished At
     */
    finished_at?: string | null;
    /**
     * Id
     */
    id: string;
    /**
     * Identifier Count
     *
     * How many `S-1299` identifiers the listing returned.
     */
    identifier_count: number;
    /**
     * Is Current
     *
     * Whether this is the list that stands for its company and competence.
     */
    is_current?: boolean;
    /**
     * Listed Receipts
     *
     * Every receipt the listing returned, in the order eSocial listed them — not only the one taken. More than one means the competence was rectified or reopened; the **newest** was downloaded, and this is what was on offer.
     */
    listed_receipts?: Array<string>;
    /**
     * Receipt
     *
     * The receipt of the closing the workers were read out of. `null` unless the capture ingested a list — which is also what makes re-reading one impossible: a receipt appears against one employer once, for ever.
     */
    receipt?: string | null;
    /**
     * Requested By Display Name
     *
     * Who ran it. Email for a person, client id for a machine.
     */
    requested_by_display_name: string;
    /**
     * Started At
     */
    started_at: string;
    /**
     * How the capture ended.
     *
     * - **`captured`** — the closing came back, its `cpfs` was read, and every worker in it is an entry. This is now the company's current list for the competence.
     * - **`no_closing`** — eSocial listed no `S-1299` for the month at all. The employer has not closed it. Nothing is stored beyond this row, and a list captured earlier still stands.
     * - **`unchanged`** — the newest closing eSocial listed is the one an earlier capture already ingested. **The same receipt is never read twice**, so nothing was downloaded and nothing was written.
     * - **`refused`** — nothing was ingested and something has to be looked at: eSocial refused one of the two calls (its own code and words are in `esocial_response_code` and `esocial_response_message`), or the `cpfs` could not be read (`failure_reason` says which row, never what was in it). **No partial list is ever stored.**
     */
    status: EsocialWorkerListStatus;
    /**
     * Superseded At
     *
     * When a later capture of the same competence replaced this one. `null` on the list that stands. **Nothing is deleted**: the row and its entries stay exactly as they were, because what we believed when we last reconciled is a question somebody will ask.
     */
    superseded_at?: string | null;
    /**
     * Supersedes Id
     *
     * The list this one replaced: the same company's previous current list for the same competence. `null` on the first capture of a month and on any capture that ingested nothing — a refusal supersedes nobody.
     */
    supersedes_id?: string | null;
};

/**
 * EsocialWorkerListStatus
 *
 * How one monthly worker-list capture ended. Values are part of the API contract.
 *
 * A capture is two calls -- list the competence's ``S-1299`` closings, then
 * download the newest one by its receipt -- and there are four ways that ends.
 * They are four values rather than a boolean because each is a different
 * morning, exactly as :class:`EsocialFetchStatus`'s three are.
 *
 * **A month with no closing is not a failure**, and neither is a month whose
 * closing we already hold. Both are the ordinary answer on a competence that
 * has been captured once or that the employer has not closed yet, and a status
 * that coloured either red would teach an operator to ignore the colour.
 */
export const EsocialWorkerListStatus = {
    CAPTURED: 'captured',
    NO_CLOSING: 'no_closing',
    UNCHANGED: 'unchanged',
    REFUSED: 'refused'
} as const;

/**
 * EsocialWorkerListStatus
 *
 * How one monthly worker-list capture ended. Values are part of the API contract.
 *
 * A capture is two calls -- list the competence's ``S-1299`` closings, then
 * download the newest one by its receipt -- and there are four ways that ends.
 * They are four values rather than a boolean because each is a different
 * morning, exactly as :class:`EsocialFetchStatus`'s three are.
 *
 * **A month with no closing is not a failure**, and neither is a month whose
 * closing we already hold. Both are the ordinary answer on a competence that
 * has been captured once or that the employer has not closed yet, and a status
 * that coloured either red would teach an operator to ignore the colour.
 */
export type EsocialWorkerListStatus = typeof EsocialWorkerListStatus[keyof typeof EsocialWorkerListStatus];

/**
 * EsocialWorkerReportDetailResponse
 *
 * One report and one page of its entries, every CPF masked.
 */
export type EsocialWorkerReportDetailResponse = {
    /**
     * Entries
     *
     * One page of the entries, in the file's order.
     */
    entries: Array<EsocialWorkerReportEntryResponse>;
    /**
     * Entry Count
     *
     * How many entries the report has in all.
     */
    entry_count: number;
    /**
     * Limit
     */
    limit: number;
    /**
     * Offset
     */
    offset: number;
    report: EsocialWorkerReportResponse;
};

/**
 * EsocialWorkerReportEntryResponse
 *
 * One worker the report named. The CPF masked; the rest as the report wrote it.
 */
export type EsocialWorkerReportEntryResponse = {
    /**
     * Admission Date
     */
    admission_date?: string | null;
    /**
     * Admission Receipt
     */
    admission_receipt?: string | null;
    /**
     * Base Salary
     */
    base_salary?: string | null;
    /**
     * Birth Date
     */
    birth_date?: string | null;
    /**
     * Category Code
     *
     * eSocial's *categoria do trabalhador* code, verbatim.
     */
    category_code?: string | null;
    /**
     * Cpf Masked
     *
     * The worker's CPF, masked `***.NNN.NNN-**`. The full number is encrypted at rest and not served by this API at all.
     */
    cpf_masked: string;
    /**
     * Establishment Cnpj
     *
     * The establishment's registration, digits only.
     */
    establishment_cnpj?: string | null;
    /**
     * Full Name
     */
    full_name: string;
    /**
     * Id
     */
    id: string;
    /**
     * Last Contract Change Date
     */
    last_contract_change_date?: string | null;
    /**
     * Latest S2205 Receipt
     */
    latest_s2205_receipt?: string | null;
    /**
     * Latest S2206 Receipt
     */
    latest_s2206_receipt?: string | null;
    /**
     * Matricula
     */
    matricula?: string | null;
    /**
     * Position
     *
     * Which data row of the file, counting from one, in the file's order.
     */
    position: number;
    /**
     * Termination Date
     */
    termination_date?: string | null;
    /**
     * Termination Reason Code
     *
     * eSocial's *motivo de desligamento* code, verbatim.
     */
    termination_reason_code?: string | null;
    /**
     * Termination Receipt
     */
    termination_receipt?: string | null;
};

/**
 * EsocialWorkerReportFilters
 *
 * What round one asks the portal for. Every field optional; the default is every vínculo.
 */
export type EsocialWorkerReportFilters = {
    /**
     * Active Only
     *
     * `false` (the default) asks for *todos os vínculos*; `true` for active vínculos only. Always typed into the portal, never left as it showed.
     */
    active_only?: boolean;
    /**
     * Admission From
     *
     * Only workers admitted on or after this date.
     */
    admission_from?: string | null;
    /**
     * Termination From
     *
     * Only workers terminated on or after this date.
     */
    termination_from?: string | null;
};

/**
 * EsocialWorkerReportListResponse
 */
export type EsocialWorkerReportListResponse = {
    /**
     * Count
     */
    count: number;
    /**
     * Items
     */
    items: Array<EsocialWorkerReportResponse>;
    /**
     * Limit
     */
    limit: number;
    /**
     * Offset
     */
    offset: number;
};

/**
 * EsocialWorkerReportRequest
 *
 * Which company to request a report for, and with which filters.
 */
export type EsocialWorkerReportRequest = {
    /**
     * Company Id
     *
     * The customer company. Its CNPJ is the represented employer on the portal, so a company without one is `422` before any browser is launched.
     */
    company_id: string;
    filters?: EsocialWorkerReportFilters;
};

/**
 * EsocialWorkerReportResponse
 *
 * One report: what was asked, where it stands on the portal, and what was read.
 */
export type EsocialWorkerReportResponse = {
    /**
     * Available Until
     *
     * The portal's *Disponível até*, as last shown on its list.
     */
    available_until?: string | null;
    /**
     * Collect Walk Ids
     *
     * Every portal walk that collected it, in order.
     */
    collect_walk_ids?: Array<string>;
    /**
     * Collected At
     *
     * When the file was read into entries.
     */
    collected_at?: string | null;
    /**
     * Collected By Display Name
     */
    collected_by_display_name?: string | null;
    /**
     * Company Id
     */
    company_id: string;
    /**
     * Consolidated Through
     *
     * The portal's *Data da Informação* for the file: filings the portal received up to that day are in it, later ones are not. Read off the file's name on the portal's storage; the walk of 2026-10-27 showed it a week behind the request date. `null` when the name did not carry it, or nothing was downloaded.
     */
    consolidated_through?: string | null;
    /**
     * Employer Cnpj
     */
    employer_cnpj: string;
    /**
     * Employer Cnpj Formatted
     */
    employer_cnpj_formatted: string;
    /**
     * Entry Count
     *
     * How many entries were stored. Zero for every status but `collected`.
     */
    entry_count: number;
    environment: EsocialEnvironment;
    /**
     * Failure Reason
     *
     * Why nothing was stored. Names a column or a row's position — **never a value**.
     */
    failure_reason?: string | null;
    /**
     * File Byte Size
     */
    file_byte_size?: number | null;
    /**
     * File Name
     *
     * The portal's filename.
     */
    file_name?: string | null;
    /**
     * The filters sent, as given.
     */
    filters: EsocialWorkerReportFilters;
    /**
     * Has File
     *
     * Whether the xlsx is sealed and readable from `/esocial/worker-reports/{id}/file`. A file the reader refused is still kept.
     */
    has_file: boolean;
    /**
     * Id
     */
    id: string;
    /**
     * Is Current
     *
     * Whether this is the collected report that stands for its company.
     */
    is_current: boolean;
    /**
     * Last Collect At
     */
    last_collect_at?: string | null;
    /**
     * Portal Message
     *
     * The portal's answer to the request, verbatim.
     */
    portal_message?: string | null;
    /**
     * Portal Requested At
     *
     * The portal's own *Solicitado em* for this request — what identifies it on the portal's list, and unique per employer. `null` when the request never reached the list.
     */
    portal_requested_at?: string | null;
    /**
     * Portal Row Count
     *
     * *Registros Recuperados*: how many rows the portal said the report has.
     */
    portal_row_count?: number | null;
    /**
     * Portal State
     *
     * The portal's state word for the report, verbatim, as last shown.
     */
    portal_state?: string | null;
    /**
     * Request Walk Id
     *
     * The portal walk that requested it; its screenshots are under `/esocial/portal-walks/{run_id}`.
     */
    request_walk_id?: string | null;
    /**
     * Requested At
     *
     * When we asked, on this platform's clock.
     */
    requested_at: string;
    /**
     * Requested By Display Name
     */
    requested_by_display_name: string;
    /**
     * Where the report stands.
     *
     * - **`requested`** — the portal accepted the request; nobody has collected it.
     * - **`pending`** — collected, and the portal still says *Pendente*. Collect again later.
     * - **`collected`** — the xlsx is sealed and its rows are entries.
     * - **`empty`** — the portal says *Vazio*: the filters matched nobody.
     * - **`exceeded`** — the portal says *Excedido*: too large to produce.
     * - **`expired`** — past *Disponível até* with no file.
     * - **`refused`** — the walk failed, the portal refused, or the file could not be read; `failure_reason` says which, and never quotes a row.
     */
    status: EsocialWorkerReportStatus;
    /**
     * Superseded At
     *
     * When a later collected report replaced this one. **Nothing is deleted**: the row and its entries stay exactly as they were.
     */
    superseded_at?: string | null;
    /**
     * Supersedes Id
     *
     * The company's previous current report, which this one replaced.
     */
    supersedes_id?: string | null;
};

/**
 * EsocialWorkerReportStatus
 *
 * Where one *Relação de Trabalhadores* report stands. Values are part of the API contract.
 *
 * A report is two portal sessions, possibly hours apart -- **request** and
 * **collect** -- and the portal is asynchronous in between, so most of these
 * are the portal's own answer about a report rather than ours about a run.
 * Each is a different morning, which is why they are values rather than a
 * boolean: "not ready yet", "nobody matched the filters" and "the portal
 * refused" ask three different people to do three different things.
 */
export const EsocialWorkerReportStatus = {
    REQUESTED: 'requested',
    PENDING: 'pending',
    COLLECTED: 'collected',
    EMPTY: 'empty',
    EXCEEDED: 'exceeded',
    EXPIRED: 'expired',
    REFUSED: 'refused'
} as const;

/**
 * EsocialWorkerReportStatus
 *
 * Where one *Relação de Trabalhadores* report stands. Values are part of the API contract.
 *
 * A report is two portal sessions, possibly hours apart -- **request** and
 * **collect** -- and the portal is asynchronous in between, so most of these
 * are the portal's own answer about a report rather than ours about a run.
 * Each is a different morning, which is why they are values rather than a
 * boolean: "not ready yet", "nobody matched the filters" and "the portal
 * refused" ask three different people to do three different things.
 */
export type EsocialWorkerReportStatus = typeof EsocialWorkerReportStatus[keyof typeof EsocialWorkerReportStatus];

/**
 * FieldChangeResponse
 *
 * One field, before and after.
 *
 * Both ends are always present. A creation is ``before: null`` and a deletion
 * is ``after: null``, so a client never has to branch on the action to read a
 * change.
 */
export type FieldChangeResponse = {
    /**
     * After
     *
     * The value after the change, or `null` if the record ceased to exist.
     */
    after?: unknown;
    /**
     * Before
     *
     * The value before the change, or `null` if the record did not exist.
     */
    before?: unknown;
};

/**
 * FileFormat
 */
export const FileFormat = {
    XLSX: 'xlsx',
    CSV: 'csv',
    PDF: 'pdf'
} as const;

/**
 * FileFormat
 */
export type FileFormat = typeof FileFormat[keyof typeof FileFormat];

/**
 * FileNameLabelUpdate
 *
 * Set or clear the insurer file-name label of a company or of an economic group.
 */
export type FileNameLabelUpdate = {
    /**
     * File Name Label
     *
     * What insurer file names carry in place of the registered name -- `Actega` where the company is registered as `ACTEGA`, say -- or `null` to clear it. Surrounding whitespace is trimmed; case is kept exactly as typed, and each space becomes an underscore in the file name. Letters, digits, `_`, `-` and spaces only, at most 100 characters: anything else, or a blank value, is a `422` on this field, because nothing typed here may be silently dropped from the file name. Applies to files generated from now on; files already generated keep their names.
     */
    file_name_label: string | null;
};

/**
 * FileRefusalReason
 *
 * Why an uploaded file was refused as a whole, before any row was read.
 *
 * Carried on ``details[].reason`` of a ``validation_error`` so a client can
 * say what is wrong with the file in its own words, rather than parsing the
 * English ``message``. Values are part of the public contract.
 */
export const FileRefusalReason = {
    MISSING_COLUMNS: 'missing_columns',
    EMPTY_FILE: 'empty_file',
    UNREADABLE_FORMAT: 'unreadable_format',
    LEGACY_XLS: 'legacy_xls',
    NO_HEADER: 'no_header',
    NO_ROWS: 'no_rows',
    TOO_MANY_ROWS: 'too_many_rows',
    FILE_TOO_LARGE: 'file_too_large'
} as const;

/**
 * FileRefusalReason
 *
 * Why an uploaded file was refused as a whole, before any row was read.
 *
 * Carried on ``details[].reason`` of a ``validation_error`` so a client can
 * say what is wrong with the file in its own words, rather than parsing the
 * English ``message``. Values are part of the public contract.
 */
export type FileRefusalReason = typeof FileRefusalReason[keyof typeof FileRefusalReason];

/**
 * FlagCode
 *
 * The kinds of problem a queue item can carry. Values are part of the API contract.
 */
export const FlagCode = {
    ROSTER_CONFLICT: 'roster_conflict',
    LATE_ARRIVAL: 'late_arrival',
    MISSING_EVIDENCE: 'missing_evidence',
    DUPLICATE_IN_CYCLE: 'duplicate_in_cycle'
} as const;

/**
 * FlagCode
 *
 * The kinds of problem a queue item can carry. Values are part of the API contract.
 */
export type FlagCode = typeof FlagCode[keyof typeof FlagCode];

/**
 * FlagSeverity
 *
 * How much a flag is in the way. Values are part of the API contract.
 */
export const FlagSeverity = { BLOCKING: 'blocking', WARNING: 'warning' } as const;

/**
 * FlagSeverity
 *
 * How much a flag is in the way. Values are part of the API contract.
 */
export type FlagSeverity = typeof FlagSeverity[keyof typeof FlagSeverity];

/**
 * GroupBaselineImportContract
 *
 * One contract a group-wide import loaded, the company holding it, and its record.
 */
export type GroupBaselineImportContract = {
    /**
     * Already Present
     *
     * Rows whose person was already on the contract. Zero on a first run; every row when the same file is run again.
     */
    already_present: number;
    /**
     * Baseline Import Id
     *
     * The contract's own import record, readable at `GET /contracts/{contract_id}/baseline-imports/{baseline_import_id}`.
     */
    baseline_import_id: string;
    /**
     * Company Id
     *
     * The company the contract belongs to. Every row loaded into the contract belongs to this company.
     */
    company_id: string;
    /**
     * Company Name
     */
    company_name: string;
    /**
     * Contract Id
     */
    contract_id: string;
    /**
     * Coverage Periods Opened
     */
    coverage_periods_opened: number;
    /**
     * Modulo
     */
    modulo: number;
    /**
     * Overridden
     *
     * True when this contract had moved on and the import overrode it.
     */
    overridden: boolean;
    /**
     * People Registered
     *
     * People added to the roster. Zero when re-running the same file.
     */
    people_registered: number;
    /**
     * Row Count
     *
     * People rows read from the file.
     */
    row_count: number;
    /**
     * Salary Versions Recorded
     */
    salary_versions_recorded: number;
    /**
     * Subestipulante
     */
    subestipulante: number;
};

/**
 * GroupBaselineImportResponse
 *
 * What a group-wide baseline import did, per contract and in total.
 */
export type GroupBaselineImportResponse = {
    /**
     * Baseline Date
     *
     * The form's `baseline_date`: the date the imported population is insured from by default. A row carrying its own `Início da cobertura` is insured from that date instead; this value is the one for every row that does not.
     */
    baseline_date: string;
    /**
     * Contracts
     *
     * One entry per contract that received rows, by company name, then by subestipulante and módulo -- so a client groups them by company by reading them in order.
     */
    contracts: Array<GroupBaselineImportContract>;
    /**
     * Group Id
     */
    group_id: string;
    /**
     * Source Filename
     */
    source_filename: string;
    /**
     * Source Media Type
     *
     * The format read from the file's content: an `.xlsx` workbook or a CSV.
     */
    source_media_type: string;
    /**
     * The counts summed over the whole group.
     */
    totals: BaselineImportCounts;
};

/**
 * GroupInsuredPersonLookupResponse
 *
 * One person across every contract of a group's companies that carries their CPF.
 */
export type GroupInsuredPersonLookupResponse = {
    /**
     * Birth Date
     *
     * Taken from the first of `contracts`. Two contracts that disagree on it are not reconciled here; each contract's own value is in its history.
     */
    birth_date: string;
    /**
     * Contracts
     *
     * Ordered by contract kind (`vida` before `saude`), then by when the contract was created, then by its id.
     */
    contracts: Array<GroupLookupContractEntry>;
    /**
     * Cpf
     *
     * Canonical eleven digits.
     */
    cpf: string;
    /**
     * Full Name
     *
     * Taken from the first of `contracts`. Two contracts that disagree on it are not reconciled here; each contract's own value is in its history.
     */
    full_name: string;
};

/**
 * GroupLookupContractEntry
 *
 * A `LookupContractEntry` within a group, naming its company.
 */
export type GroupLookupContractEntry = {
    /**
     * Company Id
     *
     * The company of the group this contract belongs to.
     */
    company_id: string;
    /**
     * Contract Id
     */
    contract_id: string;
    /**
     * What insurance the contract is, as on `ContractResponse.kind`: `vida` or `saude`. More kinds may be added in a later minor version.
     */
    contract_kind: ContractKind;
    /**
     * Covered Today
     *
     * Whether one of this person's coverage periods on this contract contains today, in America/Sao_Paulo. An entry whose coverage has ended -- or not yet begun -- is still listed, with `false`.
     */
    covered_today: boolean;
    /**
     * Insured Person Id
     *
     * This person's record on this contract -- one per contract, so the same person has a different one under each. Read their history on the contract through `GET /contracts/{contract_id}/insured-people/{insured_person_id}/history`.
     */
    insured_person_id: string;
    /**
     * Modulo
     */
    modulo: number;
    /**
     * Subestipulante
     */
    subestipulante: number;
};

/**
 * GroupMemberResponse
 *
 * One contract inside a group's single file, with the company that holds it.
 */
export type GroupMemberResponse = {
    /**
     * Approved
     *
     * True when the contract's cycle has a sign-off standing.
     */
    approved: boolean;
    /**
     * Company Id
     */
    company_id: string;
    /**
     * Company Name
     */
    company_name: string;
    /**
     * Contract Id
     */
    contract_id: string;
    /**
     * Cutoff Date
     */
    cutoff_date: string;
    /**
     * Modulo
     */
    modulo: number;
    /**
     * Row Count
     *
     * Rows that snapshot puts in the file. `null` until it is approved.
     */
    row_count?: number | null;
    /**
     * Snapshot Id
     *
     * The frozen snapshot the sign-off pinned, whose rows go into the file.
     */
    snapshot_id?: string | null;
    /**
     * This contract's own stage, exactly as its standing reports it.
     */
    stage: CycleStage;
    /**
     * Subestipulante
     */
    subestipulante: number;
    /**
     * The contract's own attempt standing for the month.
     */
    submission?: SubmissionResponse | null;
};

/**
 * GroupPrepare
 *
 * Package one economic group's month with one insurer as a single file.
 */
export type GroupPrepare = {
    /**
     * Cycle Month
     *
     * The month being filed, written `YYYY-MM`. The cutoff date it resolves to comes from the contract's `reference_date_rule`, and is never sent by a caller.
     */
    cycle_month: string;
};

/**
 * GroupRef
 *
 * The company group (*grupo econômico*) a group-scoped user is registered at.
 */
export type GroupRef = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
};

/**
 * GroupRosterContractEntry
 *
 * A `RosterContractEntry` of a group's population, naming its company.
 */
export type GroupRosterContractEntry = {
    /**
     * Certificate Number
     *
     * The insurer's certificate number for this person (*Certificado*). Text, so leading zeros are kept. `null` until vyttech records it.
     */
    certificate_number?: string | null;
    /**
     * Company Id
     *
     * The company of the group this contract belongs to.
     */
    company_id: string;
    /**
     * Contract Id
     */
    contract_id: string;
    /**
     * What insurance the contract is, as on `ContractResponse.kind`: `vida` or `saude`. More kinds may be added in a later minor version.
     */
    contract_kind: ContractKind;
    /**
     * Coverage From
     */
    coverage_from: string;
    /**
     * Coverage Period Id
     */
    coverage_period_id: string;
    /**
     * Coverage To
     */
    coverage_to?: string | null;
    /**
     * Insured Capital
     *
     * The capital the insurer holds this person insured for (*IS Segurado*), to two decimal places and serialised as a string. The insurer's figure, recorded by vyttech -- never derived from the salary here. `null` until recorded.
     */
    insured_capital?: string | null;
    /**
     * Insured Person Id
     *
     * This person's record on this contract -- one per contract, so the same person has a different one under each. Read their history on the contract through `GET /contracts/{contract_id}/insured-people/{insured_person_id}/history`.
     */
    insured_person_id: string;
    /**
     * Modulo
     */
    modulo: number;
    /**
     * Premium
     *
     * The premium the insurer charges for this person (*Prêmio*), to two decimal places and serialised as a string. May be `0.00`, which the insurer uses on an exclusion. `null` until recorded.
     */
    premium?: string | null;
    /**
     * Salary
     *
     * Salary in force on this contract on the projected date, or `null` if no version had taken effect by then. Never zero as a stand-in for missing.
     */
    salary?: string | null;
    /**
     * Subestipulante
     */
    subestipulante: number;
};

/**
 * GroupRosterPerson
 *
 * One person of a group, under every contract of its companies that covers them.
 */
export type GroupRosterPerson = {
    /**
     * Birth Date
     *
     * Taken from the first of `contracts`. Two contracts that disagree on it are not reconciled here; each contract's own value is in its history.
     */
    birth_date: string;
    /**
     * Contracts
     *
     * Ordered by contract kind (`vida` before `saude`), then by when the contract was created, then by its id.
     */
    contracts: Array<GroupRosterContractEntry>;
    /**
     * Cpf
     *
     * Canonical eleven digits.
     */
    cpf: string;
    /**
     * Full Name
     *
     * Taken from the first of `contracts`. Two contracts that disagree on it are not reconciled here; each contract's own value is in its history.
     */
    full_name: string;
};

/**
 * GroupRosterResponse
 *
 * Everyone insured under any contract of a group's companies on one date.
 */
export type GroupRosterResponse = {
    /**
     * As Of
     *
     * The date projected, as a calendar date in America/Sao_Paulo.
     */
    as_of: string;
    /**
     * Count
     *
     * How many people, not how many entries.
     */
    count: number;
    /**
     * Group Id
     */
    group_id: string;
    /**
     * Items
     *
     * One item per CPF, sorted by name -- compared without regard to case -- and then by CPF.
     */
    items: Array<GroupRosterPerson>;
};

/**
 * GroupSpreadsheetUploadAbsenceResponse
 *
 * Somebody covered under a contract of a member company whom the sheet never mentions.
 */
export type GroupSpreadsheetUploadAbsenceResponse = {
    /**
     * The company of the group whose contract the person is covered under.
     */
    company: SpreadsheetUploadCompany;
    /**
     * The contract the person is covered under.
     */
    contract: SpreadsheetUploadContract;
    /**
     * Cpf
     *
     * Canonical eleven digits.
     */
    cpf: string;
    /**
     * Full Name
     *
     * As the roster has it on the comparison date.
     */
    full_name: string;
};

/**
 * GroupSpreadsheetUploadCompanyCounts
 *
 * One company's share of a group batch: its rows by outcome, and its absences.
 */
export type GroupSpreadsheetUploadCompanyCounts = {
    /**
     * Absent Count
     */
    absent_count?: number;
    /**
     * Committed Count
     */
    committed_count: number;
    /**
     * The member company these rows went to. `null` groups the lines that went to no company -- section titles, and rows refused before a contract could be chosen.
     */
    company: SpreadsheetUploadCompany | null;
    /**
     * Ignored Count
     */
    ignored_count: number;
    /**
     * Ready Count
     */
    ready_count: number;
    /**
     * Rejected Count
     */
    rejected_count: number;
    /**
     * Row Count
     */
    row_count: number;
    /**
     * Section Title Count
     */
    section_title_count: number;
    /**
     * Upload Id
     *
     * The company-level upload holding this company's part: what a user of that company reads it as, through the company's own upload routes. `null` on the entry of unrouted rows.
     */
    upload_id?: string | null;
};

/**
 * GroupSpreadsheetUploadContractCounts
 *
 * One contract's share of a group batch, naming its company.
 */
export type GroupSpreadsheetUploadContractCounts = {
    /**
     * Committed Count
     */
    committed_count: number;
    /**
     * The company holding the contract; `null` on the entry of unrouted rows.
     */
    company?: SpreadsheetUploadCompany | null;
    /**
     * The contract these rows were routed to. `null` groups the rows routed to none -- section titles, and rows refused as `contract_mismatch` or `ambiguous_contract` before a contract could be chosen.
     */
    contract: SpreadsheetUploadContract | null;
    /**
     * Ignored Count
     */
    ignored_count: number;
    /**
     * Ready Count
     */
    ready_count: number;
    /**
     * Rejected Count
     */
    rejected_count: number;
    /**
     * Row Count
     */
    row_count: number;
    /**
     * Section Title Count
     */
    section_title_count: number;
};

/**
 * GroupSpreadsheetUploadListResponse
 */
export type GroupSpreadsheetUploadListResponse = {
    /**
     * Count
     */
    count: number;
    /**
     * Items
     */
    items: Array<GroupSpreadsheetUploadResponse>;
    /**
     * Limit
     */
    limit: number;
    /**
     * Offset
     */
    offset: number;
};

/**
 * GroupSpreadsheetUploadPreviewResponse
 *
 * A group batch and every line of its sheet: the document the group decides on.
 */
export type GroupSpreadsheetUploadPreviewResponse = {
    /**
     * Absences
     *
     * The people `upload.absent_count` counts, by company, contract and name: covered on the comparison date under a contract of a member company, and on no line of the file. Nothing is done about them. A person listed under another company of the group than the one they are insured in is absent here *and* `unmarked_not_on_roster` on their row: a move between companies is never treated as a transfer.
     */
    absences?: Array<GroupSpreadsheetUploadAbsenceResponse>;
    /**
     * Is Duplicate
     *
     * True when this file had already been committed for this group, and the response is the batch that holds those deltas. Nothing was created.
     */
    is_duplicate?: boolean;
    /**
     * Rows
     */
    rows: Array<GroupSpreadsheetUploadRowResponse>;
    upload: GroupSpreadsheetUploadResponse;
};

/**
 * GroupSpreadsheetUploadResponse
 *
 * A group batch: the file it came from, and what was made of every line of it.
 */
export type GroupSpreadsheetUploadResponse = {
    /**
     * Absent Count
     *
     * People covered on the comparison date under any contract of any member company whose CPF is on no line of the file. Counted only when the file has at least one row with no action marked.
     */
    absent_count?: number;
    /**
     * Committed Count
     */
    committed_count: number;
    /**
     * Company Counts
     *
     * The counts above per member company, by company name; the lines that went to no company are the last entry, with `company` `null`. The entries add up to the batch's counts.
     */
    company_counts?: Array<GroupSpreadsheetUploadCompanyCounts>;
    /**
     * Confirmed At
     */
    confirmed_at?: string | null;
    /**
     * Confirmed By Display Name
     */
    confirmed_by_display_name?: string | null;
    /**
     * Contract Counts
     *
     * The counts per contract, each naming its company, ordered by company name and then Subestipulante and Módulo; the unrouted lines are the last entry, with `contract` and `company` `null`.
     */
    contract_counts?: Array<GroupSpreadsheetUploadContractCounts>;
    /**
     * Default Effective Date
     */
    default_effective_date?: string | null;
    /**
     * Group Id
     */
    group_id: string;
    /**
     * Id
     */
    id: string;
    /**
     * Ignored Count
     */
    ignored_count: number;
    layout: SheetLayout;
    /**
     * Ready Count
     */
    ready_count: number;
    /**
     * Rejected Count
     */
    rejected_count: number;
    /**
     * Row Count
     *
     * Data rows in the file, every company's and the unrouted ones.
     */
    row_count: number;
    /**
     * Section Title Count
     */
    section_title_count: number;
    /**
     * Source Byte Count
     */
    source_byte_count: number;
    /**
     * Source Filename
     */
    source_filename: string;
    /**
     * Source Format
     */
    source_format: string;
    /**
     * Source Media Type
     */
    source_media_type: string;
    /**
     * Source Sha256
     */
    source_sha256: string;
    /**
     * `previewed` means no delta exists for any row yet. `committed` means the ready rows of every company have been submitted as deltas.
     */
    status: UploadStatus;
    /**
     * Uploaded At
     */
    uploaded_at: string;
    /**
     * Uploaded By Display Name
     */
    uploaded_by_display_name: string;
};

/**
 * GroupSpreadsheetUploadRowResponse
 *
 * One line of a group sheet, with the company and contract it went to.
 */
export type GroupSpreadsheetUploadRowResponse = {
    /**
     * The single action the sheet's three mark columns collapsed into. `null` on an ignored row, and on one refused before the action could be read.
     */
    action?: DeltaAction | null;
    /**
     * Birth Date
     */
    birth_date?: string | null;
    /**
     * The company of the group whose contract this row was routed to. `null` exactly when `contract` is: a section title, or a row whose codes fit no contract of the group (`contract_mismatch`) or several (`ambiguous_contract`) -- such a row belongs to no company.
     */
    company?: SpreadsheetUploadCompany | null;
    /**
     * The contract this row is validated and committed against: the one its `Subestipulante` and `Módulo` selected among the company's. Set on every person row that resolved one, `ignored` rows included; `null` on a `section_title` and on a row whose codes fit no contract (`contract_mismatch`) or several (`ambiguous_contract`). Always set on a `ready` or `committed` row. On a per-contract upload it is that contract.
     */
    contract?: SpreadsheetUploadContract | null;
    /**
     * Cpf
     *
     * Canonical eleven digits.
     */
    cpf?: string | null;
    /**
     * Delta Id
     *
     * The delta this row became, once the upload is confirmed.
     */
    delta_id?: string | null;
    /**
     * Effective Date
     *
     * When the movement takes effect: the row's own date column, a date written into the marked action column, or the date sent with the upload -- whichever is the most specific.
     */
    effective_date?: string | null;
    /**
     * Full Name
     */
    full_name?: string | null;
    /**
     * `ready` will become a delta when the upload is confirmed; `committed` already has; `ignored` marked no action column and is not a problem; `rejected` was refused, and says why; `section_title` is not a person but a heading between blocks of them -- text in one cell and nothing else -- and is never refused and never a delta.
     */
    outcome: RowOutcome;
    /**
     * Rejection Code
     *
     * Stable, machine-readable reason a row was refused: `multiple_actions`, `contract_mismatch`, `ambiguous_contract`, `invalid_cpf`, `missing_effective_date`, `missing_value`, `invalid_value`, `duplicate_row`, `roster_conflict`, `identity_mismatch`, `unmarked_not_on_roster`, `unmarked_salary_differs` or `not_well_formed`. Branch on this, never on the message.
     *
     * The two `unmarked_*` codes are for rows with no action marked, which claim the person is already insured as listed: `unmarked_not_on_roster` when nobody with the row's CPF is covered under the row's contract on the comparison date (an inclusão that was never marked), and `unmarked_salary_differs` when they are but the salary in force then is another one, or there is none (a salary change that was never marked). An unmarked row whose CPF or salary cannot be read is `invalid_cpf`, `missing_value` or `invalid_value`, like a marked one.
     */
    rejection_code?: string | null;
    /**
     * Rejection Context
     *
     * The structured half of the refusal, for the refusals that have one, keyed by the field it is about. Set on `identity_mismatch`: what the Receita Federal has registered for each field this row contradicted -- the name as the registry wrote it, the date of birth as ISO 8601. Set on `unmarked_salary_differs`: `salary`, the salary in force on the comparison date as an exact decimal at two places -- absent when there is none. `null` everywhere else, and on a CPF the registry has never issued, where there is no record to quote. A client composing its own copy reads this and the code; the message is prose and is not for parsing.
     */
    rejection_context?: {
        [key: string]: string;
    } | null;
    /**
     * Rejection Field
     *
     * The column at fault, by its canonical key.
     */
    rejection_field?: string | null;
    /**
     * Rejection Reason
     *
     * Written for whoever has to correct the sheet, and carrying no value from it beyond a masked CPF. That holds for an `identity_mismatch` too: this sentence names the fields that disagreed and nothing else, because it is stored in the clear. What the registry holds is on `rejection_context`, which is encrypted at rest like the row's own name and date of birth.
     */
    rejection_reason?: string | null;
    /**
     * Row Number
     *
     * The line number in the uploaded file, with the header as line 1 -- so it is the number the person sees in their own spreadsheet.
     */
    row_number: number;
    /**
     * Salary
     *
     * An exact decimal, serialised as a string so no precision is lost.
     */
    salary?: string | null;
};

/**
 * GroupStandingResponse
 *
 * One economic group's month with one insurer whose layout files one file for all of it.
 */
export type GroupStandingResponse = {
    /**
     * Already Filed Contract Ids
     *
     * Members whose month was already sent in another file. A file prepared now leaves them out rather than filing them twice.
     */
    already_filed_contract_ids: Array<string>;
    /**
     * Ambiguous Contract Ids
     *
     * Members sharing a subestipulante and módulo with another member of the group. The file cannot be prepared while there are any.
     */
    ambiguous_contract_ids: Array<string>;
    /**
     * Approved Count
     *
     * How many of the members have a sign-off standing.
     */
    approved_count: number;
    /**
     * Attempts
     *
     * Every file prepared for the month, newest first, refused ones included.
     */
    attempts: Array<GroupSubmissionResponse>;
    /**
     * Can Prepare
     *
     * True when the file can be prepared now: every member still to file is approved, no two of them share their codes, none stands prepared in another file, and no file is standing for the month.
     */
    can_prepare: boolean;
    /**
     * Company Count
     */
    company_count: number;
    /**
     * Contract Count
     */
    contract_count: number;
    /**
     * Cycle Month
     *
     * The month being filed, written `YYYY-MM`. The cutoff date it resolves to comes from the contract's `reference_date_rule`, and is never sent by a caller.
     */
    cycle_month: string;
    /**
     * Group Id
     */
    group_id: string;
    /**
     * Group Name
     */
    group_name: string;
    /**
     * Held Elsewhere Contract Ids
     *
     * Members whose month stands prepared in another file -- a company file prepared before the company joined the group, or a group file the company left with. Mark that file sent or failed where it is (each member's `submission` names it); the group file waits for it.
     */
    held_elsewhere_contract_ids: Array<string>;
    /**
     * Insurer Code
     */
    insurer_code: string;
    /**
     * Insurer Id
     */
    insurer_id: string;
    /**
     * Insurer Name
     */
    insurer_name: string;
    /**
     * Layout Code
     */
    layout_code: string;
    /**
     * Members
     *
     * The live file's own members when one stands; otherwise every contract the group's companies have with the insurer now. In the file's order.
     */
    members: Array<GroupMemberResponse>;
    /**
     * The month in one word: `open` while any member has no sign-off, `approved` once all do and no file is standing, `prepared` and `sent` as the file is, and `failed` when the last file was refused.
     */
    stage: CycleStage;
    /**
     * The file standing for the month, prepared or filed.
     */
    submission?: GroupSubmissionResponse | null;
    /**
     * Unapproved Contract Ids
     *
     * The members still without a sign-off, which is what holds the file back.
     */
    unapproved_contract_ids: Array<string>;
};

/**
 * GroupSubmissionResponse
 *
 * One economic group's month with one insurer, filed as one file, and what came back.
 */
export type GroupSubmissionResponse = {
    channel?: SubmissionChannel | null;
    /**
     * Company Count
     *
     * How many companies the file covers.
     */
    company_count: number;
    /**
     * Company Ids
     *
     * The companies the file covers, in the order their rows first appear.
     */
    company_ids: Array<string>;
    /**
     * Contract Count
     *
     * How many contracts the file packages.
     */
    contract_count: number;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Cycle Month
     *
     * The month being filed, written `YYYY-MM`. The cutoff date it resolves to comes from the contract's `reference_date_rule`, and is never sent by a caller.
     */
    cycle_month: string;
    /**
     * Failed At
     */
    failed_at?: string | null;
    failed_by?: PrincipalReference | null;
    /**
     * Failure Reason
     */
    failure_reason?: string | null;
    /**
     * File Byte Count
     */
    file_byte_count: number;
    /**
     * File Media Type
     */
    file_media_type: string;
    /**
     * File Name
     */
    file_name: string;
    /**
     * File Sha256
     *
     * SHA-256 of the file's bytes. Downloading it from `/submissions/group-files/{id}/file` returns exactly these bytes.
     */
    file_sha256: string;
    /**
     * Group Id
     */
    group_id: string;
    /**
     * Group Name
     */
    group_name: string;
    /**
     * Has Proof
     */
    has_proof: boolean;
    /**
     * Id
     */
    id: string;
    /**
     * Insurer Id
     */
    insurer_id: string;
    /**
     * Layout Code
     */
    layout_code: string;
    /**
     * Notes
     */
    notes?: string | null;
    /**
     * Prepared At
     */
    prepared_at: string;
    /**
     * Who packaged the file.
     */
    prepared_by: PrincipalReference;
    /**
     * Reference
     */
    reference?: string | null;
    /**
     * Row Count
     *
     * Rows in the file: the sum of the members' approved snapshots.
     */
    row_count: number;
    sent_by?: PrincipalReference | null;
    /**
     * `prepared` once the file is packaged, `sent` once it is filed and evidenced, `failed` when the insurer refused it. Every member submission carries the same status.
     */
    status: SubmissionStatus;
    /**
     * Submission Ids
     *
     * The member contracts' own attempts this file packages, in the order their rows are in it. Fixed when the file is prepared: later changes to the group's membership do not change them.
     */
    submission_ids: Array<string>;
    /**
     * Submitted At
     */
    submitted_at?: string | null;
};

/**
 * GroupSubmissionSent
 *
 * Mark a group's file as filed, with the protocol or a note.
 *
 * `SubmissionSent` without `proof_document_id`: a receipt filed as a document
 * belongs to one company's store, and a group file belongs to several. **At
 * least one of `reference` and `notes` is required**, refused with `422`
 * otherwise.
 */
export type GroupSubmissionSent = {
    /**
     * Which door the file went through.
     */
    channel: SubmissionChannel;
    /**
     * Notes
     *
     * What whoever filed it wants the next person to know.
     */
    notes?: string | null;
    /**
     * Reference
     *
     * The insurer's protocol number, receipt identifier or file name.
     */
    reference?: string | null;
    /**
     * Sent At
     *
     * When the file really reached the insurer. Defaults to now; a moment in the future is refused. A value without an offset is read as São Paulo wall-clock time.
     */
    sent_at?: string | null;
};

/**
 * HealthResponse
 *
 * Liveness and readiness in one payload.
 */
export type HealthResponse = {
    /**
     * Build Commit
     *
     * Source commit the running build was produced from.
     */
    build_commit: string;
    database: DatabaseHealth;
    /**
     * Environment
     *
     * Deployment environment this instance is running as.
     */
    environment: string;
    /**
     * Service
     *
     * Service name.
     */
    service: string;
    /**
     * Overall service status.
     */
    status: ComponentStatus;
    /**
     * Version
     *
     * Application version of the running build.
     */
    version: string;
};

/**
 * InsuredOnDateResponse
 *
 * One person as they stood on the projected date.
 *
 * The insurer's values are the current ones whatever the date: they are not
 * dated, so a past projection shows what the insurer says about the person now.
 */
export type InsuredOnDateResponse = {
    /**
     * Birth Date
     */
    birth_date: string;
    /**
     * Certificate Number
     *
     * The insurer's certificate number for this person (*Certificado*). Text, so leading zeros are kept. `null` until vyttech records it.
     */
    certificate_number?: string | null;
    /**
     * Coverage From
     */
    coverage_from: string;
    /**
     * Coverage Period Id
     */
    coverage_period_id: string;
    /**
     * Coverage To
     */
    coverage_to?: string | null;
    /**
     * Cpf
     */
    cpf: string;
    /**
     * Full Name
     */
    full_name: string;
    /**
     * Insured Capital
     *
     * The capital the insurer holds this person insured for (*IS Segurado*), to two decimal places and serialised as a string. The insurer's figure, recorded by vyttech -- never derived from the salary here. `null` until recorded.
     */
    insured_capital?: string | null;
    /**
     * Insured Person Id
     */
    insured_person_id: string;
    /**
     * Premium
     *
     * The premium the insurer charges for this person (*Prêmio*), to two decimal places and serialised as a string. May be `0.00`, which the insurer uses on an exclusion. `null` until recorded.
     */
    premium?: string | null;
    /**
     * Salary
     *
     * Salary in force on the projected date, or `null` if no version had taken effect by then. Never zero as a stand-in for missing.
     */
    salary?: string | null;
};

/**
 * InsuredPersonLookupRequest
 *
 * Who to find. The CPF is in the body so it never reaches a URL or an access log.
 */
export type InsuredPersonLookupRequest = {
    /**
     * Cpf
     *
     * The person's CPF. Punctuation is ignored, so `123.456.789-09` and `12345678909` find the same person. Both check digits are verified; a number that fails them is a `422`.
     */
    cpf: string;
};

/**
 * InsuredPersonLookupResponse
 *
 * One person across every contract of the company that carries their CPF.
 */
export type InsuredPersonLookupResponse = {
    /**
     * Birth Date
     *
     * Taken from the first of `contracts`. Two contracts that disagree on it are not reconciled here; each contract's own value is in its history.
     */
    birth_date: string;
    /**
     * Contracts
     *
     * Ordered by contract kind (`vida` before `saude`), then by when the contract was created, then by its id.
     */
    contracts: Array<LookupContractEntry>;
    /**
     * Cpf
     *
     * Canonical eleven digits.
     */
    cpf: string;
    /**
     * Full Name
     *
     * Taken from the first of `contracts`. Two contracts that disagree on it are not reconciled here; each contract's own value is in its history.
     */
    full_name: string;
};

/**
 * InsuredPersonResponse
 *
 * A person's identity, and what the insurer says about them.
 *
 * Every personal field here is encrypted at rest. The last three are the
 * insurer's values: one current value each rather than a history, set by
 * vyttech staff and by nothing a customer files.
 */
export type InsuredPersonResponse = {
    /**
     * Birth Date
     */
    birth_date: string;
    /**
     * Certificate Number
     *
     * The insurer's certificate number for this person (*Certificado*). Text, so leading zeros are kept. `null` until vyttech records it.
     */
    certificate_number?: string | null;
    /**
     * Contract Id
     */
    contract_id: string;
    /**
     * Cpf
     *
     * Canonical eleven digits, without punctuation.
     */
    cpf: string;
    /**
     * Full Name
     */
    full_name: string;
    /**
     * Id
     */
    id: string;
    /**
     * Insured Capital
     *
     * The capital the insurer holds this person insured for (*IS Segurado*), to two decimal places and serialised as a string. The insurer's figure, recorded by vyttech -- never derived from the salary here. `null` until recorded.
     */
    insured_capital?: string | null;
    /**
     * Premium
     *
     * The premium the insurer charges for this person (*Prêmio*), to two decimal places and serialised as a string. May be `0.00`, which the insurer uses on an exclusion. `null` until recorded.
     */
    premium?: string | null;
};

/**
 * InsurerCreate
 *
 * Register an insurer. vyttech staff only.
 */
export type InsurerCreate = {
    /**
     * Code
     *
     * Short stable identifier, stored upper-cased and unique.
     */
    code: string;
    /**
     * Layout Code
     *
     * Which registered file layout this insurer's snapshots are rendered through. `null` means the default layout, which is what every insurer we send to today uses. See `GET /snapshot-layouts` for the registered codes.
     */
    layout_code?: string | null;
    /**
     * Name
     */
    name: string;
};

/**
 * InsurerLayoutUpdate
 *
 * Point an insurer at another file layout. vyttech staff only.
 */
export type InsurerLayoutUpdate = {
    /**
     * Layout Code
     *
     * The registered layout their snapshots are rendered through from now on, or `null` for the default layout. A code nothing is registered under is refused. Snapshots already generated keep the layout they were rendered through.
     */
    layout_code: string | null;
};

/**
 * InsurerListResponse
 *
 * Every insurer on record.
 */
export type InsurerListResponse = {
    /**
     * Count
     */
    count: number;
    /**
     * Items
     */
    items: Array<InsurerResponse>;
};

/**
 * InsurerResponse
 *
 * An insurer we send files to.
 */
export type InsurerResponse = {
    /**
     * Code
     */
    code: string;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Id
     */
    id: string;
    /**
     * Is Active
     */
    is_active: boolean;
    /**
     * Layout Code
     *
     * Which registered file layout this insurer's snapshots are rendered through. `null` means the default layout, which is what every insurer we send to today uses. See `GET /snapshot-layouts` for the registered codes.
     */
    layout_code?: string | null;
    /**
     * Name
     */
    name: string;
};

/**
 * InsurerValuesUpdate
 *
 * Set a person's insurer-side values. Omitted means unchanged; `null` clears.
 *
 * Unlike :class:`ContractUpdate`, a null here has a meaning of its own: every
 * one of these columns *can* be empty, and emptying one is a thing staff need
 * to do. So the distinction is between a field that is absent and a field that
 * is present with no value, and the route reads it from ``model_fields_set``.
 *
 * ``extra="forbid"`` is what keeps the rest of the person out of reach: a body
 * carrying ``full_name`` or ``cpf`` is refused outright rather than silently
 * ignored, because those change only through a delta.
 */
export type InsurerValuesUpdate = {
    /**
     * Certificate Number
     *
     * The insurer's certificate number (*Certificado*). Surrounding spaces are trimmed and the rest is kept exactly, leading zeros included; it may not be empty or longer than 30 characters. `null` clears it.
     */
    certificate_number?: string | null;
    /**
     * Insured Capital
     *
     * Capital insured (*IS Segurado*), as the insurer states it. Zero or more, at most two decimal places. `null` clears it.
     */
    insured_capital?: number | string | null;
    /**
     * Premium
     *
     * Premium (*Prêmio*), as the insurer states it. Zero or more -- the insurer writes zero on an exclusion -- at most two decimal places. `null` clears it.
     */
    premium?: number | string | null;
};

/**
 * IntakeChannel
 *
 * How a roster delta got to us. Values are part of the API contract.
 */
export const IntakeChannel = {
    SPREADSHEET: 'spreadsheet',
    ESOCIAL: 'esocial',
    PORTAL: 'portal',
    API: 'api',
    BACK_OFFICE: 'back_office'
} as const;

/**
 * IntakeChannel
 *
 * How a roster delta got to us. Values are part of the API contract.
 */
export type IntakeChannel = typeof IntakeChannel[keyof typeof IntakeChannel];

/**
 * LayoutColumnResponse
 *
 * One column of a registered layout, as configured.
 */
export type LayoutColumnResponse = {
    /**
     * Header
     */
    header: string;
    /**
     * Key
     */
    key: string;
    /**
     * Max Length
     */
    max_length?: number | null;
    /**
     * Required
     */
    required: boolean;
    /**
     * Source
     *
     * Which projected field fills the column.
     */
    source: string;
};

/**
 * LayoutListResponse
 *
 * Every layout the server can render a file through.
 */
export type LayoutListResponse = {
    /**
     * Count
     */
    count: number;
    /**
     * Items
     */
    items: Array<LayoutResponse>;
};

/**
 * LayoutResponse
 *
 * A registered insurer file layout, as configured.
 *
 * Served so that a client -- and whoever is reconciling our output against an
 * insurer's specification -- can see the columns, the formats and the dialect
 * without reading the source.
 */
export type LayoutResponse = {
    /**
     * Code
     */
    code: string;
    /**
     * Columns
     */
    columns: Array<LayoutColumnResponse>;
    /**
     * Cpf Punctuation
     */
    cpf_punctuation: boolean;
    /**
     * Date Format
     */
    date_format: string;
    /**
     * Decimal Places
     */
    decimal_places: number;
    /**
     * Decimal Separator
     */
    decimal_separator: string;
    /**
     * Delimiter
     */
    delimiter: string;
    /**
     * Encoding
     */
    encoding: string;
    /**
     * Includes Header
     */
    includes_header: boolean;
    /**
     * Line Terminator
     */
    line_terminator: string;
    /**
     * Media Type
     */
    media_type: string;
    /**
     * Name
     */
    name: string;
    /**
     * Thousands Separator
     */
    thousands_separator: string;
};

/**
 * LeavePeriodResponse
 *
 * A stretch of days a person was away -- an *afastamento*. Both ends inclusive.
 */
export type LeavePeriodResponse = {
    /**
     * Created At
     *
     * When this absence was recorded.
     */
    created_at: string;
    /**
     * Effective From
     *
     * First day away, inclusive.
     */
    effective_from: string;
    /**
     * Effective To
     *
     * Last day away, inclusive. `null` means the return date is not known yet; it is filled in by approving a leave that supersedes the one that opened this period.
     */
    effective_to?: string | null;
    /**
     * Id
     */
    id: string;
};

/**
 * LeaveSubmission
 *
 * Record a temporary absence -- an *afastamento*. `effective_date` is the **first day away**.
 *
 * `leave_end_date` is the last day away, inclusive, and is optional: send it
 * when the return date is already on the paperwork -- eSocial's S-2230 usually
 * reports both ends at once -- and leave it out when it is not known yet. A
 * leave filed without it is completed later by another leave naming this one in
 * `supersedes_delta_id` and carrying the date; the original stays exactly where
 * it is.
 *
 * A leave changes nothing else about the person. They stay insured for its
 * whole length, their salary is untouched, and the monthly file says what it
 * would have said anyway.
 */
export type LeaveSubmission = {
    /**
     * Action
     */
    action: 'leave';
    /**
     * Cpf
     *
     * The person's CPF. Punctuation is ignored, so `123.456.789-09` and `12345678909` are the same number. Both check digits are verified; a mistyped or transposed digit is rejected rather than stored.
     */
    cpf: string;
    /**
     * Effective Date
     *
     * Calendar date the change takes effect, `YYYY-MM-DD`, in America/Sao_Paulo. Mandatory for every action. It may fall in a future month, in the current cycle, or in the past -- a date before the last snapshot sent to the insurer is accepted and flagged as retroactive.
     */
    effective_date: string;
    /**
     * Leave End Date
     *
     * Last day away, inclusive, `YYYY-MM-DD`. Omit it -- or send `null` -- when the return date is not known yet; the leave is then open-ended and is completed by a later delta that supersedes it. It may not come before `effective_date`.
     */
    leave_end_date?: string | null;
};

/**
 * LookupContractEntry
 *
 * One contract that carries the person, covered today or not.
 */
export type LookupContractEntry = {
    /**
     * Contract Id
     */
    contract_id: string;
    /**
     * What insurance the contract is, as on `ContractResponse.kind`: `vida` or `saude`. More kinds may be added in a later minor version.
     */
    contract_kind: ContractKind;
    /**
     * Covered Today
     *
     * Whether one of this person's coverage periods on this contract contains today, in America/Sao_Paulo. An entry whose coverage has ended -- or not yet begun -- is still listed, with `false`.
     */
    covered_today: boolean;
    /**
     * Insured Person Id
     *
     * This person's record on this contract -- one per contract, so the same person has a different one under each. Read their history on the contract through `GET /contracts/{contract_id}/insured-people/{insured_person_id}/history`.
     */
    insured_person_id: string;
    /**
     * Modulo
     */
    modulo: number;
    /**
     * Subestipulante
     */
    subestipulante: number;
};

/**
 * MachineClientCreate
 *
 * What vyttech states when issuing a credential to one customer company.
 *
 * The company is not a field: it comes from the `X-Company-Id` header, which
 * is the same scope every other write is made inside. A credential that named
 * its own tenant in the body could be issued for a company the request was not
 * scoped to, which is the one mistake this resource must not allow.
 */
export type MachineClientCreate = {
    /**
     * Name
     *
     * What this credential is for, so it can be recognised later.
     */
    name: string;
    /**
     * `machine_read` for a credential that only reads, `machine_write` for one that also submits deltas. Anything else is rejected.
     */
    role?: Role;
};

/**
 * MachineClientCreatedResponse
 *
 * The one and only response that carries the client secret.
 *
 * It is not stored in a readable form anywhere, so it cannot be shown again.
 * A client that loses it needs a new credential, not a recovery.
 */
export type MachineClientCreatedResponse = {
    /**
     * Client Id
     *
     * Public half of the credential. Safe to log and to show on a screen.
     */
    client_id: string;
    /**
     * Client Secret
     *
     * Shown exactly once. Store it now; it cannot be retrieved later.
     */
    client_secret: string;
    /**
     * Company Id
     *
     * The single customer company this credential can ever reach.
     */
    company_id: string;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Id
     *
     * Identifier of the credential itself, used to manage it.
     */
    id: string;
    /**
     * Last Used At
     *
     * When it last successfully exchanged its secret for a token.
     */
    last_used_at?: string | null;
    /**
     * Name
     */
    name: string;
    /**
     * Revoked At
     *
     * When the credential was revoked. `null` while it is still usable.
     */
    revoked_at?: string | null;
    role: Role;
    /**
     * Secret Rotated At
     *
     * When the secret was last replaced. `null` if it is the original one.
     */
    secret_rotated_at?: string | null;
};

/**
 * MachineClientListResponse
 */
export type MachineClientListResponse = {
    /**
     * Count
     */
    count: number;
    /**
     * Items
     */
    items: Array<MachineClientResponse>;
};

/**
 * MachineClientResponse
 *
 * A credential as everybody but its holder sees it: without the secret.
 */
export type MachineClientResponse = {
    /**
     * Client Id
     *
     * Public half of the credential. Safe to log and to show on a screen.
     */
    client_id: string;
    /**
     * Company Id
     *
     * The single customer company this credential can ever reach.
     */
    company_id: string;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Id
     *
     * Identifier of the credential itself, used to manage it.
     */
    id: string;
    /**
     * Last Used At
     *
     * When it last successfully exchanged its secret for a token.
     */
    last_used_at?: string | null;
    /**
     * Name
     */
    name: string;
    /**
     * Revoked At
     *
     * When the credential was revoked. `null` while it is still usable.
     */
    revoked_at?: string | null;
    role: Role;
    /**
     * Secret Rotated At
     *
     * When the secret was last replaced. `null` if it is the original one.
     */
    secret_rotated_at?: string | null;
};

/**
 * MachineTokenRequest
 *
 * The issued credentials of a machine client.
 */
export type MachineTokenRequest = {
    /**
     * Client Id
     *
     * The public half of the credential, as it was issued.
     */
    client_id: string;
    /**
     * Client Secret
     *
     * The secret half. Shown once, at issue or at rotation, and never again.
     */
    client_secret: string;
};

/**
 * MailAttachmentResponse
 *
 * One attached file, and which path it went down.
 */
export type MailAttachmentResponse = {
    /**
     * Attempt
     *
     * Which run of ingestion produced this line. 1 on arrival.
     */
    attempt: number;
    /**
     * Byte Size
     */
    byte_size: number;
    /**
     * Content Sha256
     */
    content_sha256: string;
    /**
     * Declared Media Type
     *
     * What the sending client claimed the file is. Kept beside what we made of it and consulted for nothing: the format is read from the bytes.
     */
    declared_media_type: string;
    /**
     * Document Id
     *
     * The supporting document this attachment became.
     */
    document_id?: string | null;
    /**
     * Filename
     *
     * As the sending client named it. Nothing branches on it.
     */
    filename: string;
    /**
     * `spreadsheet` became an upload preview -- **no delta exists until it is confirmed**. `document` became a supporting document with `channel: email`. `ignored` is part of the message rather than a file somebody sent, a signature logo above all. `rejected` was refused, and says why.
     */
    outcome: AttachmentOutcome;
    /**
     * Part Number
     *
     * Position in the message, from one, counting the ignored parts too.
     */
    part_number: number;
    /**
     * Rejection Code
     *
     * Stable, machine-readable reason an attachment was refused: `empty_attachment`, `attachment_too_large` or `unreadable_attachment`. Branch on this, never on the message.
     */
    rejection_code?: string | null;
    /**
     * Rejection Reason
     *
     * Written for whoever has to tell the customer what to send instead.
     */
    rejection_reason?: string | null;
    /**
     * Spreadsheet Upload Id
     *
     * The preview this attachment became. Read and confirm it through `/contracts/{contract_id}/spreadsheet-uploads/{upload_id}`.
     */
    spreadsheet_upload_id?: string | null;
};

/**
 * MailMessageDetailResponse
 *
 * A message and its attachments: everything known about one email.
 */
export type MailMessageDetailResponse = {
    /**
     * Attachments
     */
    attachments: Array<MailAttachmentResponse>;
    message: MailMessageResponse;
};

/**
 * MailMessageDiscard
 *
 * Take a message off the queue without acting on it.
 */
export type MailMessageDiscard = {
    /**
     * Reason
     *
     * Why it is not ours to act on. Recorded on the message and in the trail.
     */
    reason: string;
};

/**
 * MailMessageListResponse
 */
export type MailMessageListResponse = {
    /**
     * Count
     */
    count: number;
    /**
     * Items
     */
    items: Array<MailMessageResponse>;
    /**
     * Limit
     */
    limit: number;
    /**
     * Offset
     */
    offset: number;
    /**
     * Outstanding Count
     *
     * How many messages are waiting for somebody -- unrouted plus refused, across the whole mailbox and regardless of the filters on this listing. The number this bridge exists to keep at zero.
     */
    outstanding_count: number;
};

/**
 * MailMessageResponse
 *
 * One message out of the mailbox, and what was made of it.
 */
export type MailMessageResponse = {
    /**
     * Attachment Count
     */
    attachment_count: number;
    /**
     * Attempt Count
     *
     * How many times ingestion has been run over this message. One on arrival.
     */
    attempt_count: number;
    /**
     * Company Id
     *
     * Whose it turned out to be. Null on an unrouted message, which belongs to no company at all.
     */
    company_id?: string | null;
    /**
     * Contract Id
     */
    contract_id?: string | null;
    /**
     * Discarded At
     */
    discarded_at?: string | null;
    /**
     * Discarded By Display Name
     */
    discarded_by_display_name?: string | null;
    /**
     * Discarded Reason
     */
    discarded_reason?: string | null;
    /**
     * Document Count
     */
    document_count: number;
    /**
     * From Address
     *
     * The `From` address, lower-cased. The only thing routing reads.
     */
    from_address: string;
    /**
     * Id
     */
    id: string;
    /**
     * Ignored Count
     *
     * Parts of the message rather than files somebody sent.
     */
    ignored_count: number;
    /**
     * Last Attempted At
     */
    last_attempted_at: string;
    /**
     * Mailbox
     *
     * Which mailbox it came out of. A label; nothing is ever sent from it.
     */
    mailbox: string;
    /**
     * Message Id
     *
     * The `Message-ID` header as it arrived, for matching a support conversation.
     */
    message_id?: string;
    /**
     * Received At
     *
     * When we pulled it out of the mailbox.
     */
    received_at: string;
    /**
     * Refusal Code
     *
     * Why nothing was taken: `sender_not_registered`, `unreadable_message` or `no_usable_attachment`. Null on an ingested message.
     */
    refusal_code?: string | null;
    /**
     * Refusal Reason
     */
    refusal_reason?: string | null;
    /**
     * Rejected Count
     */
    rejected_count: number;
    /**
     * Sender Id
     *
     * The registration that routed it.
     */
    sender_id?: string | null;
    /**
     * Sent At
     *
     * The `Date` header: when the sender says they sent it. **Never an effective date** -- no movement in this product takes its date from a message header.
     */
    sent_at?: string | null;
    /**
     * Source Byte Count
     */
    source_byte_count: number;
    /**
     * Source Sha256
     *
     * SHA-256 of the whole message. This is what identifies it -- not the `Message-ID` header, which is a thing the sending client chose.
     */
    source_sha256: string;
    /**
     * Spreadsheet Count
     */
    spreadsheet_count: number;
    /**
     * `ingested` -- routed, and at least one attachment reached a path. `unrouted` -- the address it came from is not registered against any contract, so we do not know whose it is. `refused` -- routed, and nothing in it could be taken. `discarded` -- an operator decided it is not ours to act on.
     *
     * The last three are the queue: they are what `outstanding=true` returns and what somebody has to work through.
     */
    status: MailMessageStatus;
    /**
     * Subject
     *
     * As the sender wrote it. Stored encrypted, because a subject line usually names the person the paperwork is about.
     */
    subject?: string | null;
};

/**
 * MailMessageStatus
 *
 * Where one message from the vyttech mailbox got to.
 */
export const MailMessageStatus = {
    INGESTED: 'ingested',
    UNROUTED: 'unrouted',
    REFUSED: 'refused',
    DISCARDED: 'discarded'
} as const;

/**
 * MailMessageStatus
 *
 * Where one message from the vyttech mailbox got to.
 */
export type MailMessageStatus = typeof MailMessageStatus[keyof typeof MailMessageStatus];

/**
 * MailboxPollResponse
 *
 * What one poll did.
 */
export type MailboxPollResponse = {
    /**
     * Duplicates
     *
     * Messages we had already read, recognised by the digest of their bytes. Nothing was done again.
     */
    duplicates: number;
    /**
     * Fetched
     *
     * Messages read out of the mailbox this poll. Not always the length of `messages`: one that failed outright is counted here and has no entry there, and that difference is what says something went wrong.
     */
    fetched: number;
    /**
     * Ingested
     *
     * Routed, with at least one attachment taken.
     */
    ingested: number;
    /**
     * Mailbox
     */
    mailbox: string;
    /**
     * Messages
     */
    messages: Array<MailMessageResponse>;
    /**
     * Refused
     *
     * Routed, and nothing in them could be taken.
     */
    refused: number;
    /**
     * Unrouted
     *
     * From an address nobody has registered.
     */
    unrouted: number;
};

/**
 * MailboxSenderCreate
 *
 * Trust one address to file movements for one contract.
 */
export type MailboxSenderCreate = {
    /**
     * Address
     *
     * The address messages will come from, matched against the `From` header after trimming and lower-casing. It must not already be registered against another contract.
     */
    address: string;
    /**
     * Contract Id
     *
     * The contract this sender's spreadsheets and paperwork are about.
     */
    contract_id: string;
    /**
     * What this sender's attached paperwork is recorded as. **Configuration, not inference**: nothing in this product reads a document, and that includes reading its filename. Leave it alone and every document from this address is `other`, which is what 'we do not know' honestly looks like.
     */
    default_document_kind?: DocumentKind;
    /**
     * Label
     *
     * What to call this sender on screen. A label for a person, nothing more.
     */
    label?: string | null;
};

/**
 * MailboxSenderListResponse
 */
export type MailboxSenderListResponse = {
    /**
     * Count
     */
    count: number;
    /**
     * Items
     */
    items: Array<MailboxSenderResponse>;
    /**
     * Limit
     */
    limit: number;
    /**
     * Offset
     */
    offset: number;
};

/**
 * MailboxSenderResponse
 *
 * One registration: the address, the contract, and whether it is still live.
 */
export type MailboxSenderResponse = {
    /**
     * Address
     */
    address: string;
    /**
     * Company Id
     */
    company_id: string;
    /**
     * Contract Id
     */
    contract_id: string;
    default_document_kind: DocumentKind;
    /**
     * Id
     */
    id: string;
    /**
     * Is Live
     *
     * False once the registration has been withdrawn. The row is kept either way.
     */
    is_live: boolean;
    /**
     * Label
     */
    label?: string | null;
    /**
     * Registered At
     */
    registered_at: string;
    /**
     * Registered By Display Name
     */
    registered_by_display_name: string;
    /**
     * Revoked At
     */
    revoked_at?: string | null;
    /**
     * Revoked By Display Name
     */
    revoked_by_display_name?: string | null;
    /**
     * Revoked Reason
     */
    revoked_reason?: string | null;
};

/**
 * MailboxSenderRevocation
 *
 * Withdraw trust from a registration.
 */
export type MailboxSenderRevocation = {
    /**
     * Reason
     *
     * Why the address is no longer trusted. Recorded on the registration.
     */
    reason?: string | null;
};

/**
 * Movement
 *
 * What happened to one person between the previous cycle and this one.
 *
 * Worked out when a monthly snapshot is generated -- see
 * :mod:`app.snapshots.movements` -- and stored on every row it writes, so the
 * next cycle can read back who the insurer was told about. The words the insurer
 * reads in a file are not these values: they are labels on the layout's
 * configuration, because the words are the insurer's. Values are part of the API
 * contract.
 */
export const Movement = {
    UNCHANGED: 'unchanged',
    INCLUSION: 'inclusion',
    ALTERATION: 'alteration',
    EXCLUSION: 'exclusion'
} as const;

/**
 * Movement
 *
 * What happened to one person between the previous cycle and this one.
 *
 * Worked out when a monthly snapshot is generated -- see
 * :mod:`app.snapshots.movements` -- and stored on every row it writes, so the
 * next cycle can read back who the insurer was told about. The words the insurer
 * reads in a file are not these values: they are labels on the layout's
 * configuration, because the words are the insurer's. Values are part of the API
 * contract.
 */
export type Movement = typeof Movement[keyof typeof Movement];

/**
 * MovementStage
 *
 * Where one movement stands, said the way a customer would say it.
 */
export const MovementStage = {
    RECEIVED: 'received',
    FLAGGED: 'flagged',
    SCHEDULED: 'scheduled',
    APPROVED: 'approved',
    INCLUDED: 'included',
    REJECTED: 'rejected',
    RETURNED: 'returned',
    CANCELLED: 'cancelled'
} as const;

/**
 * MovementStage
 *
 * Where one movement stands, said the way a customer would say it.
 */
export type MovementStage = typeof MovementStage[keyof typeof MovementStage];

/**
 * MovementStageCounts
 *
 * How many movements are in each stage, over the page that carries them.
 *
 * The page and not the contract: a stage is derived per movement from the
 * cycle it lands in, so counting the ones that were not returned would mean
 * reading them. With the default limit a contract's whole history is one page,
 * and `total` beside these says when it is not.
 *
 * Every stage is a field and every field is always present. A client renders a
 * fixed set of headings from this, and a stage that vanished when its count
 * reached zero would be a heading that vanished with it.
 */
export type MovementStageCounts = {
    /**
     * Approved
     */
    approved: number;
    /**
     * Cancelled
     */
    cancelled: number;
    /**
     * Flagged
     */
    flagged: number;
    /**
     * Included
     */
    included: number;
    /**
     * Received
     */
    received: number;
    /**
     * Rejected
     */
    rejected: number;
    /**
     * Returned
     */
    returned: number;
    /**
     * Scheduled
     */
    scheduled: number;
};

/**
 * MovementStatusListResponse
 *
 * One contract's movements, each with where it stands.
 */
export type MovementStatusListResponse = {
    /**
     * Contract Id
     */
    contract_id: string;
    /**
     * Count
     *
     * How many movements are on this page.
     */
    count: number;
    /**
     * How many movements are in each stage, over this page.
     */
    counts: MovementStageCounts;
    /**
     * Cycle Month
     *
     * The cutoff being worked on -- the month every `scheduled` answer is relative to. Defaults to the current month in São Paulo.
     */
    cycle_month: string;
    /**
     * Items
     *
     * This page of movements, most recently filed first.
     */
    items: Array<MovementStatusResponse>;
    /**
     * Limit
     */
    limit: number;
    /**
     * Offset
     */
    offset: number;
    /**
     * Submitted Through
     *
     * The last day already reported to the insurer, or `null` when nothing has been. A movement dated on or before it is a late arrival.
     */
    submitted_through?: string | null;
    /**
     * Total
     *
     * How many movements this contract has filed in total.
     */
    total: number;
};

/**
 * MovementStatusResponse
 *
 * One movement, and where it stands.
 */
export type MovementStatusResponse = {
    /**
     * Cutoff Date
     *
     * The single calendar date that cycle reads the roster at. Derived from the contract's `reference_date_rule`; never supplied by a caller.
     */
    cutoff_date: string;
    /**
     * Cycle Is Signed Off
     *
     * True when a vyttech analyst has signed that cutoff off, which is what makes an approved movement `included`. A sign-off that was withdrawn afterwards is not one that stands, so this goes back to `false`.
     */
    cycle_is_signed_off: boolean;
    /**
     * Cycle Month
     *
     * The month whose file this movement first appears on, written `YYYY-MM` -- its own, or a later one when it takes effect after this cutoff. The same arithmetic the back-office queue is partitioned by.
     */
    cycle_month: string;
    delta: DeltaResponse;
    /**
     * Is Late Arrival
     *
     * True when the effective date falls on or before the last day already reported to the insurer. Such a movement is kept and looked at, never refused for being late -- and it is why a movement is `flagged`.
     */
    is_late_arrival: boolean;
    /**
     * Needs Customer Action
     *
     * True when the next move is the customer's rather than ours -- today, exactly the returned movements. Everything else is waiting on vyttech, on a cutoff, or on nothing at all.
     */
    needs_customer_action: boolean;
    /**
     * Where this movement stands, in the terms whoever filed it would use.
     *
     * * `received` -- filed, undecided, and due on the cutoff being worked on now.
     * * `flagged` -- something about it has to be looked at first; today that is always an effective date inside a month the insurer has already been sent.
     * * `scheduled` -- due on a later month's file, and being held until then. Nothing is waiting on it and it is not late.
     * * `approved` -- decided in its favour and in the roster from its effective date; the file it belongs to has not been signed off yet.
     * * `included` -- on a cutoff that has been signed off, which is the last thing that happens here before the file goes to the insurer.
     * * `rejected` -- refused, with a reason on the movement.
     * * `returned` -- handed back to be filed again, with what to correct. The one stage waiting on the customer rather than on us.
     * * `cancelled` -- withdrawn by whoever filed it.
     *
     * Derived on every read from the movement, the cycle it lands in and the sign-offs that stand; there is no column anywhere holding one.
     */
    stage: MovementStage;
};

/**
 * NoticeFailed
 *
 * Record that the insurer would not take this notice.
 */
export type NoticeFailed = {
    /**
     * Reason
     *
     * What the insurer said, or what went wrong on the way. Mandatory: whoever raises the next notice has only this to go on, and it is what decides whether this contract's `off_cycle_rule` was right in the first place.
     */
    reason: string;
};

/**
 * NoticeItemResponse
 *
 * One movement on a notice, and the row the insurer was sent about it.
 */
export type NoticeItemResponse = {
    /**
     * Cycle Month
     *
     * The cycle whose monthly file carries this movement anyway, written `YYYY-MM`. An off-cycle notice does not replace that file and does not take the movement off it: the roster is effective-dated, the snapshot projects it, and the person appears on the next file exactly as they would have. The insurer reconciles against that file, so a person left off it because they had been notified early would vanish from their records.
     */
    cycle_month: string;
    /**
     * Delta Id
     */
    delta_id: string;
    /**
     * Effective Date
     *
     * The movement's own effective date, and the date the person's values were rendered at. Not the day the notice was raised: an admission on the 25th describes somebody the roster does not cover on the 3rd.
     */
    effective_date: string;
    /**
     * Insured Person Id
     */
    insured_person_id: string;
    /**
     * Sequence
     *
     * Position in the file, from 1.
     */
    sequence: number;
    /**
     * Values
     *
     * The rendered cells, keyed by the layout's column keys -- the record of what was written, not the projected values. A salary kept as a number would not say whether the file wrote `3500,00` or `3500.00`, which is exactly the difference the insurer's parser cares about.
     */
    values: {
        [key: string]: string;
    };
};

/**
 * NoticeListResponse
 *
 * Off-cycle notices in scope, most recently raised first, refused ones included.
 */
export type NoticeListResponse = {
    /**
     * Count
     */
    count: number;
    /**
     * Items
     */
    items: Array<NoticeResponse>;
    /**
     * Limit
     */
    limit: number;
    /**
     * Offset
     */
    offset: number;
};

/**
 * NoticeRaise
 *
 * Compose an off-cycle notice for one or more approved movements.
 */
export type NoticeRaise = {
    /**
     * Delta Ids
     *
     * The movements to tell the insurer about, in the order they should appear in the file.
     *
     * **Every one has to be approved**, on this contract, not already covered by a monthly file the insurer has, and not already named by a notice standing. **All of them have to carry the same action**: the insurer's layout says who somebody is and not what is happening to them, so one file with inclusions and exclusions in it does not say what is being asked for each row. Raise one notice per action.
     */
    delta_ids: Array<string>;
    /**
     * Reason
     *
     * Why this could not wait for the cutoff. Mandatory, and it is the whole justification for going round the monthly rhythm -- "admitida no dia 25, o arquivo de abril só fecha no dia 20 do mês que vem" is something somebody can be held to. An exception route with no recorded reason becomes the ordinary route within a quarter.
     */
    reason: string;
};

/**
 * NoticeResponse
 *
 * One off-cycle notice: what it said, where it went, and what came back.
 */
export type NoticeResponse = {
    /**
     * The single kind of movement this notice is about. One notice carries one action; a file with inclusions and exclusions in it does not say what is being asked for each row.
     */
    action: DeltaAction;
    /**
     * Which door it went through. Set when it is marked sent.
     */
    channel?: SubmissionChannel | null;
    /**
     * Company Id
     */
    company_id: string;
    /**
     * Contract Id
     */
    contract_id: string;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Cycle Month
     *
     * The cycle whose monthly file carries this movement anyway, written `YYYY-MM`. An off-cycle notice does not replace that file and does not take the movement off it: the roster is effective-dated, the snapshot projects it, and the person appears on the next file exactly as they would have. The insurer reconciles against that file, so a person left off it because they had been notified early would vanish from their records.
     */
    cycle_month: string;
    /**
     * Failed At
     */
    failed_at?: string | null;
    /**
     * Who recorded the refusal. `null` unless it failed.
     */
    failed_by?: PrincipalReference | null;
    /**
     * Failure Reason
     */
    failure_reason?: string | null;
    /**
     * File Byte Count
     */
    file_byte_count: number;
    /**
     * File Media Type
     */
    file_media_type: string;
    /**
     * File Name
     *
     * What the composed file is called. Deliberately not the monthly file's name: the two must not be confused in an operations mailbox.
     */
    file_name: string;
    /**
     * File Sha256
     *
     * SHA-256 of the composed bytes. **The file itself is not served here** -- read it at `GET /off-cycle-notices/{notice_id}/file`.
     */
    file_sha256: string;
    /**
     * Has Proof
     *
     * True when at least one of `reference`, `proof_document_id` and `notes` is present. Always true on a `sent` notice -- the marker will not be recorded without one.
     */
    has_proof: boolean;
    /**
     * Id
     */
    id: string;
    /**
     * Item Count
     */
    item_count: number;
    /**
     * Items
     *
     * The movements this notice named, in the order they were written.
     */
    items: Array<NoticeItemResponse>;
    /**
     * Layout Code
     *
     * The insurer's file layout the rows were rendered through.
     */
    layout_code: string;
    /**
     * Notes
     */
    notes?: string | null;
    /**
     * Proof Document Id
     *
     * The receipt, filed in this company's document store. Read it at `GET /documents/{document_id}/content`.
     */
    proof_document_id?: string | null;
    /**
     * Raised At
     */
    raised_at: string;
    /**
     * Who composed it.
     */
    raised_by: PrincipalReference;
    /**
     * Raised On
     *
     * The calendar day in São Paulo the notice was composed on.
     */
    raised_on: string;
    /**
     * Reason
     *
     * Why this could not wait for the cutoff.
     */
    reason: string;
    /**
     * Reference
     */
    reference?: string | null;
    /**
     * Sent At
     *
     * When the insurer was told. `null` until it is marked sent.
     */
    sent_at?: string | null;
    /**
     * Who told the insurer. `null` until it is marked sent.
     */
    sent_by?: PrincipalReference | null;
    /**
     * `prepared` once the notice is composed and out of the building, `sent` once the insurer has been told and it is evidenced, `failed` when they would not take it -- which frees its movements for a fresh notice.
     *
     * **A notice does not move the submission watermark.** `covers_through` on a monthly submission means the insurer has been told everything up to that day; a notice about named people says nothing of the kind. A movement dated inside the period a notice covers is exactly as retroactive afterwards as it was before.
     */
    status: OffCycleNoticeStatus;
};

/**
 * NoticeSent
 *
 * Mark a notice as told to the insurer, with whatever came back.
 *
 * **At least one of `reference`, `proof_document_id` and `notes` is required.**
 * The same rule the monthly marker holds, and it binds harder here: a monthly
 * submission leaves a frozen file and a sign-off behind it whatever the marker
 * says, and an off-cycle notice leaves nothing but this row.
 */
export type NoticeSent = {
    /**
     * Which door the notice went through. Half of any answer to "prove you told them on the 25th"; the other half is the proof below.
     */
    channel: SubmissionChannel;
    /**
     * Notes
     *
     * What whoever sent it wants the next person to know. The form of proof that carries the honest case -- "enviado por e-mail às 09:14, sem protocolo de volta" is evidence; an empty record is not.
     */
    notes?: string | null;
    /**
     * Proof Document Id
     *
     * A receipt already uploaded to this company's document store, filed as the evidence behind this notice. Upload it at `POST /documents` first; the bytes are never sent here.
     */
    proof_document_id?: string | null;
    /**
     * Reference
     *
     * The insurer's protocol number, ticket or receipt identifier -- whatever came back. Free text, because what comes back differs per insurer and per door.
     */
    reference?: string | null;
    /**
     * Sent At
     *
     * When the insurer was really told. Defaults to now, and is accepted because an urgent notice goes out first and gets written up afterwards -- which is what makes it urgent. A moment in the future is refused.
     *
     * Send an offset. **A value without one is read as São Paulo wall-clock time**, which is what the person reading the clock in front of them meant.
     */
    sent_at?: string | null;
};

/**
 * NotificationDelivery
 *
 * Where the *emailing* of one notification stands. Values are contractual.
 *
 * The in-app half has no state: the row is the notification, and the portal
 * reads it. This enumeration is only about the copy that also goes out by
 * email, which is the half that can be behind.
 */
export const NotificationDelivery = {
    NOT_APPLICABLE: 'not_applicable',
    PENDING: 'pending',
    SENT: 'sent'
} as const;

/**
 * NotificationDelivery
 *
 * Where the *emailing* of one notification stands. Values are contractual.
 *
 * The in-app half has no state: the row is the notification, and the portal
 * reads it. This enumeration is only about the copy that also goes out by
 * email, which is the half that can be behind.
 */
export type NotificationDelivery = typeof NotificationDelivery[keyof typeof NotificationDelivery];

/**
 * NotificationEvent
 *
 * Something that happened to a customer's request. Values are contractual.
 */
export const NotificationEvent = {
    MOVEMENT_FLAGGED: 'movement.flagged',
    MOVEMENT_APPROVED: 'movement.approved',
    MOVEMENT_REJECTED: 'movement.rejected',
    MOVEMENT_RETURNED: 'movement.returned',
    MOVEMENT_DATE_CORRECTED: 'movement.date_corrected',
    CYCLE_APPROVED: 'cycle.approved',
    CYCLE_REOPENED: 'cycle.reopened'
} as const;

/**
 * NotificationEvent
 *
 * Something that happened to a customer's request. Values are contractual.
 */
export type NotificationEvent = typeof NotificationEvent[keyof typeof NotificationEvent];

/**
 * NotificationListResponse
 *
 * A page of one company's notifications, most recent first.
 */
export type NotificationListResponse = {
    /**
     * Count
     *
     * How many notifications are on this page.
     */
    count: number;
    /**
     * Items
     */
    items: Array<NotificationResponse>;
    /**
     * Limit
     */
    limit: number;
    /**
     * Offset
     */
    offset: number;
    /**
     * Unread Count
     *
     * How many the company has not marked read, across everything and not just this page. The number on the badge.
     */
    unread_count: number;
};

/**
 * NotificationResponse
 *
 * One thing a customer was told, as references rather than as prose.
 */
export type NotificationResponse = {
    /**
     * Company Id
     */
    company_id: string;
    /**
     * Contract Id
     *
     * The contract this is about. Set on every event today.
     */
    contract_id?: string | null;
    /**
     * Cycle Month
     *
     * The cutoff, on a `cycle.*` event and `null` otherwise, written `YYYY-MM`.
     */
    cycle_month?: string | null;
    /**
     * Delivered At
     */
    delivered_at?: string | null;
    /**
     * Where the *email* copy stands. `not_applicable` is an event shown in the portal and not emailed; `pending` is waiting in the outbox; `sent` has gone. The portal shows every notification whatever this says -- it is about the email, not about the notification.
     */
    delivery: NotificationDelivery;
    /**
     * Delta Id
     *
     * The movement, on a `movement.*` event and `null` otherwise. Read it with `GET /deltas/{delta_id}`, which is where the reason, the dates and the person are.
     */
    delta_id?: string | null;
    /**
     * What happened. Every value is something vyttech did to a request the customer filed, or to the cutoff their requests are on -- never an acknowledgement of what the customer did themselves.
     *
     * * `movement.flagged` -- it arrived dated inside a month the insurer has already been sent, so it is being looked at rather than refused.
     * * `movement.approved` -- it is in the roster from its effective date.
     * * `movement.rejected` -- refused; the reason is on the movement.
     * * `movement.returned` -- handed back to be filed again; what to correct is on the movement.
     * * `movement.date_corrected` -- an analyst replaced the effective date with the one the signed paperwork says; what was filed is the movement's `original_effective_date`.
     * * `cycle.approved` -- the cutoff was signed off, so the movements on it go to the insurer. One per contract per cycle.
     * * `cycle.reopened` -- that sign-off was withdrawn.
     */
    event: NotificationEvent;
    /**
     * Id
     */
    id: string;
    /**
     * Movement Count
     *
     * How many movements that cutoff carried, on a `cycle.*` event.
     */
    movement_count?: number | null;
    /**
     * Occurred At
     *
     * When the thing being reported happened.
     */
    occurred_at: string;
    /**
     * Read At
     *
     * When somebody at the company marked it read. One mark per notification rather than one per person: a customer's team share an inbox, which is the thing this replaces.
     */
    read_at?: string | null;
};

/**
 * NotificationsReadResponse
 *
 * What marking everything read actually marked.
 */
export type NotificationsReadResponse = {
    /**
     * Marked Read
     *
     * How many notifications this call moved from unread to read.
     */
    marked_read: number;
    /**
     * Unread Count
     *
     * How many are still unread afterwards.
     */
    unread_count: number;
};

/**
 * NrInscMode
 *
 * Which form of the employer's CNPJ goes in ``ideEmpregador/nrInsc``.
 *
 * A dimension of the experiment rather than a setting. The schema takes eight
 * to fifteen digits, the manual does not say which eSocial wants for a
 * consultation, and the answer may well differ between the two consultations
 * and between the two environments -- which is precisely the kind of thing
 * only a probe can establish.
 */
export const NrInscMode = { FULL: 'full', BASE: 'base' } as const;

/**
 * NrInscMode
 *
 * Which form of the employer's CNPJ goes in ``ideEmpregador/nrInsc``.
 *
 * A dimension of the experiment rather than a setting. The schema takes eight
 * to fifteen digits, the manual does not say which eSocial wants for a
 * consultation, and the answer may well differ between the two consultations
 * and between the two environments -- which is precisely the kind of thing
 * only a probe can establish.
 */
export type NrInscMode = typeof NrInscMode[keyof typeof NrInscMode];

/**
 * OffCycleNoticeStatus
 *
 * Where one off-cycle notice stands. Values are part of the API contract.
 */
export const OffCycleNoticeStatus = {
    PREPARED: 'prepared',
    SENT: 'sent',
    FAILED: 'failed'
} as const;

/**
 * OffCycleNoticeStatus
 *
 * Where one off-cycle notice stands. Values are part of the API contract.
 */
export type OffCycleNoticeStatus = typeof OffCycleNoticeStatus[keyof typeof OffCycleNoticeStatus];

/**
 * OffCycleRule
 *
 * Whether this contract's insurer takes movements between cutoffs, and which.
 *
 * A term of the insurer's contract, recorded per contract. The values are
 * ordered from "the monthly file is the only channel" outwards, and each is a
 * thing a real contract says.
 */
export const OffCycleRule = {
    NOT_PERMITTED: 'not_permitted',
    ADMISSIONS_ONLY: 'admissions_only',
    ALL_MOVEMENTS: 'all_movements'
} as const;

/**
 * OffCycleRule
 *
 * Whether this contract's insurer takes movements between cutoffs, and which.
 *
 * A term of the insurer's contract, recorded per contract. The values are
 * ordered from "the monthly file is the only channel" outwards, and each is a
 * thing a real contract says.
 */
export type OffCycleRule = typeof OffCycleRule[keyof typeof OffCycleRule];

/**
 * Permission
 *
 * A single thing a principal may do. Values appear in API documentation.
 */
export const Permission = {
    DATA_READ: 'data:read',
    DATA_WRITE: 'data:write',
    USER_READ: 'user:read',
    USER_WRITE: 'user:write',
    COMPANY_READ: 'company:read',
    COMPANY_WRITE: 'company:write',
    CONTRACT_WRITE: 'contract:write',
    MACHINE_CLIENT_READ: 'machine_client:read',
    MACHINE_CLIENT_WRITE: 'machine_client:write',
    AUDIT_READ: 'audit:read',
    REVIEW_READ: 'review:read',
    REVIEW_WRITE: 'review:write',
    SUBMISSION_READ: 'submission:read',
    SUBMISSION_WRITE: 'submission:write',
    MAILBOX_READ: 'mailbox:read',
    MAILBOX_WRITE: 'mailbox:write',
    CERTIFICATES_READ: 'certificates:read',
    CERTIFICATES_WRITE: 'certificates:write',
    ESOCIAL_READ: 'esocial:read',
    ESOCIAL_WRITE: 'esocial:write',
    CLOCK_READ: 'clock:read',
    CLOCK_WRITE: 'clock:write'
} as const;

/**
 * Permission
 *
 * A single thing a principal may do. Values appear in API documentation.
 */
export type Permission = typeof Permission[keyof typeof Permission];

/**
 * PersonHistoryResponse
 *
 * Everything on record for one person, superseded rows included.
 */
export type PersonHistoryResponse = {
    /**
     * Coverage Periods
     *
     * Every period, oldest first, closed ones included.
     */
    coverage_periods: Array<CoveragePeriodResponse>;
    /**
     * Leave Periods
     *
     * Every absence on record, oldest first, completed ones included. Beside the coverage rather than inside it: a person on leave stays insured for the whole of it, so their coverage periods and salary versions say what they would have said anyway, and so does the month's file.
     */
    leave_periods?: Array<LeavePeriodResponse>;
    person: InsuredPersonResponse;
    /**
     * Salary Versions
     *
     * Every version ever recorded, oldest first, including ones that have been superseded by a later effective date or by a correction.
     */
    salary_versions: Array<SalaryVersionResponse>;
};

/**
 * PortalWalkFailure
 *
 * Why a stage failed, when it is the portal's answer rather than the browser's (#479).
 *
 * Beside :class:`~app.esocial.diagnostics.DiagnosticFailure`, which names what
 * went wrong with the browser or the network: these name what the portal said.
 * Values are part of the API contract.
 */
export const PortalWalkFailure = { PROCURACAO_MISSING: 'procuracao_missing', SESSION_EXPIRED: 'session_expired' } as const;

/**
 * PortalWalkFailure
 *
 * Why a stage failed, when it is the portal's answer rather than the browser's (#479).
 *
 * Beside :class:`~app.esocial.diagnostics.DiagnosticFailure`, which names what
 * went wrong with the browser or the network: these name what the portal said.
 * Values are part of the API contract.
 */
export type PortalWalkFailure = typeof PortalWalkFailure[keyof typeof PortalWalkFailure];

/**
 * PrincipalReference
 *
 * Who did something. A user or a machine client, named so an operator knows which.
 */
export type PrincipalReference = {
    /**
     * Display Name
     *
     * Email address for a person, client id for a machine client.
     */
    display_name: string;
    /**
     * Id
     */
    id: string;
    /**
     * Type
     *
     * `vyttech_staff`, `customer_user` or `machine_client`.
     */
    type: string;
};

/**
 * PrincipalResponse
 *
 * Who the server decided the caller is.
 *
 * Returned by the token endpoints and by `GET /auth/me`, so a client never has
 * to decode a token to know its own role or company.
 */
export type PrincipalResponse = {
    /**
     * Authentication Methods
     *
     * How the principal proved who it is, using OIDC `amr` values.
     */
    authentication_methods: Array<string>;
    /**
     * Companies
     *
     * The companies this principal may act for, by name: its one company for a company-scoped user or machine client, and the active companies currently in its group for a group-scoped user (possibly none). Each `id` is a valid `X-Company-Id`. Re-read on every request, so a change of group membership shows here without signing in again. `null` for vyttech staff, who may act for any company.
     */
    companies?: Array<CompanyRef> | null;
    /**
     * The customer company this principal is registered at. `null` for vyttech staff and for a user registered at a company group, who has `group` instead.
     */
    company?: CompanyRef | null;
    /**
     * Display Name
     *
     * Email address for a person, client id for a machine client.
     */
    display_name: string;
    /**
     * Expires At
     */
    expires_at: string;
    /**
     * The company group this user is registered at, or `null` for everyone else. A group-scoped user acts for one company of the group per request, named in `X-Company-Id`.
     */
    group?: GroupRef | null;
    /**
     * Hidden Columns
     *
     * The columns this user has hidden in each table of the web app, keyed by table identifier, each value the keys of the hidden columns -- as last recorded with `PUT /auth/me/table-columns/{table_id}`. A table with nothing hidden has no entry. A display preference only; a client ignores keys that name no column it shows. Empty for machine clients.
     */
    hidden_columns?: {
        [key: string]: Array<string>;
    };
    /**
     * Id
     */
    id: string;
    /**
     * Issued At
     */
    issued_at: string;
    /**
     * Permissions
     *
     * Everything this principal's role allows, so a client can hide what it cannot do rather than discovering it by being refused.
     */
    permissions: Array<Permission>;
    role: Role;
    /**
     * The company the customer portal opens on: the one this user last chose with `PUT /auth/me/selected-company`, while it is still among `companies`, and otherwise `null`. A user registered at one company gets that company. Only a default for a client to start from -- every request is still scoped by `X-Company-Id`. `null` for vyttech staff and machine clients.
     */
    selected_company?: CompanyRef | null;
    /**
     * Token Id
     *
     * Identifier of the presented token; quote it in support.
     */
    token_id: string;
    type: PrincipalType;
};

/**
 * PrincipalType
 *
 * What kind of thing authenticated. Values are part of the API contract.
 */
export const PrincipalType = {
    VYTTECH_STAFF: 'vyttech_staff',
    CUSTOMER_USER: 'customer_user',
    MACHINE_CLIENT: 'machine_client'
} as const;

/**
 * PrincipalType
 *
 * What kind of thing authenticated. Values are part of the API contract.
 */
export type PrincipalType = typeof PrincipalType[keyof typeof PrincipalType];

/**
 * ProbeDirection
 *
 * Which half of one exchange somebody is asking to read back.
 *
 * The values are not decoration: each is the ``half`` its bytes are keyed
 * under by :func:`storage_key_for` and the tail of the context they are sealed
 * with, so "request" names the same thing in the URL, in the bucket and in the
 * envelope. One word, three places, and no mapping table to get wrong.
 *
 * The two are worth telling apart in the trail rather than folding into "read
 * the exchange", because they are not equally sensitive: a ``worker_query``'s
 * *request* carries a CPF in plain and its response carries identifiers, and
 * the entry should say which of those was taken out.
 */
export const ProbeDirection = { REQUEST: 'request', RESPONSE: 'response' } as const;

/**
 * ProbeDirection
 *
 * Which half of one exchange somebody is asking to read back.
 *
 * The values are not decoration: each is the ``half`` its bytes are keyed
 * under by :func:`storage_key_for` and the tail of the context they are sealed
 * with, so "request" names the same thing in the URL, in the bucket and in the
 * envelope. One word, three places, and no mapping table to get wrong.
 *
 * The two are worth telling apart in the trail rather than folding into "read
 * the exchange", because they are not equally sensitive: a ``worker_query``'s
 * *request* carries a CPF in plain and its response carries identifiers, and
 * the entry should say which of those was taken out.
 */
export type ProbeDirection = typeof ProbeDirection[keyof typeof ProbeDirection];

/**
 * ProbeStepKind
 *
 * Which of the five calls one step makes. Values are part of the API contract.
 *
 * They are named for the *question* rather than for the SOAP operation, but
 * each maps to exactly one operation and the result says which -- somebody
 * comparing our evidence against the developer manual needs the manual's word.
 */
export const ProbeStepKind = {
    TABLE_QUERY: 'table_query',
    EMPLOYER_QUERY: 'employer_query',
    WORKER_QUERY: 'worker_query',
    DOWNLOAD_BY_ID: 'download_by_id',
    DOWNLOAD_BY_RECEIPT: 'download_by_receipt'
} as const;

/**
 * ProbeStepKind
 *
 * Which of the five calls one step makes. Values are part of the API contract.
 *
 * They are named for the *question* rather than for the SOAP operation, but
 * each maps to exactly one operation and the result says which -- somebody
 * comparing our evidence against the developer manual needs the manual's word.
 */
export type ProbeStepKind = typeof ProbeStepKind[keyof typeof ProbeStepKind];

/**
 * ProjectedPersonResponse
 *
 * One person as they stood on the reference date, before any layout saw them.
 */
export type ProjectedPersonResponse = {
    /**
     * Birth Date
     */
    birth_date: string;
    /**
     * Cpf
     */
    cpf: string;
    /**
     * Full Name
     */
    full_name: string;
    /**
     * Insured Person Id
     */
    insured_person_id: string;
    /**
     * Salary
     *
     * Exact decimal as a string, or `null` when no salary version is in force on the reference date. Never zero, which is a number somebody could bill.
     */
    salary: string | null;
};

/**
 * ReferenceDateRule
 *
 * Which calendar date a cycle projects the roster at.
 *
 * The rule is applied to the *cycle month* -- the month whose file is being
 * built -- and produces the single date the projection is taken on.
 */
export const ReferenceDateRule = {
    CUTOFF_DATE: 'cutoff_date',
    COVERAGE_MONTH_START: 'coverage_month_start',
    COVERAGE_MONTH_END: 'coverage_month_end'
} as const;

/**
 * ReferenceDateRule
 *
 * Which calendar date a cycle projects the roster at.
 *
 * The rule is applied to the *cycle month* -- the month whose file is being
 * built -- and produces the single date the projection is taken on.
 */
export type ReferenceDateRule = typeof ReferenceDateRule[keyof typeof ReferenceDateRule];

/**
 * ReportState
 *
 * A requested report's state, parsed from the portal's word for it.
 *
 * ``unknown`` is a reading, not a failure: the portal's own word is always
 * recorded beside this, verbatim, and a word this does not know is the
 * evidence that the list here is incomplete.
 */
export const ReportState = {
    PENDING: 'pending',
    PROCESSED: 'processed',
    EMPTY: 'empty',
    DONE: 'done',
    EXCEEDED: 'exceeded',
    UNKNOWN: 'unknown'
} as const;

/**
 * ReportState
 *
 * A requested report's state, parsed from the portal's word for it.
 *
 * ``unknown`` is a reading, not a failure: the portal's own word is always
 * recorded beside this, verbatim, and a word this does not know is the
 * evidence that the list here is incomplete.
 */
export type ReportState = typeof ReportState[keyof typeof ReportState];

/**
 * ReviewCycleResponse
 *
 * One contract's cycle, as the back-office reads it.
 */
export type ReviewCycleResponse = {
    /**
     * The sign-off standing for this cycle, or `null` while it is open.
     */
    approval?: CycleApprovalResponse | null;
    /**
     * Blocking Count
     *
     * How many of them carry a blocking flag. The cycle cannot be approved while this is above zero.
     */
    blocking_count: number;
    /**
     * Can Be Approved
     *
     * True when nothing in the cycle blocks the gate as things stand. Not a permission check, and not a promise: approving still needs a previewed snapshot.
     */
    can_be_approved: boolean;
    /**
     * Company Id
     */
    company_id: string;
    /**
     * Contract Id
     */
    contract_id: string;
    /**
     * Cutoff Date
     *
     * The single calendar date this cycle reads the roster at -- the cutoff being approved. Derived from the contract; never supplied.
     */
    cutoff_date: string;
    /**
     * Cycle Month
     *
     * The month whose cycle this is, written `YYYY-MM`. The cutoff date it resolves to comes from the contract's `reference_date_rule`, and is never sent by a caller.
     */
    cycle_month: string;
    /**
     * Insurer Code
     */
    insurer_code: string;
    /**
     * Insurer Name
     */
    insurer_name: string;
    /**
     * Is Approved
     *
     * True when a sign-off stands for this cycle.
     */
    is_approved: boolean;
    /**
     * Item Count
     */
    item_count: number;
    /**
     * Items
     *
     * Every movement that will be on this cutoff's file, earliest effective date first. Includes movements from a cycle already past that have still not been reported -- the next file is the one they go on.
     */
    items: Array<ReviewItemResponse>;
    /**
     * Late Arrival Count
     *
     * Movements dated inside a month whose file the insurer already has. Never part of this cycle; read them at `/late-arrivals`.
     */
    late_arrival_count: number;
    /**
     * Submitted Through
     *
     * The last day already reported to the insurer, or `null` when nothing has been. What decides which movements are late arrivals.
     */
    submitted_through?: string | null;
    /**
     * Upcoming Count
     *
     * Movements scheduled for a later cycle. Read them at `/upcoming`.
     */
    upcoming_count: number;
};

/**
 * ReviewCycleSummaryResponse
 *
 * One contract's cycle in a line, for the list of contracts to work through.
 */
export type ReviewCycleSummaryResponse = {
    /**
     * Blocking Count
     */
    blocking_count: number;
    /**
     * Can Be Approved
     */
    can_be_approved: boolean;
    /**
     * Company Id
     */
    company_id: string;
    /**
     * Company Name
     */
    company_name: string;
    /**
     * Contract Id
     */
    contract_id: string;
    /**
     * Cutoff Date
     *
     * The single calendar date this cycle reads the roster at -- the cutoff being approved. Derived from the contract; never supplied.
     */
    cutoff_date: string;
    /**
     * Cycle Month
     *
     * The month whose cycle this is, written `YYYY-MM`. The cutoff date it resolves to comes from the contract's `reference_date_rule`, and is never sent by a caller.
     */
    cycle_month: string;
    /**
     * Insurer Code
     */
    insurer_code: string;
    /**
     * Insurer Name
     */
    insurer_name: string;
    /**
     * Is Approved
     */
    is_approved: boolean;
    /**
     * Item Count
     */
    item_count: number;
    /**
     * Late Arrival Count
     */
    late_arrival_count: number;
    /**
     * Modulo
     */
    modulo: number;
    /**
     * Subestipulante
     */
    subestipulante: number;
    /**
     * Upcoming Count
     */
    upcoming_count: number;
};

/**
 * ReviewItemListResponse
 *
 * A listing of movements -- the forward view, or the late arrivals.
 */
export type ReviewItemListResponse = {
    /**
     * Contract Id
     */
    contract_id: string;
    /**
     * Count
     */
    count: number;
    /**
     * Cycle Month
     *
     * The cycle the listing was read relative to.
     */
    cycle_month: string;
    /**
     * Items
     */
    items: Array<ReviewItemResponse>;
};

/**
 * ReviewItemResponse
 *
 * One movement awaiting a decision, with everything the analyst needs beside it.
 */
export type ReviewItemResponse = {
    /**
     * How this movement reached us: from an uploaded spreadsheet, translated from an event the employer filed with eSocial, typed in the portal, posted by a machine client, or filed by vyttech. Derived from what recorded it; there is no channel column on a movement.
     */
    channel: IntakeChannel;
    /**
     * Cycle Month
     *
     * The cycle whose file this movement first appears on -- its own, or a later one when it takes effect after this cutoff.
     */
    cycle_month: string;
    delta: DeltaResponse;
    /**
     * Document Ids
     *
     * The supporting documents standing behind this movement right now, oldest link first. Read one with `GET /documents/{document_id}/content`.
     */
    document_ids: Array<string>;
    /**
     * Flags
     *
     * Everything worth knowing about this movement, worst first.
     */
    flags: Array<ValidationFlagResponse>;
    /**
     * Is Blocked
     *
     * True when at least one flag is `blocking`.
     */
    is_blocked: boolean;
};

/**
 * ReviewQueueResponse
 *
 * The back-office's front page: every contract's cycle, one line each.
 */
export type ReviewQueueResponse = {
    /**
     * Count
     */
    count: number;
    /**
     * Cycle Month
     *
     * The month whose cycle this is, written `YYYY-MM`. The cutoff date it resolves to comes from the contract's `reference_date_rule`, and is never sent by a caller.
     */
    cycle_month: string;
    /**
     * Items
     */
    items: Array<ReviewCycleSummaryResponse>;
    /**
     * Limit
     */
    limit: number;
    /**
     * Offset
     */
    offset: number;
};

/**
 * ReviewRejection
 *
 * Refuse one movement. Nothing is written to the roster.
 */
export type ReviewRejection = {
    /**
     * Reason
     *
     * Written for whoever has to act on it. It reaches the customer, so it says what is wrong rather than that something is.
     */
    reason: string;
};

/**
 * ReviewReturn
 *
 * Hand one movement back to the customer to file again, correctly.
 */
export type ReviewReturn = {
    /**
     * Reason
     *
     * Written for whoever has to act on it. It reaches the customer, so it says what is wrong rather than that something is.
     */
    reason: string;
};

/**
 * Role
 *
 * Every role a principal can hold. Values are part of the API contract.
 */
export const Role = {
    VYTTECH_ADMIN: 'vyttech_admin',
    VYTTECH_ANALYST: 'vyttech_analyst',
    CUSTOMER_ADMIN: 'customer_admin',
    CUSTOMER_USER: 'customer_user',
    MACHINE_READ: 'machine_read',
    MACHINE_WRITE: 'machine_write'
} as const;

/**
 * Role
 *
 * Every role a principal can hold. Values are part of the API contract.
 */
export type Role = typeof Role[keyof typeof Role];

/**
 * RosterContractEntry
 *
 * One contract a person is insured under on the projected date.
 *
 * Every field after the contract's own means what it means on
 * `InsuredOnDateResponse`, for this person on this contract.
 */
export type RosterContractEntry = {
    /**
     * Certificate Number
     *
     * The insurer's certificate number for this person (*Certificado*). Text, so leading zeros are kept. `null` until vyttech records it.
     */
    certificate_number?: string | null;
    /**
     * Contract Id
     */
    contract_id: string;
    /**
     * What insurance the contract is, as on `ContractResponse.kind`: `vida` or `saude`. More kinds may be added in a later minor version.
     */
    contract_kind: ContractKind;
    /**
     * Coverage From
     */
    coverage_from: string;
    /**
     * Coverage Period Id
     */
    coverage_period_id: string;
    /**
     * Coverage To
     */
    coverage_to?: string | null;
    /**
     * Insured Capital
     *
     * The capital the insurer holds this person insured for (*IS Segurado*), to two decimal places and serialised as a string. The insurer's figure, recorded by vyttech -- never derived from the salary here. `null` until recorded.
     */
    insured_capital?: string | null;
    /**
     * Insured Person Id
     *
     * This person's record on this contract -- one per contract, so the same person has a different one under each. Read their history on the contract through `GET /contracts/{contract_id}/insured-people/{insured_person_id}/history`.
     */
    insured_person_id: string;
    /**
     * Modulo
     */
    modulo: number;
    /**
     * Premium
     *
     * The premium the insurer charges for this person (*Prêmio*), to two decimal places and serialised as a string. May be `0.00`, which the insurer uses on an exclusion. `null` until recorded.
     */
    premium?: string | null;
    /**
     * Salary
     *
     * Salary in force on this contract on the projected date, or `null` if no version had taken effect by then. Never zero as a stand-in for missing.
     */
    salary?: string | null;
    /**
     * Subestipulante
     */
    subestipulante: number;
};

/**
 * RosterPerson
 *
 * One person, under every contract in scope that covers them on the date.
 */
export type RosterPerson = {
    /**
     * Birth Date
     *
     * Taken from the first of `contracts`. Two contracts that disagree on it are not reconciled here; each contract's own value is in its history.
     */
    birth_date: string;
    /**
     * Contracts
     *
     * Ordered by contract kind (`vida` before `saude`), then by when the contract was created, then by its id.
     */
    contracts: Array<RosterContractEntry>;
    /**
     * Cpf
     *
     * Canonical eleven digits.
     */
    cpf: string;
    /**
     * Full Name
     *
     * Taken from the first of `contracts`. Two contracts that disagree on it are not reconciled here; each contract's own value is in its history.
     */
    full_name: string;
};

/**
 * RosterProjectionResponse
 *
 * Everyone insured under one contract on one date.
 */
export type RosterProjectionResponse = {
    /**
     * As Of
     *
     * The date projected, as a calendar date in America/Sao_Paulo.
     */
    as_of: string;
    /**
     * Contract Id
     */
    contract_id: string;
    /**
     * Count
     */
    count: number;
    /**
     * Items
     */
    items: Array<InsuredOnDateResponse>;
};

/**
 * RowOutcome
 *
 * What the preview says about one row.
 *
 * The difference between ``IGNORED`` and ``REJECTED`` is the point of the whole
 * design. A customer's sheet lists everybody, and most rows in it are not
 * movements this month -- that is the normal state of the file, not an error,
 * and reporting it as one would bury the rows that really are wrong.
 */
export const RowOutcome = {
    READY: 'ready',
    COMMITTED: 'committed',
    IGNORED: 'ignored',
    REJECTED: 'rejected',
    SECTION_TITLE: 'section_title'
} as const;

/**
 * RowOutcome
 *
 * What the preview says about one row.
 *
 * The difference between ``IGNORED`` and ``REJECTED`` is the point of the whole
 * design. A customer's sheet lists everybody, and most rows in it are not
 * movements this month -- that is the normal state of the file, not an error,
 * and reporting it as one would bury the rows that really are wrong.
 */
export type RowOutcome = typeof RowOutcome[keyof typeof RowOutcome];

/**
 * SalaryChangeSubmission
 *
 * Change what somebody earns, from `effective_date` onwards.
 */
export type SalaryChangeSubmission = {
    /**
     * Action
     */
    action: 'salary_change';
    /**
     * Cpf
     *
     * The person's CPF. Punctuation is ignored, so `123.456.789-09` and `12345678909` are the same number. Both check digits are verified; a mistyped or transposed digit is rejected rather than stored.
     */
    cpf: string;
    /**
     * Effective Date
     *
     * Calendar date the change takes effect, `YYYY-MM-DD`, in America/Sao_Paulo. Mandatory for every action. It may fall in a future month, in the current cycle, or in the past -- a date before the last snapshot sent to the insurer is accepted and flagged as retroactive.
     */
    effective_date: string;
    /**
     * Salary
     *
     * Monthly salary, to at most two decimal places. Send it as a JSON string to be certain no precision is lost in transit; it is stored and returned as an exact decimal and never as a binary float.
     */
    salary: number | string;
};

/**
 * SalaryVersionResponse
 *
 * One salary, in force from its effective date until a later version supersedes it.
 */
export type SalaryVersionResponse = {
    /**
     * Created At
     *
     * When this version was recorded. Breaks the tie when two versions share an effective date: the one recorded later is the correction.
     */
    created_at: string;
    /**
     * Effective From
     */
    effective_from: string;
    /**
     * Id
     */
    id: string;
    /**
     * Salary
     *
     * Amount to two decimal places. Serialised as a string so no precision is lost.
     */
    salary: string;
};

/**
 * SelectedCompanyRequest
 *
 * The company the customer portal should open on for the signed-in user.
 */
export type SelectedCompanyRequest = {
    /**
     * Company Id
     *
     * One of the companies in the principal's `companies`.
     */
    company_id: string;
};

/**
 * SessionOutcome
 *
 * How a session went. Values are part of the API contract.
 */
export const SessionOutcome = {
    COMPLETED: 'completed',
    PARTIAL: 'partial',
    FAILED: 'failed'
} as const;

/**
 * SessionOutcome
 *
 * How a session went. Values are part of the API contract.
 */
export type SessionOutcome = typeof SessionOutcome[keyof typeof SessionOutcome];

/**
 * SheetLayout
 *
 * Which of the two sheets a file turned out to be.
 *
 * Read off the header rather than declared by the caller: a sheet with an
 * effective-date column is the template, and one without is the legacy layout.
 */
export const SheetLayout = { LEGACY: 'legacy', I8LIFE: 'i8life' } as const;

/**
 * SheetLayout
 *
 * Which of the two sheets a file turned out to be.
 *
 * Read off the header rather than declared by the caller: a sheet with an
 * effective-date column is the template, and one without is the legacy layout.
 */
export type SheetLayout = typeof SheetLayout[keyof typeof SheetLayout];

/**
 * SnapshotApproval
 *
 * Freeze a snapshot. After this it is what the insurer gets.
 */
export type SnapshotApproval = {
    /**
     * Acknowledge Exceptions
     *
     * Approve even though rows were held back and the file therefore does not describe the whole population. Required for an incomplete snapshot, so that sending one is a decision somebody made rather than a default.
     */
    acknowledge_exceptions?: boolean;
};

/**
 * SnapshotColumnResponse
 *
 * One column of the layout: its stable key and its header in the file.
 */
export type SnapshotColumnResponse = {
    /**
     * Header
     */
    header: string;
    /**
     * Key
     */
    key: string;
};

/**
 * SnapshotComparisonResponse
 *
 * This cycle against the last approved one for the same contract, or the roster.
 */
export type SnapshotComparisonResponse = {
    /**
     * Added
     */
    added: Array<ComparedPersonResponse>;
    /**
     * Added Count
     */
    added_count: number;
    /**
     * Changed
     */
    changed: Array<ComparedPersonResponse>;
    /**
     * Changed Count
     */
    changed_count: number;
    /**
     * Previous Snapshot Id
     *
     * `null` when no approved snapshot precedes this cycle. The previous side is then the roster as of the previous cycle's reference date -- the one the file's movements were read from -- so a contract onboarded from a baseline reports only who was included, removed or altered.
     */
    previous_snapshot_id: string | null;
    /**
     * Removed
     */
    removed: Array<ComparedPersonResponse>;
    /**
     * Removed Count
     */
    removed_count: number;
    /**
     * Snapshot Id
     */
    snapshot_id: string;
};

/**
 * SnapshotDetailResponse
 *
 * One cycle with the file's rows and the people it could not render.
 */
export type SnapshotDetailResponse = {
    /**
     * Approved At
     */
    approved_at?: string | null;
    /**
     * Approved By Display Name
     */
    approved_by_display_name?: string | null;
    /**
     * Columns
     *
     * The layout's columns, in the order the file writes them.
     */
    columns: Array<SnapshotColumnResponse>;
    /**
     * Company Id
     */
    company_id: string;
    /**
     * Contract Id
     */
    contract_id: string;
    /**
     * Cycle Month
     *
     * The month whose file this is, written `YYYY-MM`. The calendar date the population is read at is derived from it by the contract's `reference_date_rule`, and is never sent by a caller.
     */
    cycle_month: string;
    /**
     * Exception Count
     */
    exception_count: number;
    /**
     * Exceptions
     */
    exceptions: Array<SnapshotExceptionResponse>;
    /**
     * File Byte Count
     */
    file_byte_count: number;
    /**
     * File Media Type
     */
    file_media_type: string;
    /**
     * File Name
     */
    file_name: string;
    /**
     * File Sha256
     *
     * SHA-256 of the rendered bytes.
     */
    file_sha256: string;
    /**
     * Generated At
     */
    generated_at: string;
    /**
     * Generated By Display Name
     */
    generated_by_display_name: string;
    /**
     * Id
     */
    id: string;
    /**
     * Is Complete
     *
     * False when rows were held back; see `exception_count`.
     */
    is_complete: boolean;
    /**
     * Layout Code
     *
     * The insurer file layout it was rendered through.
     */
    layout_code: string;
    /**
     * Reference Date
     *
     * The single calendar date the population was projected at. Everything in the file is true of this day and of no other.
     */
    reference_date: string;
    /**
     * The contract's rule as it stood when this ran, kept here because the contract's setting may change afterwards and this snapshot still has to explain its own date.
     */
    reference_date_rule: ReferenceDateRule;
    /**
     * Row Count
     */
    row_count: number;
    /**
     * Rows
     */
    rows: Array<SnapshotRowResponse>;
    status: SnapshotStatus;
};

/**
 * SnapshotExceptionResponse
 *
 * A person the layout would not write, named with the field and the reason.
 */
export type SnapshotExceptionResponse = {
    /**
     * Code
     *
     * Machine-readable kind of problem.
     */
    code: string;
    /**
     * Cpf
     *
     * The eleven digits.
     */
    cpf: string;
    /**
     * Field
     *
     * The layout column key the problem is in.
     */
    field: string;
    /**
     * Full Name
     *
     * `null` only when the missing name is itself the problem.
     */
    full_name: string | null;
    /**
     * Insured Person Id
     */
    insured_person_id: string | null;
    /**
     * Reason
     *
     * Written for whoever has to fix the data.
     */
    reason: string;
    /**
     * Sequence
     *
     * 1-based position among this snapshot's exceptions.
     */
    sequence: number;
};

/**
 * SnapshotFieldChangeResponse
 *
 * One cell of one row that differs from the previous approved file.
 */
export type SnapshotFieldChangeResponse = {
    /**
     * After
     */
    after: string;
    /**
     * Before
     */
    before: string;
    /**
     * Field
     *
     * The layout column key whose cell moved.
     */
    field: string;
};

/**
 * SnapshotGenerate
 *
 * Build (or rebuild) one cycle's file.
 */
export type SnapshotGenerate = {
    /**
     * Cycle Month
     *
     * The month whose file this is, written `YYYY-MM`. The calendar date the population is read at is derived from it by the contract's `reference_date_rule`, and is never sent by a caller.
     */
    cycle_month: string;
};

/**
 * SnapshotListResponse
 *
 * A page of snapshots, newest cycle first.
 */
export type SnapshotListResponse = {
    /**
     * Count
     */
    count: number;
    /**
     * Items
     */
    items: Array<SnapshotResponse>;
    /**
     * Limit
     */
    limit: number;
    /**
     * Offset
     */
    offset: number;
};

/**
 * SnapshotProjectionResponse
 *
 * The population a snapshot would be built from, and the date it was read at.
 *
 * The projection half of the engine on its own: no layout, no formatting, no
 * file. `subestipulante` and `modulo` appear once, at the top, exactly as they
 * are stored on the contract.
 */
export type SnapshotProjectionResponse = {
    /**
     * Contract Id
     */
    contract_id: string;
    /**
     * Count
     */
    count: number;
    /**
     * Cycle Month
     *
     * The month whose file this is, written `YYYY-MM`. The calendar date the population is read at is derived from it by the contract's `reference_date_rule`, and is never sent by a caller.
     */
    cycle_month: string;
    /**
     * Items
     */
    items: Array<ProjectedPersonResponse>;
    /**
     * Modulo
     *
     * The contract's code, identical on every row.
     */
    modulo: number;
    /**
     * Reference Date
     *
     * The single calendar date the population was projected at. Everything in the file is true of this day and of no other.
     */
    reference_date: string;
    reference_date_rule: ReferenceDateRule;
    /**
     * Subestipulante
     *
     * The contract's code, identical on every row of the resulting file.
     */
    subestipulante: number;
};

/**
 * SnapshotResponse
 *
 * One cycle, without its rows.
 */
export type SnapshotResponse = {
    /**
     * Approved At
     */
    approved_at?: string | null;
    /**
     * Approved By Display Name
     */
    approved_by_display_name?: string | null;
    /**
     * Company Id
     */
    company_id: string;
    /**
     * Contract Id
     */
    contract_id: string;
    /**
     * Cycle Month
     *
     * The month whose file this is, written `YYYY-MM`. The calendar date the population is read at is derived from it by the contract's `reference_date_rule`, and is never sent by a caller.
     */
    cycle_month: string;
    /**
     * Exception Count
     */
    exception_count: number;
    /**
     * File Byte Count
     */
    file_byte_count: number;
    /**
     * File Media Type
     */
    file_media_type: string;
    /**
     * File Name
     */
    file_name: string;
    /**
     * File Sha256
     *
     * SHA-256 of the rendered bytes.
     */
    file_sha256: string;
    /**
     * Generated At
     */
    generated_at: string;
    /**
     * Generated By Display Name
     */
    generated_by_display_name: string;
    /**
     * Id
     */
    id: string;
    /**
     * Is Complete
     *
     * False when rows were held back; see `exception_count`.
     */
    is_complete: boolean;
    /**
     * Layout Code
     *
     * The insurer file layout it was rendered through.
     */
    layout_code: string;
    /**
     * Reference Date
     *
     * The single calendar date the population was projected at. Everything in the file is true of this day and of no other.
     */
    reference_date: string;
    /**
     * The contract's rule as it stood when this ran, kept here because the contract's setting may change afterwards and this snapshot still has to explain its own date.
     */
    reference_date_rule: ReferenceDateRule;
    /**
     * Row Count
     */
    row_count: number;
    status: SnapshotStatus;
};

/**
 * SnapshotRowResponse
 *
 * One line of the file, as the values it was rendered as.
 */
export type SnapshotRowResponse = {
    /**
     * Insured Person Id
     *
     * `null` if that person's record has since been removed.
     */
    insured_person_id: string | null;
    /**
     * What happened to this person since the previous cycle: `inclusion` (covered now, not on the previous cycle), `alteration` (on both, with a different salary in force), `exclusion` (on the previous cycle and no longer covered -- written only by a layout that carries exclusions, with every amount at zero) or `unchanged`. The previous cycle is the last approved snapshot, or the roster on the previous cycle's reference date before any has been approved. Set on every row whatever the layout, whether or not the file has a column for it; `null` only on rows stored before movements were recorded.
     */
    movement: Movement | null;
    /**
     * Row Number
     *
     * 1-based position in the file.
     */
    row_number: number;
    /**
     * Values
     *
     * The rendered cells, keyed by the layout's column keys. Strings, because a cell of the file is a string.
     */
    values: {
        [key: string]: string;
    };
};

/**
 * SnapshotStatus
 *
 * Where a snapshot is in its life. Values are part of the API contract.
 */
export const SnapshotStatus = { DRAFT: 'draft', APPROVED: 'approved' } as const;

/**
 * SnapshotStatus
 *
 * Where a snapshot is in its life. Values are part of the API contract.
 */
export type SnapshotStatus = typeof SnapshotStatus[keyof typeof SnapshotStatus];

/**
 * SnapshotSubmissionCreate
 *
 * Record that a snapshot has been sent to the insurer.
 *
 * The delta layer needs exactly one fact from the snapshot pipeline: the last
 * date the insurer has already been told about, which is what makes a delta
 * retroactive. Rendering and sending snapshots belongs to #212; this endpoint
 * is the seam it will take over, and exists now so the retroactive path is
 * exercisable rather than theoretical.
 */
export type SnapshotSubmissionCreate = {
    /**
     * Covers Through
     *
     * Last calendar day whose state the submitted snapshot described.
     */
    covers_through: string;
    /**
     * Reference
     *
     * The insurer's file name or protocol number, when we have one.
     */
    reference?: string | null;
};

/**
 * SnapshotSubmissionResponse
 *
 * Confirmation that the insurer has been told about everything up to a date.
 */
export type SnapshotSubmissionResponse = {
    /**
     * Contract Id
     */
    contract_id: string;
    /**
     * Covers Through
     */
    covers_through: string;
    /**
     * Id
     */
    id: string;
    /**
     * Reference
     */
    reference?: string | null;
    /**
     * Submitted At
     */
    submitted_at: string;
};

/**
 * SortDirection
 *
 * Which way a list's ``sort`` runs.
 */
export const SortDirection = { ASC: 'asc', DESC: 'desc' } as const;

/**
 * SortDirection
 *
 * Which way a list's ``sort`` runs.
 */
export type SortDirection = typeof SortDirection[keyof typeof SortDirection];

/**
 * SpreadsheetUploadAbsenceResponse
 *
 * Somebody on the roster whom the file does not mention at all.
 */
export type SpreadsheetUploadAbsenceResponse = {
    /**
     * The contract the person is covered under.
     */
    contract: SpreadsheetUploadContract;
    /**
     * Cpf
     *
     * Canonical eleven digits.
     */
    cpf: string;
    /**
     * Full Name
     *
     * As the roster has it on the comparison date.
     */
    full_name: string;
};

/**
 * SpreadsheetUploadCompany
 *
 * Which company of the group a row, an absence or a count belongs to.
 */
export type SpreadsheetUploadCompany = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
};

/**
 * SpreadsheetUploadContract
 *
 * Which contract a row was routed to, by the codes that chose it.
 */
export type SpreadsheetUploadContract = {
    /**
     * Id
     */
    id: string;
    /**
     * Modulo
     *
     * The contract's Módulo. The file selects it; it never sets it.
     */
    modulo: number;
    /**
     * Subestipulante
     *
     * The contract's Subestipulante. The file selects it; it never sets it.
     */
    subestipulante: number;
};

/**
 * SpreadsheetUploadContractCounts
 *
 * One contract's share of a batch: its rows, counted by outcome.
 */
export type SpreadsheetUploadContractCounts = {
    /**
     * Committed Count
     */
    committed_count: number;
    /**
     * The contract these rows were routed to. `null` groups the rows routed to none -- section titles, and rows refused as `contract_mismatch` or `ambiguous_contract` before a contract could be chosen.
     */
    contract: SpreadsheetUploadContract | null;
    /**
     * Ignored Count
     */
    ignored_count: number;
    /**
     * Ready Count
     */
    ready_count: number;
    /**
     * Rejected Count
     */
    rejected_count: number;
    /**
     * Row Count
     */
    row_count: number;
    /**
     * Section Title Count
     */
    section_title_count: number;
};

/**
 * SpreadsheetUploadListResponse
 */
export type SpreadsheetUploadListResponse = {
    /**
     * Count
     */
    count: number;
    /**
     * Items
     */
    items: Array<SpreadsheetUploadResponse>;
    /**
     * Limit
     */
    limit: number;
    /**
     * Offset
     */
    offset: number;
};

/**
 * SpreadsheetUploadPreviewResponse
 *
 * A batch and every one of its rows: the document the customer decides on.
 */
export type SpreadsheetUploadPreviewResponse = {
    /**
     * Absences
     *
     * The people `upload.absent_count` counts, ordered by contract and name: covered on the comparison date -- the upload's `default_effective_date`, or the day it was sent -- under a contract the upload covers, and on no line of the file. Stored with the batch, so this is the same list before and after confirming. Nothing is done about them: they stay insured unless an exclusão is sent. Reading a non-empty list is recorded in the audit trail.
     */
    absences?: Array<SpreadsheetUploadAbsenceResponse>;
    /**
     * Is Duplicate
     *
     * True when this file had already been committed against this contract -- or, for a company-level upload, anywhere in this company -- and the response is the batch that holds those deltas. Nothing was created, and no movement exists twice.
     */
    is_duplicate?: boolean;
    /**
     * Rows
     */
    rows: Array<SpreadsheetUploadRowResponse>;
    upload: SpreadsheetUploadResponse;
};

/**
 * SpreadsheetUploadResponse
 *
 * A batch: the file it came from, and what was made of every line of it.
 */
export type SpreadsheetUploadResponse = {
    /**
     * Absent Count
     *
     * People covered on the comparison date under a contract this upload covers -- its own contract, or every contract of the company for a company-level upload -- whose CPF is on no line of the file. Counted only when the file has at least one row with no action marked, because only such a file lists everybody; zero for a file of movements alone. Not lines of the file, so outside the arithmetic of the counts above. An absence is never a delta: the person stays insured unless an exclusão is sent.
     */
    absent_count?: number;
    /**
     * Committed Count
     *
     * Rows that became deltas.
     */
    committed_count: number;
    /**
     * Company Id
     */
    company_id: string;
    /**
     * Confirmed At
     */
    confirmed_at?: string | null;
    /**
     * Confirmed By Display Name
     */
    confirmed_by_display_name?: string | null;
    /**
     * Contract Counts
     *
     * The counts above, broken down per contract the rows were routed to, ordered by Subestipulante and Módulo; rows routed to none are the last entry, with `contract` `null`. Each entry's counts add up to its `row_count`, and the entries' add up to the batch's.
     */
    contract_counts?: Array<SpreadsheetUploadContractCounts>;
    /**
     * Contract Id
     *
     * The contract a per-contract upload was sent against. `null` for a company-level upload, whose rows each name the contract they were routed to and whose `contract_counts` break the totals down per contract.
     */
    contract_id?: string | null;
    /**
     * Default Effective Date
     *
     * The date applied to rows whose movement carries none of its own. Required in practice for a legacy sheet, which has nowhere to write one.
     */
    default_effective_date?: string | null;
    /**
     * Group Upload Id
     *
     * Set when this batch is one company's part of a sheet sent for the whole economic group: the group upload it belongs to. Such a part holds this company's rows and absences only, has no file of its own to download, and is confirmed with the group upload -- never on its own. `null` for every other upload.
     */
    group_upload_id?: string | null;
    /**
     * Id
     */
    id: string;
    /**
     * Ignored Count
     *
     * Rows that marked no action. Not a problem.
     */
    ignored_count: number;
    /**
     * Which sheet this was, read off its header: `i8life` when it has an effective-date column, `legacy` when it does not.
     */
    layout: SheetLayout;
    /**
     * Ready Count
     *
     * Rows that will become deltas. Zero once the upload is committed.
     */
    ready_count: number;
    /**
     * Rejected Count
     *
     * Rows that were refused, each with a reason.
     */
    rejected_count: number;
    /**
     * Row Count
     *
     * Data rows in the file; the header is not one.
     */
    row_count: number;
    /**
     * Section Title Count
     *
     * Rows that are headings between blocks of people rather than people: text in one cell and nothing else. Neither a movement nor a problem.
     */
    section_title_count: number;
    /**
     * Source Byte Count
     */
    source_byte_count: number;
    /**
     * Source Filename
     */
    source_filename: string;
    /**
     * Source Format
     *
     * `xlsx` or `csv`, sniffed from the content rather than the filename.
     */
    source_format: string;
    /**
     * Source Media Type
     */
    source_media_type: string;
    /**
     * Source Sha256
     *
     * SHA-256 of the uploaded bytes. Two uploads of the same file share it, which is how re-sending a file is recognised instead of duplicated.
     */
    source_sha256: string;
    /**
     * `previewed` means no delta exists for any row yet. `committed` means the ready rows have been submitted as deltas.
     */
    status: UploadStatus;
    /**
     * Uploaded At
     */
    uploaded_at: string;
    /**
     * Uploaded By Display Name
     */
    uploaded_by_display_name: string;
};

/**
 * SpreadsheetUploadRowResponse
 *
 * One line of the sheet: what we read it as, and what we decided about it.
 */
export type SpreadsheetUploadRowResponse = {
    /**
     * The single action the sheet's three mark columns collapsed into. `null` on an ignored row, and on one refused before the action could be read.
     */
    action?: DeltaAction | null;
    /**
     * Birth Date
     */
    birth_date?: string | null;
    /**
     * The contract this row is validated and committed against: the one its `Subestipulante` and `Módulo` selected among the company's. Set on every person row that resolved one, `ignored` rows included; `null` on a `section_title` and on a row whose codes fit no contract (`contract_mismatch`) or several (`ambiguous_contract`). Always set on a `ready` or `committed` row. On a per-contract upload it is that contract.
     */
    contract?: SpreadsheetUploadContract | null;
    /**
     * Cpf
     *
     * Canonical eleven digits.
     */
    cpf?: string | null;
    /**
     * Delta Id
     *
     * The delta this row became, once the upload is confirmed.
     */
    delta_id?: string | null;
    /**
     * Effective Date
     *
     * When the movement takes effect: the row's own date column, a date written into the marked action column, or the date sent with the upload -- whichever is the most specific.
     */
    effective_date?: string | null;
    /**
     * Full Name
     */
    full_name?: string | null;
    /**
     * `ready` will become a delta when the upload is confirmed; `committed` already has; `ignored` marked no action column and is not a problem; `rejected` was refused, and says why; `section_title` is not a person but a heading between blocks of them -- text in one cell and nothing else -- and is never refused and never a delta.
     */
    outcome: RowOutcome;
    /**
     * Rejection Code
     *
     * Stable, machine-readable reason a row was refused: `multiple_actions`, `contract_mismatch`, `ambiguous_contract`, `invalid_cpf`, `missing_effective_date`, `missing_value`, `invalid_value`, `duplicate_row`, `roster_conflict`, `identity_mismatch`, `unmarked_not_on_roster`, `unmarked_salary_differs` or `not_well_formed`. Branch on this, never on the message.
     *
     * The two `unmarked_*` codes are for rows with no action marked, which claim the person is already insured as listed: `unmarked_not_on_roster` when nobody with the row's CPF is covered under the row's contract on the comparison date (an inclusão that was never marked), and `unmarked_salary_differs` when they are but the salary in force then is another one, or there is none (a salary change that was never marked). An unmarked row whose CPF or salary cannot be read is `invalid_cpf`, `missing_value` or `invalid_value`, like a marked one.
     */
    rejection_code?: string | null;
    /**
     * Rejection Context
     *
     * The structured half of the refusal, for the refusals that have one, keyed by the field it is about. Set on `identity_mismatch`: what the Receita Federal has registered for each field this row contradicted -- the name as the registry wrote it, the date of birth as ISO 8601. Set on `unmarked_salary_differs`: `salary`, the salary in force on the comparison date as an exact decimal at two places -- absent when there is none. `null` everywhere else, and on a CPF the registry has never issued, where there is no record to quote. A client composing its own copy reads this and the code; the message is prose and is not for parsing.
     */
    rejection_context?: {
        [key: string]: string;
    } | null;
    /**
     * Rejection Field
     *
     * The column at fault, by its canonical key.
     */
    rejection_field?: string | null;
    /**
     * Rejection Reason
     *
     * Written for whoever has to correct the sheet, and carrying no value from it beyond a masked CPF. That holds for an `identity_mismatch` too: this sentence names the fields that disagreed and nothing else, because it is stored in the clear. What the registry holds is on `rejection_context`, which is encrypted at rest like the row's own name and date of birth.
     */
    rejection_reason?: string | null;
    /**
     * Row Number
     *
     * The line number in the uploaded file, with the header as line 1 -- so it is the number the person sees in their own spreadsheet.
     */
    row_number: number;
    /**
     * Salary
     *
     * An exact decimal, serialised as a string so no precision is lost.
     */
    salary?: string | null;
};

/**
 * StageOutcome
 *
 * What became of one stage.
 *
 * ``skipped`` is a real outcome rather than an absence: "we did not try" and
 * "we tried and it worked" must not look the same on a screen somebody is
 * about to trust a deployment on.
 */
export const StageOutcome = {
    PASSED: 'passed',
    FAILED: 'failed',
    SKIPPED: 'skipped'
} as const;

/**
 * StageOutcome
 *
 * What became of one stage.
 *
 * ``skipped`` is a real outcome rather than an absence: "we did not try" and
 * "we tried and it worked" must not look the same on a screen somebody is
 * about to trust a deployment on.
 */
export type StageOutcome = typeof StageOutcome[keyof typeof StageOutcome];

/**
 * StageStatus
 */
export const StageStatus = {
    COMPLETED: 'completed',
    FAILED: 'failed',
    SKIPPED: 'skipped',
    INHERITED: 'inherited'
} as const;

/**
 * StageStatus
 */
export type StageStatus = typeof StageStatus[keyof typeof StageStatus];

/**
 * StagedEventStatus
 *
 * Where one fetched event got to. Values are part of the API contract.
 *
 * ``staged`` is where a fetch leaves everything; the other three are what
 * confirming the run decided, one per event. See the module docstring.
 */
export const StagedEventStatus = {
    STAGED: 'staged',
    COMMITTED: 'committed',
    NO_ACTION: 'no_action',
    REFUSED: 'refused'
} as const;

/**
 * StagedEventStatus
 *
 * Where one fetched event got to. Values are part of the API contract.
 *
 * ``staged`` is where a fetch leaves everything; the other three are what
 * confirming the run decided, one per event. See the module docstring.
 */
export type StagedEventStatus = typeof StagedEventStatus[keyof typeof StagedEventStatus];

/**
 * SubmissionCadence
 *
 * How often a contract's movement file goes to the insurer.
 *
 * An enum rather than a boolean or a hardcoded assumption because the file
 * builder reads it as configuration: a second cadence is a value added here
 * plus a rule for interpreting the cutoff under it, not a change to the shape
 * of the contract. Monthly is the only one any current contract uses.
 */
export const SubmissionCadence = { MONTHLY: 'monthly' } as const;

/**
 * SubmissionCadence
 *
 * How often a contract's movement file goes to the insurer.
 *
 * An enum rather than a boolean or a hardcoded assumption because the file
 * builder reads it as configuration: a second cadence is a value added here
 * plus a rule for interpreting the cutoff under it, not a change to the shape
 * of the contract. Monthly is the only one any current contract uses.
 */
export type SubmissionCadence = typeof SubmissionCadence[keyof typeof SubmissionCadence];

/**
 * SubmissionCalendarResponse
 *
 * The month across every contract in scope: what has gone out, and what has not.
 */
export type SubmissionCalendarResponse = {
    /**
     * Count
     */
    count: number;
    /**
     * Cycle Month
     *
     * The month being filed, written `YYYY-MM`. The cutoff date it resolves to comes from the contract's `reference_date_rule`, and is never sent by a caller.
     */
    cycle_month: string;
    /**
     * Items
     */
    items: Array<CycleStandingResponse>;
    /**
     * Limit
     */
    limit: number;
    /**
     * Offset
     */
    offset: number;
    /**
     * Outstanding Count
     *
     * How many of them have not been filed. The number the screen exists to drive to zero every month.
     */
    outstanding_count: number;
    /**
     * Overdue Count
     *
     * How many of those are already past their cutoff.
     */
    overdue_count: number;
};

/**
 * SubmissionChannel
 *
 * How the file reached the insurer.
 *
 * Recorded because it is half of the answer to "prove you filed March": the
 * other half is the protocol number or the receipt, and neither is worth much
 * without knowing which door the file went through. The list is what our
 * insurers actually accept today, plus ``other`` for a door we meet next.
 *
 * Distinct from :class:`~app.documents.kinds.DocumentChannel`, which is how a
 * customer's paperwork reached *us*, and from
 * :class:`~app.audit.actions.AuditChannel`, which is how the request that
 * recorded this arrived. All three can differ on one submission: an analyst
 * filing through the insurer's web portal records the fact over the API.
 */
export const SubmissionChannel = {
    EMAIL: 'email',
    INSURER_PORTAL: 'insurer_portal',
    SFTP: 'sftp',
    OTHER: 'other'
} as const;

/**
 * SubmissionChannel
 *
 * How the file reached the insurer.
 *
 * Recorded because it is half of the answer to "prove you filed March": the
 * other half is the protocol number or the receipt, and neither is worth much
 * without knowing which door the file went through. The list is what our
 * insurers actually accept today, plus ``other`` for a door we meet next.
 *
 * Distinct from :class:`~app.documents.kinds.DocumentChannel`, which is how a
 * customer's paperwork reached *us*, and from
 * :class:`~app.audit.actions.AuditChannel`, which is how the request that
 * recorded this arrived. All three can differ on one submission: an analyst
 * filing through the insurer's web portal records the fact over the API.
 */
export type SubmissionChannel = typeof SubmissionChannel[keyof typeof SubmissionChannel];

/**
 * SubmissionCycleResponse
 *
 * Which date a cycle reads the roster at, and why.
 *
 * Exposed on its own so a client can show the cutoff a cycle will use before
 * anything is generated -- and so that "the rule is configuration" is
 * observable rather than only assertable.
 */
export type SubmissionCycleResponse = {
    /**
     * Contract Id
     */
    contract_id: string;
    /**
     * Cutoff Day
     *
     * The contract's cutoff day, as configured.
     */
    cutoff_day: number;
    /**
     * Cycle Month
     *
     * The month whose file this is, written `YYYY-MM`. The calendar date the population is read at is derived from it by the contract's `reference_date_rule`, and is never sent by a caller.
     */
    cycle_month: string;
    /**
     * Layout Code
     *
     * The layout this contract's insurer's files are rendered through.
     */
    layout_code: string;
    /**
     * Reference Date
     *
     * The single calendar date the population was projected at. Everything in the file is true of this day and of no other.
     */
    reference_date: string;
    reference_date_rule: ReferenceDateRule;
    /**
     * Snapshot Id
     *
     * The snapshot already generated for this cycle, if any.
     */
    snapshot_id?: string | null;
    snapshot_status?: SnapshotStatus | null;
};

/**
 * SubmissionFailed
 *
 * Record that an attempt at filing did not land.
 */
export type SubmissionFailed = {
    /**
     * Reason
     *
     * What the insurer said, or what went wrong on the way. Mandatory: whoever prepares the next attempt has only this to go on.
     */
    reason: string;
};

/**
 * SubmissionListResponse
 *
 * Every attempt on one contract, refused ones included, most recent cycle first.
 */
export type SubmissionListResponse = {
    /**
     * Contract Id
     */
    contract_id: string;
    /**
     * Count
     */
    count: number;
    /**
     * Items
     */
    items: Array<SubmissionResponse>;
};

/**
 * SubmissionPrepare
 *
 * Package an approved cycle's file for filing.
 */
export type SubmissionPrepare = {
    /**
     * Cycle Month
     *
     * The month being filed, written `YYYY-MM`. The cutoff date it resolves to comes from the contract's `reference_date_rule`, and is never sent by a caller.
     */
    cycle_month: string;
    /**
     * Snapshot Sha256
     *
     * The file you are about to take out, as the approval reported it. Sent so that filing something other than what was signed off is a `409` rather than a surprise in a dispute. Omit it to package whatever the cycle's approval pinned, which is what a client that has just read the approval is doing anyway.
     */
    snapshot_sha256?: string | null;
};

/**
 * SubmissionResponse
 *
 * One attempt at filing one cycle, and what came back.
 */
export type SubmissionResponse = {
    /**
     * Which door the file went through. Set when it is marked sent.
     */
    channel?: SubmissionChannel | null;
    /**
     * Company Id
     */
    company_id: string;
    /**
     * Consolidated Submission Id
     *
     * The company-wide file this attempt went out inside, when the insurer's layout files one file per company and month. `null` for a contract filed on its own. Such an attempt is marked sent or failed only through that file, at `/submissions/consolidated/{id}`.
     */
    consolidated_submission_id?: string | null;
    /**
     * Contract Id
     */
    contract_id: string;
    /**
     * Covers Through
     *
     * The last calendar day whose state this file describes, inclusive. Once the submission is `sent` this is what makes a later movement dated on or before it a late arrival rather than part of the next file.
     */
    covers_through: string;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Cycle Approval Id
     *
     * The sign-off this was packaged out of.
     */
    cycle_approval_id?: string | null;
    /**
     * Cycle Month
     *
     * The month being filed, written `YYYY-MM`. The cutoff date it resolves to comes from the contract's `reference_date_rule`, and is never sent by a caller. `null` on a manual watermark.
     */
    cycle_month?: string | null;
    /**
     * Failed At
     */
    failed_at?: string | null;
    /**
     * Who recorded the refusal. `null` unless it failed.
     */
    failed_by?: PrincipalReference | null;
    /**
     * Failure Reason
     */
    failure_reason?: string | null;
    /**
     * Group Submission Id
     *
     * The group-wide file this attempt went out inside, when the contract's company belongs to an economic group and the insurer's layout files one file per customer and month. `null` otherwise. Such an attempt is marked sent or failed only through that file, at `/submissions/group-files/{id}`.
     */
    group_submission_id?: string | null;
    /**
     * Has Proof
     *
     * True when at least one of `reference`, `proof_document_id` and `notes` is present. Always true on a `sent` attempt this pipeline produced -- the marker will not be recorded without one -- and readable so that a screen can say which months are evidenced without re-deriving the rule.
     */
    has_proof: boolean;
    /**
     * Id
     */
    id: string;
    /**
     * Notes
     */
    notes?: string | null;
    /**
     * Prepared At
     */
    prepared_at?: string | null;
    /**
     * Who packaged the file. `null` on a manual watermark.
     */
    prepared_by?: PrincipalReference | null;
    /**
     * Proof Document Id
     *
     * The receipt, filed in this company's document store. Read it at `GET /documents/{document_id}/content`.
     */
    proof_document_id?: string | null;
    /**
     * Reference
     */
    reference?: string | null;
    /**
     * Who filed it. `null` until it is marked sent.
     */
    sent_by?: PrincipalReference | null;
    /**
     * Snapshot Id
     *
     * The frozen snapshot whose bytes are being filed. **The file itself is not served here** -- read it at `GET /snapshots/{snapshot_id}/file`, which is the one authenticated path to it, with its own tenancy check.
     */
    snapshot_id?: string | null;
    /**
     * Snapshot Sha256
     *
     * SHA-256 of those bytes. What makes "the insurer has exactly this file" checkable without reading the file.
     */
    snapshot_sha256?: string | null;
    /**
     * `cycle` for an attempt this pipeline produced, which can show the approval and the frozen file behind it. `manual` for a watermark asserted by hand through `POST /contracts/{id}/snapshot-submissions`, which has no file behind it and claims none -- what a month filed before this system existed looks like.
     */
    source: SubmissionSource;
    /**
     * `prepared` once the file is packaged and out of the building, `sent` once it has been filed and evidenced, `failed` when the insurer refused it. Only `sent` moves the watermark.
     */
    status: SubmissionStatus;
    /**
     * Submitted At
     *
     * When the file reached the insurer. `null` until it is marked sent.
     */
    submitted_at?: string | null;
};

/**
 * SubmissionSent
 *
 * Mark a packaged file as filed, with whatever proof of submission came back.
 *
 * **At least one of `reference`, `proof_document_id` and `notes` is required.**
 * Which of the three is a matter of what the insurer gave back: a protocol
 * number, a receipt worth keeping as a file, or -- when nothing came back at
 * all -- a note saying so and saying what was done. A `422` names the rule when
 * none of the three is sent.
 */
export type SubmissionSent = {
    /**
     * Which door the file went through. Half of any answer to "prove you filed March"; the other half is the proof below.
     */
    channel: SubmissionChannel;
    /**
     * Notes
     *
     * What whoever filed it wants the next person to know. This is the form of proof that carries the honest case -- "filed at 14:02, the portal returned no protocol" is evidence; an empty record is not.
     */
    notes?: string | null;
    /**
     * Proof Document Id
     *
     * A receipt already uploaded to this company's document store, filed as the evidence behind this submission. Upload it at `POST /documents` first; the bytes are never sent here.
     */
    proof_document_id?: string | null;
    /**
     * Reference
     *
     * The insurer's protocol number, receipt identifier or file name -- whatever came back. Free text, because what comes back differs per insurer and per door.
     */
    reference?: string | null;
    /**
     * Sent At
     *
     * When the file really reached the insurer. Defaults to now, and is accepted because a month is usually recorded the morning after it went out. A moment in the future is refused.
     *
     * Send an offset. **A value without one is read as São Paulo wall-clock time**, which is what the person reading the clock in front of them meant -- not as UTC, which would record a moment three hours off and push anything recorded near the time it was filed into the future.
     */
    sent_at?: string | null;
};

/**
 * SubmissionSource
 *
 * What produced this record, which decides what it is able to evidence.
 *
 * The discriminator between the two kinds of row in one table, and the reason
 * a handful of its columns are nullable rather than the reason to split it.
 */
export const SubmissionSource = { CYCLE: 'cycle', MANUAL: 'manual' } as const;

/**
 * SubmissionSource
 *
 * What produced this record, which decides what it is able to evidence.
 *
 * The discriminator between the two kinds of row in one table, and the reason
 * a handful of its columns are nullable rather than the reason to split it.
 */
export type SubmissionSource = typeof SubmissionSource[keyof typeof SubmissionSource];

/**
 * SubmissionStatus
 *
 * Where one attempt at filing one cycle stands. Values are part of the API contract.
 */
export const SubmissionStatus = {
    PREPARED: 'prepared',
    SENT: 'sent',
    FAILED: 'failed'
} as const;

/**
 * SubmissionStatus
 *
 * Where one attempt at filing one cycle stands. Values are part of the API contract.
 */
export type SubmissionStatus = typeof SubmissionStatus[keyof typeof SubmissionStatus];

/**
 * TableColumnsRequest
 *
 * The columns the signed-in user has hidden in one table.
 */
export type TableColumnsRequest = {
    /**
     * Hidden
     *
     * The keys of the columns to hide, replacing whatever was stored for this table. An empty list clears the table's entry, showing every column.
     */
    hidden: Array<string>;
};

/**
 * TerminationDocumentPolicy
 *
 * How a termination with no supporting document is treated in review.
 */
export const TerminationDocumentPolicy = {
    BLOCK: 'block',
    WARN: 'warn',
    OFF: 'off'
} as const;

/**
 * TerminationDocumentPolicy
 *
 * How a termination with no supporting document is treated in review.
 */
export type TerminationDocumentPolicy = typeof TerminationDocumentPolicy[keyof typeof TerminationDocumentPolicy];

/**
 * TerminationDocumentPolicyUpdate
 *
 * Change what the review does about one company's undocumented terminations.
 */
export type TerminationDocumentPolicyUpdate = {
    /**
     * What the review queue does about a termination with no supporting document linked to it. `block`: the `missing_evidence` flag is `blocking`, the movement cannot be approved (`409`) and neither can its cycle until a document is linked. `warn`: the flag is a `warning`, for an analyst to look at and then decide -- what every company had before the setting existed. `off`: no `missing_evidence` flag is raised at all, for a customer that never sends termination paperwork. A documented termination carries no flag under any of them.
     */
    termination_document_policy: TerminationDocumentPolicy;
};

/**
 * TerminationSubmission
 *
 * Take somebody off the contract. `effective_date` is their **last covered day**.
 *
 * Inclusive, like every other date in the roster: a termination effective on
 * the 30th means the person is insured on the 30th and not on the 31st.
 */
export type TerminationSubmission = {
    /**
     * Action
     */
    action: 'termination';
    /**
     * Cpf
     *
     * The person's CPF. Punctuation is ignored, so `123.456.789-09` and `12345678909` are the same number. Both check digits are verified; a mistyped or transposed digit is rejected rather than stored.
     */
    cpf: string;
    /**
     * Effective Date
     *
     * Calendar date the change takes effect, `YYYY-MM-DD`, in America/Sao_Paulo. Mandatory for every action. It may fall in a future month, in the current cycle, or in the past -- a date before the last snapshot sent to the insurer is accepted and flagged as retroactive.
     */
    effective_date: string;
};

/**
 * TokenResponse
 *
 * A bearer token, in the shape OAuth2 clients already understand.
 */
export type TokenResponse = {
    /**
     * Access Token
     *
     * Present as `Authorization: Bearer <access_token>`.
     */
    access_token: string;
    /**
     * Expires At
     */
    expires_at: string;
    /**
     * Expires In
     *
     * Seconds until the token expires.
     */
    expires_in: number;
    principal: PrincipalResponse;
    /**
     * Token Type
     */
    token_type: string;
};

/**
 * UploadStatus
 *
 * Where a batch is between its two steps.
 */
export const UploadStatus = { PREVIEWED: 'previewed', COMMITTED: 'committed' } as const;

/**
 * UploadStatus
 *
 * Where a batch is between its two steps.
 */
export type UploadStatus = typeof UploadStatus[keyof typeof UploadStatus];

/**
 * UserCreate
 *
 * Create a person who signs in.
 */
export type UserCreate = {
    /**
     * Email
     */
    email: string;
    /**
     * Full Name
     */
    full_name: string;
    /**
     * Group Id
     *
     * Register a customer-role user at the scope of this company group (*grupo econômico*) instead of one company: they may then act for any company currently in the group, naming it in `X-Company-Id` on each request. vyttech staff only. Refused with `422` together with `X-Company-Id`, with a vyttech role, or for a group that does not exist. A user's scope is set here, at creation, and cannot be moved between a company and a group afterwards.
     */
    group_id?: string | null;
    /**
     * Password
     *
     * At least 12 characters. Stored only as an Argon2id hash; it cannot be read back.
     */
    password: string;
    /**
     * A vyttech role creates staff, who belong to no company. A customer role creates a user of the company the request is scoped to, or -- with `group_id` -- a user of a whole company group. Machine roles are not valid here.
     */
    role: Role;
};

/**
 * UserListResponse
 *
 * Every user within the request's company scope.
 */
export type UserListResponse = {
    /**
     * Count
     */
    count: number;
    /**
     * Items
     */
    items: Array<UserResponse>;
};

/**
 * UserLoginRequest
 *
 * An email address and a password. Nothing else signs a person in.
 */
export type UserLoginRequest = {
    /**
     * Email
     */
    email: string;
    /**
     * Password
     */
    password: string;
};

/**
 * UserResponse
 *
 * One person who signs in. Never carries a password or a hash of one.
 */
export type UserResponse = {
    /**
     * Company Id
     *
     * The company this user is registered at. `null` for vyttech staff, who belong to no company, and for a user registered at a company group.
     */
    company_id: string | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Email
     */
    email: string;
    /**
     * Full Name
     */
    full_name: string;
    /**
     * Group Id
     *
     * The company group this user is registered at, or `null` for vyttech staff and for a user registered at one company. Exactly one of `company_id` and `group_id` is set on a customer-role user.
     */
    group_id: string | null;
    /**
     * Id
     */
    id: string;
    /**
     * Is Active
     */
    is_active: boolean;
    /**
     * Last Login At
     */
    last_login_at: string | null;
    role: Role;
};

/**
 * ValidationFlagResponse
 *
 * One thing an analyst should know about one movement.
 */
export type ValidationFlagResponse = {
    /**
     * Machine-readable kind of problem, so a client can group it.
     */
    code: FlagCode;
    /**
     * Field
     *
     * The field at fault, when the problem is one field.
     */
    field?: string | null;
    /**
     * Message
     *
     * Written for whoever has to act on it, and carrying nothing from the row.
     */
    message: string;
    /**
     * `blocking` stops the cycle being approved at all; `warning` is for an analyst to look at and then decide.
     */
    severity: FlagSeverity;
};

/**
 * WalkMode
 *
 * What a run is for. A ``collect`` run never requests; a ``request`` run never downloads.
 */
export const WalkMode = { REQUEST: 'request', COLLECT: 'collect' } as const;

/**
 * WalkMode
 *
 * What a run is for. A ``collect`` run never requests; a ``request`` run never downloads.
 */
export type WalkMode = typeof WalkMode[keyof typeof WalkMode];

/**
 * WalkStage
 *
 * The stages, in the order a walk can run them. Values are part of the API contract.
 */
export const WalkStage = {
    LOGIN: 'login',
    PROFILE: 'profile',
    MODULE: 'module',
    REQUEST: 'request',
    LIST: 'list',
    DOWNLOAD: 'download'
} as const;

/**
 * WalkStage
 *
 * The stages, in the order a walk can run them. Values are part of the API contract.
 */
export type WalkStage = typeof WalkStage[keyof typeof WalkStage];

export type ListAuditEntriesApiV1AuditEntriesGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Entity Type
         *
         * Only entries about this kind of record, e.g. `insured_person`.
         */
        entity_type?: string | null;
        /**
         * Entity Id
         *
         * Only entries about this one record. Usually its UUID.
         */
        entity_id?: string | null;
        /**
         * Action
         *
         * Only entries of this action.
         */
        action?: AuditAction | null;
        /**
         * Occurred From
         *
         * Earliest timestamp to include, inclusive. UTC.
         */
        occurred_from?: string | null;
        /**
         * Occurred To
         *
         * Timestamp to stop before, exclusive. UTC.
         */
        occurred_to?: string | null;
        /**
         * Correlation Id
         *
         * Everything one request or one scheduled run did.
         */
        correlation_id?: string | null;
        /**
         * Actor Id
         *
         * Only entries attributed to this principal.
         */
        actor_id?: string | null;
        /**
         * Limit
         *
         * Page size. Each entry decrypts a value, so the ceiling is low.
         */
        limit?: number;
        /**
         * Offset
         *
         * How many entries to skip.
         */
        offset?: number;
    };
    url: '/api/v1/audit-entries';
};

export type ListAuditEntriesApiV1AuditEntriesGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ListAuditEntriesApiV1AuditEntriesGetError = ListAuditEntriesApiV1AuditEntriesGetErrors[keyof ListAuditEntriesApiV1AuditEntriesGetErrors];

export type ListAuditEntriesApiV1AuditEntriesGetResponses = {
    /**
     * Successful Response
     */
    200: AuditEntryListResponse;
};

export type ListAuditEntriesApiV1AuditEntriesGetResponse = ListAuditEntriesApiV1AuditEntriesGetResponses[keyof ListAuditEntriesApiV1AuditEntriesGetResponses];

export type SignOutApiV1AuthLogoutPostData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/auth/logout';
};

export type SignOutApiV1AuthLogoutPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * The credentials were not accepted. The response is identical whichever part of them was wrong, so this endpoint cannot be used to discover which identifiers exist.
     */
    401: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type SignOutApiV1AuthLogoutPostError = SignOutApiV1AuthLogoutPostErrors[keyof SignOutApiV1AuthLogoutPostErrors];

export type SignOutApiV1AuthLogoutPostResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type SignOutApiV1AuthLogoutPostResponse = SignOutApiV1AuthLogoutPostResponses[keyof SignOutApiV1AuthLogoutPostResponses];

export type MachineTokenApiV1AuthMachineTokenPostData = {
    body: MachineTokenRequest;
    path?: never;
    query?: never;
    url: '/api/v1/auth/machine/token';
};

export type MachineTokenApiV1AuthMachineTokenPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * The credentials were not accepted. The response is identical whichever part of them was wrong, so this endpoint cannot be used to discover which identifiers exist.
     */
    401: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type MachineTokenApiV1AuthMachineTokenPostError = MachineTokenApiV1AuthMachineTokenPostErrors[keyof MachineTokenApiV1AuthMachineTokenPostErrors];

export type MachineTokenApiV1AuthMachineTokenPostResponses = {
    /**
     * Successful Response
     */
    200: TokenResponse;
};

export type MachineTokenApiV1AuthMachineTokenPostResponse = MachineTokenApiV1AuthMachineTokenPostResponses[keyof MachineTokenApiV1AuthMachineTokenPostResponses];

export type ReadMeApiV1AuthMeGetData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/auth/me';
};

export type ReadMeApiV1AuthMeGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ReadMeApiV1AuthMeGetError = ReadMeApiV1AuthMeGetErrors[keyof ReadMeApiV1AuthMeGetErrors];

export type ReadMeApiV1AuthMeGetResponses = {
    /**
     * Successful Response
     */
    200: PrincipalResponse;
};

export type ReadMeApiV1AuthMeGetResponse = ReadMeApiV1AuthMeGetResponses[keyof ReadMeApiV1AuthMeGetResponses];

export type SelectCompanyApiV1AuthMeSelectedCompanyPutData = {
    body: SelectedCompanyRequest;
    path?: never;
    query?: never;
    url: '/api/v1/auth/me/selected-company';
};

export type SelectCompanyApiV1AuthMeSelectedCompanyPutErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type SelectCompanyApiV1AuthMeSelectedCompanyPutError = SelectCompanyApiV1AuthMeSelectedCompanyPutErrors[keyof SelectCompanyApiV1AuthMeSelectedCompanyPutErrors];

export type SelectCompanyApiV1AuthMeSelectedCompanyPutResponses = {
    /**
     * Successful Response
     */
    200: PrincipalResponse;
};

export type SelectCompanyApiV1AuthMeSelectedCompanyPutResponse = SelectCompanyApiV1AuthMeSelectedCompanyPutResponses[keyof SelectCompanyApiV1AuthMeSelectedCompanyPutResponses];

export type RememberTableColumnsApiV1AuthMeTableColumnsTableIdPutData = {
    body: TableColumnsRequest;
    path: {
        /**
         * Table Id
         *
         * The table, as a short lowercase slug.
         */
        table_id: string;
    };
    query?: never;
    url: '/api/v1/auth/me/table-columns/{table_id}';
};

export type RememberTableColumnsApiV1AuthMeTableColumnsTableIdPutErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type RememberTableColumnsApiV1AuthMeTableColumnsTableIdPutError = RememberTableColumnsApiV1AuthMeTableColumnsTableIdPutErrors[keyof RememberTableColumnsApiV1AuthMeTableColumnsTableIdPutErrors];

export type RememberTableColumnsApiV1AuthMeTableColumnsTableIdPutResponses = {
    /**
     * Successful Response
     */
    200: PrincipalResponse;
};

export type RememberTableColumnsApiV1AuthMeTableColumnsTableIdPutResponse = RememberTableColumnsApiV1AuthMeTableColumnsTableIdPutResponses[keyof RememberTableColumnsApiV1AuthMeTableColumnsTableIdPutResponses];

export type RefreshSessionApiV1AuthRefreshPostData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/auth/refresh';
};

export type RefreshSessionApiV1AuthRefreshPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * The credentials were not accepted. The response is identical whichever part of them was wrong, so this endpoint cannot be used to discover which identifiers exist.
     */
    401: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type RefreshSessionApiV1AuthRefreshPostError = RefreshSessionApiV1AuthRefreshPostErrors[keyof RefreshSessionApiV1AuthRefreshPostErrors];

export type RefreshSessionApiV1AuthRefreshPostResponses = {
    /**
     * Successful Response
     */
    200: TokenResponse;
};

export type RefreshSessionApiV1AuthRefreshPostResponse = RefreshSessionApiV1AuthRefreshPostResponses[keyof RefreshSessionApiV1AuthRefreshPostResponses];

export type SignInApiV1AuthTokenPostData = {
    body: UserLoginRequest;
    path?: never;
    query?: never;
    url: '/api/v1/auth/token';
};

export type SignInApiV1AuthTokenPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * The credentials were not accepted. The response is identical whichever part of them was wrong, so this endpoint cannot be used to discover which identifiers exist.
     */
    401: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type SignInApiV1AuthTokenPostError = SignInApiV1AuthTokenPostErrors[keyof SignInApiV1AuthTokenPostErrors];

export type SignInApiV1AuthTokenPostResponses = {
    /**
     * Successful Response
     */
    200: TokenResponse;
};

export type SignInApiV1AuthTokenPostResponse = SignInApiV1AuthTokenPostResponses[keyof SignInApiV1AuthTokenPostResponses];

export type ListCertificatesApiV1CertificatesGetData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/certificates';
};

export type ListCertificatesApiV1CertificatesGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ListCertificatesApiV1CertificatesGetError = ListCertificatesApiV1CertificatesGetErrors[keyof ListCertificatesApiV1CertificatesGetErrors];

export type ListCertificatesApiV1CertificatesGetResponses = {
    /**
     * Successful Response
     */
    200: A1CertificateListResponse;
};

export type ListCertificatesApiV1CertificatesGetResponse = ListCertificatesApiV1CertificatesGetResponses[keyof ListCertificatesApiV1CertificatesGetResponses];

export type UploadCertificateApiV1CertificatesPostData = {
    body: BodyUploadCertificateApiV1CertificatesPost;
    path?: never;
    query?: never;
    url: '/api/v1/certificates';
};

export type UploadCertificateApiV1CertificatesPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The file is above the size ceiling.
     */
    413: ErrorEnvelope;
    /**
     * The file is not a PKCS#12 archive.
     */
    415: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type UploadCertificateApiV1CertificatesPostError = UploadCertificateApiV1CertificatesPostErrors[keyof UploadCertificateApiV1CertificatesPostErrors];

export type UploadCertificateApiV1CertificatesPostResponses = {
    /**
     * Successful Response
     */
    201: A1CertificateResponse;
};

export type UploadCertificateApiV1CertificatesPostResponse = UploadCertificateApiV1CertificatesPostResponses[keyof UploadCertificateApiV1CertificatesPostResponses];

export type GetCertificateApiV1CertificatesCertificateIdGetData = {
    body?: never;
    path: {
        /**
         * Certificate Id
         */
        certificate_id: string;
    };
    query?: never;
    url: '/api/v1/certificates/{certificate_id}';
};

export type GetCertificateApiV1CertificatesCertificateIdGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type GetCertificateApiV1CertificatesCertificateIdGetError = GetCertificateApiV1CertificatesCertificateIdGetErrors[keyof GetCertificateApiV1CertificatesCertificateIdGetErrors];

export type GetCertificateApiV1CertificatesCertificateIdGetResponses = {
    /**
     * Successful Response
     */
    200: A1CertificateResponse;
};

export type GetCertificateApiV1CertificatesCertificateIdGetResponse = GetCertificateApiV1CertificatesCertificateIdGetResponses[keyof GetCertificateApiV1CertificatesCertificateIdGetResponses];

export type DeactivateCertificateApiV1CertificatesCertificateIdDeactivationPostData = {
    body?: never;
    path: {
        /**
         * Certificate Id
         */
        certificate_id: string;
    };
    query?: never;
    url: '/api/v1/certificates/{certificate_id}/deactivation';
};

export type DeactivateCertificateApiV1CertificatesCertificateIdDeactivationPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type DeactivateCertificateApiV1CertificatesCertificateIdDeactivationPostError = DeactivateCertificateApiV1CertificatesCertificateIdDeactivationPostErrors[keyof DeactivateCertificateApiV1CertificatesCertificateIdDeactivationPostErrors];

export type DeactivateCertificateApiV1CertificatesCertificateIdDeactivationPostResponses = {
    /**
     * Successful Response
     */
    200: A1CertificateResponse;
};

export type DeactivateCertificateApiV1CertificatesCertificateIdDeactivationPostResponse = DeactivateCertificateApiV1CertificatesCertificateIdDeactivationPostResponses[keyof DeactivateCertificateApiV1CertificatesCertificateIdDeactivationPostResponses];

export type ClearClockApiV1ClockDeleteData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/clock';
};

export type ClearClockApiV1ClockDeleteErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ClearClockApiV1ClockDeleteError = ClearClockApiV1ClockDeleteErrors[keyof ClearClockApiV1ClockDeleteErrors];

export type ClearClockApiV1ClockDeleteResponses = {
    /**
     * Successful Response
     */
    200: ClockStateResponse;
};

export type ClearClockApiV1ClockDeleteResponse = ClearClockApiV1ClockDeleteResponses[keyof ClearClockApiV1ClockDeleteResponses];

export type ReadClockApiV1ClockGetData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/clock';
};

export type ReadClockApiV1ClockGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ReadClockApiV1ClockGetError = ReadClockApiV1ClockGetErrors[keyof ReadClockApiV1ClockGetErrors];

export type ReadClockApiV1ClockGetResponses = {
    /**
     * Successful Response
     */
    200: ClockStateResponse;
};

export type ReadClockApiV1ClockGetResponse = ReadClockApiV1ClockGetResponses[keyof ReadClockApiV1ClockGetResponses];

export type SetClockApiV1ClockPutData = {
    body: ClockSimulationRequest;
    path?: never;
    query?: never;
    url: '/api/v1/clock';
};

export type SetClockApiV1ClockPutErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type SetClockApiV1ClockPutError = SetClockApiV1ClockPutErrors[keyof SetClockApiV1ClockPutErrors];

export type SetClockApiV1ClockPutResponses = {
    /**
     * Successful Response
     */
    200: ClockStateResponse;
};

export type SetClockApiV1ClockPutResponse = SetClockApiV1ClockPutResponses[keyof SetClockApiV1ClockPutResponses];

export type ListCompaniesApiV1CompaniesGetData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Q
         *
         * Text to find in the name, the slug or the CNPJ.
         */
        q?: string | null;
        /**
         * Name
         *
         * Text the name contains.
         */
        name?: string | null;
        /**
         * Slug
         *
         * Text the slug contains.
         */
        slug?: string | null;
        /**
         * Cnpj
         *
         * Digits the CNPJ contains; punctuation is ignored.
         */
        cnpj?: string | null;
        /**
         * Group
         *
         * Text the name of the company's economic group contains.
         */
        group?: string | null;
        /**
         * Without Group
         *
         * `true` keeps only the companies that belong to no economic group.
         */
        without_group?: boolean;
        /**
         * The column to sort by: `name`, `slug`, `cnpj`, `group` or `created_at`.
         */
        sort?: CompanyListSort;
        /**
         * `asc` or `desc`. Companies with no CNPJ or no group stay last.
         */
        direction?: SortDirection;
        /**
         * Limit
         *
         * At most this many companies. Absent: every one, unpaged.
         */
        limit?: number | null;
        /**
         * Offset
         *
         * How many matching companies to skip. Read only with `limit`.
         */
        offset?: number;
        /**
         * Created From
         *
         * Only those created on or after this São Paulo calendar day.
         */
        created_from?: string | null;
        /**
         * Created To
         *
         * Only those created on or before this São Paulo calendar day.
         */
        created_to?: string | null;
    };
    url: '/api/v1/companies';
};

export type ListCompaniesApiV1CompaniesGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ListCompaniesApiV1CompaniesGetError = ListCompaniesApiV1CompaniesGetErrors[keyof ListCompaniesApiV1CompaniesGetErrors];

export type ListCompaniesApiV1CompaniesGetResponses = {
    /**
     * Successful Response
     */
    200: CompanyListResponse;
};

export type ListCompaniesApiV1CompaniesGetResponse = ListCompaniesApiV1CompaniesGetResponses[keyof ListCompaniesApiV1CompaniesGetResponses];

export type CreateCompanyApiV1CompaniesPostData = {
    body: CompanyCreate;
    path?: never;
    query?: never;
    url: '/api/v1/companies';
};

export type CreateCompanyApiV1CompaniesPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type CreateCompanyApiV1CompaniesPostError = CreateCompanyApiV1CompaniesPostErrors[keyof CreateCompanyApiV1CompaniesPostErrors];

export type CreateCompanyApiV1CompaniesPostResponses = {
    /**
     * Successful Response
     */
    201: CompanyResponse;
};

export type CreateCompanyApiV1CompaniesPostResponse = CreateCompanyApiV1CompaniesPostResponses[keyof CreateCompanyApiV1CompaniesPostResponses];

export type GetCompanyApiV1CompaniesCompanyIdGetData = {
    body?: never;
    path: {
        /**
         * Company Id
         */
        company_id: string;
    };
    query?: never;
    url: '/api/v1/companies/{company_id}';
};

export type GetCompanyApiV1CompaniesCompanyIdGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type GetCompanyApiV1CompaniesCompanyIdGetError = GetCompanyApiV1CompaniesCompanyIdGetErrors[keyof GetCompanyApiV1CompaniesCompanyIdGetErrors];

export type GetCompanyApiV1CompaniesCompanyIdGetResponses = {
    /**
     * Successful Response
     */
    200: CompanyResponse;
};

export type GetCompanyApiV1CompaniesCompanyIdGetResponse = GetCompanyApiV1CompaniesCompanyIdGetResponses[keyof GetCompanyApiV1CompaniesCompanyIdGetResponses];

export type ImportCompanyBaselineApiV1CompaniesCompanyIdBaselineImportsPostData = {
    body: BodyImportCompanyBaselineApiV1CompaniesCompanyIdBaselineImportsPost;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Company Id
         */
        company_id: string;
    };
    query?: never;
    url: '/api/v1/companies/{company_id}/baseline-imports';
};

export type ImportCompanyBaselineApiV1CompaniesCompanyIdBaselineImportsPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ImportCompanyBaselineApiV1CompaniesCompanyIdBaselineImportsPostError = ImportCompanyBaselineApiV1CompaniesCompanyIdBaselineImportsPostErrors[keyof ImportCompanyBaselineApiV1CompaniesCompanyIdBaselineImportsPostErrors];

export type ImportCompanyBaselineApiV1CompaniesCompanyIdBaselineImportsPostResponses = {
    /**
     * Successful Response
     */
    201: CompanyBaselineImportResponse;
};

export type ImportCompanyBaselineApiV1CompaniesCompanyIdBaselineImportsPostResponse = ImportCompanyBaselineApiV1CompaniesCompanyIdBaselineImportsPostResponses[keyof ImportCompanyBaselineApiV1CompaniesCompanyIdBaselineImportsPostResponses];

export type SetCompanyCnpjApiV1CompaniesCompanyIdCnpjPostData = {
    body: CompanyCnpjUpdate;
    path: {
        /**
         * Company Id
         */
        company_id: string;
    };
    query?: never;
    url: '/api/v1/companies/{company_id}/cnpj';
};

export type SetCompanyCnpjApiV1CompaniesCompanyIdCnpjPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type SetCompanyCnpjApiV1CompaniesCompanyIdCnpjPostError = SetCompanyCnpjApiV1CompaniesCompanyIdCnpjPostErrors[keyof SetCompanyCnpjApiV1CompaniesCompanyIdCnpjPostErrors];

export type SetCompanyCnpjApiV1CompaniesCompanyIdCnpjPostResponses = {
    /**
     * Successful Response
     */
    200: CompanyResponse;
};

export type SetCompanyCnpjApiV1CompaniesCompanyIdCnpjPostResponse = SetCompanyCnpjApiV1CompaniesCompanyIdCnpjPostResponses[keyof SetCompanyCnpjApiV1CompaniesCompanyIdCnpjPostResponses];

export type SetCompanyFileNameLabelApiV1CompaniesCompanyIdFileNameLabelPostData = {
    body: FileNameLabelUpdate;
    path: {
        /**
         * Company Id
         */
        company_id: string;
    };
    query?: never;
    url: '/api/v1/companies/{company_id}/file-name-label';
};

export type SetCompanyFileNameLabelApiV1CompaniesCompanyIdFileNameLabelPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type SetCompanyFileNameLabelApiV1CompaniesCompanyIdFileNameLabelPostError = SetCompanyFileNameLabelApiV1CompaniesCompanyIdFileNameLabelPostErrors[keyof SetCompanyFileNameLabelApiV1CompaniesCompanyIdFileNameLabelPostErrors];

export type SetCompanyFileNameLabelApiV1CompaniesCompanyIdFileNameLabelPostResponses = {
    /**
     * Successful Response
     */
    200: CompanyResponse;
};

export type SetCompanyFileNameLabelApiV1CompaniesCompanyIdFileNameLabelPostResponse = SetCompanyFileNameLabelApiV1CompaniesCompanyIdFileNameLabelPostResponses[keyof SetCompanyFileNameLabelApiV1CompaniesCompanyIdFileNameLabelPostResponses];

export type SetCompanyGroupApiV1CompaniesCompanyIdGroupPutData = {
    body: CompanyGroupAssignment;
    path: {
        /**
         * Company Id
         */
        company_id: string;
    };
    query?: never;
    url: '/api/v1/companies/{company_id}/group';
};

export type SetCompanyGroupApiV1CompaniesCompanyIdGroupPutErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type SetCompanyGroupApiV1CompaniesCompanyIdGroupPutError = SetCompanyGroupApiV1CompaniesCompanyIdGroupPutErrors[keyof SetCompanyGroupApiV1CompaniesCompanyIdGroupPutErrors];

export type SetCompanyGroupApiV1CompaniesCompanyIdGroupPutResponses = {
    /**
     * Successful Response
     */
    200: CompanyResponse;
};

export type SetCompanyGroupApiV1CompaniesCompanyIdGroupPutResponse = SetCompanyGroupApiV1CompaniesCompanyIdGroupPutResponses[keyof SetCompanyGroupApiV1CompaniesCompanyIdGroupPutResponses];

export type LookUpCompanyInsuredPersonApiV1CompaniesCompanyIdInsuredPeopleLookupPostData = {
    body: InsuredPersonLookupRequest;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Company Id
         */
        company_id: string;
    };
    query?: never;
    url: '/api/v1/companies/{company_id}/insured-people/lookup';
};

export type LookUpCompanyInsuredPersonApiV1CompaniesCompanyIdInsuredPeopleLookupPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type LookUpCompanyInsuredPersonApiV1CompaniesCompanyIdInsuredPeopleLookupPostError = LookUpCompanyInsuredPersonApiV1CompaniesCompanyIdInsuredPeopleLookupPostErrors[keyof LookUpCompanyInsuredPersonApiV1CompaniesCompanyIdInsuredPeopleLookupPostErrors];

export type LookUpCompanyInsuredPersonApiV1CompaniesCompanyIdInsuredPeopleLookupPostResponses = {
    /**
     * Successful Response
     */
    200: InsuredPersonLookupResponse;
};

export type LookUpCompanyInsuredPersonApiV1CompaniesCompanyIdInsuredPeopleLookupPostResponse = LookUpCompanyInsuredPersonApiV1CompaniesCompanyIdInsuredPeopleLookupPostResponses[keyof LookUpCompanyInsuredPersonApiV1CompaniesCompanyIdInsuredPeopleLookupPostResponses];

export type ProjectCompanyRosterApiV1CompaniesCompanyIdRosterGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Company Id
         */
        company_id: string;
    };
    query?: {
        /**
         * As Of
         *
         * Calendar date to project, `YYYY-MM-DD`, in America/Sao_Paulo. Defaults to today there. Past dates reproduce what was true then; future dates apply terminations that have already been filed but have not yet taken effect.
         */
        as_of?: string | null;
    };
    url: '/api/v1/companies/{company_id}/roster';
};

export type ProjectCompanyRosterApiV1CompaniesCompanyIdRosterGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ProjectCompanyRosterApiV1CompaniesCompanyIdRosterGetError = ProjectCompanyRosterApiV1CompaniesCompanyIdRosterGetErrors[keyof ProjectCompanyRosterApiV1CompaniesCompanyIdRosterGetErrors];

export type ProjectCompanyRosterApiV1CompaniesCompanyIdRosterGetResponses = {
    /**
     * Successful Response
     */
    200: CompanyRosterResponse;
};

export type ProjectCompanyRosterApiV1CompaniesCompanyIdRosterGetResponse = ProjectCompanyRosterApiV1CompaniesCompanyIdRosterGetResponses[keyof ProjectCompanyRosterApiV1CompaniesCompanyIdRosterGetResponses];

export type ListCompanySpreadsheetUploadsApiV1CompaniesCompanyIdSpreadsheetUploadsGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Company Id
         */
        company_id: string;
    };
    query?: {
        /**
         * Limit
         */
        limit?: number;
        /**
         * Offset
         */
        offset?: number;
    };
    url: '/api/v1/companies/{company_id}/spreadsheet-uploads';
};

export type ListCompanySpreadsheetUploadsApiV1CompaniesCompanyIdSpreadsheetUploadsGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ListCompanySpreadsheetUploadsApiV1CompaniesCompanyIdSpreadsheetUploadsGetError = ListCompanySpreadsheetUploadsApiV1CompaniesCompanyIdSpreadsheetUploadsGetErrors[keyof ListCompanySpreadsheetUploadsApiV1CompaniesCompanyIdSpreadsheetUploadsGetErrors];

export type ListCompanySpreadsheetUploadsApiV1CompaniesCompanyIdSpreadsheetUploadsGetResponses = {
    /**
     * Successful Response
     */
    200: SpreadsheetUploadListResponse;
};

export type ListCompanySpreadsheetUploadsApiV1CompaniesCompanyIdSpreadsheetUploadsGetResponse = ListCompanySpreadsheetUploadsApiV1CompaniesCompanyIdSpreadsheetUploadsGetResponses[keyof ListCompanySpreadsheetUploadsApiV1CompaniesCompanyIdSpreadsheetUploadsGetResponses];

export type UploadCompanySpreadsheetApiV1CompaniesCompanyIdSpreadsheetUploadsPostData = {
    body: BodyUploadCompanySpreadsheetApiV1CompaniesCompanyIdSpreadsheetUploadsPost;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Company Id
         */
        company_id: string;
    };
    query?: never;
    url: '/api/v1/companies/{company_id}/spreadsheet-uploads';
};

export type UploadCompanySpreadsheetApiV1CompaniesCompanyIdSpreadsheetUploadsPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The file was refused as a whole, so no row was read and nothing was stored. `error.code` is `validation_error` and each entry of `details` carries `reason`, one of: `missing_columns` (one detail per column the sheet lacks, named in `column` as the template labels it), `empty_file`, `unreadable_format` (neither a readable `.xlsx` nor a CSV), `legacy_xls` (a pre-2007 `.xls`), `no_header`, `no_rows` (a header and nothing under it), `too_many_rows` and `file_too_large` (both with the ceiling in `limit`: data rows, or bytes). Branch on `reason`; `message` is English and may be reworded.
     *
     * A malformed form field is the platform's ordinary `validation_error`, with `field` set and no `reason`.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type UploadCompanySpreadsheetApiV1CompaniesCompanyIdSpreadsheetUploadsPostError = UploadCompanySpreadsheetApiV1CompaniesCompanyIdSpreadsheetUploadsPostErrors[keyof UploadCompanySpreadsheetApiV1CompaniesCompanyIdSpreadsheetUploadsPostErrors];

export type UploadCompanySpreadsheetApiV1CompaniesCompanyIdSpreadsheetUploadsPostResponses = {
    /**
     * Successful Response
     */
    201: SpreadsheetUploadPreviewResponse;
};

export type UploadCompanySpreadsheetApiV1CompaniesCompanyIdSpreadsheetUploadsPostResponse = UploadCompanySpreadsheetApiV1CompaniesCompanyIdSpreadsheetUploadsPostResponses[keyof UploadCompanySpreadsheetApiV1CompaniesCompanyIdSpreadsheetUploadsPostResponses];

export type GetCompanySpreadsheetUploadApiV1CompaniesCompanyIdSpreadsheetUploadsUploadIdGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Company Id
         */
        company_id: string;
        /**
         * Upload Id
         */
        upload_id: string;
    };
    query?: {
        /**
         * Outcome
         *
         * Only rows with this outcome. Omit for the whole sheet.
         */
        outcome?: RowOutcome | null;
    };
    url: '/api/v1/companies/{company_id}/spreadsheet-uploads/{upload_id}';
};

export type GetCompanySpreadsheetUploadApiV1CompaniesCompanyIdSpreadsheetUploadsUploadIdGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type GetCompanySpreadsheetUploadApiV1CompaniesCompanyIdSpreadsheetUploadsUploadIdGetError = GetCompanySpreadsheetUploadApiV1CompaniesCompanyIdSpreadsheetUploadsUploadIdGetErrors[keyof GetCompanySpreadsheetUploadApiV1CompaniesCompanyIdSpreadsheetUploadsUploadIdGetErrors];

export type GetCompanySpreadsheetUploadApiV1CompaniesCompanyIdSpreadsheetUploadsUploadIdGetResponses = {
    /**
     * Successful Response
     */
    200: SpreadsheetUploadPreviewResponse;
};

export type GetCompanySpreadsheetUploadApiV1CompaniesCompanyIdSpreadsheetUploadsUploadIdGetResponse = GetCompanySpreadsheetUploadApiV1CompaniesCompanyIdSpreadsheetUploadsUploadIdGetResponses[keyof GetCompanySpreadsheetUploadApiV1CompaniesCompanyIdSpreadsheetUploadsUploadIdGetResponses];

export type ListCompanySpreadsheetUploadAbsencesApiV1CompaniesCompanyIdSpreadsheetUploadsUploadIdAbsencesGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Company Id
         */
        company_id: string;
        /**
         * Upload Id
         */
        upload_id: string;
    };
    query?: never;
    url: '/api/v1/companies/{company_id}/spreadsheet-uploads/{upload_id}/absences';
};

export type ListCompanySpreadsheetUploadAbsencesApiV1CompaniesCompanyIdSpreadsheetUploadsUploadIdAbsencesGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ListCompanySpreadsheetUploadAbsencesApiV1CompaniesCompanyIdSpreadsheetUploadsUploadIdAbsencesGetError = ListCompanySpreadsheetUploadAbsencesApiV1CompaniesCompanyIdSpreadsheetUploadsUploadIdAbsencesGetErrors[keyof ListCompanySpreadsheetUploadAbsencesApiV1CompaniesCompanyIdSpreadsheetUploadsUploadIdAbsencesGetErrors];

export type ListCompanySpreadsheetUploadAbsencesApiV1CompaniesCompanyIdSpreadsheetUploadsUploadIdAbsencesGetResponses = {
    /**
     * Response List Company Spreadsheet Upload Absences Api V1 Companies  Company Id  Spreadsheet Uploads  Upload Id  Absences Get
     *
     * Successful Response
     */
    200: Array<SpreadsheetUploadAbsenceResponse>;
};

export type ListCompanySpreadsheetUploadAbsencesApiV1CompaniesCompanyIdSpreadsheetUploadsUploadIdAbsencesGetResponse = ListCompanySpreadsheetUploadAbsencesApiV1CompaniesCompanyIdSpreadsheetUploadsUploadIdAbsencesGetResponses[keyof ListCompanySpreadsheetUploadAbsencesApiV1CompaniesCompanyIdSpreadsheetUploadsUploadIdAbsencesGetResponses];

export type ConfirmCompanySpreadsheetUploadApiV1CompaniesCompanyIdSpreadsheetUploadsUploadIdConfirmPostData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Company Id
         */
        company_id: string;
        /**
         * Upload Id
         */
        upload_id: string;
    };
    query?: never;
    url: '/api/v1/companies/{company_id}/spreadsheet-uploads/{upload_id}/confirm';
};

export type ConfirmCompanySpreadsheetUploadApiV1CompaniesCompanyIdSpreadsheetUploadsUploadIdConfirmPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ConfirmCompanySpreadsheetUploadApiV1CompaniesCompanyIdSpreadsheetUploadsUploadIdConfirmPostError = ConfirmCompanySpreadsheetUploadApiV1CompaniesCompanyIdSpreadsheetUploadsUploadIdConfirmPostErrors[keyof ConfirmCompanySpreadsheetUploadApiV1CompaniesCompanyIdSpreadsheetUploadsUploadIdConfirmPostErrors];

export type ConfirmCompanySpreadsheetUploadApiV1CompaniesCompanyIdSpreadsheetUploadsUploadIdConfirmPostResponses = {
    /**
     * Successful Response
     */
    200: SpreadsheetUploadPreviewResponse;
};

export type ConfirmCompanySpreadsheetUploadApiV1CompaniesCompanyIdSpreadsheetUploadsUploadIdConfirmPostResponse = ConfirmCompanySpreadsheetUploadApiV1CompaniesCompanyIdSpreadsheetUploadsUploadIdConfirmPostResponses[keyof ConfirmCompanySpreadsheetUploadApiV1CompaniesCompanyIdSpreadsheetUploadsUploadIdConfirmPostResponses];

export type ListCompanySpreadsheetUploadRowsApiV1CompaniesCompanyIdSpreadsheetUploadsUploadIdRowsGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Company Id
         */
        company_id: string;
        /**
         * Upload Id
         */
        upload_id: string;
    };
    query?: {
        /**
         * Outcome
         *
         * Only rows with this outcome.
         */
        outcome?: RowOutcome | null;
    };
    url: '/api/v1/companies/{company_id}/spreadsheet-uploads/{upload_id}/rows';
};

export type ListCompanySpreadsheetUploadRowsApiV1CompaniesCompanyIdSpreadsheetUploadsUploadIdRowsGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ListCompanySpreadsheetUploadRowsApiV1CompaniesCompanyIdSpreadsheetUploadsUploadIdRowsGetError = ListCompanySpreadsheetUploadRowsApiV1CompaniesCompanyIdSpreadsheetUploadsUploadIdRowsGetErrors[keyof ListCompanySpreadsheetUploadRowsApiV1CompaniesCompanyIdSpreadsheetUploadsUploadIdRowsGetErrors];

export type ListCompanySpreadsheetUploadRowsApiV1CompaniesCompanyIdSpreadsheetUploadsUploadIdRowsGetResponses = {
    /**
     * Response List Company Spreadsheet Upload Rows Api V1 Companies  Company Id  Spreadsheet Uploads  Upload Id  Rows Get
     *
     * Successful Response
     */
    200: Array<SpreadsheetUploadRowResponse>;
};

export type ListCompanySpreadsheetUploadRowsApiV1CompaniesCompanyIdSpreadsheetUploadsUploadIdRowsGetResponse = ListCompanySpreadsheetUploadRowsApiV1CompaniesCompanyIdSpreadsheetUploadsUploadIdRowsGetResponses[keyof ListCompanySpreadsheetUploadRowsApiV1CompaniesCompanyIdSpreadsheetUploadsUploadIdRowsGetResponses];

export type DownloadCompanySpreadsheetUploadSourceApiV1CompaniesCompanyIdSpreadsheetUploadsUploadIdSourceGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Company Id
         */
        company_id: string;
        /**
         * Upload Id
         */
        upload_id: string;
    };
    query?: never;
    url: '/api/v1/companies/{company_id}/spreadsheet-uploads/{upload_id}/source';
};

export type DownloadCompanySpreadsheetUploadSourceApiV1CompaniesCompanyIdSpreadsheetUploadsUploadIdSourceGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type DownloadCompanySpreadsheetUploadSourceApiV1CompaniesCompanyIdSpreadsheetUploadsUploadIdSourceGetError = DownloadCompanySpreadsheetUploadSourceApiV1CompaniesCompanyIdSpreadsheetUploadsUploadIdSourceGetErrors[keyof DownloadCompanySpreadsheetUploadSourceApiV1CompaniesCompanyIdSpreadsheetUploadsUploadIdSourceGetErrors];

export type DownloadCompanySpreadsheetUploadSourceApiV1CompaniesCompanyIdSpreadsheetUploadsUploadIdSourceGetResponses = {
    /**
     * The file as uploaded.
     */
    200: Blob | File;
};

export type DownloadCompanySpreadsheetUploadSourceApiV1CompaniesCompanyIdSpreadsheetUploadsUploadIdSourceGetResponse = DownloadCompanySpreadsheetUploadSourceApiV1CompaniesCompanyIdSpreadsheetUploadsUploadIdSourceGetResponses[keyof DownloadCompanySpreadsheetUploadSourceApiV1CompaniesCompanyIdSpreadsheetUploadsUploadIdSourceGetResponses];

export type SetCompanyTerminationDocumentPolicyApiV1CompaniesCompanyIdTerminationDocumentPolicyPutData = {
    body: TerminationDocumentPolicyUpdate;
    path: {
        /**
         * Company Id
         */
        company_id: string;
    };
    query?: never;
    url: '/api/v1/companies/{company_id}/termination-document-policy';
};

export type SetCompanyTerminationDocumentPolicyApiV1CompaniesCompanyIdTerminationDocumentPolicyPutErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type SetCompanyTerminationDocumentPolicyApiV1CompaniesCompanyIdTerminationDocumentPolicyPutError = SetCompanyTerminationDocumentPolicyApiV1CompaniesCompanyIdTerminationDocumentPolicyPutErrors[keyof SetCompanyTerminationDocumentPolicyApiV1CompaniesCompanyIdTerminationDocumentPolicyPutErrors];

export type SetCompanyTerminationDocumentPolicyApiV1CompaniesCompanyIdTerminationDocumentPolicyPutResponses = {
    /**
     * Successful Response
     */
    200: CompanyResponse;
};

export type SetCompanyTerminationDocumentPolicyApiV1CompaniesCompanyIdTerminationDocumentPolicyPutResponse = SetCompanyTerminationDocumentPolicyApiV1CompaniesCompanyIdTerminationDocumentPolicyPutResponses[keyof SetCompanyTerminationDocumentPolicyApiV1CompaniesCompanyIdTerminationDocumentPolicyPutResponses];

export type ListContractsApiV1ContractsGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/api/v1/contracts';
};

export type ListContractsApiV1ContractsGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ListContractsApiV1ContractsGetError = ListContractsApiV1ContractsGetErrors[keyof ListContractsApiV1ContractsGetErrors];

export type ListContractsApiV1ContractsGetResponses = {
    /**
     * Successful Response
     */
    200: ContractListResponse;
};

export type ListContractsApiV1ContractsGetResponse = ListContractsApiV1ContractsGetResponses[keyof ListContractsApiV1ContractsGetResponses];

export type CreateContractApiV1ContractsPostData = {
    body: ContractCreate;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/api/v1/contracts';
};

export type CreateContractApiV1ContractsPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type CreateContractApiV1ContractsPostError = CreateContractApiV1ContractsPostErrors[keyof CreateContractApiV1ContractsPostErrors];

export type CreateContractApiV1ContractsPostResponses = {
    /**
     * Successful Response
     */
    201: ContractResponse;
};

export type CreateContractApiV1ContractsPostResponse = CreateContractApiV1ContractsPostResponses[keyof CreateContractApiV1ContractsPostResponses];

export type GetContractApiV1ContractsContractIdGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Contract Id
         */
        contract_id: string;
    };
    query?: never;
    url: '/api/v1/contracts/{contract_id}';
};

export type GetContractApiV1ContractsContractIdGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type GetContractApiV1ContractsContractIdGetError = GetContractApiV1ContractsContractIdGetErrors[keyof GetContractApiV1ContractsContractIdGetErrors];

export type GetContractApiV1ContractsContractIdGetResponses = {
    /**
     * Successful Response
     */
    200: ContractResponse;
};

export type GetContractApiV1ContractsContractIdGetResponse = GetContractApiV1ContractsContractIdGetResponses[keyof GetContractApiV1ContractsContractIdGetResponses];

export type UpdateContractApiV1ContractsContractIdPatchData = {
    body: ContractUpdate;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Contract Id
         */
        contract_id: string;
    };
    query?: never;
    url: '/api/v1/contracts/{contract_id}';
};

export type UpdateContractApiV1ContractsContractIdPatchErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type UpdateContractApiV1ContractsContractIdPatchError = UpdateContractApiV1ContractsContractIdPatchErrors[keyof UpdateContractApiV1ContractsContractIdPatchErrors];

export type UpdateContractApiV1ContractsContractIdPatchResponses = {
    /**
     * Successful Response
     */
    200: ContractResponse;
};

export type UpdateContractApiV1ContractsContractIdPatchResponse = UpdateContractApiV1ContractsContractIdPatchResponses[keyof UpdateContractApiV1ContractsContractIdPatchResponses];

export type ListBaselineImportsApiV1ContractsContractIdBaselineImportsGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Contract Id
         */
        contract_id: string;
    };
    query?: never;
    url: '/api/v1/contracts/{contract_id}/baseline-imports';
};

export type ListBaselineImportsApiV1ContractsContractIdBaselineImportsGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ListBaselineImportsApiV1ContractsContractIdBaselineImportsGetError = ListBaselineImportsApiV1ContractsContractIdBaselineImportsGetErrors[keyof ListBaselineImportsApiV1ContractsContractIdBaselineImportsGetErrors];

export type ListBaselineImportsApiV1ContractsContractIdBaselineImportsGetResponses = {
    /**
     * Successful Response
     */
    200: BaselineImportListResponse;
};

export type ListBaselineImportsApiV1ContractsContractIdBaselineImportsGetResponse = ListBaselineImportsApiV1ContractsContractIdBaselineImportsGetResponses[keyof ListBaselineImportsApiV1ContractsContractIdBaselineImportsGetResponses];

export type ImportBaselineApiV1ContractsContractIdBaselineImportsPostData = {
    body: BodyImportBaselineApiV1ContractsContractIdBaselineImportsPost;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Contract Id
         */
        contract_id: string;
    };
    query?: never;
    url: '/api/v1/contracts/{contract_id}/baseline-imports';
};

export type ImportBaselineApiV1ContractsContractIdBaselineImportsPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ImportBaselineApiV1ContractsContractIdBaselineImportsPostError = ImportBaselineApiV1ContractsContractIdBaselineImportsPostErrors[keyof ImportBaselineApiV1ContractsContractIdBaselineImportsPostErrors];

export type ImportBaselineApiV1ContractsContractIdBaselineImportsPostResponses = {
    /**
     * Successful Response
     */
    201: BaselineImportResponse;
};

export type ImportBaselineApiV1ContractsContractIdBaselineImportsPostResponse = ImportBaselineApiV1ContractsContractIdBaselineImportsPostResponses[keyof ImportBaselineApiV1ContractsContractIdBaselineImportsPostResponses];

export type GetBaselineImportApiV1ContractsContractIdBaselineImportsBaselineImportIdGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Contract Id
         */
        contract_id: string;
        /**
         * Baseline Import Id
         */
        baseline_import_id: string;
    };
    query?: never;
    url: '/api/v1/contracts/{contract_id}/baseline-imports/{baseline_import_id}';
};

export type GetBaselineImportApiV1ContractsContractIdBaselineImportsBaselineImportIdGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type GetBaselineImportApiV1ContractsContractIdBaselineImportsBaselineImportIdGetError = GetBaselineImportApiV1ContractsContractIdBaselineImportsBaselineImportIdGetErrors[keyof GetBaselineImportApiV1ContractsContractIdBaselineImportsBaselineImportIdGetErrors];

export type GetBaselineImportApiV1ContractsContractIdBaselineImportsBaselineImportIdGetResponses = {
    /**
     * Successful Response
     */
    200: BaselineImportResponse;
};

export type GetBaselineImportApiV1ContractsContractIdBaselineImportsBaselineImportIdGetResponse = GetBaselineImportApiV1ContractsContractIdBaselineImportsBaselineImportIdGetResponses[keyof GetBaselineImportApiV1ContractsContractIdBaselineImportsBaselineImportIdGetResponses];

export type DownloadBaselineImportSourceApiV1ContractsContractIdBaselineImportsBaselineImportIdSourceGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Contract Id
         */
        contract_id: string;
        /**
         * Baseline Import Id
         */
        baseline_import_id: string;
    };
    query?: never;
    url: '/api/v1/contracts/{contract_id}/baseline-imports/{baseline_import_id}/source';
};

export type DownloadBaselineImportSourceApiV1ContractsContractIdBaselineImportsBaselineImportIdSourceGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type DownloadBaselineImportSourceApiV1ContractsContractIdBaselineImportsBaselineImportIdSourceGetError = DownloadBaselineImportSourceApiV1ContractsContractIdBaselineImportsBaselineImportIdSourceGetErrors[keyof DownloadBaselineImportSourceApiV1ContractsContractIdBaselineImportsBaselineImportIdSourceGetErrors];

export type DownloadBaselineImportSourceApiV1ContractsContractIdBaselineImportsBaselineImportIdSourceGetResponses = {
    /**
     * The source file as uploaded.
     */
    200: Blob | File;
};

export type DownloadBaselineImportSourceApiV1ContractsContractIdBaselineImportsBaselineImportIdSourceGetResponse = DownloadBaselineImportSourceApiV1ContractsContractIdBaselineImportsBaselineImportIdSourceGetResponses[keyof DownloadBaselineImportSourceApiV1ContractsContractIdBaselineImportsBaselineImportIdSourceGetResponses];

export type SubmitDeltaApiV1ContractsContractIdDeltasPostData = {
    body: DeltaCreate;
    headers?: {
        /**
         * Idempotency-Key
         *
         * Opaque, caller-chosen key that makes this write safe to retry. Repeating a request with the same key returns the original response and creates nothing a second time -- which is what a bulk importer needs after a timeout. Reusing a key with a different body is rejected with `idempotency_key_reused`.
         */
        'Idempotency-Key'?: string | null;
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Contract Id
         */
        contract_id: string;
    };
    query?: never;
    url: '/api/v1/contracts/{contract_id}/deltas';
};

export type SubmitDeltaApiV1ContractsContractIdDeltasPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type SubmitDeltaApiV1ContractsContractIdDeltasPostError = SubmitDeltaApiV1ContractsContractIdDeltasPostErrors[keyof SubmitDeltaApiV1ContractsContractIdDeltasPostErrors];

export type SubmitDeltaApiV1ContractsContractIdDeltasPostResponses = {
    /**
     * Successful Response
     */
    201: DeltaResponse;
};

export type SubmitDeltaApiV1ContractsContractIdDeltasPostResponse = SubmitDeltaApiV1ContractsContractIdDeltasPostResponses[keyof SubmitDeltaApiV1ContractsContractIdDeltasPostResponses];

export type SubmitDeltasInBulkApiV1ContractsContractIdDeltasBulkPostData = {
    body: BulkDeltaCreate;
    headers?: {
        /**
         * Idempotency-Key
         *
         * Opaque, caller-chosen key that makes this write safe to retry. Repeating a request with the same key returns the original response and creates nothing a second time -- which is what a bulk importer needs after a timeout. Reusing a key with a different body is rejected with `idempotency_key_reused`.
         */
        'Idempotency-Key'?: string | null;
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Contract Id
         */
        contract_id: string;
    };
    query?: never;
    url: '/api/v1/contracts/{contract_id}/deltas/bulk';
};

export type SubmitDeltasInBulkApiV1ContractsContractIdDeltasBulkPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type SubmitDeltasInBulkApiV1ContractsContractIdDeltasBulkPostError = SubmitDeltasInBulkApiV1ContractsContractIdDeltasBulkPostErrors[keyof SubmitDeltasInBulkApiV1ContractsContractIdDeltasBulkPostErrors];

export type SubmitDeltasInBulkApiV1ContractsContractIdDeltasBulkPostResponses = {
    /**
     * Successful Response
     */
    200: BulkDeltaResponse;
};

export type SubmitDeltasInBulkApiV1ContractsContractIdDeltasBulkPostResponse = SubmitDeltasInBulkApiV1ContractsContractIdDeltasBulkPostResponses[keyof SubmitDeltasInBulkApiV1ContractsContractIdDeltasBulkPostResponses];

export type GetPersonHistoryApiV1ContractsContractIdInsuredPeopleInsuredPersonIdHistoryGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Contract Id
         */
        contract_id: string;
        /**
         * Insured Person Id
         */
        insured_person_id: string;
    };
    query?: never;
    url: '/api/v1/contracts/{contract_id}/insured-people/{insured_person_id}/history';
};

export type GetPersonHistoryApiV1ContractsContractIdInsuredPeopleInsuredPersonIdHistoryGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type GetPersonHistoryApiV1ContractsContractIdInsuredPeopleInsuredPersonIdHistoryGetError = GetPersonHistoryApiV1ContractsContractIdInsuredPeopleInsuredPersonIdHistoryGetErrors[keyof GetPersonHistoryApiV1ContractsContractIdInsuredPeopleInsuredPersonIdHistoryGetErrors];

export type GetPersonHistoryApiV1ContractsContractIdInsuredPeopleInsuredPersonIdHistoryGetResponses = {
    /**
     * Successful Response
     */
    200: PersonHistoryResponse;
};

export type GetPersonHistoryApiV1ContractsContractIdInsuredPeopleInsuredPersonIdHistoryGetResponse = GetPersonHistoryApiV1ContractsContractIdInsuredPeopleInsuredPersonIdHistoryGetResponses[keyof GetPersonHistoryApiV1ContractsContractIdInsuredPeopleInsuredPersonIdHistoryGetResponses];

export type SetInsurerValuesApiV1ContractsContractIdInsuredPeopleInsuredPersonIdInsurerValuesPatchData = {
    body: InsurerValuesUpdate;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Contract Id
         */
        contract_id: string;
        /**
         * Insured Person Id
         */
        insured_person_id: string;
    };
    query?: never;
    url: '/api/v1/contracts/{contract_id}/insured-people/{insured_person_id}/insurer-values';
};

export type SetInsurerValuesApiV1ContractsContractIdInsuredPeopleInsuredPersonIdInsurerValuesPatchErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type SetInsurerValuesApiV1ContractsContractIdInsuredPeopleInsuredPersonIdInsurerValuesPatchError = SetInsurerValuesApiV1ContractsContractIdInsuredPeopleInsuredPersonIdInsurerValuesPatchErrors[keyof SetInsurerValuesApiV1ContractsContractIdInsuredPeopleInsuredPersonIdInsurerValuesPatchErrors];

export type SetInsurerValuesApiV1ContractsContractIdInsuredPeopleInsuredPersonIdInsurerValuesPatchResponses = {
    /**
     * Successful Response
     */
    200: InsuredPersonResponse;
};

export type SetInsurerValuesApiV1ContractsContractIdInsuredPeopleInsuredPersonIdInsurerValuesPatchResponse = SetInsurerValuesApiV1ContractsContractIdInsuredPeopleInsuredPersonIdInsurerValuesPatchResponses[keyof SetInsurerValuesApiV1ContractsContractIdInsuredPeopleInsuredPersonIdInsurerValuesPatchResponses];

export type ReadMovementStatusApiV1ContractsContractIdMovementStatusGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Contract Id
         */
        contract_id: string;
    };
    query?: {
        /**
         * Cycle Month
         *
         * The cutoff to answer relative to, written `YYYY-MM`. Defaults to the current month in São Paulo.
         */
        cycle_month?: string | null;
        /**
         * Limit
         */
        limit?: number;
        /**
         * Offset
         */
        offset?: number;
    };
    url: '/api/v1/contracts/{contract_id}/movement-status';
};

export type ReadMovementStatusApiV1ContractsContractIdMovementStatusGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ReadMovementStatusApiV1ContractsContractIdMovementStatusGetError = ReadMovementStatusApiV1ContractsContractIdMovementStatusGetErrors[keyof ReadMovementStatusApiV1ContractsContractIdMovementStatusGetErrors];

export type ReadMovementStatusApiV1ContractsContractIdMovementStatusGetResponses = {
    /**
     * Successful Response
     */
    200: MovementStatusListResponse;
};

export type ReadMovementStatusApiV1ContractsContractIdMovementStatusGetResponse = ReadMovementStatusApiV1ContractsContractIdMovementStatusGetResponses[keyof ReadMovementStatusApiV1ContractsContractIdMovementStatusGetResponses];

export type ProjectRosterApiV1ContractsContractIdRosterGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Contract Id
         */
        contract_id: string;
    };
    query?: {
        /**
         * As Of
         *
         * Calendar date to project, `YYYY-MM-DD`, in America/Sao_Paulo. Defaults to today there. Past dates reproduce what was true then; future dates apply terminations that have already been filed but have not yet taken effect.
         */
        as_of?: string | null;
    };
    url: '/api/v1/contracts/{contract_id}/roster';
};

export type ProjectRosterApiV1ContractsContractIdRosterGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ProjectRosterApiV1ContractsContractIdRosterGetError = ProjectRosterApiV1ContractsContractIdRosterGetErrors[keyof ProjectRosterApiV1ContractsContractIdRosterGetErrors];

export type ProjectRosterApiV1ContractsContractIdRosterGetResponses = {
    /**
     * Successful Response
     */
    200: RosterProjectionResponse;
};

export type ProjectRosterApiV1ContractsContractIdRosterGetResponse = ProjectRosterApiV1ContractsContractIdRosterGetResponses[keyof ProjectRosterApiV1ContractsContractIdRosterGetResponses];

export type ProjectSnapshotApiV1ContractsContractIdSnapshotProjectionGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Contract Id
         */
        contract_id: string;
    };
    query?: {
        /**
         * Cycle Month
         *
         * The month whose cycle to read, written `YYYY-MM`. Defaults to the month of `reference_date` when that is given instead.
         */
        cycle_month?: string | null;
        /**
         * Reference Date
         *
         * Project this exact calendar date instead of deriving one from a cycle. Past dates reproduce what was true then; future dates apply movements already filed that have not yet taken effect.
         */
        reference_date?: string | null;
    };
    url: '/api/v1/contracts/{contract_id}/snapshot-projection';
};

export type ProjectSnapshotApiV1ContractsContractIdSnapshotProjectionGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ProjectSnapshotApiV1ContractsContractIdSnapshotProjectionGetError = ProjectSnapshotApiV1ContractsContractIdSnapshotProjectionGetErrors[keyof ProjectSnapshotApiV1ContractsContractIdSnapshotProjectionGetErrors];

export type ProjectSnapshotApiV1ContractsContractIdSnapshotProjectionGetResponses = {
    /**
     * Successful Response
     */
    200: SnapshotProjectionResponse;
};

export type ProjectSnapshotApiV1ContractsContractIdSnapshotProjectionGetResponse = ProjectSnapshotApiV1ContractsContractIdSnapshotProjectionGetResponses[keyof ProjectSnapshotApiV1ContractsContractIdSnapshotProjectionGetResponses];

export type RecordSnapshotSubmissionApiV1ContractsContractIdSnapshotSubmissionsPostData = {
    body: SnapshotSubmissionCreate;
    headers?: {
        /**
         * Idempotency-Key
         *
         * Opaque, caller-chosen key that makes this write safe to retry. Repeating a request with the same key returns the original response and creates nothing a second time -- which is what a bulk importer needs after a timeout. Reusing a key with a different body is rejected with `idempotency_key_reused`.
         */
        'Idempotency-Key'?: string | null;
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Contract Id
         */
        contract_id: string;
    };
    query?: never;
    url: '/api/v1/contracts/{contract_id}/snapshot-submissions';
};

export type RecordSnapshotSubmissionApiV1ContractsContractIdSnapshotSubmissionsPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type RecordSnapshotSubmissionApiV1ContractsContractIdSnapshotSubmissionsPostError = RecordSnapshotSubmissionApiV1ContractsContractIdSnapshotSubmissionsPostErrors[keyof RecordSnapshotSubmissionApiV1ContractsContractIdSnapshotSubmissionsPostErrors];

export type RecordSnapshotSubmissionApiV1ContractsContractIdSnapshotSubmissionsPostResponses = {
    /**
     * Successful Response
     */
    201: SnapshotSubmissionResponse;
};

export type RecordSnapshotSubmissionApiV1ContractsContractIdSnapshotSubmissionsPostResponse = RecordSnapshotSubmissionApiV1ContractsContractIdSnapshotSubmissionsPostResponses[keyof RecordSnapshotSubmissionApiV1ContractsContractIdSnapshotSubmissionsPostResponses];

export type GenerateSnapshotApiV1ContractsContractIdSnapshotsPostData = {
    body: SnapshotGenerate;
    headers?: {
        /**
         * Idempotency-Key
         *
         * Opaque, caller-chosen key that makes this write safe to retry. Repeating a request with the same key returns the original response and generates nothing a second time. Reusing a key with a different body is rejected with `idempotency_key_reused`.
         */
        'Idempotency-Key'?: string | null;
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Contract Id
         */
        contract_id: string;
    };
    query?: never;
    url: '/api/v1/contracts/{contract_id}/snapshots';
};

export type GenerateSnapshotApiV1ContractsContractIdSnapshotsPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type GenerateSnapshotApiV1ContractsContractIdSnapshotsPostError = GenerateSnapshotApiV1ContractsContractIdSnapshotsPostErrors[keyof GenerateSnapshotApiV1ContractsContractIdSnapshotsPostErrors];

export type GenerateSnapshotApiV1ContractsContractIdSnapshotsPostResponses = {
    /**
     * Successful Response
     */
    201: SnapshotDetailResponse;
};

export type GenerateSnapshotApiV1ContractsContractIdSnapshotsPostResponse = GenerateSnapshotApiV1ContractsContractIdSnapshotsPostResponses[keyof GenerateSnapshotApiV1ContractsContractIdSnapshotsPostResponses];

export type ListSpreadsheetUploadsApiV1ContractsContractIdSpreadsheetUploadsGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Contract Id
         */
        contract_id: string;
    };
    query?: {
        /**
         * Limit
         */
        limit?: number;
        /**
         * Offset
         */
        offset?: number;
    };
    url: '/api/v1/contracts/{contract_id}/spreadsheet-uploads';
};

export type ListSpreadsheetUploadsApiV1ContractsContractIdSpreadsheetUploadsGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ListSpreadsheetUploadsApiV1ContractsContractIdSpreadsheetUploadsGetError = ListSpreadsheetUploadsApiV1ContractsContractIdSpreadsheetUploadsGetErrors[keyof ListSpreadsheetUploadsApiV1ContractsContractIdSpreadsheetUploadsGetErrors];

export type ListSpreadsheetUploadsApiV1ContractsContractIdSpreadsheetUploadsGetResponses = {
    /**
     * Successful Response
     */
    200: SpreadsheetUploadListResponse;
};

export type ListSpreadsheetUploadsApiV1ContractsContractIdSpreadsheetUploadsGetResponse = ListSpreadsheetUploadsApiV1ContractsContractIdSpreadsheetUploadsGetResponses[keyof ListSpreadsheetUploadsApiV1ContractsContractIdSpreadsheetUploadsGetResponses];

export type UploadSpreadsheetApiV1ContractsContractIdSpreadsheetUploadsPostData = {
    body: BodyUploadSpreadsheetApiV1ContractsContractIdSpreadsheetUploadsPost;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Contract Id
         */
        contract_id: string;
    };
    query?: never;
    url: '/api/v1/contracts/{contract_id}/spreadsheet-uploads';
};

export type UploadSpreadsheetApiV1ContractsContractIdSpreadsheetUploadsPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The file was refused as a whole, so no row was read and nothing was stored. `error.code` is `validation_error` and each entry of `details` carries `reason`, one of: `missing_columns` (one detail per column the sheet lacks, named in `column` as the template labels it), `empty_file`, `unreadable_format` (neither a readable `.xlsx` nor a CSV), `legacy_xls` (a pre-2007 `.xls`), `no_header`, `no_rows` (a header and nothing under it), `too_many_rows` and `file_too_large` (both with the ceiling in `limit`: data rows, or bytes). Branch on `reason`; `message` is English and may be reworded.
     *
     * A malformed form field is the platform's ordinary `validation_error`, with `field` set and no `reason`.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type UploadSpreadsheetApiV1ContractsContractIdSpreadsheetUploadsPostError = UploadSpreadsheetApiV1ContractsContractIdSpreadsheetUploadsPostErrors[keyof UploadSpreadsheetApiV1ContractsContractIdSpreadsheetUploadsPostErrors];

export type UploadSpreadsheetApiV1ContractsContractIdSpreadsheetUploadsPostResponses = {
    /**
     * Successful Response
     */
    201: SpreadsheetUploadPreviewResponse;
};

export type UploadSpreadsheetApiV1ContractsContractIdSpreadsheetUploadsPostResponse = UploadSpreadsheetApiV1ContractsContractIdSpreadsheetUploadsPostResponses[keyof UploadSpreadsheetApiV1ContractsContractIdSpreadsheetUploadsPostResponses];

export type GetSpreadsheetUploadApiV1ContractsContractIdSpreadsheetUploadsUploadIdGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Contract Id
         */
        contract_id: string;
        /**
         * Upload Id
         */
        upload_id: string;
    };
    query?: {
        /**
         * Outcome
         *
         * Only rows with this outcome. Omit for the whole sheet.
         */
        outcome?: RowOutcome | null;
    };
    url: '/api/v1/contracts/{contract_id}/spreadsheet-uploads/{upload_id}';
};

export type GetSpreadsheetUploadApiV1ContractsContractIdSpreadsheetUploadsUploadIdGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type GetSpreadsheetUploadApiV1ContractsContractIdSpreadsheetUploadsUploadIdGetError = GetSpreadsheetUploadApiV1ContractsContractIdSpreadsheetUploadsUploadIdGetErrors[keyof GetSpreadsheetUploadApiV1ContractsContractIdSpreadsheetUploadsUploadIdGetErrors];

export type GetSpreadsheetUploadApiV1ContractsContractIdSpreadsheetUploadsUploadIdGetResponses = {
    /**
     * Successful Response
     */
    200: SpreadsheetUploadPreviewResponse;
};

export type GetSpreadsheetUploadApiV1ContractsContractIdSpreadsheetUploadsUploadIdGetResponse = GetSpreadsheetUploadApiV1ContractsContractIdSpreadsheetUploadsUploadIdGetResponses[keyof GetSpreadsheetUploadApiV1ContractsContractIdSpreadsheetUploadsUploadIdGetResponses];

export type ListSpreadsheetUploadAbsencesApiV1ContractsContractIdSpreadsheetUploadsUploadIdAbsencesGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Contract Id
         */
        contract_id: string;
        /**
         * Upload Id
         */
        upload_id: string;
    };
    query?: never;
    url: '/api/v1/contracts/{contract_id}/spreadsheet-uploads/{upload_id}/absences';
};

export type ListSpreadsheetUploadAbsencesApiV1ContractsContractIdSpreadsheetUploadsUploadIdAbsencesGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ListSpreadsheetUploadAbsencesApiV1ContractsContractIdSpreadsheetUploadsUploadIdAbsencesGetError = ListSpreadsheetUploadAbsencesApiV1ContractsContractIdSpreadsheetUploadsUploadIdAbsencesGetErrors[keyof ListSpreadsheetUploadAbsencesApiV1ContractsContractIdSpreadsheetUploadsUploadIdAbsencesGetErrors];

export type ListSpreadsheetUploadAbsencesApiV1ContractsContractIdSpreadsheetUploadsUploadIdAbsencesGetResponses = {
    /**
     * Response List Spreadsheet Upload Absences Api V1 Contracts  Contract Id  Spreadsheet Uploads  Upload Id  Absences Get
     *
     * Successful Response
     */
    200: Array<SpreadsheetUploadAbsenceResponse>;
};

export type ListSpreadsheetUploadAbsencesApiV1ContractsContractIdSpreadsheetUploadsUploadIdAbsencesGetResponse = ListSpreadsheetUploadAbsencesApiV1ContractsContractIdSpreadsheetUploadsUploadIdAbsencesGetResponses[keyof ListSpreadsheetUploadAbsencesApiV1ContractsContractIdSpreadsheetUploadsUploadIdAbsencesGetResponses];

export type ConfirmSpreadsheetUploadApiV1ContractsContractIdSpreadsheetUploadsUploadIdConfirmPostData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Contract Id
         */
        contract_id: string;
        /**
         * Upload Id
         */
        upload_id: string;
    };
    query?: never;
    url: '/api/v1/contracts/{contract_id}/spreadsheet-uploads/{upload_id}/confirm';
};

export type ConfirmSpreadsheetUploadApiV1ContractsContractIdSpreadsheetUploadsUploadIdConfirmPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ConfirmSpreadsheetUploadApiV1ContractsContractIdSpreadsheetUploadsUploadIdConfirmPostError = ConfirmSpreadsheetUploadApiV1ContractsContractIdSpreadsheetUploadsUploadIdConfirmPostErrors[keyof ConfirmSpreadsheetUploadApiV1ContractsContractIdSpreadsheetUploadsUploadIdConfirmPostErrors];

export type ConfirmSpreadsheetUploadApiV1ContractsContractIdSpreadsheetUploadsUploadIdConfirmPostResponses = {
    /**
     * Successful Response
     */
    200: SpreadsheetUploadPreviewResponse;
};

export type ConfirmSpreadsheetUploadApiV1ContractsContractIdSpreadsheetUploadsUploadIdConfirmPostResponse = ConfirmSpreadsheetUploadApiV1ContractsContractIdSpreadsheetUploadsUploadIdConfirmPostResponses[keyof ConfirmSpreadsheetUploadApiV1ContractsContractIdSpreadsheetUploadsUploadIdConfirmPostResponses];

export type ListSpreadsheetUploadRowsApiV1ContractsContractIdSpreadsheetUploadsUploadIdRowsGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Contract Id
         */
        contract_id: string;
        /**
         * Upload Id
         */
        upload_id: string;
    };
    query?: {
        /**
         * Outcome
         *
         * Only rows with this outcome.
         */
        outcome?: RowOutcome | null;
    };
    url: '/api/v1/contracts/{contract_id}/spreadsheet-uploads/{upload_id}/rows';
};

export type ListSpreadsheetUploadRowsApiV1ContractsContractIdSpreadsheetUploadsUploadIdRowsGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ListSpreadsheetUploadRowsApiV1ContractsContractIdSpreadsheetUploadsUploadIdRowsGetError = ListSpreadsheetUploadRowsApiV1ContractsContractIdSpreadsheetUploadsUploadIdRowsGetErrors[keyof ListSpreadsheetUploadRowsApiV1ContractsContractIdSpreadsheetUploadsUploadIdRowsGetErrors];

export type ListSpreadsheetUploadRowsApiV1ContractsContractIdSpreadsheetUploadsUploadIdRowsGetResponses = {
    /**
     * Response List Spreadsheet Upload Rows Api V1 Contracts  Contract Id  Spreadsheet Uploads  Upload Id  Rows Get
     *
     * Successful Response
     */
    200: Array<SpreadsheetUploadRowResponse>;
};

export type ListSpreadsheetUploadRowsApiV1ContractsContractIdSpreadsheetUploadsUploadIdRowsGetResponse = ListSpreadsheetUploadRowsApiV1ContractsContractIdSpreadsheetUploadsUploadIdRowsGetResponses[keyof ListSpreadsheetUploadRowsApiV1ContractsContractIdSpreadsheetUploadsUploadIdRowsGetResponses];

export type DownloadSpreadsheetUploadSourceApiV1ContractsContractIdSpreadsheetUploadsUploadIdSourceGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Contract Id
         */
        contract_id: string;
        /**
         * Upload Id
         */
        upload_id: string;
    };
    query?: never;
    url: '/api/v1/contracts/{contract_id}/spreadsheet-uploads/{upload_id}/source';
};

export type DownloadSpreadsheetUploadSourceApiV1ContractsContractIdSpreadsheetUploadsUploadIdSourceGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type DownloadSpreadsheetUploadSourceApiV1ContractsContractIdSpreadsheetUploadsUploadIdSourceGetError = DownloadSpreadsheetUploadSourceApiV1ContractsContractIdSpreadsheetUploadsUploadIdSourceGetErrors[keyof DownloadSpreadsheetUploadSourceApiV1ContractsContractIdSpreadsheetUploadsUploadIdSourceGetErrors];

export type DownloadSpreadsheetUploadSourceApiV1ContractsContractIdSpreadsheetUploadsUploadIdSourceGetResponses = {
    /**
     * The file as uploaded.
     */
    200: Blob | File;
};

export type DownloadSpreadsheetUploadSourceApiV1ContractsContractIdSpreadsheetUploadsUploadIdSourceGetResponse = DownloadSpreadsheetUploadSourceApiV1ContractsContractIdSpreadsheetUploadsUploadIdSourceGetResponses[keyof DownloadSpreadsheetUploadSourceApiV1ContractsContractIdSpreadsheetUploadsUploadIdSourceGetResponses];

export type GetSubmissionCycleApiV1ContractsContractIdSubmissionCycleGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Contract Id
         */
        contract_id: string;
    };
    query: {
        /**
         * Cycle Month
         *
         * The month whose cycle to read, written `YYYY-MM`. Defaults to the month of `reference_date` when that is given instead.
         */
        cycle_month: string;
    };
    url: '/api/v1/contracts/{contract_id}/submission-cycle';
};

export type GetSubmissionCycleApiV1ContractsContractIdSubmissionCycleGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type GetSubmissionCycleApiV1ContractsContractIdSubmissionCycleGetError = GetSubmissionCycleApiV1ContractsContractIdSubmissionCycleGetErrors[keyof GetSubmissionCycleApiV1ContractsContractIdSubmissionCycleGetErrors];

export type GetSubmissionCycleApiV1ContractsContractIdSubmissionCycleGetResponses = {
    /**
     * Successful Response
     */
    200: SubmissionCycleResponse;
};

export type GetSubmissionCycleApiV1ContractsContractIdSubmissionCycleGetResponse = GetSubmissionCycleApiV1ContractsContractIdSubmissionCycleGetResponses[keyof GetSubmissionCycleApiV1ContractsContractIdSubmissionCycleGetResponses];

export type ListDeltasApiV1DeltasGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Contract Id
         *
         * Only this contract's.
         */
        contract_id?: string | null;
        /**
         * Status
         *
         * Only deltas in this lifecycle status.
         */
        status?: DeltaStatus | null;
        /**
         * Action
         *
         * Only this kind of change.
         */
        action?: DeltaAction | null;
        /**
         * Effective From
         *
         * Only deltas taking effect on or after this date.
         */
        effective_from?: string | null;
        /**
         * Effective To
         *
         * Only deltas taking effect on or before this date.
         */
        effective_to?: string | null;
        /**
         * Cpf
         *
         * Only deltas about this person. Punctuation is ignored.
         */
        cpf?: string | null;
        /**
         * Requires Review
         *
         * `true` for the review queue, `false` for everything else.
         */
        requires_review?: boolean | null;
        /**
         * Supersedes Delta Id
         *
         * Only the deltas that correct this one.
         */
        supersedes_delta_id?: string | null;
        /**
         * Limit
         */
        limit?: number;
        /**
         * Offset
         */
        offset?: number;
    };
    url: '/api/v1/deltas';
};

export type ListDeltasApiV1DeltasGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ListDeltasApiV1DeltasGetError = ListDeltasApiV1DeltasGetErrors[keyof ListDeltasApiV1DeltasGetErrors];

export type ListDeltasApiV1DeltasGetResponses = {
    /**
     * Successful Response
     */
    200: DeltaListResponse;
};

export type ListDeltasApiV1DeltasGetResponse = ListDeltasApiV1DeltasGetResponses[keyof ListDeltasApiV1DeltasGetResponses];

export type GetDeltaApiV1DeltasDeltaIdGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Delta Id
         */
        delta_id: string;
    };
    query?: never;
    url: '/api/v1/deltas/{delta_id}';
};

export type GetDeltaApiV1DeltasDeltaIdGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type GetDeltaApiV1DeltasDeltaIdGetError = GetDeltaApiV1DeltasDeltaIdGetErrors[keyof GetDeltaApiV1DeltasDeltaIdGetErrors];

export type GetDeltaApiV1DeltasDeltaIdGetResponses = {
    /**
     * Successful Response
     */
    200: DeltaResponse;
};

export type GetDeltaApiV1DeltasDeltaIdGetResponse = GetDeltaApiV1DeltasDeltaIdGetResponses[keyof GetDeltaApiV1DeltasDeltaIdGetResponses];

export type ApproveDeltaApiV1DeltasDeltaIdApprovePostData = {
    body?: never;
    headers?: {
        /**
         * Idempotency-Key
         *
         * Opaque, caller-chosen key that makes this write safe to retry. Repeating a request with the same key returns the original response and creates nothing a second time -- which is what a bulk importer needs after a timeout. Reusing a key with a different body is rejected with `idempotency_key_reused`.
         */
        'Idempotency-Key'?: string | null;
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Delta Id
         */
        delta_id: string;
    };
    query?: never;
    url: '/api/v1/deltas/{delta_id}/approve';
};

export type ApproveDeltaApiV1DeltasDeltaIdApprovePostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ApproveDeltaApiV1DeltasDeltaIdApprovePostError = ApproveDeltaApiV1DeltasDeltaIdApprovePostErrors[keyof ApproveDeltaApiV1DeltasDeltaIdApprovePostErrors];

export type ApproveDeltaApiV1DeltasDeltaIdApprovePostResponses = {
    /**
     * Successful Response
     */
    200: DeltaResponse;
};

export type ApproveDeltaApiV1DeltasDeltaIdApprovePostResponse = ApproveDeltaApiV1DeltasDeltaIdApprovePostResponses[keyof ApproveDeltaApiV1DeltasDeltaIdApprovePostResponses];

export type CancelDeltaApiV1DeltasDeltaIdCancelPostData = {
    body?: DeltaCancellation;
    headers?: {
        /**
         * Idempotency-Key
         *
         * Opaque, caller-chosen key that makes this write safe to retry. Repeating a request with the same key returns the original response and creates nothing a second time -- which is what a bulk importer needs after a timeout. Reusing a key with a different body is rejected with `idempotency_key_reused`.
         */
        'Idempotency-Key'?: string | null;
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Delta Id
         */
        delta_id: string;
    };
    query?: never;
    url: '/api/v1/deltas/{delta_id}/cancel';
};

export type CancelDeltaApiV1DeltasDeltaIdCancelPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type CancelDeltaApiV1DeltasDeltaIdCancelPostError = CancelDeltaApiV1DeltasDeltaIdCancelPostErrors[keyof CancelDeltaApiV1DeltasDeltaIdCancelPostErrors];

export type CancelDeltaApiV1DeltasDeltaIdCancelPostResponses = {
    /**
     * Successful Response
     */
    200: DeltaResponse;
};

export type CancelDeltaApiV1DeltasDeltaIdCancelPostResponse = CancelDeltaApiV1DeltasDeltaIdCancelPostResponses[keyof CancelDeltaApiV1DeltasDeltaIdCancelPostResponses];

export type ListDocumentsForDeltaApiV1DeltasDeltaIdDocumentsGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Delta Id
         */
        delta_id: string;
    };
    query?: {
        /**
         * Limit
         */
        limit?: number;
        /**
         * Offset
         */
        offset?: number;
    };
    url: '/api/v1/deltas/{delta_id}/documents';
};

export type ListDocumentsForDeltaApiV1DeltasDeltaIdDocumentsGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ListDocumentsForDeltaApiV1DeltasDeltaIdDocumentsGetError = ListDocumentsForDeltaApiV1DeltasDeltaIdDocumentsGetErrors[keyof ListDocumentsForDeltaApiV1DeltasDeltaIdDocumentsGetErrors];

export type ListDocumentsForDeltaApiV1DeltasDeltaIdDocumentsGetResponses = {
    /**
     * Successful Response
     */
    200: DocumentListResponse;
};

export type ListDocumentsForDeltaApiV1DeltasDeltaIdDocumentsGetResponse = ListDocumentsForDeltaApiV1DeltasDeltaIdDocumentsGetResponses[keyof ListDocumentsForDeltaApiV1DeltasDeltaIdDocumentsGetResponses];

export type RejectDeltaApiV1DeltasDeltaIdRejectPostData = {
    body: DeltaRejection;
    headers?: {
        /**
         * Idempotency-Key
         *
         * Opaque, caller-chosen key that makes this write safe to retry. Repeating a request with the same key returns the original response and creates nothing a second time -- which is what a bulk importer needs after a timeout. Reusing a key with a different body is rejected with `idempotency_key_reused`.
         */
        'Idempotency-Key'?: string | null;
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Delta Id
         */
        delta_id: string;
    };
    query?: never;
    url: '/api/v1/deltas/{delta_id}/reject';
};

export type RejectDeltaApiV1DeltasDeltaIdRejectPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type RejectDeltaApiV1DeltasDeltaIdRejectPostError = RejectDeltaApiV1DeltasDeltaIdRejectPostErrors[keyof RejectDeltaApiV1DeltasDeltaIdRejectPostErrors];

export type RejectDeltaApiV1DeltasDeltaIdRejectPostResponses = {
    /**
     * Successful Response
     */
    200: DeltaResponse;
};

export type RejectDeltaApiV1DeltasDeltaIdRejectPostResponse = RejectDeltaApiV1DeltasDeltaIdRejectPostResponses[keyof RejectDeltaApiV1DeltasDeltaIdRejectPostResponses];

export type ListDocumentsApiV1DocumentsGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Kind
         *
         * Only this kind of paperwork.
         */
        kind?: DocumentKind | null;
        /**
         * Channel
         *
         * Only documents that arrived this way.
         */
        channel?: DocumentChannel | null;
        /**
         * Delta Id
         *
         * Only the documents currently linked to this delta.
         */
        delta_id?: string | null;
        /**
         * Unmatched
         *
         * `true` for documents with no live link -- the ones still waiting to be matched to a change. `false` for the ones already attached to one.
         */
        unmatched?: boolean | null;
        /**
         * Current
         *
         * `true` for versions nothing has replaced, `false` for superseded ones.
         */
        current?: boolean | null;
        /**
         * Content Sha256
         *
         * Exact SHA-256 of a document, hex. Answers 'do we already hold this file?'
         */
        content_sha256?: string | null;
        /**
         * Limit
         */
        limit?: number;
        /**
         * Offset
         */
        offset?: number;
    };
    url: '/api/v1/documents';
};

export type ListDocumentsApiV1DocumentsGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ListDocumentsApiV1DocumentsGetError = ListDocumentsApiV1DocumentsGetErrors[keyof ListDocumentsApiV1DocumentsGetErrors];

export type ListDocumentsApiV1DocumentsGetResponses = {
    /**
     * Successful Response
     */
    200: DocumentListResponse;
};

export type ListDocumentsApiV1DocumentsGetResponse = ListDocumentsApiV1DocumentsGetResponses[keyof ListDocumentsApiV1DocumentsGetResponses];

export type UploadDocumentApiV1DocumentsPostData = {
    body: BodyUploadDocumentApiV1DocumentsPost;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/api/v1/documents';
};

export type UploadDocumentApiV1DocumentsPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The document is above the size ceiling.
     */
    413: ErrorEnvelope;
    /**
     * The file's content is not an accepted type.
     */
    415: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type UploadDocumentApiV1DocumentsPostError = UploadDocumentApiV1DocumentsPostErrors[keyof UploadDocumentApiV1DocumentsPostErrors];

export type UploadDocumentApiV1DocumentsPostResponses = {
    /**
     * Successful Response
     */
    201: DocumentResponse;
};

export type UploadDocumentApiV1DocumentsPostResponse = UploadDocumentApiV1DocumentsPostResponses[keyof UploadDocumentApiV1DocumentsPostResponses];

export type GetDocumentApiV1DocumentsDocumentIdGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Document Id
         */
        document_id: string;
    };
    query?: never;
    url: '/api/v1/documents/{document_id}';
};

export type GetDocumentApiV1DocumentsDocumentIdGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type GetDocumentApiV1DocumentsDocumentIdGetError = GetDocumentApiV1DocumentsDocumentIdGetErrors[keyof GetDocumentApiV1DocumentsDocumentIdGetErrors];

export type GetDocumentApiV1DocumentsDocumentIdGetResponses = {
    /**
     * Successful Response
     */
    200: DocumentResponse;
};

export type GetDocumentApiV1DocumentsDocumentIdGetResponse = GetDocumentApiV1DocumentsDocumentIdGetResponses[keyof GetDocumentApiV1DocumentsDocumentIdGetResponses];

export type DownloadDocumentApiV1DocumentsDocumentIdContentGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Document Id
         */
        document_id: string;
    };
    query?: never;
    url: '/api/v1/documents/{document_id}/content';
};

export type DownloadDocumentApiV1DocumentsDocumentIdContentGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type DownloadDocumentApiV1DocumentsDocumentIdContentGetError = DownloadDocumentApiV1DocumentsDocumentIdContentGetErrors[keyof DownloadDocumentApiV1DocumentsDocumentIdContentGetErrors];

export type DownloadDocumentApiV1DocumentsDocumentIdContentGetResponses = {
    /**
     * The document as uploaded.
     */
    200: Blob | File;
};

export type DownloadDocumentApiV1DocumentsDocumentIdContentGetResponse = DownloadDocumentApiV1DocumentsDocumentIdContentGetResponses[keyof DownloadDocumentApiV1DocumentsDocumentIdContentGetResponses];

export type LinkDocumentApiV1DocumentsDocumentIdLinksPostData = {
    body: DocumentLinkCreate;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Document Id
         */
        document_id: string;
    };
    query?: never;
    url: '/api/v1/documents/{document_id}/links';
};

export type LinkDocumentApiV1DocumentsDocumentIdLinksPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type LinkDocumentApiV1DocumentsDocumentIdLinksPostError = LinkDocumentApiV1DocumentsDocumentIdLinksPostErrors[keyof LinkDocumentApiV1DocumentsDocumentIdLinksPostErrors];

export type LinkDocumentApiV1DocumentsDocumentIdLinksPostResponses = {
    /**
     * Successful Response
     */
    201: DocumentResponse;
};

export type LinkDocumentApiV1DocumentsDocumentIdLinksPostResponse = LinkDocumentApiV1DocumentsDocumentIdLinksPostResponses[keyof LinkDocumentApiV1DocumentsDocumentIdLinksPostResponses];

export type UnlinkDocumentApiV1DocumentsDocumentIdLinksDeltaIdDeleteData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Document Id
         */
        document_id: string;
        /**
         * Delta Id
         */
        delta_id: string;
    };
    query?: never;
    url: '/api/v1/documents/{document_id}/links/{delta_id}';
};

export type UnlinkDocumentApiV1DocumentsDocumentIdLinksDeltaIdDeleteErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type UnlinkDocumentApiV1DocumentsDocumentIdLinksDeltaIdDeleteError = UnlinkDocumentApiV1DocumentsDocumentIdLinksDeltaIdDeleteErrors[keyof UnlinkDocumentApiV1DocumentsDocumentIdLinksDeltaIdDeleteErrors];

export type UnlinkDocumentApiV1DocumentsDocumentIdLinksDeltaIdDeleteResponses = {
    /**
     * Successful Response
     */
    200: DocumentResponse;
};

export type UnlinkDocumentApiV1DocumentsDocumentIdLinksDeltaIdDeleteResponse = UnlinkDocumentApiV1DocumentsDocumentIdLinksDeltaIdDeleteResponses[keyof UnlinkDocumentApiV1DocumentsDocumentIdLinksDeltaIdDeleteResponses];

export type SupersedeDocumentApiV1DocumentsDocumentIdSupersedePostData = {
    body: BodySupersedeDocumentApiV1DocumentsDocumentIdSupersedePost;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Document Id
         */
        document_id: string;
    };
    query?: never;
    url: '/api/v1/documents/{document_id}/supersede';
};

export type SupersedeDocumentApiV1DocumentsDocumentIdSupersedePostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The document is above the size ceiling.
     */
    413: ErrorEnvelope;
    /**
     * The file's content is not an accepted type.
     */
    415: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type SupersedeDocumentApiV1DocumentsDocumentIdSupersedePostError = SupersedeDocumentApiV1DocumentsDocumentIdSupersedePostErrors[keyof SupersedeDocumentApiV1DocumentsDocumentIdSupersedePostErrors];

export type SupersedeDocumentApiV1DocumentsDocumentIdSupersedePostResponses = {
    /**
     * Successful Response
     */
    201: DocumentResponse;
};

export type SupersedeDocumentApiV1DocumentsDocumentIdSupersedePostResponse = SupersedeDocumentApiV1DocumentsDocumentIdSupersedePostResponses[keyof SupersedeDocumentApiV1DocumentsDocumentIdSupersedePostResponses];

export type RunDiagnosticsApiV1EsocialDiagnosticsPostData = {
    /**
     * Body
     */
    body?: EsocialDiagnosticRequest | null;
    path?: never;
    query?: never;
    url: '/api/v1/esocial/diagnostics';
};

export type RunDiagnosticsApiV1EsocialDiagnosticsPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type RunDiagnosticsApiV1EsocialDiagnosticsPostError = RunDiagnosticsApiV1EsocialDiagnosticsPostErrors[keyof RunDiagnosticsApiV1EsocialDiagnosticsPostErrors];

export type RunDiagnosticsApiV1EsocialDiagnosticsPostResponses = {
    /**
     * Successful Response
     */
    200: EsocialDiagnosticResponse;
};

export type RunDiagnosticsApiV1EsocialDiagnosticsPostResponse = RunDiagnosticsApiV1EsocialDiagnosticsPostResponses[keyof RunDiagnosticsApiV1EsocialDiagnosticsPostResponses];

export type ListEventsApiV1EsocialEventsGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Company Id
         *
         * Only this company's events.
         */
        company_id?: string | null;
        /**
         * Event Type
         *
         * Only this eSocial event type.
         */
        event_type?: EsocialEventType | null;
        /**
         * Fetch Id
         *
         * Only the events one run staged.
         */
        fetch_id?: string | null;
        /**
         * Limit
         */
        limit?: number;
        /**
         * Offset
         */
        offset?: number;
    };
    url: '/api/v1/esocial/events';
};

export type ListEventsApiV1EsocialEventsGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ListEventsApiV1EsocialEventsGetError = ListEventsApiV1EsocialEventsGetErrors[keyof ListEventsApiV1EsocialEventsGetErrors];

export type ListEventsApiV1EsocialEventsGetResponses = {
    /**
     * Successful Response
     */
    200: EsocialEventListResponse;
};

export type ListEventsApiV1EsocialEventsGetResponse = ListEventsApiV1EsocialEventsGetResponses[keyof ListEventsApiV1EsocialEventsGetResponses];

export type DownloadEventSourceApiV1EsocialEventsEventIdSourceGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Event Id
         */
        event_id: string;
    };
    query?: never;
    url: '/api/v1/esocial/events/{event_id}/source';
};

export type DownloadEventSourceApiV1EsocialEventsEventIdSourceGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type DownloadEventSourceApiV1EsocialEventsEventIdSourceGetError = DownloadEventSourceApiV1EsocialEventsEventIdSourceGetErrors[keyof DownloadEventSourceApiV1EsocialEventsEventIdSourceGetErrors];

export type DownloadEventSourceApiV1EsocialEventsEventIdSourceGetResponses = {
    /**
     * The event as the employer filed it.
     */
    200: Blob | File;
};

export type DownloadEventSourceApiV1EsocialEventsEventIdSourceGetResponse = DownloadEventSourceApiV1EsocialEventsEventIdSourceGetResponses[keyof DownloadEventSourceApiV1EsocialEventsEventIdSourceGetResponses];

export type ListFetchesApiV1EsocialFetchesGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Company Id
         *
         * Only this company's runs.
         */
        company_id?: string | null;
        /**
         * Limit
         */
        limit?: number;
        /**
         * Offset
         */
        offset?: number;
    };
    url: '/api/v1/esocial/fetches';
};

export type ListFetchesApiV1EsocialFetchesGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ListFetchesApiV1EsocialFetchesGetError = ListFetchesApiV1EsocialFetchesGetErrors[keyof ListFetchesApiV1EsocialFetchesGetErrors];

export type ListFetchesApiV1EsocialFetchesGetResponses = {
    /**
     * Successful Response
     */
    200: EsocialFetchListResponse;
};

export type ListFetchesApiV1EsocialFetchesGetResponse = ListFetchesApiV1EsocialFetchesGetResponses[keyof ListFetchesApiV1EsocialFetchesGetResponses];

export type FetchEventsApiV1EsocialFetchesPostData = {
    body: EsocialFetchRequest;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/api/v1/esocial/fetches';
};

export type FetchEventsApiV1EsocialFetchesPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type FetchEventsApiV1EsocialFetchesPostError = FetchEventsApiV1EsocialFetchesPostErrors[keyof FetchEventsApiV1EsocialFetchesPostErrors];

export type FetchEventsApiV1EsocialFetchesPostResponses = {
    /**
     * Successful Response
     */
    200: EsocialFetchResultResponse;
};

export type FetchEventsApiV1EsocialFetchesPostResponse = FetchEventsApiV1EsocialFetchesPostResponses[keyof FetchEventsApiV1EsocialFetchesPostResponses];

export type ConfirmFetchApiV1EsocialFetchesFetchIdConfirmPostData = {
    body: EsocialFetchConfirmRequest;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Fetch Id
         */
        fetch_id: string;
    };
    query?: never;
    url: '/api/v1/esocial/fetches/{fetch_id}/confirm';
};

export type ConfirmFetchApiV1EsocialFetchesFetchIdConfirmPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ConfirmFetchApiV1EsocialFetchesFetchIdConfirmPostError = ConfirmFetchApiV1EsocialFetchesFetchIdConfirmPostErrors[keyof ConfirmFetchApiV1EsocialFetchesFetchIdConfirmPostErrors];

export type ConfirmFetchApiV1EsocialFetchesFetchIdConfirmPostResponses = {
    /**
     * Successful Response
     */
    200: EsocialFetchCommitResponse;
};

export type ConfirmFetchApiV1EsocialFetchesFetchIdConfirmPostResponse = ConfirmFetchApiV1EsocialFetchesFetchIdConfirmPostResponses[keyof ConfirmFetchApiV1EsocialFetchesFetchIdConfirmPostResponses];

export type ReadPortalBrowserApiV1EsocialPortalBrowserGetData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/esocial/portal-browser';
};

export type ReadPortalBrowserApiV1EsocialPortalBrowserGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ReadPortalBrowserApiV1EsocialPortalBrowserGetError = ReadPortalBrowserApiV1EsocialPortalBrowserGetErrors[keyof ReadPortalBrowserApiV1EsocialPortalBrowserGetErrors];

export type ReadPortalBrowserApiV1EsocialPortalBrowserGetResponses = {
    /**
     * Successful Response
     */
    200: EsocialPortalBrowserResponse;
};

export type ReadPortalBrowserApiV1EsocialPortalBrowserGetResponse = ReadPortalBrowserApiV1EsocialPortalBrowserGetResponses[keyof ReadPortalBrowserApiV1EsocialPortalBrowserGetResponses];

export type ListPortalSessionsApiV1EsocialPortalSessionsGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Limit
         */
        limit?: number;
        /**
         * Offset
         */
        offset?: number;
    };
    url: '/api/v1/esocial/portal-sessions';
};

export type ListPortalSessionsApiV1EsocialPortalSessionsGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ListPortalSessionsApiV1EsocialPortalSessionsGetError = ListPortalSessionsApiV1EsocialPortalSessionsGetErrors[keyof ListPortalSessionsApiV1EsocialPortalSessionsGetErrors];

export type ListPortalSessionsApiV1EsocialPortalSessionsGetResponses = {
    /**
     * Successful Response
     */
    200: EsocialPortalSessionListResponse;
};

export type ListPortalSessionsApiV1EsocialPortalSessionsGetResponse = ListPortalSessionsApiV1EsocialPortalSessionsGetResponses[keyof ListPortalSessionsApiV1EsocialPortalSessionsGetResponses];

export type RunPortalSessionApiV1EsocialPortalSessionsPostData = {
    body: EsocialPortalSessionRequest;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/api/v1/esocial/portal-sessions';
};

export type RunPortalSessionApiV1EsocialPortalSessionsPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type RunPortalSessionApiV1EsocialPortalSessionsPostError = RunPortalSessionApiV1EsocialPortalSessionsPostErrors[keyof RunPortalSessionApiV1EsocialPortalSessionsPostErrors];

export type RunPortalSessionApiV1EsocialPortalSessionsPostResponses = {
    /**
     * Successful Response
     */
    200: EsocialPortalSessionResponse;
};

export type RunPortalSessionApiV1EsocialPortalSessionsPostResponse = RunPortalSessionApiV1EsocialPortalSessionsPostResponses[keyof RunPortalSessionApiV1EsocialPortalSessionsPostResponses];

export type ReadPortalSessionApiV1EsocialPortalSessionsSessionIdGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Session Id
         */
        session_id: string;
    };
    query?: never;
    url: '/api/v1/esocial/portal-sessions/{session_id}';
};

export type ReadPortalSessionApiV1EsocialPortalSessionsSessionIdGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ReadPortalSessionApiV1EsocialPortalSessionsSessionIdGetError = ReadPortalSessionApiV1EsocialPortalSessionsSessionIdGetErrors[keyof ReadPortalSessionApiV1EsocialPortalSessionsSessionIdGetErrors];

export type ReadPortalSessionApiV1EsocialPortalSessionsSessionIdGetResponses = {
    /**
     * Successful Response
     */
    200: EsocialPortalSessionResponse;
};

export type ReadPortalSessionApiV1EsocialPortalSessionsSessionIdGetResponse = ReadPortalSessionApiV1EsocialPortalSessionsSessionIdGetResponses[keyof ReadPortalSessionApiV1EsocialPortalSessionsSessionIdGetResponses];

export type ReadPortalSessionActionScreenshotApiV1EsocialPortalSessionsSessionIdLoginActionsNumberGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Session Id
         */
        session_id: string;
        /**
         * Number
         *
         * The action's number in the login, from one.
         */
        number: number;
    };
    query?: never;
    url: '/api/v1/esocial/portal-sessions/{session_id}/login/actions/{number}';
};

export type ReadPortalSessionActionScreenshotApiV1EsocialPortalSessionsSessionIdLoginActionsNumberGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ReadPortalSessionActionScreenshotApiV1EsocialPortalSessionsSessionIdLoginActionsNumberGetError = ReadPortalSessionActionScreenshotApiV1EsocialPortalSessionsSessionIdLoginActionsNumberGetErrors[keyof ReadPortalSessionActionScreenshotApiV1EsocialPortalSessionsSessionIdLoginActionsNumberGetErrors];

export type ReadPortalSessionActionScreenshotApiV1EsocialPortalSessionsSessionIdLoginActionsNumberGetResponses = {
    /**
     * The screenshot exactly as it was sealed.
     */
    200: Blob | File;
};

export type ReadPortalSessionActionScreenshotApiV1EsocialPortalSessionsSessionIdLoginActionsNumberGetResponse = ReadPortalSessionActionScreenshotApiV1EsocialPortalSessionsSessionIdLoginActionsNumberGetResponses[keyof ReadPortalSessionActionScreenshotApiV1EsocialPortalSessionsSessionIdLoginActionsNumberGetResponses];

export type ReadPortalSessionEvidenceApiV1EsocialPortalSessionsSessionIdLoginArtefactGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Session Id
         */
        session_id: string;
        /**
         * Which artefact of the login to read.
         */
        artefact: Artefact;
    };
    query?: never;
    url: '/api/v1/esocial/portal-sessions/{session_id}/login/{artefact}';
};

export type ReadPortalSessionEvidenceApiV1EsocialPortalSessionsSessionIdLoginArtefactGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ReadPortalSessionEvidenceApiV1EsocialPortalSessionsSessionIdLoginArtefactGetError = ReadPortalSessionEvidenceApiV1EsocialPortalSessionsSessionIdLoginArtefactGetErrors[keyof ReadPortalSessionEvidenceApiV1EsocialPortalSessionsSessionIdLoginArtefactGetErrors];

export type ReadPortalSessionEvidenceApiV1EsocialPortalSessionsSessionIdLoginArtefactGetResponses = {
    /**
     * The artefact exactly as it was sealed.
     */
    200: Blob | File;
};

export type ReadPortalSessionEvidenceApiV1EsocialPortalSessionsSessionIdLoginArtefactGetResponse = ReadPortalSessionEvidenceApiV1EsocialPortalSessionsSessionIdLoginArtefactGetResponses[keyof ReadPortalSessionEvidenceApiV1EsocialPortalSessionsSessionIdLoginArtefactGetResponses];

export type ListPortalWalksApiV1EsocialPortalWalksGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Company Id
         *
         * Only this company's walks.
         */
        company_id?: string | null;
        /**
         * Limit
         */
        limit?: number;
        /**
         * Offset
         */
        offset?: number;
    };
    url: '/api/v1/esocial/portal-walks';
};

export type ListPortalWalksApiV1EsocialPortalWalksGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ListPortalWalksApiV1EsocialPortalWalksGetError = ListPortalWalksApiV1EsocialPortalWalksGetErrors[keyof ListPortalWalksApiV1EsocialPortalWalksGetErrors];

export type ListPortalWalksApiV1EsocialPortalWalksGetResponses = {
    /**
     * Successful Response
     */
    200: EsocialPortalWalkListResponse;
};

export type ListPortalWalksApiV1EsocialPortalWalksGetResponse = ListPortalWalksApiV1EsocialPortalWalksGetResponses[keyof ListPortalWalksApiV1EsocialPortalWalksGetResponses];

export type RunPortalWalkApiV1EsocialPortalWalksPostData = {
    body: EsocialPortalWalkRequest;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/api/v1/esocial/portal-walks';
};

export type RunPortalWalkApiV1EsocialPortalWalksPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type RunPortalWalkApiV1EsocialPortalWalksPostError = RunPortalWalkApiV1EsocialPortalWalksPostErrors[keyof RunPortalWalkApiV1EsocialPortalWalksPostErrors];

export type RunPortalWalkApiV1EsocialPortalWalksPostResponses = {
    /**
     * Successful Response
     */
    200: EsocialPortalWalkResponse;
};

export type RunPortalWalkApiV1EsocialPortalWalksPostResponse = RunPortalWalkApiV1EsocialPortalWalksPostResponses[keyof RunPortalWalkApiV1EsocialPortalWalksPostResponses];

export type ReadPortalWalkApiV1EsocialPortalWalksRunIdGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Run Id
         */
        run_id: string;
    };
    query?: never;
    url: '/api/v1/esocial/portal-walks/{run_id}';
};

export type ReadPortalWalkApiV1EsocialPortalWalksRunIdGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ReadPortalWalkApiV1EsocialPortalWalksRunIdGetError = ReadPortalWalkApiV1EsocialPortalWalksRunIdGetErrors[keyof ReadPortalWalkApiV1EsocialPortalWalksRunIdGetErrors];

export type ReadPortalWalkApiV1EsocialPortalWalksRunIdGetResponses = {
    /**
     * Successful Response
     */
    200: EsocialPortalWalkResponse;
};

export type ReadPortalWalkApiV1EsocialPortalWalksRunIdGetResponse = ReadPortalWalkApiV1EsocialPortalWalksRunIdGetResponses[keyof ReadPortalWalkApiV1EsocialPortalWalksRunIdGetResponses];

export type DownloadPortalWalkBundleApiV1EsocialPortalWalksRunIdBundleGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Run Id
         */
        run_id: string;
    };
    query?: never;
    url: '/api/v1/esocial/portal-walks/{run_id}/bundle';
};

export type DownloadPortalWalkBundleApiV1EsocialPortalWalksRunIdBundleGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type DownloadPortalWalkBundleApiV1EsocialPortalWalksRunIdBundleGetError = DownloadPortalWalkBundleApiV1EsocialPortalWalksRunIdBundleGetErrors[keyof DownloadPortalWalkBundleApiV1EsocialPortalWalksRunIdBundleGetErrors];

export type DownloadPortalWalkBundleApiV1EsocialPortalWalksRunIdBundleGetResponses = {
    /**
     * The whole walk as one zip.
     */
    200: Blob | File;
};

export type DownloadPortalWalkBundleApiV1EsocialPortalWalksRunIdBundleGetResponse = DownloadPortalWalkBundleApiV1EsocialPortalWalksRunIdBundleGetResponses[keyof DownloadPortalWalkBundleApiV1EsocialPortalWalksRunIdBundleGetResponses];

export type ReadPortalWalkActionScreenshotApiV1EsocialPortalWalksRunIdStagesIndexActionsNumberGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Run Id
         */
        run_id: string;
        /**
         * Index
         *
         * The stage's position in the walk, from zero.
         */
        index: number;
        /**
         * Number
         *
         * The action's number in the stage, from one.
         */
        number: number;
    };
    query?: never;
    url: '/api/v1/esocial/portal-walks/{run_id}/stages/{index}/actions/{number}';
};

export type ReadPortalWalkActionScreenshotApiV1EsocialPortalWalksRunIdStagesIndexActionsNumberGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ReadPortalWalkActionScreenshotApiV1EsocialPortalWalksRunIdStagesIndexActionsNumberGetError = ReadPortalWalkActionScreenshotApiV1EsocialPortalWalksRunIdStagesIndexActionsNumberGetErrors[keyof ReadPortalWalkActionScreenshotApiV1EsocialPortalWalksRunIdStagesIndexActionsNumberGetErrors];

export type ReadPortalWalkActionScreenshotApiV1EsocialPortalWalksRunIdStagesIndexActionsNumberGetResponses = {
    /**
     * The screenshot exactly as it was sealed.
     */
    200: Blob | File;
};

export type ReadPortalWalkActionScreenshotApiV1EsocialPortalWalksRunIdStagesIndexActionsNumberGetResponse = ReadPortalWalkActionScreenshotApiV1EsocialPortalWalksRunIdStagesIndexActionsNumberGetResponses[keyof ReadPortalWalkActionScreenshotApiV1EsocialPortalWalksRunIdStagesIndexActionsNumberGetResponses];

export type ReadPortalWalkEvidenceApiV1EsocialPortalWalksRunIdStagesIndexArtefactGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Run Id
         */
        run_id: string;
        /**
         * Index
         *
         * The stage's position in the walk, from zero.
         */
        index: number;
        /**
         * Which artefact of that stage to read.
         */
        artefact: Artefact;
    };
    query?: never;
    url: '/api/v1/esocial/portal-walks/{run_id}/stages/{index}/{artefact}';
};

export type ReadPortalWalkEvidenceApiV1EsocialPortalWalksRunIdStagesIndexArtefactGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ReadPortalWalkEvidenceApiV1EsocialPortalWalksRunIdStagesIndexArtefactGetError = ReadPortalWalkEvidenceApiV1EsocialPortalWalksRunIdStagesIndexArtefactGetErrors[keyof ReadPortalWalkEvidenceApiV1EsocialPortalWalksRunIdStagesIndexArtefactGetErrors];

export type ReadPortalWalkEvidenceApiV1EsocialPortalWalksRunIdStagesIndexArtefactGetResponses = {
    /**
     * The artefact exactly as it was sealed.
     */
    200: Blob | File;
};

export type ReadPortalWalkEvidenceApiV1EsocialPortalWalksRunIdStagesIndexArtefactGetResponse = ReadPortalWalkEvidenceApiV1EsocialPortalWalksRunIdStagesIndexArtefactGetResponses[keyof ReadPortalWalkEvidenceApiV1EsocialPortalWalksRunIdStagesIndexArtefactGetResponses];

export type RunProbeApiV1EsocialProbesPostData = {
    body: EsocialProbeRequest;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/api/v1/esocial/probes';
};

export type RunProbeApiV1EsocialProbesPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type RunProbeApiV1EsocialProbesPostError = RunProbeApiV1EsocialProbesPostErrors[keyof RunProbeApiV1EsocialProbesPostErrors];

export type RunProbeApiV1EsocialProbesPostResponses = {
    /**
     * Successful Response
     */
    200: EsocialProbeResponse;
};

export type RunProbeApiV1EsocialProbesPostResponse = RunProbeApiV1EsocialProbesPostResponses[keyof RunProbeApiV1EsocialProbesPostResponses];

export type ReadProbeEvidenceApiV1EsocialProbesRunIdStepsStepIndexDirectionGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Run Id
         */
        run_id: string;
        /**
         * Step Index
         *
         * The step's position in the run, counting from zero, as the result reports it.
         */
        step_index: number;
        /**
         * Which half of the exchange to read back.
         */
        direction: ProbeDirection;
    };
    query?: never;
    url: '/api/v1/esocial/probes/{run_id}/steps/{step_index}/{direction}';
};

export type ReadProbeEvidenceApiV1EsocialProbesRunIdStepsStepIndexDirectionGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ReadProbeEvidenceApiV1EsocialProbesRunIdStepsStepIndexDirectionGetError = ReadProbeEvidenceApiV1EsocialProbesRunIdStepsStepIndexDirectionGetErrors[keyof ReadProbeEvidenceApiV1EsocialProbesRunIdStepsStepIndexDirectionGetErrors];

export type ReadProbeEvidenceApiV1EsocialProbesRunIdStepsStepIndexDirectionGetResponses = {
    /**
     * The bytes exactly as they were exchanged.
     */
    200: Blob | File;
};

export type ReadProbeEvidenceApiV1EsocialProbesRunIdStepsStepIndexDirectionGetResponse = ReadProbeEvidenceApiV1EsocialProbesRunIdStepsStepIndexDirectionGetResponses[keyof ReadProbeEvidenceApiV1EsocialProbesRunIdStepsStepIndexDirectionGetResponses];

export type ListWorkerListsApiV1EsocialWorkerListsGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Company Id
         *
         * Only this company's captures.
         */
        company_id?: string | null;
        /**
         * Competence
         *
         * Only captures of this month, `YYYY-MM`.
         */
        competence?: string | null;
        /**
         * Limit
         */
        limit?: number;
        /**
         * Offset
         */
        offset?: number;
    };
    url: '/api/v1/esocial/worker-lists';
};

export type ListWorkerListsApiV1EsocialWorkerListsGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ListWorkerListsApiV1EsocialWorkerListsGetError = ListWorkerListsApiV1EsocialWorkerListsGetErrors[keyof ListWorkerListsApiV1EsocialWorkerListsGetErrors];

export type ListWorkerListsApiV1EsocialWorkerListsGetResponses = {
    /**
     * Successful Response
     */
    200: EsocialWorkerListListResponse;
};

export type ListWorkerListsApiV1EsocialWorkerListsGetResponse = ListWorkerListsApiV1EsocialWorkerListsGetResponses[keyof ListWorkerListsApiV1EsocialWorkerListsGetResponses];

export type CaptureWorkerListApiV1EsocialWorkerListsPostData = {
    body: EsocialWorkerListRequest;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/api/v1/esocial/worker-lists';
};

export type CaptureWorkerListApiV1EsocialWorkerListsPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type CaptureWorkerListApiV1EsocialWorkerListsPostError = CaptureWorkerListApiV1EsocialWorkerListsPostErrors[keyof CaptureWorkerListApiV1EsocialWorkerListsPostErrors];

export type CaptureWorkerListApiV1EsocialWorkerListsPostResponses = {
    /**
     * Successful Response
     */
    200: EsocialWorkerListDetailResponse;
};

export type CaptureWorkerListApiV1EsocialWorkerListsPostResponse = CaptureWorkerListApiV1EsocialWorkerListsPostResponses[keyof CaptureWorkerListApiV1EsocialWorkerListsPostResponses];

export type ReadWorkerListApiV1EsocialWorkerListsListIdGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * List Id
         */
        list_id: string;
    };
    query?: never;
    url: '/api/v1/esocial/worker-lists/{list_id}';
};

export type ReadWorkerListApiV1EsocialWorkerListsListIdGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ReadWorkerListApiV1EsocialWorkerListsListIdGetError = ReadWorkerListApiV1EsocialWorkerListsListIdGetErrors[keyof ReadWorkerListApiV1EsocialWorkerListsListIdGetErrors];

export type ReadWorkerListApiV1EsocialWorkerListsListIdGetResponses = {
    /**
     * Successful Response
     */
    200: EsocialWorkerListDetailResponse;
};

export type ReadWorkerListApiV1EsocialWorkerListsListIdGetResponse = ReadWorkerListApiV1EsocialWorkerListsListIdGetResponses[keyof ReadWorkerListApiV1EsocialWorkerListsListIdGetResponses];

export type ListWorkerReportsApiV1EsocialWorkerReportsGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Company Id
         *
         * Only this company's reports.
         */
        company_id?: string | null;
        /**
         * Status
         *
         * Only reports in this status.
         */
        status?: EsocialWorkerReportStatus | null;
        /**
         * Limit
         */
        limit?: number;
        /**
         * Offset
         */
        offset?: number;
    };
    url: '/api/v1/esocial/worker-reports';
};

export type ListWorkerReportsApiV1EsocialWorkerReportsGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ListWorkerReportsApiV1EsocialWorkerReportsGetError = ListWorkerReportsApiV1EsocialWorkerReportsGetErrors[keyof ListWorkerReportsApiV1EsocialWorkerReportsGetErrors];

export type ListWorkerReportsApiV1EsocialWorkerReportsGetResponses = {
    /**
     * Successful Response
     */
    200: EsocialWorkerReportListResponse;
};

export type ListWorkerReportsApiV1EsocialWorkerReportsGetResponse = ListWorkerReportsApiV1EsocialWorkerReportsGetResponses[keyof ListWorkerReportsApiV1EsocialWorkerReportsGetResponses];

export type RequestWorkerReportApiV1EsocialWorkerReportsPostData = {
    body: EsocialWorkerReportRequest;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/api/v1/esocial/worker-reports';
};

export type RequestWorkerReportApiV1EsocialWorkerReportsPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type RequestWorkerReportApiV1EsocialWorkerReportsPostError = RequestWorkerReportApiV1EsocialWorkerReportsPostErrors[keyof RequestWorkerReportApiV1EsocialWorkerReportsPostErrors];

export type RequestWorkerReportApiV1EsocialWorkerReportsPostResponses = {
    /**
     * Successful Response
     */
    200: EsocialWorkerReportResponse;
};

export type RequestWorkerReportApiV1EsocialWorkerReportsPostResponse = RequestWorkerReportApiV1EsocialWorkerReportsPostResponses[keyof RequestWorkerReportApiV1EsocialWorkerReportsPostResponses];

export type ReadWorkerReportApiV1EsocialWorkerReportsReportIdGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Report Id
         */
        report_id: string;
    };
    query?: {
        /**
         * Limit
         */
        limit?: number;
        /**
         * Offset
         */
        offset?: number;
    };
    url: '/api/v1/esocial/worker-reports/{report_id}';
};

export type ReadWorkerReportApiV1EsocialWorkerReportsReportIdGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ReadWorkerReportApiV1EsocialWorkerReportsReportIdGetError = ReadWorkerReportApiV1EsocialWorkerReportsReportIdGetErrors[keyof ReadWorkerReportApiV1EsocialWorkerReportsReportIdGetErrors];

export type ReadWorkerReportApiV1EsocialWorkerReportsReportIdGetResponses = {
    /**
     * Successful Response
     */
    200: EsocialWorkerReportDetailResponse;
};

export type ReadWorkerReportApiV1EsocialWorkerReportsReportIdGetResponse = ReadWorkerReportApiV1EsocialWorkerReportsReportIdGetResponses[keyof ReadWorkerReportApiV1EsocialWorkerReportsReportIdGetResponses];

export type CollectWorkerReportApiV1EsocialWorkerReportsReportIdCollectPostData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Report Id
         */
        report_id: string;
    };
    query?: never;
    url: '/api/v1/esocial/worker-reports/{report_id}/collect';
};

export type CollectWorkerReportApiV1EsocialWorkerReportsReportIdCollectPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type CollectWorkerReportApiV1EsocialWorkerReportsReportIdCollectPostError = CollectWorkerReportApiV1EsocialWorkerReportsReportIdCollectPostErrors[keyof CollectWorkerReportApiV1EsocialWorkerReportsReportIdCollectPostErrors];

export type CollectWorkerReportApiV1EsocialWorkerReportsReportIdCollectPostResponses = {
    /**
     * Successful Response
     */
    200: EsocialWorkerReportDetailResponse;
};

export type CollectWorkerReportApiV1EsocialWorkerReportsReportIdCollectPostResponse = CollectWorkerReportApiV1EsocialWorkerReportsReportIdCollectPostResponses[keyof CollectWorkerReportApiV1EsocialWorkerReportsReportIdCollectPostResponses];

export type ReadWorkerReportFileApiV1EsocialWorkerReportsReportIdFileGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Report Id
         */
        report_id: string;
    };
    query?: never;
    url: '/api/v1/esocial/worker-reports/{report_id}/file';
};

export type ReadWorkerReportFileApiV1EsocialWorkerReportsReportIdFileGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ReadWorkerReportFileApiV1EsocialWorkerReportsReportIdFileGetError = ReadWorkerReportFileApiV1EsocialWorkerReportsReportIdFileGetErrors[keyof ReadWorkerReportFileApiV1EsocialWorkerReportsReportIdFileGetErrors];

export type ReadWorkerReportFileApiV1EsocialWorkerReportsReportIdFileGetResponses = {
    /**
     * The report exactly as it was sealed.
     */
    200: Blob | File;
};

export type ReadWorkerReportFileApiV1EsocialWorkerReportsReportIdFileGetResponse = ReadWorkerReportFileApiV1EsocialWorkerReportsReportIdFileGetResponses[keyof ReadWorkerReportFileApiV1EsocialWorkerReportsReportIdFileGetResponses];

export type ListGroupsApiV1GroupsGetData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Q
         *
         * Text to find in the name or the slug.
         */
        q?: string | null;
        /**
         * Name
         *
         * Text the name contains.
         */
        name?: string | null;
        /**
         * Slug
         *
         * Text the slug contains.
         */
        slug?: string | null;
        /**
         * The column to sort by: `name`, `slug`, `member_count` or `created_at`.
         */
        sort?: CompanyGroupListSort;
        /**
         * `asc` or `desc`.
         */
        direction?: SortDirection;
        /**
         * Limit
         *
         * At most this many groups. Absent: every one, unpaged.
         */
        limit?: number | null;
        /**
         * Offset
         *
         * How many matching groups to skip. Read only with `limit`.
         */
        offset?: number;
        /**
         * Created From
         *
         * Only those created on or after this São Paulo calendar day.
         */
        created_from?: string | null;
        /**
         * Created To
         *
         * Only those created on or before this São Paulo calendar day.
         */
        created_to?: string | null;
    };
    url: '/api/v1/groups';
};

export type ListGroupsApiV1GroupsGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ListGroupsApiV1GroupsGetError = ListGroupsApiV1GroupsGetErrors[keyof ListGroupsApiV1GroupsGetErrors];

export type ListGroupsApiV1GroupsGetResponses = {
    /**
     * Successful Response
     */
    200: CompanyGroupListResponse;
};

export type ListGroupsApiV1GroupsGetResponse = ListGroupsApiV1GroupsGetResponses[keyof ListGroupsApiV1GroupsGetResponses];

export type CreateGroupApiV1GroupsPostData = {
    body: CompanyGroupCreate;
    path?: never;
    query?: never;
    url: '/api/v1/groups';
};

export type CreateGroupApiV1GroupsPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type CreateGroupApiV1GroupsPostError = CreateGroupApiV1GroupsPostErrors[keyof CreateGroupApiV1GroupsPostErrors];

export type CreateGroupApiV1GroupsPostResponses = {
    /**
     * Successful Response
     */
    201: CompanyGroupDetailResponse;
};

export type CreateGroupApiV1GroupsPostResponse = CreateGroupApiV1GroupsPostResponses[keyof CreateGroupApiV1GroupsPostResponses];

export type GetGroupApiV1GroupsGroupIdGetData = {
    body?: never;
    path: {
        /**
         * Group Id
         */
        group_id: string;
    };
    query?: never;
    url: '/api/v1/groups/{group_id}';
};

export type GetGroupApiV1GroupsGroupIdGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type GetGroupApiV1GroupsGroupIdGetError = GetGroupApiV1GroupsGroupIdGetErrors[keyof GetGroupApiV1GroupsGroupIdGetErrors];

export type GetGroupApiV1GroupsGroupIdGetResponses = {
    /**
     * Successful Response
     */
    200: CompanyGroupDetailResponse;
};

export type GetGroupApiV1GroupsGroupIdGetResponse = GetGroupApiV1GroupsGroupIdGetResponses[keyof GetGroupApiV1GroupsGroupIdGetResponses];

export type RenameGroupApiV1GroupsGroupIdPatchData = {
    body: CompanyGroupRename;
    path: {
        /**
         * Group Id
         */
        group_id: string;
    };
    query?: never;
    url: '/api/v1/groups/{group_id}';
};

export type RenameGroupApiV1GroupsGroupIdPatchErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type RenameGroupApiV1GroupsGroupIdPatchError = RenameGroupApiV1GroupsGroupIdPatchErrors[keyof RenameGroupApiV1GroupsGroupIdPatchErrors];

export type RenameGroupApiV1GroupsGroupIdPatchResponses = {
    /**
     * Successful Response
     */
    200: CompanyGroupDetailResponse;
};

export type RenameGroupApiV1GroupsGroupIdPatchResponse = RenameGroupApiV1GroupsGroupIdPatchResponses[keyof RenameGroupApiV1GroupsGroupIdPatchResponses];

export type ImportGroupBaselineApiV1GroupsGroupIdBaselineImportsPostData = {
    body: BodyImportGroupBaselineApiV1GroupsGroupIdBaselineImportsPost;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Group Id
         */
        group_id: string;
    };
    query?: never;
    url: '/api/v1/groups/{group_id}/baseline-imports';
};

export type ImportGroupBaselineApiV1GroupsGroupIdBaselineImportsPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ImportGroupBaselineApiV1GroupsGroupIdBaselineImportsPostError = ImportGroupBaselineApiV1GroupsGroupIdBaselineImportsPostErrors[keyof ImportGroupBaselineApiV1GroupsGroupIdBaselineImportsPostErrors];

export type ImportGroupBaselineApiV1GroupsGroupIdBaselineImportsPostResponses = {
    /**
     * Successful Response
     */
    201: GroupBaselineImportResponse;
};

export type ImportGroupBaselineApiV1GroupsGroupIdBaselineImportsPostResponse = ImportGroupBaselineApiV1GroupsGroupIdBaselineImportsPostResponses[keyof ImportGroupBaselineApiV1GroupsGroupIdBaselineImportsPostResponses];

export type SetGroupFileNameLabelApiV1GroupsGroupIdFileNameLabelPostData = {
    body: FileNameLabelUpdate;
    path: {
        /**
         * Group Id
         */
        group_id: string;
    };
    query?: never;
    url: '/api/v1/groups/{group_id}/file-name-label';
};

export type SetGroupFileNameLabelApiV1GroupsGroupIdFileNameLabelPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type SetGroupFileNameLabelApiV1GroupsGroupIdFileNameLabelPostError = SetGroupFileNameLabelApiV1GroupsGroupIdFileNameLabelPostErrors[keyof SetGroupFileNameLabelApiV1GroupsGroupIdFileNameLabelPostErrors];

export type SetGroupFileNameLabelApiV1GroupsGroupIdFileNameLabelPostResponses = {
    /**
     * Successful Response
     */
    200: CompanyGroupDetailResponse;
};

export type SetGroupFileNameLabelApiV1GroupsGroupIdFileNameLabelPostResponse = SetGroupFileNameLabelApiV1GroupsGroupIdFileNameLabelPostResponses[keyof SetGroupFileNameLabelApiV1GroupsGroupIdFileNameLabelPostResponses];

export type LookUpGroupInsuredPersonApiV1GroupsGroupIdInsuredPeopleLookupPostData = {
    body: InsuredPersonLookupRequest;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Group Id
         */
        group_id: string;
    };
    query?: never;
    url: '/api/v1/groups/{group_id}/insured-people/lookup';
};

export type LookUpGroupInsuredPersonApiV1GroupsGroupIdInsuredPeopleLookupPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type LookUpGroupInsuredPersonApiV1GroupsGroupIdInsuredPeopleLookupPostError = LookUpGroupInsuredPersonApiV1GroupsGroupIdInsuredPeopleLookupPostErrors[keyof LookUpGroupInsuredPersonApiV1GroupsGroupIdInsuredPeopleLookupPostErrors];

export type LookUpGroupInsuredPersonApiV1GroupsGroupIdInsuredPeopleLookupPostResponses = {
    /**
     * Successful Response
     */
    200: GroupInsuredPersonLookupResponse;
};

export type LookUpGroupInsuredPersonApiV1GroupsGroupIdInsuredPeopleLookupPostResponse = LookUpGroupInsuredPersonApiV1GroupsGroupIdInsuredPeopleLookupPostResponses[keyof LookUpGroupInsuredPersonApiV1GroupsGroupIdInsuredPeopleLookupPostResponses];

export type ProjectGroupRosterApiV1GroupsGroupIdRosterGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Group Id
         */
        group_id: string;
    };
    query?: {
        /**
         * As Of
         *
         * Calendar date to project, `YYYY-MM-DD`, in America/Sao_Paulo. Defaults to today there. Past dates reproduce what was true then; future dates apply terminations that have already been filed but have not yet taken effect.
         */
        as_of?: string | null;
    };
    url: '/api/v1/groups/{group_id}/roster';
};

export type ProjectGroupRosterApiV1GroupsGroupIdRosterGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ProjectGroupRosterApiV1GroupsGroupIdRosterGetError = ProjectGroupRosterApiV1GroupsGroupIdRosterGetErrors[keyof ProjectGroupRosterApiV1GroupsGroupIdRosterGetErrors];

export type ProjectGroupRosterApiV1GroupsGroupIdRosterGetResponses = {
    /**
     * Successful Response
     */
    200: GroupRosterResponse;
};

export type ProjectGroupRosterApiV1GroupsGroupIdRosterGetResponse = ProjectGroupRosterApiV1GroupsGroupIdRosterGetResponses[keyof ProjectGroupRosterApiV1GroupsGroupIdRosterGetResponses];

export type ListGroupSpreadsheetUploadsApiV1GroupsGroupIdSpreadsheetUploadsGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Group Id
         */
        group_id: string;
    };
    query?: {
        /**
         * Limit
         */
        limit?: number;
        /**
         * Offset
         */
        offset?: number;
    };
    url: '/api/v1/groups/{group_id}/spreadsheet-uploads';
};

export type ListGroupSpreadsheetUploadsApiV1GroupsGroupIdSpreadsheetUploadsGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ListGroupSpreadsheetUploadsApiV1GroupsGroupIdSpreadsheetUploadsGetError = ListGroupSpreadsheetUploadsApiV1GroupsGroupIdSpreadsheetUploadsGetErrors[keyof ListGroupSpreadsheetUploadsApiV1GroupsGroupIdSpreadsheetUploadsGetErrors];

export type ListGroupSpreadsheetUploadsApiV1GroupsGroupIdSpreadsheetUploadsGetResponses = {
    /**
     * Successful Response
     */
    200: GroupSpreadsheetUploadListResponse;
};

export type ListGroupSpreadsheetUploadsApiV1GroupsGroupIdSpreadsheetUploadsGetResponse = ListGroupSpreadsheetUploadsApiV1GroupsGroupIdSpreadsheetUploadsGetResponses[keyof ListGroupSpreadsheetUploadsApiV1GroupsGroupIdSpreadsheetUploadsGetResponses];

export type UploadGroupSpreadsheetApiV1GroupsGroupIdSpreadsheetUploadsPostData = {
    body: BodyUploadGroupSpreadsheetApiV1GroupsGroupIdSpreadsheetUploadsPost;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Group Id
         */
        group_id: string;
    };
    query?: never;
    url: '/api/v1/groups/{group_id}/spreadsheet-uploads';
};

export type UploadGroupSpreadsheetApiV1GroupsGroupIdSpreadsheetUploadsPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The file was refused as a whole, so no row was read and nothing was stored. `error.code` is `validation_error` and each entry of `details` carries `reason`, one of: `missing_columns` (one detail per column the sheet lacks, named in `column` as the template labels it), `empty_file`, `unreadable_format` (neither a readable `.xlsx` nor a CSV), `legacy_xls` (a pre-2007 `.xls`), `no_header`, `no_rows` (a header and nothing under it), `too_many_rows` and `file_too_large` (both with the ceiling in `limit`: data rows, or bytes). Branch on `reason`; `message` is English and may be reworded.
     *
     * A malformed form field is the platform's ordinary `validation_error`, with `field` set and no `reason`.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type UploadGroupSpreadsheetApiV1GroupsGroupIdSpreadsheetUploadsPostError = UploadGroupSpreadsheetApiV1GroupsGroupIdSpreadsheetUploadsPostErrors[keyof UploadGroupSpreadsheetApiV1GroupsGroupIdSpreadsheetUploadsPostErrors];

export type UploadGroupSpreadsheetApiV1GroupsGroupIdSpreadsheetUploadsPostResponses = {
    /**
     * Successful Response
     */
    201: GroupSpreadsheetUploadPreviewResponse;
};

export type UploadGroupSpreadsheetApiV1GroupsGroupIdSpreadsheetUploadsPostResponse = UploadGroupSpreadsheetApiV1GroupsGroupIdSpreadsheetUploadsPostResponses[keyof UploadGroupSpreadsheetApiV1GroupsGroupIdSpreadsheetUploadsPostResponses];

export type GetGroupSpreadsheetUploadApiV1GroupsGroupIdSpreadsheetUploadsUploadIdGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Group Id
         */
        group_id: string;
        /**
         * Upload Id
         */
        upload_id: string;
    };
    query?: {
        /**
         * Outcome
         *
         * Only rows with this outcome. Omit for the whole sheet.
         */
        outcome?: RowOutcome | null;
    };
    url: '/api/v1/groups/{group_id}/spreadsheet-uploads/{upload_id}';
};

export type GetGroupSpreadsheetUploadApiV1GroupsGroupIdSpreadsheetUploadsUploadIdGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type GetGroupSpreadsheetUploadApiV1GroupsGroupIdSpreadsheetUploadsUploadIdGetError = GetGroupSpreadsheetUploadApiV1GroupsGroupIdSpreadsheetUploadsUploadIdGetErrors[keyof GetGroupSpreadsheetUploadApiV1GroupsGroupIdSpreadsheetUploadsUploadIdGetErrors];

export type GetGroupSpreadsheetUploadApiV1GroupsGroupIdSpreadsheetUploadsUploadIdGetResponses = {
    /**
     * Successful Response
     */
    200: GroupSpreadsheetUploadPreviewResponse;
};

export type GetGroupSpreadsheetUploadApiV1GroupsGroupIdSpreadsheetUploadsUploadIdGetResponse = GetGroupSpreadsheetUploadApiV1GroupsGroupIdSpreadsheetUploadsUploadIdGetResponses[keyof GetGroupSpreadsheetUploadApiV1GroupsGroupIdSpreadsheetUploadsUploadIdGetResponses];

export type ListGroupSpreadsheetUploadAbsencesApiV1GroupsGroupIdSpreadsheetUploadsUploadIdAbsencesGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Group Id
         */
        group_id: string;
        /**
         * Upload Id
         */
        upload_id: string;
    };
    query?: never;
    url: '/api/v1/groups/{group_id}/spreadsheet-uploads/{upload_id}/absences';
};

export type ListGroupSpreadsheetUploadAbsencesApiV1GroupsGroupIdSpreadsheetUploadsUploadIdAbsencesGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ListGroupSpreadsheetUploadAbsencesApiV1GroupsGroupIdSpreadsheetUploadsUploadIdAbsencesGetError = ListGroupSpreadsheetUploadAbsencesApiV1GroupsGroupIdSpreadsheetUploadsUploadIdAbsencesGetErrors[keyof ListGroupSpreadsheetUploadAbsencesApiV1GroupsGroupIdSpreadsheetUploadsUploadIdAbsencesGetErrors];

export type ListGroupSpreadsheetUploadAbsencesApiV1GroupsGroupIdSpreadsheetUploadsUploadIdAbsencesGetResponses = {
    /**
     * Response List Group Spreadsheet Upload Absences Api V1 Groups  Group Id  Spreadsheet Uploads  Upload Id  Absences Get
     *
     * Successful Response
     */
    200: Array<GroupSpreadsheetUploadAbsenceResponse>;
};

export type ListGroupSpreadsheetUploadAbsencesApiV1GroupsGroupIdSpreadsheetUploadsUploadIdAbsencesGetResponse = ListGroupSpreadsheetUploadAbsencesApiV1GroupsGroupIdSpreadsheetUploadsUploadIdAbsencesGetResponses[keyof ListGroupSpreadsheetUploadAbsencesApiV1GroupsGroupIdSpreadsheetUploadsUploadIdAbsencesGetResponses];

export type ConfirmGroupSpreadsheetUploadApiV1GroupsGroupIdSpreadsheetUploadsUploadIdConfirmPostData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Group Id
         */
        group_id: string;
        /**
         * Upload Id
         */
        upload_id: string;
    };
    query?: never;
    url: '/api/v1/groups/{group_id}/spreadsheet-uploads/{upload_id}/confirm';
};

export type ConfirmGroupSpreadsheetUploadApiV1GroupsGroupIdSpreadsheetUploadsUploadIdConfirmPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ConfirmGroupSpreadsheetUploadApiV1GroupsGroupIdSpreadsheetUploadsUploadIdConfirmPostError = ConfirmGroupSpreadsheetUploadApiV1GroupsGroupIdSpreadsheetUploadsUploadIdConfirmPostErrors[keyof ConfirmGroupSpreadsheetUploadApiV1GroupsGroupIdSpreadsheetUploadsUploadIdConfirmPostErrors];

export type ConfirmGroupSpreadsheetUploadApiV1GroupsGroupIdSpreadsheetUploadsUploadIdConfirmPostResponses = {
    /**
     * Successful Response
     */
    200: GroupSpreadsheetUploadPreviewResponse;
};

export type ConfirmGroupSpreadsheetUploadApiV1GroupsGroupIdSpreadsheetUploadsUploadIdConfirmPostResponse = ConfirmGroupSpreadsheetUploadApiV1GroupsGroupIdSpreadsheetUploadsUploadIdConfirmPostResponses[keyof ConfirmGroupSpreadsheetUploadApiV1GroupsGroupIdSpreadsheetUploadsUploadIdConfirmPostResponses];

export type ListGroupSpreadsheetUploadRowsApiV1GroupsGroupIdSpreadsheetUploadsUploadIdRowsGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Group Id
         */
        group_id: string;
        /**
         * Upload Id
         */
        upload_id: string;
    };
    query?: {
        /**
         * Outcome
         *
         * Only rows with this outcome.
         */
        outcome?: RowOutcome | null;
    };
    url: '/api/v1/groups/{group_id}/spreadsheet-uploads/{upload_id}/rows';
};

export type ListGroupSpreadsheetUploadRowsApiV1GroupsGroupIdSpreadsheetUploadsUploadIdRowsGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ListGroupSpreadsheetUploadRowsApiV1GroupsGroupIdSpreadsheetUploadsUploadIdRowsGetError = ListGroupSpreadsheetUploadRowsApiV1GroupsGroupIdSpreadsheetUploadsUploadIdRowsGetErrors[keyof ListGroupSpreadsheetUploadRowsApiV1GroupsGroupIdSpreadsheetUploadsUploadIdRowsGetErrors];

export type ListGroupSpreadsheetUploadRowsApiV1GroupsGroupIdSpreadsheetUploadsUploadIdRowsGetResponses = {
    /**
     * Response List Group Spreadsheet Upload Rows Api V1 Groups  Group Id  Spreadsheet Uploads  Upload Id  Rows Get
     *
     * Successful Response
     */
    200: Array<GroupSpreadsheetUploadRowResponse>;
};

export type ListGroupSpreadsheetUploadRowsApiV1GroupsGroupIdSpreadsheetUploadsUploadIdRowsGetResponse = ListGroupSpreadsheetUploadRowsApiV1GroupsGroupIdSpreadsheetUploadsUploadIdRowsGetResponses[keyof ListGroupSpreadsheetUploadRowsApiV1GroupsGroupIdSpreadsheetUploadsUploadIdRowsGetResponses];

export type DownloadGroupSpreadsheetUploadSourceApiV1GroupsGroupIdSpreadsheetUploadsUploadIdSourceGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Group Id
         */
        group_id: string;
        /**
         * Upload Id
         */
        upload_id: string;
    };
    query?: never;
    url: '/api/v1/groups/{group_id}/spreadsheet-uploads/{upload_id}/source';
};

export type DownloadGroupSpreadsheetUploadSourceApiV1GroupsGroupIdSpreadsheetUploadsUploadIdSourceGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type DownloadGroupSpreadsheetUploadSourceApiV1GroupsGroupIdSpreadsheetUploadsUploadIdSourceGetError = DownloadGroupSpreadsheetUploadSourceApiV1GroupsGroupIdSpreadsheetUploadsUploadIdSourceGetErrors[keyof DownloadGroupSpreadsheetUploadSourceApiV1GroupsGroupIdSpreadsheetUploadsUploadIdSourceGetErrors];

export type DownloadGroupSpreadsheetUploadSourceApiV1GroupsGroupIdSpreadsheetUploadsUploadIdSourceGetResponses = {
    /**
     * The file as uploaded.
     */
    200: Blob | File;
};

export type DownloadGroupSpreadsheetUploadSourceApiV1GroupsGroupIdSpreadsheetUploadsUploadIdSourceGetResponse = DownloadGroupSpreadsheetUploadSourceApiV1GroupsGroupIdSpreadsheetUploadsUploadIdSourceGetResponses[keyof DownloadGroupSpreadsheetUploadSourceApiV1GroupsGroupIdSpreadsheetUploadsUploadIdSourceGetResponses];

export type HealthApiV1HealthGetData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/health';
};

export type HealthApiV1HealthGetErrors = {
    /**
     * A dependency is unhealthy.
     */
    503: HealthResponse;
};

export type HealthApiV1HealthGetError = HealthApiV1HealthGetErrors[keyof HealthApiV1HealthGetErrors];

export type HealthApiV1HealthGetResponses = {
    /**
     * Successful Response
     */
    200: HealthResponse;
};

export type HealthApiV1HealthGetResponse = HealthApiV1HealthGetResponses[keyof HealthApiV1HealthGetResponses];

export type ListInsurersApiV1InsurersGetData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/insurers';
};

export type ListInsurersApiV1InsurersGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ListInsurersApiV1InsurersGetError = ListInsurersApiV1InsurersGetErrors[keyof ListInsurersApiV1InsurersGetErrors];

export type ListInsurersApiV1InsurersGetResponses = {
    /**
     * Successful Response
     */
    200: InsurerListResponse;
};

export type ListInsurersApiV1InsurersGetResponse = ListInsurersApiV1InsurersGetResponses[keyof ListInsurersApiV1InsurersGetResponses];

export type CreateInsurerApiV1InsurersPostData = {
    body: InsurerCreate;
    path?: never;
    query?: never;
    url: '/api/v1/insurers';
};

export type CreateInsurerApiV1InsurersPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type CreateInsurerApiV1InsurersPostError = CreateInsurerApiV1InsurersPostErrors[keyof CreateInsurerApiV1InsurersPostErrors];

export type CreateInsurerApiV1InsurersPostResponses = {
    /**
     * Successful Response
     */
    201: InsurerResponse;
};

export type CreateInsurerApiV1InsurersPostResponse = CreateInsurerApiV1InsurersPostResponses[keyof CreateInsurerApiV1InsurersPostResponses];

export type GetInsurerApiV1InsurersInsurerIdGetData = {
    body?: never;
    path: {
        /**
         * Insurer Id
         */
        insurer_id: string;
    };
    query?: never;
    url: '/api/v1/insurers/{insurer_id}';
};

export type GetInsurerApiV1InsurersInsurerIdGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type GetInsurerApiV1InsurersInsurerIdGetError = GetInsurerApiV1InsurersInsurerIdGetErrors[keyof GetInsurerApiV1InsurersInsurerIdGetErrors];

export type GetInsurerApiV1InsurersInsurerIdGetResponses = {
    /**
     * Successful Response
     */
    200: InsurerResponse;
};

export type GetInsurerApiV1InsurersInsurerIdGetResponse = GetInsurerApiV1InsurersInsurerIdGetResponses[keyof GetInsurerApiV1InsurersInsurerIdGetResponses];

export type SetInsurerLayoutApiV1InsurersInsurerIdLayoutPutData = {
    body: InsurerLayoutUpdate;
    path: {
        /**
         * Insurer Id
         */
        insurer_id: string;
    };
    query?: never;
    url: '/api/v1/insurers/{insurer_id}/layout';
};

export type SetInsurerLayoutApiV1InsurersInsurerIdLayoutPutErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type SetInsurerLayoutApiV1InsurersInsurerIdLayoutPutError = SetInsurerLayoutApiV1InsurersInsurerIdLayoutPutErrors[keyof SetInsurerLayoutApiV1InsurersInsurerIdLayoutPutErrors];

export type SetInsurerLayoutApiV1InsurersInsurerIdLayoutPutResponses = {
    /**
     * Successful Response
     */
    200: InsurerResponse;
};

export type SetInsurerLayoutApiV1InsurersInsurerIdLayoutPutResponse = SetInsurerLayoutApiV1InsurersInsurerIdLayoutPutResponses[keyof SetInsurerLayoutApiV1InsurersInsurerIdLayoutPutResponses];

export type ListMachineClientsApiV1MachineClientsGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/api/v1/machine-clients';
};

export type ListMachineClientsApiV1MachineClientsGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ListMachineClientsApiV1MachineClientsGetError = ListMachineClientsApiV1MachineClientsGetErrors[keyof ListMachineClientsApiV1MachineClientsGetErrors];

export type ListMachineClientsApiV1MachineClientsGetResponses = {
    /**
     * Successful Response
     */
    200: MachineClientListResponse;
};

export type ListMachineClientsApiV1MachineClientsGetResponse = ListMachineClientsApiV1MachineClientsGetResponses[keyof ListMachineClientsApiV1MachineClientsGetResponses];

export type IssueMachineClientApiV1MachineClientsPostData = {
    body: MachineClientCreate;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/api/v1/machine-clients';
};

export type IssueMachineClientApiV1MachineClientsPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type IssueMachineClientApiV1MachineClientsPostError = IssueMachineClientApiV1MachineClientsPostErrors[keyof IssueMachineClientApiV1MachineClientsPostErrors];

export type IssueMachineClientApiV1MachineClientsPostResponses = {
    /**
     * Successful Response
     */
    201: MachineClientCreatedResponse;
};

export type IssueMachineClientApiV1MachineClientsPostResponse = IssueMachineClientApiV1MachineClientsPostResponses[keyof IssueMachineClientApiV1MachineClientsPostResponses];

export type GetMachineClientApiV1MachineClientsMachineClientIdGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Machine Client Id
         */
        machine_client_id: string;
    };
    query?: never;
    url: '/api/v1/machine-clients/{machine_client_id}';
};

export type GetMachineClientApiV1MachineClientsMachineClientIdGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type GetMachineClientApiV1MachineClientsMachineClientIdGetError = GetMachineClientApiV1MachineClientsMachineClientIdGetErrors[keyof GetMachineClientApiV1MachineClientsMachineClientIdGetErrors];

export type GetMachineClientApiV1MachineClientsMachineClientIdGetResponses = {
    /**
     * Successful Response
     */
    200: MachineClientResponse;
};

export type GetMachineClientApiV1MachineClientsMachineClientIdGetResponse = GetMachineClientApiV1MachineClientsMachineClientIdGetResponses[keyof GetMachineClientApiV1MachineClientsMachineClientIdGetResponses];

export type RevokeMachineClientApiV1MachineClientsMachineClientIdRevocationPostData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Machine Client Id
         */
        machine_client_id: string;
    };
    query?: never;
    url: '/api/v1/machine-clients/{machine_client_id}/revocation';
};

export type RevokeMachineClientApiV1MachineClientsMachineClientIdRevocationPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type RevokeMachineClientApiV1MachineClientsMachineClientIdRevocationPostError = RevokeMachineClientApiV1MachineClientsMachineClientIdRevocationPostErrors[keyof RevokeMachineClientApiV1MachineClientsMachineClientIdRevocationPostErrors];

export type RevokeMachineClientApiV1MachineClientsMachineClientIdRevocationPostResponses = {
    /**
     * Successful Response
     */
    200: MachineClientResponse;
};

export type RevokeMachineClientApiV1MachineClientsMachineClientIdRevocationPostResponse = RevokeMachineClientApiV1MachineClientsMachineClientIdRevocationPostResponses[keyof RevokeMachineClientApiV1MachineClientsMachineClientIdRevocationPostResponses];

export type RotateMachineClientApiV1MachineClientsMachineClientIdRotationPostData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Machine Client Id
         */
        machine_client_id: string;
    };
    query?: never;
    url: '/api/v1/machine-clients/{machine_client_id}/rotation';
};

export type RotateMachineClientApiV1MachineClientsMachineClientIdRotationPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type RotateMachineClientApiV1MachineClientsMachineClientIdRotationPostError = RotateMachineClientApiV1MachineClientsMachineClientIdRotationPostErrors[keyof RotateMachineClientApiV1MachineClientsMachineClientIdRotationPostErrors];

export type RotateMachineClientApiV1MachineClientsMachineClientIdRotationPostResponses = {
    /**
     * Successful Response
     */
    200: MachineClientCreatedResponse;
};

export type RotateMachineClientApiV1MachineClientsMachineClientIdRotationPostResponse = RotateMachineClientApiV1MachineClientsMachineClientIdRotationPostResponses[keyof RotateMachineClientApiV1MachineClientsMachineClientIdRotationPostResponses];

export type ListMessagesApiV1MailboxMessagesGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Status
         *
         * Only messages in this state.
         */
        status?: MailMessageStatus | null;
        /**
         * Outstanding
         *
         * Only the messages waiting for somebody: unrouted and refused.
         */
        outstanding?: boolean;
        /**
         * Company Id
         *
         * Only messages that turned out to belong to this company.
         */
        company_id?: string | null;
        /**
         * Limit
         */
        limit?: number;
        /**
         * Offset
         */
        offset?: number;
    };
    url: '/api/v1/mailbox/messages';
};

export type ListMessagesApiV1MailboxMessagesGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ListMessagesApiV1MailboxMessagesGetError = ListMessagesApiV1MailboxMessagesGetErrors[keyof ListMessagesApiV1MailboxMessagesGetErrors];

export type ListMessagesApiV1MailboxMessagesGetResponses = {
    /**
     * Successful Response
     */
    200: MailMessageListResponse;
};

export type ListMessagesApiV1MailboxMessagesGetResponse = ListMessagesApiV1MailboxMessagesGetResponses[keyof ListMessagesApiV1MailboxMessagesGetResponses];

export type GetMessageApiV1MailboxMessagesMessageIdGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Message Id
         */
        message_id: string;
    };
    query?: {
        /**
         * Attempt
         *
         * Which run of ingestion to read. Omit for the latest.
         */
        attempt?: number | null;
    };
    url: '/api/v1/mailbox/messages/{message_id}';
};

export type GetMessageApiV1MailboxMessagesMessageIdGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type GetMessageApiV1MailboxMessagesMessageIdGetError = GetMessageApiV1MailboxMessagesMessageIdGetErrors[keyof GetMessageApiV1MailboxMessagesMessageIdGetErrors];

export type GetMessageApiV1MailboxMessagesMessageIdGetResponses = {
    /**
     * Successful Response
     */
    200: MailMessageDetailResponse;
};

export type GetMessageApiV1MailboxMessagesMessageIdGetResponse = GetMessageApiV1MailboxMessagesMessageIdGetResponses[keyof GetMessageApiV1MailboxMessagesMessageIdGetResponses];

export type DiscardMessageApiV1MailboxMessagesMessageIdDiscardPostData = {
    body: MailMessageDiscard;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Message Id
         */
        message_id: string;
    };
    query?: never;
    url: '/api/v1/mailbox/messages/{message_id}/discard';
};

export type DiscardMessageApiV1MailboxMessagesMessageIdDiscardPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type DiscardMessageApiV1MailboxMessagesMessageIdDiscardPostError = DiscardMessageApiV1MailboxMessagesMessageIdDiscardPostErrors[keyof DiscardMessageApiV1MailboxMessagesMessageIdDiscardPostErrors];

export type DiscardMessageApiV1MailboxMessagesMessageIdDiscardPostResponses = {
    /**
     * Successful Response
     */
    200: MailMessageResponse;
};

export type DiscardMessageApiV1MailboxMessagesMessageIdDiscardPostResponse = DiscardMessageApiV1MailboxMessagesMessageIdDiscardPostResponses[keyof DiscardMessageApiV1MailboxMessagesMessageIdDiscardPostResponses];

export type RetryMessageApiV1MailboxMessagesMessageIdRetryPostData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Message Id
         */
        message_id: string;
    };
    query?: never;
    url: '/api/v1/mailbox/messages/{message_id}/retry';
};

export type RetryMessageApiV1MailboxMessagesMessageIdRetryPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type RetryMessageApiV1MailboxMessagesMessageIdRetryPostError = RetryMessageApiV1MailboxMessagesMessageIdRetryPostErrors[keyof RetryMessageApiV1MailboxMessagesMessageIdRetryPostErrors];

export type RetryMessageApiV1MailboxMessagesMessageIdRetryPostResponses = {
    /**
     * Successful Response
     */
    200: MailMessageDetailResponse;
};

export type RetryMessageApiV1MailboxMessagesMessageIdRetryPostResponse = RetryMessageApiV1MailboxMessagesMessageIdRetryPostResponses[keyof RetryMessageApiV1MailboxMessagesMessageIdRetryPostResponses];

export type DownloadMessageSourceApiV1MailboxMessagesMessageIdSourceGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Message Id
         */
        message_id: string;
    };
    query?: never;
    url: '/api/v1/mailbox/messages/{message_id}/source';
};

export type DownloadMessageSourceApiV1MailboxMessagesMessageIdSourceGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type DownloadMessageSourceApiV1MailboxMessagesMessageIdSourceGetError = DownloadMessageSourceApiV1MailboxMessagesMessageIdSourceGetErrors[keyof DownloadMessageSourceApiV1MailboxMessagesMessageIdSourceGetErrors];

export type DownloadMessageSourceApiV1MailboxMessagesMessageIdSourceGetResponses = {
    /**
     * The message as it arrived.
     */
    200: Blob | File;
};

export type DownloadMessageSourceApiV1MailboxMessagesMessageIdSourceGetResponse = DownloadMessageSourceApiV1MailboxMessagesMessageIdSourceGetResponses[keyof DownloadMessageSourceApiV1MailboxMessagesMessageIdSourceGetResponses];

export type PollMailboxApiV1MailboxPollsPostData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Limit
         *
         * How many messages to take. Omit for the configured batch size.
         */
        limit?: number | null;
    };
    url: '/api/v1/mailbox/polls';
};

export type PollMailboxApiV1MailboxPollsPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type PollMailboxApiV1MailboxPollsPostError = PollMailboxApiV1MailboxPollsPostErrors[keyof PollMailboxApiV1MailboxPollsPostErrors];

export type PollMailboxApiV1MailboxPollsPostResponses = {
    /**
     * Successful Response
     */
    200: MailboxPollResponse;
};

export type PollMailboxApiV1MailboxPollsPostResponse = PollMailboxApiV1MailboxPollsPostResponses[keyof PollMailboxApiV1MailboxPollsPostResponses];

export type ListSendersApiV1MailboxSendersGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Contract Id
         *
         * Only this contract's.
         */
        contract_id?: string | null;
        /**
         * Include Revoked
         *
         * Include withdrawn registrations.
         */
        include_revoked?: boolean;
        /**
         * Limit
         */
        limit?: number;
        /**
         * Offset
         */
        offset?: number;
    };
    url: '/api/v1/mailbox/senders';
};

export type ListSendersApiV1MailboxSendersGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ListSendersApiV1MailboxSendersGetError = ListSendersApiV1MailboxSendersGetErrors[keyof ListSendersApiV1MailboxSendersGetErrors];

export type ListSendersApiV1MailboxSendersGetResponses = {
    /**
     * Successful Response
     */
    200: MailboxSenderListResponse;
};

export type ListSendersApiV1MailboxSendersGetResponse = ListSendersApiV1MailboxSendersGetResponses[keyof ListSendersApiV1MailboxSendersGetResponses];

export type RegisterSenderApiV1MailboxSendersPostData = {
    body: MailboxSenderCreate;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/api/v1/mailbox/senders';
};

export type RegisterSenderApiV1MailboxSendersPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type RegisterSenderApiV1MailboxSendersPostError = RegisterSenderApiV1MailboxSendersPostErrors[keyof RegisterSenderApiV1MailboxSendersPostErrors];

export type RegisterSenderApiV1MailboxSendersPostResponses = {
    /**
     * Successful Response
     */
    201: MailboxSenderResponse;
};

export type RegisterSenderApiV1MailboxSendersPostResponse = RegisterSenderApiV1MailboxSendersPostResponses[keyof RegisterSenderApiV1MailboxSendersPostResponses];

export type RevokeSenderApiV1MailboxSendersSenderIdRevocationPostData = {
    /**
     * Body
     */
    body?: MailboxSenderRevocation | null;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Sender Id
         */
        sender_id: string;
    };
    query?: never;
    url: '/api/v1/mailbox/senders/{sender_id}/revocation';
};

export type RevokeSenderApiV1MailboxSendersSenderIdRevocationPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type RevokeSenderApiV1MailboxSendersSenderIdRevocationPostError = RevokeSenderApiV1MailboxSendersSenderIdRevocationPostErrors[keyof RevokeSenderApiV1MailboxSendersSenderIdRevocationPostErrors];

export type RevokeSenderApiV1MailboxSendersSenderIdRevocationPostResponses = {
    /**
     * Successful Response
     */
    200: MailboxSenderResponse;
};

export type RevokeSenderApiV1MailboxSendersSenderIdRevocationPostResponse = RevokeSenderApiV1MailboxSendersSenderIdRevocationPostResponses[keyof RevokeSenderApiV1MailboxSendersSenderIdRevocationPostResponses];

export type ListNotificationsApiV1NotificationsGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Contract Id
         *
         * Only what is about this contract.
         */
        contract_id?: string | null;
        /**
         * Event
         *
         * Only this kind of news.
         */
        event?: NotificationEvent | null;
        /**
         * Unread Only
         *
         * Only what nobody at the company has marked read.
         */
        unread_only?: boolean;
        /**
         * Limit
         */
        limit?: number;
        /**
         * Offset
         */
        offset?: number;
    };
    url: '/api/v1/notifications';
};

export type ListNotificationsApiV1NotificationsGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ListNotificationsApiV1NotificationsGetError = ListNotificationsApiV1NotificationsGetErrors[keyof ListNotificationsApiV1NotificationsGetErrors];

export type ListNotificationsApiV1NotificationsGetResponses = {
    /**
     * Successful Response
     */
    200: NotificationListResponse;
};

export type ListNotificationsApiV1NotificationsGetResponse = ListNotificationsApiV1NotificationsGetResponses[keyof ListNotificationsApiV1NotificationsGetResponses];

export type MarkAllNotificationsReadApiV1NotificationsReadPostData = {
    body?: never;
    headers?: {
        /**
         * Idempotency-Key
         *
         * Opaque, caller-chosen key that makes this write safe to retry. Repeating a request with the same key returns the original response and marks nothing a second time.
         */
        'Idempotency-Key'?: string | null;
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Contract Id
         *
         * Only what is about this contract.
         */
        contract_id?: string | null;
    };
    url: '/api/v1/notifications/read';
};

export type MarkAllNotificationsReadApiV1NotificationsReadPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type MarkAllNotificationsReadApiV1NotificationsReadPostError = MarkAllNotificationsReadApiV1NotificationsReadPostErrors[keyof MarkAllNotificationsReadApiV1NotificationsReadPostErrors];

export type MarkAllNotificationsReadApiV1NotificationsReadPostResponses = {
    /**
     * Successful Response
     */
    200: NotificationsReadResponse;
};

export type MarkAllNotificationsReadApiV1NotificationsReadPostResponse = MarkAllNotificationsReadApiV1NotificationsReadPostResponses[keyof MarkAllNotificationsReadApiV1NotificationsReadPostResponses];

export type MarkNotificationReadApiV1NotificationsNotificationIdReadPostData = {
    body?: never;
    headers?: {
        /**
         * Idempotency-Key
         *
         * Opaque, caller-chosen key that makes this write safe to retry. Repeating a request with the same key returns the original response and marks nothing a second time.
         */
        'Idempotency-Key'?: string | null;
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Notification Id
         */
        notification_id: string;
    };
    query?: never;
    url: '/api/v1/notifications/{notification_id}/read';
};

export type MarkNotificationReadApiV1NotificationsNotificationIdReadPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type MarkNotificationReadApiV1NotificationsNotificationIdReadPostError = MarkNotificationReadApiV1NotificationsNotificationIdReadPostErrors[keyof MarkNotificationReadApiV1NotificationsNotificationIdReadPostErrors];

export type MarkNotificationReadApiV1NotificationsNotificationIdReadPostResponses = {
    /**
     * Successful Response
     */
    200: NotificationResponse;
};

export type MarkNotificationReadApiV1NotificationsNotificationIdReadPostResponse = MarkNotificationReadApiV1NotificationsNotificationIdReadPostResponses[keyof MarkNotificationReadApiV1NotificationsNotificationIdReadPostResponses];

export type ListNoticesApiV1OffCycleNoticesGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Contract Id
         *
         * Only this contract's.
         */
        contract_id?: string | null;
        /**
         * Status
         *
         * Only notices in this state.
         */
        status?: OffCycleNoticeStatus | null;
        /**
         * Cycle Month
         *
         * Only notices whose movements land on this cycle's file. Every cycle's when omitted.
         */
        cycle_month?: string | null;
        /**
         * Limit
         */
        limit?: number;
        /**
         * Offset
         */
        offset?: number;
    };
    url: '/api/v1/off-cycle-notices';
};

export type ListNoticesApiV1OffCycleNoticesGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ListNoticesApiV1OffCycleNoticesGetError = ListNoticesApiV1OffCycleNoticesGetErrors[keyof ListNoticesApiV1OffCycleNoticesGetErrors];

export type ListNoticesApiV1OffCycleNoticesGetResponses = {
    /**
     * Successful Response
     */
    200: NoticeListResponse;
};

export type ListNoticesApiV1OffCycleNoticesGetResponse = ListNoticesApiV1OffCycleNoticesGetResponses[keyof ListNoticesApiV1OffCycleNoticesGetResponses];

export type RaiseNoticeApiV1OffCycleNoticesContractsContractIdPostData = {
    body: NoticeRaise;
    headers?: {
        /**
         * Idempotency-Key
         *
         * Opaque, caller-chosen key that makes this write safe to retry. Repeating a request with the same key returns the original response and records nothing a second time. Reusing a key with a different body is rejected with `idempotency_key_reused`.
         */
        'Idempotency-Key'?: string | null;
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Contract Id
         */
        contract_id: string;
    };
    query?: never;
    url: '/api/v1/off-cycle-notices/contracts/{contract_id}';
};

export type RaiseNoticeApiV1OffCycleNoticesContractsContractIdPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type RaiseNoticeApiV1OffCycleNoticesContractsContractIdPostError = RaiseNoticeApiV1OffCycleNoticesContractsContractIdPostErrors[keyof RaiseNoticeApiV1OffCycleNoticesContractsContractIdPostErrors];

export type RaiseNoticeApiV1OffCycleNoticesContractsContractIdPostResponses = {
    /**
     * Successful Response
     */
    201: NoticeResponse;
};

export type RaiseNoticeApiV1OffCycleNoticesContractsContractIdPostResponse = RaiseNoticeApiV1OffCycleNoticesContractsContractIdPostResponses[keyof RaiseNoticeApiV1OffCycleNoticesContractsContractIdPostResponses];

export type ReadEligibleApiV1OffCycleNoticesContractsContractIdEligibleGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Contract Id
         */
        contract_id: string;
    };
    query?: {
        /**
         * Limit
         */
        limit?: number;
    };
    url: '/api/v1/off-cycle-notices/contracts/{contract_id}/eligible';
};

export type ReadEligibleApiV1OffCycleNoticesContractsContractIdEligibleGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ReadEligibleApiV1OffCycleNoticesContractsContractIdEligibleGetError = ReadEligibleApiV1OffCycleNoticesContractsContractIdEligibleGetErrors[keyof ReadEligibleApiV1OffCycleNoticesContractsContractIdEligibleGetErrors];

export type ReadEligibleApiV1OffCycleNoticesContractsContractIdEligibleGetResponses = {
    /**
     * Successful Response
     */
    200: EligibleMovementListResponse;
};

export type ReadEligibleApiV1OffCycleNoticesContractsContractIdEligibleGetResponse = ReadEligibleApiV1OffCycleNoticesContractsContractIdEligibleGetResponses[keyof ReadEligibleApiV1OffCycleNoticesContractsContractIdEligibleGetResponses];

export type ReadNoticeApiV1OffCycleNoticesNoticeIdGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Notice Id
         */
        notice_id: string;
    };
    query?: never;
    url: '/api/v1/off-cycle-notices/{notice_id}';
};

export type ReadNoticeApiV1OffCycleNoticesNoticeIdGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ReadNoticeApiV1OffCycleNoticesNoticeIdGetError = ReadNoticeApiV1OffCycleNoticesNoticeIdGetErrors[keyof ReadNoticeApiV1OffCycleNoticesNoticeIdGetErrors];

export type ReadNoticeApiV1OffCycleNoticesNoticeIdGetResponses = {
    /**
     * Successful Response
     */
    200: NoticeResponse;
};

export type ReadNoticeApiV1OffCycleNoticesNoticeIdGetResponse = ReadNoticeApiV1OffCycleNoticesNoticeIdGetResponses[keyof ReadNoticeApiV1OffCycleNoticesNoticeIdGetResponses];

export type MarkNoticeFailedApiV1OffCycleNoticesNoticeIdFailedPostData = {
    body: NoticeFailed;
    headers?: {
        /**
         * Idempotency-Key
         *
         * Opaque, caller-chosen key that makes this write safe to retry. Repeating a request with the same key returns the original response and records nothing a second time. Reusing a key with a different body is rejected with `idempotency_key_reused`.
         */
        'Idempotency-Key'?: string | null;
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Notice Id
         */
        notice_id: string;
    };
    query?: never;
    url: '/api/v1/off-cycle-notices/{notice_id}/failed';
};

export type MarkNoticeFailedApiV1OffCycleNoticesNoticeIdFailedPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type MarkNoticeFailedApiV1OffCycleNoticesNoticeIdFailedPostError = MarkNoticeFailedApiV1OffCycleNoticesNoticeIdFailedPostErrors[keyof MarkNoticeFailedApiV1OffCycleNoticesNoticeIdFailedPostErrors];

export type MarkNoticeFailedApiV1OffCycleNoticesNoticeIdFailedPostResponses = {
    /**
     * Successful Response
     */
    200: NoticeResponse;
};

export type MarkNoticeFailedApiV1OffCycleNoticesNoticeIdFailedPostResponse = MarkNoticeFailedApiV1OffCycleNoticesNoticeIdFailedPostResponses[keyof MarkNoticeFailedApiV1OffCycleNoticesNoticeIdFailedPostResponses];

export type DownloadNoticeFileApiV1OffCycleNoticesNoticeIdFileGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Notice Id
         */
        notice_id: string;
    };
    query?: never;
    url: '/api/v1/off-cycle-notices/{notice_id}/file';
};

export type DownloadNoticeFileApiV1OffCycleNoticesNoticeIdFileGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type DownloadNoticeFileApiV1OffCycleNoticesNoticeIdFileGetError = DownloadNoticeFileApiV1OffCycleNoticesNoticeIdFileGetErrors[keyof DownloadNoticeFileApiV1OffCycleNoticesNoticeIdFileGetErrors];

export type DownloadNoticeFileApiV1OffCycleNoticesNoticeIdFileGetResponses = {
    /**
     * The composed notice.
     */
    200: Blob | File;
};

export type DownloadNoticeFileApiV1OffCycleNoticesNoticeIdFileGetResponse = DownloadNoticeFileApiV1OffCycleNoticesNoticeIdFileGetResponses[keyof DownloadNoticeFileApiV1OffCycleNoticesNoticeIdFileGetResponses];

export type MarkNoticeSentApiV1OffCycleNoticesNoticeIdSentPostData = {
    body: NoticeSent;
    headers?: {
        /**
         * Idempotency-Key
         *
         * Opaque, caller-chosen key that makes this write safe to retry. Repeating a request with the same key returns the original response and records nothing a second time. Reusing a key with a different body is rejected with `idempotency_key_reused`.
         */
        'Idempotency-Key'?: string | null;
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Notice Id
         */
        notice_id: string;
    };
    query?: never;
    url: '/api/v1/off-cycle-notices/{notice_id}/sent';
};

export type MarkNoticeSentApiV1OffCycleNoticesNoticeIdSentPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type MarkNoticeSentApiV1OffCycleNoticesNoticeIdSentPostError = MarkNoticeSentApiV1OffCycleNoticesNoticeIdSentPostErrors[keyof MarkNoticeSentApiV1OffCycleNoticesNoticeIdSentPostErrors];

export type MarkNoticeSentApiV1OffCycleNoticesNoticeIdSentPostResponses = {
    /**
     * Successful Response
     */
    200: NoticeResponse;
};

export type MarkNoticeSentApiV1OffCycleNoticesNoticeIdSentPostResponse = MarkNoticeSentApiV1OffCycleNoticesNoticeIdSentPostResponses[keyof MarkNoticeSentApiV1OffCycleNoticesNoticeIdSentPostResponses];

export type ReadCycleApiV1ReviewCyclesContractIdGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Contract Id
         */
        contract_id: string;
    };
    query?: {
        /**
         * Cycle Month
         *
         * The cycle to read, written `YYYY-MM`. Defaults to the current month in São Paulo, which is the cutoff an analyst is working on.
         */
        cycle_month?: string | null;
    };
    url: '/api/v1/review/cycles/{contract_id}';
};

export type ReadCycleApiV1ReviewCyclesContractIdGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ReadCycleApiV1ReviewCyclesContractIdGetError = ReadCycleApiV1ReviewCyclesContractIdGetErrors[keyof ReadCycleApiV1ReviewCyclesContractIdGetErrors];

export type ReadCycleApiV1ReviewCyclesContractIdGetResponses = {
    /**
     * Successful Response
     */
    200: ReviewCycleResponse;
};

export type ReadCycleApiV1ReviewCyclesContractIdGetResponse = ReadCycleApiV1ReviewCyclesContractIdGetResponses[keyof ReadCycleApiV1ReviewCyclesContractIdGetResponses];

export type ReadApprovalsApiV1ReviewCyclesContractIdApprovalsGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Contract Id
         */
        contract_id: string;
    };
    query?: {
        /**
         * Cycle Month
         *
         * Only this cycle's sign-offs. Every cycle's when omitted.
         */
        cycle_month?: string | null;
    };
    url: '/api/v1/review/cycles/{contract_id}/approvals';
};

export type ReadApprovalsApiV1ReviewCyclesContractIdApprovalsGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ReadApprovalsApiV1ReviewCyclesContractIdApprovalsGetError = ReadApprovalsApiV1ReviewCyclesContractIdApprovalsGetErrors[keyof ReadApprovalsApiV1ReviewCyclesContractIdApprovalsGetErrors];

export type ReadApprovalsApiV1ReviewCyclesContractIdApprovalsGetResponses = {
    /**
     * Successful Response
     */
    200: CycleApprovalListResponse;
};

export type ReadApprovalsApiV1ReviewCyclesContractIdApprovalsGetResponse = ReadApprovalsApiV1ReviewCyclesContractIdApprovalsGetResponses[keyof ReadApprovalsApiV1ReviewCyclesContractIdApprovalsGetResponses];

export type ApproveCycleApiV1ReviewCyclesContractIdApprovePostData = {
    body: CycleApprovalRequest;
    headers?: {
        /**
         * Idempotency-Key
         *
         * Opaque, caller-chosen key that makes this write safe to retry. Repeating a request with the same key returns the original response and decides nothing a second time. Reusing a key with a different body is rejected with `idempotency_key_reused`.
         */
        'Idempotency-Key'?: string | null;
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Contract Id
         */
        contract_id: string;
    };
    query?: never;
    url: '/api/v1/review/cycles/{contract_id}/approve';
};

export type ApproveCycleApiV1ReviewCyclesContractIdApprovePostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ApproveCycleApiV1ReviewCyclesContractIdApprovePostError = ApproveCycleApiV1ReviewCyclesContractIdApprovePostErrors[keyof ApproveCycleApiV1ReviewCyclesContractIdApprovePostErrors];

export type ApproveCycleApiV1ReviewCyclesContractIdApprovePostResponses = {
    /**
     * Successful Response
     */
    200: CycleApprovalResponse;
};

export type ApproveCycleApiV1ReviewCyclesContractIdApprovePostResponse = ApproveCycleApiV1ReviewCyclesContractIdApprovePostResponses[keyof ApproveCycleApiV1ReviewCyclesContractIdApprovePostResponses];

export type ReadLateArrivalsApiV1ReviewCyclesContractIdLateArrivalsGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Contract Id
         */
        contract_id: string;
    };
    query?: {
        /**
         * Cycle Month
         *
         * The cycle to read, written `YYYY-MM`. Defaults to the current month in São Paulo, which is the cutoff an analyst is working on.
         */
        cycle_month?: string | null;
    };
    url: '/api/v1/review/cycles/{contract_id}/late-arrivals';
};

export type ReadLateArrivalsApiV1ReviewCyclesContractIdLateArrivalsGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ReadLateArrivalsApiV1ReviewCyclesContractIdLateArrivalsGetError = ReadLateArrivalsApiV1ReviewCyclesContractIdLateArrivalsGetErrors[keyof ReadLateArrivalsApiV1ReviewCyclesContractIdLateArrivalsGetErrors];

export type ReadLateArrivalsApiV1ReviewCyclesContractIdLateArrivalsGetResponses = {
    /**
     * Successful Response
     */
    200: ReviewItemListResponse;
};

export type ReadLateArrivalsApiV1ReviewCyclesContractIdLateArrivalsGetResponse = ReadLateArrivalsApiV1ReviewCyclesContractIdLateArrivalsGetResponses[keyof ReadLateArrivalsApiV1ReviewCyclesContractIdLateArrivalsGetResponses];

export type ReadPreviewApiV1ReviewCyclesContractIdPreviewGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Contract Id
         */
        contract_id: string;
    };
    query?: {
        /**
         * Cycle Month
         *
         * The cycle to read, written `YYYY-MM`. Defaults to the current month in São Paulo, which is the cutoff an analyst is working on.
         */
        cycle_month?: string | null;
    };
    url: '/api/v1/review/cycles/{contract_id}/preview';
};

export type ReadPreviewApiV1ReviewCyclesContractIdPreviewGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ReadPreviewApiV1ReviewCyclesContractIdPreviewGetError = ReadPreviewApiV1ReviewCyclesContractIdPreviewGetErrors[keyof ReadPreviewApiV1ReviewCyclesContractIdPreviewGetErrors];

export type ReadPreviewApiV1ReviewCyclesContractIdPreviewGetResponses = {
    /**
     * Successful Response
     */
    200: CyclePreviewResponse;
};

export type ReadPreviewApiV1ReviewCyclesContractIdPreviewGetResponse = ReadPreviewApiV1ReviewCyclesContractIdPreviewGetResponses[keyof ReadPreviewApiV1ReviewCyclesContractIdPreviewGetResponses];

export type PreviewCycleApiV1ReviewCyclesContractIdPreviewPostData = {
    body?: never;
    headers?: {
        /**
         * Idempotency-Key
         *
         * Opaque, caller-chosen key that makes this write safe to retry. Repeating a request with the same key returns the original response and decides nothing a second time. Reusing a key with a different body is rejected with `idempotency_key_reused`.
         */
        'Idempotency-Key'?: string | null;
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Contract Id
         */
        contract_id: string;
    };
    query?: {
        /**
         * Cycle Month
         *
         * The cycle to read, written `YYYY-MM`. Defaults to the current month in São Paulo, which is the cutoff an analyst is working on.
         */
        cycle_month?: string | null;
    };
    url: '/api/v1/review/cycles/{contract_id}/preview';
};

export type PreviewCycleApiV1ReviewCyclesContractIdPreviewPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type PreviewCycleApiV1ReviewCyclesContractIdPreviewPostError = PreviewCycleApiV1ReviewCyclesContractIdPreviewPostErrors[keyof PreviewCycleApiV1ReviewCyclesContractIdPreviewPostErrors];

export type PreviewCycleApiV1ReviewCyclesContractIdPreviewPostResponses = {
    /**
     * Successful Response
     */
    200: CyclePreviewResponse;
};

export type PreviewCycleApiV1ReviewCyclesContractIdPreviewPostResponse = PreviewCycleApiV1ReviewCyclesContractIdPreviewPostResponses[keyof PreviewCycleApiV1ReviewCyclesContractIdPreviewPostResponses];

export type ReopenCycleApiV1ReviewCyclesContractIdReopenPostData = {
    body: CycleReopenRequest;
    headers?: {
        /**
         * Idempotency-Key
         *
         * Opaque, caller-chosen key that makes this write safe to retry. Repeating a request with the same key returns the original response and decides nothing a second time. Reusing a key with a different body is rejected with `idempotency_key_reused`.
         */
        'Idempotency-Key'?: string | null;
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Contract Id
         */
        contract_id: string;
    };
    query?: never;
    url: '/api/v1/review/cycles/{contract_id}/reopen';
};

export type ReopenCycleApiV1ReviewCyclesContractIdReopenPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ReopenCycleApiV1ReviewCyclesContractIdReopenPostError = ReopenCycleApiV1ReviewCyclesContractIdReopenPostErrors[keyof ReopenCycleApiV1ReviewCyclesContractIdReopenPostErrors];

export type ReopenCycleApiV1ReviewCyclesContractIdReopenPostResponses = {
    /**
     * Successful Response
     */
    200: CycleApprovalResponse;
};

export type ReopenCycleApiV1ReviewCyclesContractIdReopenPostResponse = ReopenCycleApiV1ReviewCyclesContractIdReopenPostResponses[keyof ReopenCycleApiV1ReviewCyclesContractIdReopenPostResponses];

export type ReadUpcomingApiV1ReviewCyclesContractIdUpcomingGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Contract Id
         */
        contract_id: string;
    };
    query?: {
        /**
         * Cycle Month
         *
         * The cycle to read, written `YYYY-MM`. Defaults to the current month in São Paulo, which is the cutoff an analyst is working on.
         */
        cycle_month?: string | null;
    };
    url: '/api/v1/review/cycles/{contract_id}/upcoming';
};

export type ReadUpcomingApiV1ReviewCyclesContractIdUpcomingGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ReadUpcomingApiV1ReviewCyclesContractIdUpcomingGetError = ReadUpcomingApiV1ReviewCyclesContractIdUpcomingGetErrors[keyof ReadUpcomingApiV1ReviewCyclesContractIdUpcomingGetErrors];

export type ReadUpcomingApiV1ReviewCyclesContractIdUpcomingGetResponses = {
    /**
     * Successful Response
     */
    200: ReviewItemListResponse;
};

export type ReadUpcomingApiV1ReviewCyclesContractIdUpcomingGetResponse = ReadUpcomingApiV1ReviewCyclesContractIdUpcomingGetResponses[keyof ReadUpcomingApiV1ReviewCyclesContractIdUpcomingGetResponses];

export type ApproveItemApiV1ReviewItemsDeltaIdApprovePostData = {
    body?: never;
    headers?: {
        /**
         * Idempotency-Key
         *
         * Opaque, caller-chosen key that makes this write safe to retry. Repeating a request with the same key returns the original response and decides nothing a second time. Reusing a key with a different body is rejected with `idempotency_key_reused`.
         */
        'Idempotency-Key'?: string | null;
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Delta Id
         */
        delta_id: string;
    };
    query?: never;
    url: '/api/v1/review/items/{delta_id}/approve';
};

export type ApproveItemApiV1ReviewItemsDeltaIdApprovePostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ApproveItemApiV1ReviewItemsDeltaIdApprovePostError = ApproveItemApiV1ReviewItemsDeltaIdApprovePostErrors[keyof ApproveItemApiV1ReviewItemsDeltaIdApprovePostErrors];

export type ApproveItemApiV1ReviewItemsDeltaIdApprovePostResponses = {
    /**
     * Successful Response
     */
    200: DeltaResponse;
};

export type ApproveItemApiV1ReviewItemsDeltaIdApprovePostResponse = ApproveItemApiV1ReviewItemsDeltaIdApprovePostResponses[keyof ApproveItemApiV1ReviewItemsDeltaIdApprovePostResponses];

export type CorrectEffectiveDateApiV1ReviewItemsDeltaIdEffectiveDatePostData = {
    body: EffectiveDateCorrection;
    headers?: {
        /**
         * Idempotency-Key
         *
         * Opaque, caller-chosen key that makes this write safe to retry. Repeating a request with the same key returns the original response and decides nothing a second time. Reusing a key with a different body is rejected with `idempotency_key_reused`.
         */
        'Idempotency-Key'?: string | null;
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Delta Id
         */
        delta_id: string;
    };
    query?: never;
    url: '/api/v1/review/items/{delta_id}/effective-date';
};

export type CorrectEffectiveDateApiV1ReviewItemsDeltaIdEffectiveDatePostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type CorrectEffectiveDateApiV1ReviewItemsDeltaIdEffectiveDatePostError = CorrectEffectiveDateApiV1ReviewItemsDeltaIdEffectiveDatePostErrors[keyof CorrectEffectiveDateApiV1ReviewItemsDeltaIdEffectiveDatePostErrors];

export type CorrectEffectiveDateApiV1ReviewItemsDeltaIdEffectiveDatePostResponses = {
    /**
     * Successful Response
     */
    200: DeltaResponse;
};

export type CorrectEffectiveDateApiV1ReviewItemsDeltaIdEffectiveDatePostResponse = CorrectEffectiveDateApiV1ReviewItemsDeltaIdEffectiveDatePostResponses[keyof CorrectEffectiveDateApiV1ReviewItemsDeltaIdEffectiveDatePostResponses];

export type RejectItemApiV1ReviewItemsDeltaIdRejectPostData = {
    body: ReviewRejection;
    headers?: {
        /**
         * Idempotency-Key
         *
         * Opaque, caller-chosen key that makes this write safe to retry. Repeating a request with the same key returns the original response and decides nothing a second time. Reusing a key with a different body is rejected with `idempotency_key_reused`.
         */
        'Idempotency-Key'?: string | null;
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Delta Id
         */
        delta_id: string;
    };
    query?: never;
    url: '/api/v1/review/items/{delta_id}/reject';
};

export type RejectItemApiV1ReviewItemsDeltaIdRejectPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type RejectItemApiV1ReviewItemsDeltaIdRejectPostError = RejectItemApiV1ReviewItemsDeltaIdRejectPostErrors[keyof RejectItemApiV1ReviewItemsDeltaIdRejectPostErrors];

export type RejectItemApiV1ReviewItemsDeltaIdRejectPostResponses = {
    /**
     * Successful Response
     */
    200: DeltaResponse;
};

export type RejectItemApiV1ReviewItemsDeltaIdRejectPostResponse = RejectItemApiV1ReviewItemsDeltaIdRejectPostResponses[keyof RejectItemApiV1ReviewItemsDeltaIdRejectPostResponses];

export type ReturnItemApiV1ReviewItemsDeltaIdReturnPostData = {
    body: ReviewReturn;
    headers?: {
        /**
         * Idempotency-Key
         *
         * Opaque, caller-chosen key that makes this write safe to retry. Repeating a request with the same key returns the original response and decides nothing a second time. Reusing a key with a different body is rejected with `idempotency_key_reused`.
         */
        'Idempotency-Key'?: string | null;
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Delta Id
         */
        delta_id: string;
    };
    query?: never;
    url: '/api/v1/review/items/{delta_id}/return';
};

export type ReturnItemApiV1ReviewItemsDeltaIdReturnPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ReturnItemApiV1ReviewItemsDeltaIdReturnPostError = ReturnItemApiV1ReviewItemsDeltaIdReturnPostErrors[keyof ReturnItemApiV1ReviewItemsDeltaIdReturnPostErrors];

export type ReturnItemApiV1ReviewItemsDeltaIdReturnPostResponses = {
    /**
     * Successful Response
     */
    200: DeltaResponse;
};

export type ReturnItemApiV1ReviewItemsDeltaIdReturnPostResponse = ReturnItemApiV1ReviewItemsDeltaIdReturnPostResponses[keyof ReturnItemApiV1ReviewItemsDeltaIdReturnPostResponses];

export type ReadQueueApiV1ReviewQueueGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Cycle Month
         *
         * The cycle to read, written `YYYY-MM`. Defaults to the current month in São Paulo, which is the cutoff an analyst is working on.
         */
        cycle_month?: string | null;
        /**
         * Limit
         */
        limit?: number;
        /**
         * Offset
         */
        offset?: number;
    };
    url: '/api/v1/review/queue';
};

export type ReadQueueApiV1ReviewQueueGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ReadQueueApiV1ReviewQueueGetError = ReadQueueApiV1ReviewQueueGetErrors[keyof ReadQueueApiV1ReviewQueueGetErrors];

export type ReadQueueApiV1ReviewQueueGetResponses = {
    /**
     * Successful Response
     */
    200: ReviewQueueResponse;
};

export type ReadQueueApiV1ReviewQueueGetResponse = ReadQueueApiV1ReviewQueueGetResponses[keyof ReadQueueApiV1ReviewQueueGetResponses];

export type ListLayoutsApiV1SnapshotLayoutsGetData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/snapshot-layouts';
};

export type ListLayoutsApiV1SnapshotLayoutsGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ListLayoutsApiV1SnapshotLayoutsGetError = ListLayoutsApiV1SnapshotLayoutsGetErrors[keyof ListLayoutsApiV1SnapshotLayoutsGetErrors];

export type ListLayoutsApiV1SnapshotLayoutsGetResponses = {
    /**
     * Successful Response
     */
    200: LayoutListResponse;
};

export type ListLayoutsApiV1SnapshotLayoutsGetResponse = ListLayoutsApiV1SnapshotLayoutsGetResponses[keyof ListLayoutsApiV1SnapshotLayoutsGetResponses];

export type ListSnapshotsApiV1SnapshotsGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Contract Id
         *
         * Only this contract's.
         */
        contract_id?: string | null;
        /**
         * Status
         *
         * Only snapshots in this state.
         */
        status?: SnapshotStatus | null;
        /**
         * Cycle Month
         *
         * Only this cycle, written `YYYY-MM`.
         */
        cycle_month?: string | null;
        /**
         * Limit
         */
        limit?: number;
        /**
         * Offset
         */
        offset?: number;
    };
    url: '/api/v1/snapshots';
};

export type ListSnapshotsApiV1SnapshotsGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ListSnapshotsApiV1SnapshotsGetError = ListSnapshotsApiV1SnapshotsGetErrors[keyof ListSnapshotsApiV1SnapshotsGetErrors];

export type ListSnapshotsApiV1SnapshotsGetResponses = {
    /**
     * Successful Response
     */
    200: SnapshotListResponse;
};

export type ListSnapshotsApiV1SnapshotsGetResponse = ListSnapshotsApiV1SnapshotsGetResponses[keyof ListSnapshotsApiV1SnapshotsGetResponses];

export type GetSnapshotApiV1SnapshotsSnapshotIdGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Snapshot Id
         */
        snapshot_id: string;
    };
    query?: never;
    url: '/api/v1/snapshots/{snapshot_id}';
};

export type GetSnapshotApiV1SnapshotsSnapshotIdGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type GetSnapshotApiV1SnapshotsSnapshotIdGetError = GetSnapshotApiV1SnapshotsSnapshotIdGetErrors[keyof GetSnapshotApiV1SnapshotsSnapshotIdGetErrors];

export type GetSnapshotApiV1SnapshotsSnapshotIdGetResponses = {
    /**
     * Successful Response
     */
    200: SnapshotDetailResponse;
};

export type GetSnapshotApiV1SnapshotsSnapshotIdGetResponse = GetSnapshotApiV1SnapshotsSnapshotIdGetResponses[keyof GetSnapshotApiV1SnapshotsSnapshotIdGetResponses];

export type ApproveSnapshotApiV1SnapshotsSnapshotIdApprovePostData = {
    body?: SnapshotApproval;
    headers?: {
        /**
         * Idempotency-Key
         *
         * Opaque, caller-chosen key that makes this write safe to retry. Repeating a request with the same key returns the original response and generates nothing a second time. Reusing a key with a different body is rejected with `idempotency_key_reused`.
         */
        'Idempotency-Key'?: string | null;
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Snapshot Id
         */
        snapshot_id: string;
    };
    query?: never;
    url: '/api/v1/snapshots/{snapshot_id}/approve';
};

export type ApproveSnapshotApiV1SnapshotsSnapshotIdApprovePostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ApproveSnapshotApiV1SnapshotsSnapshotIdApprovePostError = ApproveSnapshotApiV1SnapshotsSnapshotIdApprovePostErrors[keyof ApproveSnapshotApiV1SnapshotsSnapshotIdApprovePostErrors];

export type ApproveSnapshotApiV1SnapshotsSnapshotIdApprovePostResponses = {
    /**
     * Successful Response
     */
    200: SnapshotDetailResponse;
};

export type ApproveSnapshotApiV1SnapshotsSnapshotIdApprovePostResponse = ApproveSnapshotApiV1SnapshotsSnapshotIdApprovePostResponses[keyof ApproveSnapshotApiV1SnapshotsSnapshotIdApprovePostResponses];

export type CompareSnapshotApiV1SnapshotsSnapshotIdComparisonGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Snapshot Id
         */
        snapshot_id: string;
    };
    query?: never;
    url: '/api/v1/snapshots/{snapshot_id}/comparison';
};

export type CompareSnapshotApiV1SnapshotsSnapshotIdComparisonGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type CompareSnapshotApiV1SnapshotsSnapshotIdComparisonGetError = CompareSnapshotApiV1SnapshotsSnapshotIdComparisonGetErrors[keyof CompareSnapshotApiV1SnapshotsSnapshotIdComparisonGetErrors];

export type CompareSnapshotApiV1SnapshotsSnapshotIdComparisonGetResponses = {
    /**
     * Successful Response
     */
    200: SnapshotComparisonResponse;
};

export type CompareSnapshotApiV1SnapshotsSnapshotIdComparisonGetResponse = CompareSnapshotApiV1SnapshotsSnapshotIdComparisonGetResponses[keyof CompareSnapshotApiV1SnapshotsSnapshotIdComparisonGetResponses];

export type DownloadSnapshotFileApiV1SnapshotsSnapshotIdFileGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Snapshot Id
         */
        snapshot_id: string;
    };
    query?: never;
    url: '/api/v1/snapshots/{snapshot_id}/file';
};

export type DownloadSnapshotFileApiV1SnapshotsSnapshotIdFileGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type DownloadSnapshotFileApiV1SnapshotsSnapshotIdFileGetError = DownloadSnapshotFileApiV1SnapshotsSnapshotIdFileGetErrors[keyof DownloadSnapshotFileApiV1SnapshotsSnapshotIdFileGetErrors];

export type DownloadSnapshotFileApiV1SnapshotsSnapshotIdFileGetResponses = {
    /**
     * The rendered file.
     */
    200: Blob | File;
};

export type DownloadSnapshotFileApiV1SnapshotsSnapshotIdFileGetResponse = DownloadSnapshotFileApiV1SnapshotsSnapshotIdFileGetResponses[keyof DownloadSnapshotFileApiV1SnapshotsSnapshotIdFileGetResponses];

export type DownloadTemplateApiV1SpreadsheetUploadsTemplateGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Format
         *
         * `xlsx` for a workbook, `csv` for a semicolon-separated file.
         */
        format?: string;
    };
    url: '/api/v1/spreadsheet-uploads/template';
};

export type DownloadTemplateApiV1SpreadsheetUploadsTemplateGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type DownloadTemplateApiV1SpreadsheetUploadsTemplateGetError = DownloadTemplateApiV1SpreadsheetUploadsTemplateGetErrors[keyof DownloadTemplateApiV1SpreadsheetUploadsTemplateGetErrors];

export type DownloadTemplateApiV1SpreadsheetUploadsTemplateGetResponses = {
    /**
     * The blank template.
     */
    200: Blob | File;
};

export type DownloadTemplateApiV1SpreadsheetUploadsTemplateGetResponse = DownloadTemplateApiV1SpreadsheetUploadsTemplateGetResponses[keyof DownloadTemplateApiV1SpreadsheetUploadsTemplateGetResponses];

export type ReadCalendarApiV1SubmissionsGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Cycle Month
         *
         * The month to read, written `YYYY-MM`. Defaults to the current month in São Paulo, which is the cycle being worked on.
         */
        cycle_month?: string | null;
        /**
         * Limit
         */
        limit?: number;
        /**
         * Offset
         */
        offset?: number;
    };
    url: '/api/v1/submissions';
};

export type ReadCalendarApiV1SubmissionsGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ReadCalendarApiV1SubmissionsGetError = ReadCalendarApiV1SubmissionsGetErrors[keyof ReadCalendarApiV1SubmissionsGetErrors];

export type ReadCalendarApiV1SubmissionsGetResponses = {
    /**
     * Successful Response
     */
    200: SubmissionCalendarResponse;
};

export type ReadCalendarApiV1SubmissionsGetResponse = ReadCalendarApiV1SubmissionsGetResponses[keyof ReadCalendarApiV1SubmissionsGetResponses];

export type ReadConsolidatedStandingApiV1SubmissionsCompaniesCompanyIdInsurersInsurerIdGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Company Id
         */
        company_id: string;
        /**
         * Insurer Id
         */
        insurer_id: string;
    };
    query?: {
        /**
         * Cycle Month
         *
         * The month to read, written `YYYY-MM`. Defaults to the current month in São Paulo, which is the cycle being worked on.
         */
        cycle_month?: string | null;
    };
    url: '/api/v1/submissions/companies/{company_id}/insurers/{insurer_id}';
};

export type ReadConsolidatedStandingApiV1SubmissionsCompaniesCompanyIdInsurersInsurerIdGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ReadConsolidatedStandingApiV1SubmissionsCompaniesCompanyIdInsurersInsurerIdGetError = ReadConsolidatedStandingApiV1SubmissionsCompaniesCompanyIdInsurersInsurerIdGetErrors[keyof ReadConsolidatedStandingApiV1SubmissionsCompaniesCompanyIdInsurersInsurerIdGetErrors];

export type ReadConsolidatedStandingApiV1SubmissionsCompaniesCompanyIdInsurersInsurerIdGetResponses = {
    /**
     * Successful Response
     */
    200: ConsolidatedStandingResponse;
};

export type ReadConsolidatedStandingApiV1SubmissionsCompaniesCompanyIdInsurersInsurerIdGetResponse = ReadConsolidatedStandingApiV1SubmissionsCompaniesCompanyIdInsurersInsurerIdGetResponses[keyof ReadConsolidatedStandingApiV1SubmissionsCompaniesCompanyIdInsurersInsurerIdGetResponses];

export type PrepareConsolidatedApiV1SubmissionsCompaniesCompanyIdInsurersInsurerIdPreparePostData = {
    body: ConsolidatedPrepare;
    headers?: {
        /**
         * Idempotency-Key
         *
         * Opaque, caller-chosen key that makes this write safe to retry. Repeating a request with the same key returns the original response and records nothing a second time. Reusing a key with a different body is rejected with `idempotency_key_reused`.
         */
        'Idempotency-Key'?: string | null;
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Company Id
         */
        company_id: string;
        /**
         * Insurer Id
         */
        insurer_id: string;
    };
    query?: never;
    url: '/api/v1/submissions/companies/{company_id}/insurers/{insurer_id}/prepare';
};

export type PrepareConsolidatedApiV1SubmissionsCompaniesCompanyIdInsurersInsurerIdPreparePostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type PrepareConsolidatedApiV1SubmissionsCompaniesCompanyIdInsurersInsurerIdPreparePostError = PrepareConsolidatedApiV1SubmissionsCompaniesCompanyIdInsurersInsurerIdPreparePostErrors[keyof PrepareConsolidatedApiV1SubmissionsCompaniesCompanyIdInsurersInsurerIdPreparePostErrors];

export type PrepareConsolidatedApiV1SubmissionsCompaniesCompanyIdInsurersInsurerIdPreparePostResponses = {
    /**
     * Successful Response
     */
    201: ConsolidatedSubmissionResponse;
};

export type PrepareConsolidatedApiV1SubmissionsCompaniesCompanyIdInsurersInsurerIdPreparePostResponse = PrepareConsolidatedApiV1SubmissionsCompaniesCompanyIdInsurersInsurerIdPreparePostResponses[keyof PrepareConsolidatedApiV1SubmissionsCompaniesCompanyIdInsurersInsurerIdPreparePostResponses];

export type ReadConsolidatedApiV1SubmissionsConsolidatedConsolidatedIdGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Consolidated Id
         */
        consolidated_id: string;
    };
    query?: never;
    url: '/api/v1/submissions/consolidated/{consolidated_id}';
};

export type ReadConsolidatedApiV1SubmissionsConsolidatedConsolidatedIdGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ReadConsolidatedApiV1SubmissionsConsolidatedConsolidatedIdGetError = ReadConsolidatedApiV1SubmissionsConsolidatedConsolidatedIdGetErrors[keyof ReadConsolidatedApiV1SubmissionsConsolidatedConsolidatedIdGetErrors];

export type ReadConsolidatedApiV1SubmissionsConsolidatedConsolidatedIdGetResponses = {
    /**
     * Successful Response
     */
    200: ConsolidatedSubmissionResponse;
};

export type ReadConsolidatedApiV1SubmissionsConsolidatedConsolidatedIdGetResponse = ReadConsolidatedApiV1SubmissionsConsolidatedConsolidatedIdGetResponses[keyof ReadConsolidatedApiV1SubmissionsConsolidatedConsolidatedIdGetResponses];

export type MarkConsolidatedFailedApiV1SubmissionsConsolidatedConsolidatedIdFailedPostData = {
    body: SubmissionFailed;
    headers?: {
        /**
         * Idempotency-Key
         *
         * Opaque, caller-chosen key that makes this write safe to retry. Repeating a request with the same key returns the original response and records nothing a second time. Reusing a key with a different body is rejected with `idempotency_key_reused`.
         */
        'Idempotency-Key'?: string | null;
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Consolidated Id
         */
        consolidated_id: string;
    };
    query?: never;
    url: '/api/v1/submissions/consolidated/{consolidated_id}/failed';
};

export type MarkConsolidatedFailedApiV1SubmissionsConsolidatedConsolidatedIdFailedPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type MarkConsolidatedFailedApiV1SubmissionsConsolidatedConsolidatedIdFailedPostError = MarkConsolidatedFailedApiV1SubmissionsConsolidatedConsolidatedIdFailedPostErrors[keyof MarkConsolidatedFailedApiV1SubmissionsConsolidatedConsolidatedIdFailedPostErrors];

export type MarkConsolidatedFailedApiV1SubmissionsConsolidatedConsolidatedIdFailedPostResponses = {
    /**
     * Successful Response
     */
    200: ConsolidatedSubmissionResponse;
};

export type MarkConsolidatedFailedApiV1SubmissionsConsolidatedConsolidatedIdFailedPostResponse = MarkConsolidatedFailedApiV1SubmissionsConsolidatedConsolidatedIdFailedPostResponses[keyof MarkConsolidatedFailedApiV1SubmissionsConsolidatedConsolidatedIdFailedPostResponses];

export type DownloadConsolidatedFileApiV1SubmissionsConsolidatedConsolidatedIdFileGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Consolidated Id
         */
        consolidated_id: string;
    };
    query?: never;
    url: '/api/v1/submissions/consolidated/{consolidated_id}/file';
};

export type DownloadConsolidatedFileApiV1SubmissionsConsolidatedConsolidatedIdFileGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type DownloadConsolidatedFileApiV1SubmissionsConsolidatedConsolidatedIdFileGetError = DownloadConsolidatedFileApiV1SubmissionsConsolidatedConsolidatedIdFileGetErrors[keyof DownloadConsolidatedFileApiV1SubmissionsConsolidatedConsolidatedIdFileGetErrors];

export type DownloadConsolidatedFileApiV1SubmissionsConsolidatedConsolidatedIdFileGetResponses = {
    /**
     * The rendered file.
     */
    200: Blob | File;
};

export type DownloadConsolidatedFileApiV1SubmissionsConsolidatedConsolidatedIdFileGetResponse = DownloadConsolidatedFileApiV1SubmissionsConsolidatedConsolidatedIdFileGetResponses[keyof DownloadConsolidatedFileApiV1SubmissionsConsolidatedConsolidatedIdFileGetResponses];

export type MarkConsolidatedSentApiV1SubmissionsConsolidatedConsolidatedIdSentPostData = {
    body: SubmissionSent;
    headers?: {
        /**
         * Idempotency-Key
         *
         * Opaque, caller-chosen key that makes this write safe to retry. Repeating a request with the same key returns the original response and records nothing a second time. Reusing a key with a different body is rejected with `idempotency_key_reused`.
         */
        'Idempotency-Key'?: string | null;
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Consolidated Id
         */
        consolidated_id: string;
    };
    query?: never;
    url: '/api/v1/submissions/consolidated/{consolidated_id}/sent';
};

export type MarkConsolidatedSentApiV1SubmissionsConsolidatedConsolidatedIdSentPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type MarkConsolidatedSentApiV1SubmissionsConsolidatedConsolidatedIdSentPostError = MarkConsolidatedSentApiV1SubmissionsConsolidatedConsolidatedIdSentPostErrors[keyof MarkConsolidatedSentApiV1SubmissionsConsolidatedConsolidatedIdSentPostErrors];

export type MarkConsolidatedSentApiV1SubmissionsConsolidatedConsolidatedIdSentPostResponses = {
    /**
     * Successful Response
     */
    200: ConsolidatedSubmissionResponse;
};

export type MarkConsolidatedSentApiV1SubmissionsConsolidatedConsolidatedIdSentPostResponse = MarkConsolidatedSentApiV1SubmissionsConsolidatedConsolidatedIdSentPostResponses[keyof MarkConsolidatedSentApiV1SubmissionsConsolidatedConsolidatedIdSentPostResponses];

export type ReadStandingApiV1SubmissionsContractsContractIdGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Contract Id
         */
        contract_id: string;
    };
    query?: {
        /**
         * Cycle Month
         *
         * The month to read, written `YYYY-MM`. Defaults to the current month in São Paulo, which is the cycle being worked on.
         */
        cycle_month?: string | null;
    };
    url: '/api/v1/submissions/contracts/{contract_id}';
};

export type ReadStandingApiV1SubmissionsContractsContractIdGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ReadStandingApiV1SubmissionsContractsContractIdGetError = ReadStandingApiV1SubmissionsContractsContractIdGetErrors[keyof ReadStandingApiV1SubmissionsContractsContractIdGetErrors];

export type ReadStandingApiV1SubmissionsContractsContractIdGetResponses = {
    /**
     * Successful Response
     */
    200: CycleStandingResponse;
};

export type ReadStandingApiV1SubmissionsContractsContractIdGetResponse = ReadStandingApiV1SubmissionsContractsContractIdGetResponses[keyof ReadStandingApiV1SubmissionsContractsContractIdGetResponses];

export type ReadHistoryApiV1SubmissionsContractsContractIdHistoryGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Contract Id
         */
        contract_id: string;
    };
    query?: {
        /**
         * Cycle Month
         *
         * Only this cycle's attempts. Every cycle's when omitted.
         */
        cycle_month?: string | null;
    };
    url: '/api/v1/submissions/contracts/{contract_id}/history';
};

export type ReadHistoryApiV1SubmissionsContractsContractIdHistoryGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ReadHistoryApiV1SubmissionsContractsContractIdHistoryGetError = ReadHistoryApiV1SubmissionsContractsContractIdHistoryGetErrors[keyof ReadHistoryApiV1SubmissionsContractsContractIdHistoryGetErrors];

export type ReadHistoryApiV1SubmissionsContractsContractIdHistoryGetResponses = {
    /**
     * Successful Response
     */
    200: SubmissionListResponse;
};

export type ReadHistoryApiV1SubmissionsContractsContractIdHistoryGetResponse = ReadHistoryApiV1SubmissionsContractsContractIdHistoryGetResponses[keyof ReadHistoryApiV1SubmissionsContractsContractIdHistoryGetResponses];

export type PrepareSubmissionApiV1SubmissionsContractsContractIdPreparePostData = {
    body: SubmissionPrepare;
    headers?: {
        /**
         * Idempotency-Key
         *
         * Opaque, caller-chosen key that makes this write safe to retry. Repeating a request with the same key returns the original response and records nothing a second time. Reusing a key with a different body is rejected with `idempotency_key_reused`.
         */
        'Idempotency-Key'?: string | null;
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Contract Id
         */
        contract_id: string;
    };
    query?: never;
    url: '/api/v1/submissions/contracts/{contract_id}/prepare';
};

export type PrepareSubmissionApiV1SubmissionsContractsContractIdPreparePostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type PrepareSubmissionApiV1SubmissionsContractsContractIdPreparePostError = PrepareSubmissionApiV1SubmissionsContractsContractIdPreparePostErrors[keyof PrepareSubmissionApiV1SubmissionsContractsContractIdPreparePostErrors];

export type PrepareSubmissionApiV1SubmissionsContractsContractIdPreparePostResponses = {
    /**
     * Successful Response
     */
    201: SubmissionResponse;
};

export type PrepareSubmissionApiV1SubmissionsContractsContractIdPreparePostResponse = PrepareSubmissionApiV1SubmissionsContractsContractIdPreparePostResponses[keyof PrepareSubmissionApiV1SubmissionsContractsContractIdPreparePostResponses];

export type ReadGroupFileApiV1SubmissionsGroupFilesGroupFileIdGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Group File Id
         */
        group_file_id: string;
    };
    query?: never;
    url: '/api/v1/submissions/group-files/{group_file_id}';
};

export type ReadGroupFileApiV1SubmissionsGroupFilesGroupFileIdGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ReadGroupFileApiV1SubmissionsGroupFilesGroupFileIdGetError = ReadGroupFileApiV1SubmissionsGroupFilesGroupFileIdGetErrors[keyof ReadGroupFileApiV1SubmissionsGroupFilesGroupFileIdGetErrors];

export type ReadGroupFileApiV1SubmissionsGroupFilesGroupFileIdGetResponses = {
    /**
     * Successful Response
     */
    200: GroupSubmissionResponse;
};

export type ReadGroupFileApiV1SubmissionsGroupFilesGroupFileIdGetResponse = ReadGroupFileApiV1SubmissionsGroupFilesGroupFileIdGetResponses[keyof ReadGroupFileApiV1SubmissionsGroupFilesGroupFileIdGetResponses];

export type MarkGroupFileFailedApiV1SubmissionsGroupFilesGroupFileIdFailedPostData = {
    body: SubmissionFailed;
    headers?: {
        /**
         * Idempotency-Key
         *
         * Opaque, caller-chosen key that makes this write safe to retry. Repeating a request with the same key returns the original response and records nothing a second time. Reusing a key with a different body is rejected with `idempotency_key_reused`.
         */
        'Idempotency-Key'?: string | null;
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Group File Id
         */
        group_file_id: string;
    };
    query?: never;
    url: '/api/v1/submissions/group-files/{group_file_id}/failed';
};

export type MarkGroupFileFailedApiV1SubmissionsGroupFilesGroupFileIdFailedPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type MarkGroupFileFailedApiV1SubmissionsGroupFilesGroupFileIdFailedPostError = MarkGroupFileFailedApiV1SubmissionsGroupFilesGroupFileIdFailedPostErrors[keyof MarkGroupFileFailedApiV1SubmissionsGroupFilesGroupFileIdFailedPostErrors];

export type MarkGroupFileFailedApiV1SubmissionsGroupFilesGroupFileIdFailedPostResponses = {
    /**
     * Successful Response
     */
    200: GroupSubmissionResponse;
};

export type MarkGroupFileFailedApiV1SubmissionsGroupFilesGroupFileIdFailedPostResponse = MarkGroupFileFailedApiV1SubmissionsGroupFilesGroupFileIdFailedPostResponses[keyof MarkGroupFileFailedApiV1SubmissionsGroupFilesGroupFileIdFailedPostResponses];

export type DownloadGroupFileApiV1SubmissionsGroupFilesGroupFileIdFileGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Group File Id
         */
        group_file_id: string;
    };
    query?: never;
    url: '/api/v1/submissions/group-files/{group_file_id}/file';
};

export type DownloadGroupFileApiV1SubmissionsGroupFilesGroupFileIdFileGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type DownloadGroupFileApiV1SubmissionsGroupFilesGroupFileIdFileGetError = DownloadGroupFileApiV1SubmissionsGroupFilesGroupFileIdFileGetErrors[keyof DownloadGroupFileApiV1SubmissionsGroupFilesGroupFileIdFileGetErrors];

export type DownloadGroupFileApiV1SubmissionsGroupFilesGroupFileIdFileGetResponses = {
    /**
     * The rendered file.
     */
    200: Blob | File;
};

export type DownloadGroupFileApiV1SubmissionsGroupFilesGroupFileIdFileGetResponse = DownloadGroupFileApiV1SubmissionsGroupFilesGroupFileIdFileGetResponses[keyof DownloadGroupFileApiV1SubmissionsGroupFilesGroupFileIdFileGetResponses];

export type MarkGroupFileSentApiV1SubmissionsGroupFilesGroupFileIdSentPostData = {
    body: GroupSubmissionSent;
    headers?: {
        /**
         * Idempotency-Key
         *
         * Opaque, caller-chosen key that makes this write safe to retry. Repeating a request with the same key returns the original response and records nothing a second time. Reusing a key with a different body is rejected with `idempotency_key_reused`.
         */
        'Idempotency-Key'?: string | null;
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Group File Id
         */
        group_file_id: string;
    };
    query?: never;
    url: '/api/v1/submissions/group-files/{group_file_id}/sent';
};

export type MarkGroupFileSentApiV1SubmissionsGroupFilesGroupFileIdSentPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type MarkGroupFileSentApiV1SubmissionsGroupFilesGroupFileIdSentPostError = MarkGroupFileSentApiV1SubmissionsGroupFilesGroupFileIdSentPostErrors[keyof MarkGroupFileSentApiV1SubmissionsGroupFilesGroupFileIdSentPostErrors];

export type MarkGroupFileSentApiV1SubmissionsGroupFilesGroupFileIdSentPostResponses = {
    /**
     * Successful Response
     */
    200: GroupSubmissionResponse;
};

export type MarkGroupFileSentApiV1SubmissionsGroupFilesGroupFileIdSentPostResponse = MarkGroupFileSentApiV1SubmissionsGroupFilesGroupFileIdSentPostResponses[keyof MarkGroupFileSentApiV1SubmissionsGroupFilesGroupFileIdSentPostResponses];

export type ReadGroupStandingApiV1SubmissionsGroupsGroupIdInsurersInsurerIdGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Group Id
         */
        group_id: string;
        /**
         * Insurer Id
         */
        insurer_id: string;
    };
    query?: {
        /**
         * Cycle Month
         *
         * The month to read, written `YYYY-MM`. Defaults to the current month in São Paulo, which is the cycle being worked on.
         */
        cycle_month?: string | null;
    };
    url: '/api/v1/submissions/groups/{group_id}/insurers/{insurer_id}';
};

export type ReadGroupStandingApiV1SubmissionsGroupsGroupIdInsurersInsurerIdGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ReadGroupStandingApiV1SubmissionsGroupsGroupIdInsurersInsurerIdGetError = ReadGroupStandingApiV1SubmissionsGroupsGroupIdInsurersInsurerIdGetErrors[keyof ReadGroupStandingApiV1SubmissionsGroupsGroupIdInsurersInsurerIdGetErrors];

export type ReadGroupStandingApiV1SubmissionsGroupsGroupIdInsurersInsurerIdGetResponses = {
    /**
     * Successful Response
     */
    200: GroupStandingResponse;
};

export type ReadGroupStandingApiV1SubmissionsGroupsGroupIdInsurersInsurerIdGetResponse = ReadGroupStandingApiV1SubmissionsGroupsGroupIdInsurersInsurerIdGetResponses[keyof ReadGroupStandingApiV1SubmissionsGroupsGroupIdInsurersInsurerIdGetResponses];

export type PrepareGroupFileApiV1SubmissionsGroupsGroupIdInsurersInsurerIdPreparePostData = {
    body: GroupPrepare;
    headers?: {
        /**
         * Idempotency-Key
         *
         * Opaque, caller-chosen key that makes this write safe to retry. Repeating a request with the same key returns the original response and records nothing a second time. Reusing a key with a different body is rejected with `idempotency_key_reused`.
         */
        'Idempotency-Key'?: string | null;
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Group Id
         */
        group_id: string;
        /**
         * Insurer Id
         */
        insurer_id: string;
    };
    query?: never;
    url: '/api/v1/submissions/groups/{group_id}/insurers/{insurer_id}/prepare';
};

export type PrepareGroupFileApiV1SubmissionsGroupsGroupIdInsurersInsurerIdPreparePostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type PrepareGroupFileApiV1SubmissionsGroupsGroupIdInsurersInsurerIdPreparePostError = PrepareGroupFileApiV1SubmissionsGroupsGroupIdInsurersInsurerIdPreparePostErrors[keyof PrepareGroupFileApiV1SubmissionsGroupsGroupIdInsurersInsurerIdPreparePostErrors];

export type PrepareGroupFileApiV1SubmissionsGroupsGroupIdInsurersInsurerIdPreparePostResponses = {
    /**
     * Successful Response
     */
    201: GroupSubmissionResponse;
};

export type PrepareGroupFileApiV1SubmissionsGroupsGroupIdInsurersInsurerIdPreparePostResponse = PrepareGroupFileApiV1SubmissionsGroupsGroupIdInsurersInsurerIdPreparePostResponses[keyof PrepareGroupFileApiV1SubmissionsGroupsGroupIdInsurersInsurerIdPreparePostResponses];

export type ReadSubmissionApiV1SubmissionsSubmissionIdGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Submission Id
         */
        submission_id: string;
    };
    query?: never;
    url: '/api/v1/submissions/{submission_id}';
};

export type ReadSubmissionApiV1SubmissionsSubmissionIdGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ReadSubmissionApiV1SubmissionsSubmissionIdGetError = ReadSubmissionApiV1SubmissionsSubmissionIdGetErrors[keyof ReadSubmissionApiV1SubmissionsSubmissionIdGetErrors];

export type ReadSubmissionApiV1SubmissionsSubmissionIdGetResponses = {
    /**
     * Successful Response
     */
    200: SubmissionResponse;
};

export type ReadSubmissionApiV1SubmissionsSubmissionIdGetResponse = ReadSubmissionApiV1SubmissionsSubmissionIdGetResponses[keyof ReadSubmissionApiV1SubmissionsSubmissionIdGetResponses];

export type MarkSubmissionFailedApiV1SubmissionsSubmissionIdFailedPostData = {
    body: SubmissionFailed;
    headers?: {
        /**
         * Idempotency-Key
         *
         * Opaque, caller-chosen key that makes this write safe to retry. Repeating a request with the same key returns the original response and records nothing a second time. Reusing a key with a different body is rejected with `idempotency_key_reused`.
         */
        'Idempotency-Key'?: string | null;
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Submission Id
         */
        submission_id: string;
    };
    query?: never;
    url: '/api/v1/submissions/{submission_id}/failed';
};

export type MarkSubmissionFailedApiV1SubmissionsSubmissionIdFailedPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type MarkSubmissionFailedApiV1SubmissionsSubmissionIdFailedPostError = MarkSubmissionFailedApiV1SubmissionsSubmissionIdFailedPostErrors[keyof MarkSubmissionFailedApiV1SubmissionsSubmissionIdFailedPostErrors];

export type MarkSubmissionFailedApiV1SubmissionsSubmissionIdFailedPostResponses = {
    /**
     * Successful Response
     */
    200: SubmissionResponse;
};

export type MarkSubmissionFailedApiV1SubmissionsSubmissionIdFailedPostResponse = MarkSubmissionFailedApiV1SubmissionsSubmissionIdFailedPostResponses[keyof MarkSubmissionFailedApiV1SubmissionsSubmissionIdFailedPostResponses];

export type MarkSubmissionSentApiV1SubmissionsSubmissionIdSentPostData = {
    body: SubmissionSent;
    headers?: {
        /**
         * Idempotency-Key
         *
         * Opaque, caller-chosen key that makes this write safe to retry. Repeating a request with the same key returns the original response and records nothing a second time. Reusing a key with a different body is rejected with `idempotency_key_reused`.
         */
        'Idempotency-Key'?: string | null;
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * Submission Id
         */
        submission_id: string;
    };
    query?: never;
    url: '/api/v1/submissions/{submission_id}/sent';
};

export type MarkSubmissionSentApiV1SubmissionsSubmissionIdSentPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type MarkSubmissionSentApiV1SubmissionsSubmissionIdSentPostError = MarkSubmissionSentApiV1SubmissionsSubmissionIdSentPostErrors[keyof MarkSubmissionSentApiV1SubmissionsSubmissionIdSentPostErrors];

export type MarkSubmissionSentApiV1SubmissionsSubmissionIdSentPostResponses = {
    /**
     * Successful Response
     */
    200: SubmissionResponse;
};

export type MarkSubmissionSentApiV1SubmissionsSubmissionIdSentPostResponse = MarkSubmissionSentApiV1SubmissionsSubmissionIdSentPostResponses[keyof MarkSubmissionSentApiV1SubmissionsSubmissionIdSentPostResponses];

export type ListUsersApiV1UsersGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path?: never;
    query?: {
        /**
         * Group Id
         *
         * Only the users registered at this company group. Inside a company scope this is always empty, since a group's users belong to no company.
         */
        group_id?: string | null;
    };
    url: '/api/v1/users';
};

export type ListUsersApiV1UsersGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type ListUsersApiV1UsersGetError = ListUsersApiV1UsersGetErrors[keyof ListUsersApiV1UsersGetErrors];

export type ListUsersApiV1UsersGetResponses = {
    /**
     * Successful Response
     */
    200: UserListResponse;
};

export type ListUsersApiV1UsersGetResponse = ListUsersApiV1UsersGetResponses[keyof ListUsersApiV1UsersGetResponses];

export type CreateUserApiV1UsersPostData = {
    body: UserCreate;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/api/v1/users';
};

export type CreateUserApiV1UsersPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type CreateUserApiV1UsersPostError = CreateUserApiV1UsersPostErrors[keyof CreateUserApiV1UsersPostErrors];

export type CreateUserApiV1UsersPostResponses = {
    /**
     * Successful Response
     */
    201: UserResponse;
};

export type CreateUserApiV1UsersPostResponse = CreateUserApiV1UsersPostResponses[keyof CreateUserApiV1UsersPostResponses];

export type GetUserApiV1UsersUserIdGetData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * User Id
         */
        user_id: string;
    };
    query?: never;
    url: '/api/v1/users/{user_id}';
};

export type GetUserApiV1UsersUserIdGetErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type GetUserApiV1UsersUserIdGetError = GetUserApiV1UsersUserIdGetErrors[keyof GetUserApiV1UsersUserIdGetErrors];

export type GetUserApiV1UsersUserIdGetResponses = {
    /**
     * Successful Response
     */
    200: UserResponse;
};

export type GetUserApiV1UsersUserIdGetResponse = GetUserApiV1UsersUserIdGetResponses[keyof GetUserApiV1UsersUserIdGetResponses];

export type DeactivateUserApiV1UsersUserIdDeactivationPostData = {
    body?: never;
    headers?: {
        /**
         * X-Company-Id
         *
         * Identifier of the customer company to act inside. vyttech staff use it to work within one company. A user registered at the scope of a company group must send it, naming one of the active companies currently in its group. For any other customer or machine principal it is redundant and must either be omitted or match its own company.
         */
        'X-Company-Id'?: string | null;
    };
    path: {
        /**
         * User Id
         */
        user_id: string;
    };
    query?: never;
    url: '/api/v1/users/{user_id}/deactivation';
};

export type DeactivateUserApiV1UsersUserIdDeactivationPostErrors = {
    /**
     * Malformed request: a header or a path parameter the server could not read.
     */
    400: ErrorEnvelope;
    /**
     * No credentials, or credentials that are no longer valid. Obtain a new token.
     */
    401: ErrorEnvelope;
    /**
     * Authenticated, but this principal may not do this -- or not to this company.
     */
    403: ErrorEnvelope;
    /**
     * No such resource within this principal's company scope. Another company's record is answered this way too, rather than with a 403 that would confirm it exists.
     */
    404: ErrorEnvelope;
    /**
     * The request conflicts with the current state, or with an earlier idempotency key.
     */
    409: ErrorEnvelope;
    /**
     * The request was well-formed but the server will not act on it. Every field at fault is named in `details[].field`. When the code is `identity_mismatch`, each of those details also carries `registry_value`: what the Receita Federal has registered for that field, so the caller can show the correction rather than only the complaint. When a spreadsheet upload is refused as a whole, each detail carries `reason` -- why the file was refused -- and, for `missing_columns`, the `column` it lacks, or for a ceiling, the `limit` it exceeded.
     */
    422: ErrorEnvelope;
    /**
     * This machine credential has spent its request budget for the current window. `Retry-After` says how many seconds until it is whole again.
     */
    429: ErrorEnvelope;
    /**
     * Something failed that should not have. Nothing about the cause is in the body; quote `correlation_id` when reporting it.
     */
    500: ErrorEnvelope;
};

export type DeactivateUserApiV1UsersUserIdDeactivationPostError = DeactivateUserApiV1UsersUserIdDeactivationPostErrors[keyof DeactivateUserApiV1UsersUserIdDeactivationPostErrors];

export type DeactivateUserApiV1UsersUserIdDeactivationPostResponses = {
    /**
     * Successful Response
     */
    200: UserResponse;
};

export type DeactivateUserApiV1UsersUserIdDeactivationPostResponse = DeactivateUserApiV1UsersUserIdDeactivationPostResponses[keyof DeactivateUserApiV1UsersUserIdDeactivationPostResponses];
